/**
 * The string catalogue. **Vietnamese is the source, English is the frame.**
 *
 * `DESIGN.md` §2 and spec 01 §19: "tiếng Việt trước". The audience is Vietnamese
 * administrators and staff, so `vi` is a complete `Record` and every key in the
 * product exists in it; `en` is a `Partial` of the same key set and falls back to
 * Vietnamese key by key.
 *
 * That asymmetry is the policy expressed in the type system rather than in a
 * comment: it is impossible to add an English string without a Vietnamese one,
 * and possible — deliberately — for English to lag. Today it does not lag; the
 * type says it is allowed to, which is what "a screen is not done when only its
 * English strings exist" means when you make it mechanical.
 *
 * Keys are flat and dotted. A nested object would need a path type to stay
 * type-safe and buys nothing here.
 */

export const vi = {
  'app.name': 'Workforce Pulse',
  'app.subtitle': 'Đăng nhập bằng tài khoản achau',

  'common.retry': 'Thử lại',
  'common.retrying': 'Đang thử lại…',
  'common.signOut': 'Đăng xuất',
  'common.errorCode': 'Mã lỗi',
  'common.correlationId': 'Mã tra cứu',
  'common.contactAdmin': 'Liên hệ quản trị viên của công ty để được hỗ trợ.',
  'common.never': 'chưa từng',
  'common.unknownError': 'Đã xảy ra lỗi. Vui lòng thử lại hoặc báo mã tra cứu cho bộ phận hỗ trợ.',
  'common.networkError': 'Không kết nối được tới máy chủ.',
  'common.retryAfter': 'Thử lại sau {seconds} giây.',
  'common.moreDetail': 'Chi tiết',

  // The tenth state (UI-11). The reader is told the screen stopped, that their data
  // is intact, and what to hand the administrator — never the stack.
  'renderError.title': 'Màn hình này gặp lỗi và đã dừng',
  'renderError.body':
    'Dữ liệu của bạn không bị ảnh hưởng. Thử tải lại; nếu vẫn lỗi, gửi mã tra cứu cho quản trị viên.',

  'department.label': 'Phòng ban',
  'department.all': 'Tất cả phòng ban',
  'department.unreadable': 'Chưa đọc được danh sách phòng ban. Đọc lại trang để thử lại.',

  'locale.label': 'Ngôn ngữ',
  'locale.vi': 'Tiếng Việt',
  'locale.en': 'English',
  'theme.label': 'Giao diện',
  'theme.light': 'Sáng',
  'theme.dark': 'Tối',

  'login.title': 'Đăng nhập',
  'login.username': 'Tên đăng nhập',
  'login.password': 'Mật khẩu',
  'login.submit': 'Đăng nhập',
  'login.submitting': 'Đang đăng nhập…',
  'login.failed': 'Tên đăng nhập hoặc mật khẩu không đúng.',

  'shell.mainNav': 'Điều hướng chính',
  'shell.skipToContent': 'Bỏ qua điều hướng, tới nội dung chính',
  'shell.mainLandmark': 'Nội dung chính',
  'shell.loadingSession': 'Đang xác định quyền truy cập',
  'shell.sessionFailed': 'Không đọc được phiên đăng nhập',
  'shell.signedInAs': 'Đang đăng nhập với tài khoản {name}',
  'shell.role.ceo': 'Ban điều hành',
  'shell.role.manager': 'Quản lý',
  'shell.role.employee': 'Nhân viên',
  'shell.timerSlot': 'Hẹn giờ',
  'shell.timerSlotEmpty': 'Khung này chưa gắn hẹn giờ',
  'shell.company': 'Công ty đang xem',
  'shell.companyOnly': 'Tài khoản này chỉ thuộc một công ty',
  'shell.companyCount': 'Thuộc {count} công ty',
  'shell.statesReference': 'Thư viện trạng thái giao diện',
  'shell.topbarMore': 'Cài đặt và tài khoản',

  'nav.empty.title': 'Không có mục nào khả dụng',
  'nav.empty.body':
    'Tài khoản này chưa được cấp quyền xem mục nào trong Workforce Pulse. Quyền được cấp trong màn hình Chính sách và có ghi nhật ký.',

  // Tiêu đề nhóm trên cạnh bàn (UI-13) — danh từ ngắn, không phải câu.
  'nav.group.daily': 'Hằng ngày',
  'nav.group.people': 'Nhân sự',
  'nav.group.recruiting': 'Tuyển dụng',
  'nav.group.payroll': 'Lương',
  'nav.group.operations': 'Vận hành',
  'nav.group.analytics': 'Phân tích',
  'nav.group.admin': 'Quản trị',
  'nav.group.standingHint': 'Nhóm đang mở vì bạn đang ở trong nhóm này',

  'nav.overview': 'Tổng quan',
  'nav.liveActivity': 'Hoạt động trực tiếp',
  'nav.attendance': 'Chấm công',
  'nav.timeTracking': 'Chấm giờ',
  'nav.productivity': 'Năng suất',
  'nav.applications': 'Ứng dụng',
  'nav.websites': 'Website',
  'nav.devices': 'Thiết bị',
  'nav.alerts': 'Cảnh báo',
  'nav.alertRules': 'Quy tắc cảnh báo',
  'nav.incidents': 'Sự cố',
  'nav.dataRisk': 'Rủi ro dữ liệu',
  'nav.reports': 'Báo cáo',
  'nav.aiInsights': 'Phân tích AI',
  'nav.policies': 'Chính sách',
  'nav.policyCollection': 'Chính sách thu thập',
  'nav.companySettings': 'Cấu hình công ty',
  'nav.auditLog': 'Nhật ký kiểm toán',
  'nav.myActivity': 'Hoạt động của tôi',

  'severity.critical': 'Nghiêm trọng',
  'severity.high': 'Cao',
  'severity.medium': 'Trung bình',
  'severity.low': 'Thấp',
  'severity.info': 'Thông tin',

  'state.skeleton.label': 'Đang tải',
  'state.empty.title': 'Chưa có dữ liệu',

  'state.permissionDenied.title': 'Không có quyền truy cập',
  'state.permissionDenied.WORKFORCE_PERMISSION_DENIED':
    'Tài khoản của bạn không có quyền cần thiết để xem mục này.',
  'state.permissionDenied.WORKFORCE_SCOPE_DENIED':
    'Bạn có quyền xem loại dữ liệu này, nhưng bản ghi được yêu cầu nằm ngoài phạm vi của bạn.',
  'state.permissionDenied.WORKFORCE_SENSITIVE_DATA_DENIED':
    'Mục này chứa dữ liệu nhạy cảm và cần quyền riêng.',
  'state.permissionDenied.WORKFORCE_ACCESS_REASON_REQUIRED':
    'Chính sách của công ty yêu cầu nêu lý do truy cập cho dữ liệu này.',

  'state.companyDisabled.company.title': 'Công ty chưa được kích hoạt',
  'state.companyDisabled.company.body':
    'Công ty {company} chưa được bật cho Workforce Pulse. Tài khoản của bạn không có vấn đề gì.',
  'state.companyDisabled.service.title': 'Dịch vụ đang tạm tắt',
  'state.companyDisabled.service.body':
    'Workforce Pulse đang tắt trên toàn hệ thống này. Đây là trạng thái cấu hình, không phải sự cố của tài khoản bạn.',
  'state.companyDisabled.service.contact': 'Liên hệ đơn vị vận hành hệ thống.',

  'state.dataDelayed.title': 'Dữ liệu đang trễ',
  'state.dataDelayed.agent': 'Dữ liệu từ Agent trên máy trạm đang về chậm.',
  'state.dataDelayed.achau': 'Dữ liệu công việc từ achau chưa đồng bộ được.',
  'state.dataDelayed.processing': 'Số liệu đã tính xong nhưng vẫn còn sự kiện chưa vào kịp.',
  'state.dataDelayed.since': 'Lần cập nhật gần nhất: {when}.',
  'state.dataDelayed.sinceNever': 'Chưa có lần cập nhật thành công nào.',

  'state.partialData.title': 'Dữ liệu chưa đầy đủ',
  'state.partialData.body': 'Con số hiển thị chưa bao gồm {excludes}.',
  'state.partialData.score': 'Độ đầy đủ dữ liệu: {percent}%.',
  'state.partialData.devices': 'Số thiết bị đóng góp dữ liệu: {count}.',

  'state.agentOffline.stale.title': 'Agent im lặng',
  'state.agentOffline.offline.title': 'Agent ngoại tuyến',
  'state.agentOffline.stale.body':
    'Thiết bị {device} đã bỏ lỡ vài nhịp tín hiệu. Đây là dấu hiệu sớm, chưa phải sự cố.',
  'state.agentOffline.offline.body':
    'Thiết bị {device} đã ngừng gửi dữ liệu. Khoảng thời gian này KHÔNG có nghĩa là nhân viên không làm việc.',
  'state.agentOffline.lastHeartbeat': 'Tín hiệu gần nhất: {when}.',
  'state.agentOffline.neverHeard': 'Chưa từng nhận được tín hiệu nào từ thiết bị này.',
  'state.agentOffline.device': 'thiết bị này',

  'state.lastSync.title': 'Đồng bộ gần nhất',
  'state.lastSync.agent': 'Agent',
  'state.lastSync.achau': 'achau',

  'companyBlocked.retryHint': 'Nếu quản trị viên vừa bật dịch vụ, bấm Thử lại.',

  'selectCompany.title': 'Cần chọn công ty',
  'selectCompany.body':
    'Tài khoản của bạn thuộc {count} công ty. Một phiên đăng nhập chỉ đọc dữ liệu của đúng một công ty.',
  'selectCompany.unavailable':
    'Hiện chưa đổi được công ty ở màn hình này. Liên hệ quản trị viên nếu bạn cần làm việc với một công ty khác.',
  'selectCompany.enabled': 'Đang hoạt động',
  'selectCompany.disabled': 'Chưa kích hoạt',

  'placeholder.title': 'Màn hình {screen}',
  'placeholder.body':
    'Khung điều hướng, quyền và các trạng thái dùng chung đã sẵn sàng. Nội dung của màn hình này do task {task} xây dựng.',

  'states.title': 'Thư viện trạng thái giao diện',
  'states.intro':
    'Trang tham khảo dành cho người kiểm duyệt giao diện: mười trạng thái bắt buộc, dựng bằng dữ liệu mẫu cố định. Trang này không đọc dữ liệu của công ty nào.',
  'states.driver': 'Nguồn dữ liệu',
  'states.name.skeleton': '1. Đang tải (skeleton)',
  'states.name.empty': '2. Không có dữ liệu',
  'states.name.permissionDenied': '3. Không có quyền',
  'states.name.companyDisabled': '4. Công ty chưa kích hoạt',
  'states.name.dataDelayed': '5. Dữ liệu trễ',
  'states.name.partialData': '6. Dữ liệu chưa đầy đủ',
  'states.name.agentOffline': '7. Agent ngoại tuyến',
  'states.name.retry': '8. Thử lại',
  'states.name.lastSync': '9. Đồng bộ gần nhất',
  'states.name.renderError': '10. Lỗi hiển thị',
  'states.sample.throw': 'Gây lỗi thử',
  'states.sample.throwHint':
    'Bấm để một thành phần con ném lỗi ngay lúc vẽ. Chỉ ô này đổi sang thông báo lỗi — cạnh bàn, thanh trên và phần còn lại của trang vẫn dùng được, đúng như khi một màn hình thật hỏng.',
  'states.sample.throwOk': 'Thành phần con đang vẽ bình thường.',
  'states.sample.emptyTitle': 'Chưa có thiết bị nào được đăng ký',
  'states.sample.emptyBody': 'Cấp một mã đăng ký ở màn hình Thiết bị, rồi cài Agent lên máy trạm.',
  'states.sample.company': 'Công ty Mẫu',
  'states.sample.excludes': 'khoảng 09:00–11:20, khi thiết bị thứ hai không gửi dữ liệu',

  /* ── the reference page's primitive rack (Wave 1, group A) ─────────────── */
  'states.nine.title': 'Mười trạng thái',
  'states.prim.title': 'Thành phần và token',
  'states.prim.intro':
    'Mỗi mẫu dưới đây là lớp thật và thành phần thật, không phải bản vẽ lại. Một trang tham chiếu tự vẽ lấy cái nút của nó sẽ vẫn trông đúng đúng vào ngày cái nút thật hỏng.',
  'states.prim.buttons': 'Nút',
  'states.prim.button.primary': 'Hành động chính',
  'states.prim.button.secondary': 'Hành động phụ',
  'states.prim.button.quiet': 'Lặng',
  'states.prim.button.danger': 'Nguy hiểm',
  'states.prim.button.small': 'Nhỏ (34px)',
  'states.prim.button.disabled': 'Bị vô hiệu hoá',
  'states.prim.button.linklike': 'Như liên kết',
  'states.prim.fields': 'Trường nhập',
  'states.prim.field.label': 'Mã phòng ban',
  'states.prim.field.hint': 'Nhập mã số, không phải tên phòng ban.',
  'states.prim.field.error': 'Mã phòng ban chỉ gồm chữ số.',
  'states.prim.badges': 'Huy hiệu mức độ',
  'states.prim.badge.neutral': 'Trung tính',
  'states.prim.toggles': 'Nhóm chuyển',
  'states.prim.rows': 'Dòng danh sách (44px)',
  'states.prim.row.plain': 'Dòng thường',
  'states.prim.row.selected': 'Dòng đang chọn',
  'states.prim.table': 'Bảng dữ liệu (36px)',
  'states.prim.table.col.name': 'Nhân viên',
  'states.prim.table.col.value': 'Giờ quan sát được',
  'states.prim.table.col.when': 'Bắt đầu',
  'states.prim.tile': 'Ô số liệu',
  'states.prim.tile.title': 'Tỷ lệ hoạt động',
  'states.prim.tile.detail': 'Con số luôn đi kèm nhãn và độ cũ của nó, không kèm đường xu hướng.',
  'states.prim.mono': 'Định danh và mã',
  'states.prim.flag': 'Đã xác minh',
  'states.prim.type': 'Bậc chữ',
  'states.prim.type.display': 'Display 26px / 700 — chỉ dành cho số',
  'states.prim.type.headline': 'Headline 19px / 700 — một tiêu đề mỗi trang',
  'states.prim.type.title': 'Title 15px / 600 — tiêu đề khối',
  'states.prim.type.body': 'Body 13px / 400',
  'states.prim.type.small': 'Small 12px — sàn chức năng',
  'states.prim.type.label': 'Label 11px in hoa — chỉ dùng cho nhãn',
  'states.prim.type.sample': 'Người quản lý đọc bảng này tám tiếng mỗi ngày',
  'states.prim.colour': 'Nền và sắc',
  'states.prim.colour.bg': 'Giấy',
  'states.prim.colour.surface': 'Nền chìm',
  'states.prim.colour.accent': 'Nhấn (bấm được)',
  'states.prim.colour.cta': 'Nút chính',
  'states.prim.colour.rail': 'Cạnh bàn',

  /* ═════════════ WP-K2 (Task 42) — My Activity and Time Tracking ═════════════
   *
   * `DESIGN.md` §2: administrative and neutral. These two screens are the only ones
   * where the person being measured reads their own measurement, so nothing below
   * congratulates, warns about behaviour, or asks somebody to account for a number.
   * Three strings carry most of that weight and were written last, on purpose:
   * `timeline.private.help`, `timer.validation.pending.body` and `score.notScored`.
   */

  'common.hourShort': 'g',
  'common.minuteShort': 'ph',

  'day.label': 'Ngày',
  'day.previous': 'Ngày trước',
  'day.next': 'Ngày sau',
  // The one line under every date box. The native control prints the browser's own
  // format and no attribute changes that (`components/DateField.tsx`), so the product
  // says the chosen day in its own voice underneath.
  'date.chosen': 'Đang chọn: {date}',
  'date.none': 'Chưa chọn ngày',

  'error.failed.title': 'Không đọc được {subject}',
  'error.unavailable.title': 'Chưa có {subject}',
  'error.unavailable.body':
    'Mục này chưa có dữ liệu để hiển thị. Đây không phải lỗi tài khoản của bạn.',

  'attendance.heading': 'Chấm công ngày {date}',
  'attendance.loading': 'Đang tải dữ liệu chấm công',
  'attendance.subject': 'dữ liệu chấm công',
  'attendance.window': 'Bắt đầu {first} · Kết thúc {last}',
  'attendance.noFirst': 'chưa ghi nhận',
  'attendance.noLast': 'chưa ghi nhận',
  'attendance.online': 'Trực tuyến',
  'attendance.active': 'Hoạt động',
  'attendance.idle': 'Chờ máy',
  'attendance.idle.help':
    'Chờ máy (idle): khoảng thời gian không có thao tác chuột hoặc bàn phím, theo ngưỡng công ty đặt. Đây không phải kết luận rằng bạn không làm việc.',
  'attendance.break': 'Nghỉ giữa giờ',
  'attendance.approvedOffline': 'Ngoại tuyến đã duyệt',
  'attendance.overtime': 'Ngoài giờ',
  'attendance.notReported': 'không có số liệu',
  'attendance.empty.title': 'Không có bản ghi chấm công cho ngày này',
  'attendance.empty.body':
    'Ngày này chưa có phiên làm việc nào được ghi nhận. Nếu máy trạm tắt hoặc Agent không chạy thì hệ thống không có dữ liệu để ghi — đó là lý do có nút gửi yêu cầu chỉnh sửa ở đây.',
  'attendance.correct': 'Gửi yêu cầu chỉnh sửa',
  'attendance.excludes.minutes': 'khoảng {minutes} không có dữ liệu từ máy trạm',
  'attendance.excludes.agent': 'khoảng thời gian máy trạm không gửi dữ liệu',
  'attendance.unsynced': 'Số liệu đã tính xong, một số sự kiện của ngày này vẫn đang vào.',
  'attendance.adjustments':
    'Ngày này đang được cộng {minutes} ngoại tuyến đã duyệt, nghĩa là có ít nhất một giải trình đã được duyệt. Số lần điều chỉnh không hiển thị ở đây. Dữ liệu gốc không bị sửa; điều chỉnh được ghi riêng.',
  'attendance.agentDevice': 'máy trạm của bạn',

  /* ── WP-K3 (Task 43) — the attendance grid for HR and managers ──────────────
   *
   * `web/shapes/attendance.md` §5 is the badge table these labels serve. Two
   * properties of the copy below are deliberate and are not house style:
   *
   *  - **Nothing here congratulates and nothing accuses.** `DESIGN.md` §2 asks for
   *    a neutral administrative tone; on the screen where one person reads another
   *    person's day, that is the difference between a record and an allegation.
   *    `on_time` gets two words, and `data_incomplete` says in as many words that
   *    it is a statement about the data.
   *  - **Every absence names its cause.** The twelve `attendance.reason.*` strings
   *    are sentences rather than codes, because they are printed where a number
   *    would otherwise be — and a blank where a number was expected is read as a
   *    zero by everybody who has ever read a spreadsheet.
   */

  'attendance.status.on_time': 'Đúng giờ',
  'attendance.status.late': 'Đi trễ',
  'attendance.status.early_leave': 'Về sớm',
  'attendance.status.late_and_early': 'Trễ và về sớm',
  'attendance.status.absent': 'Vắng mặt',
  'attendance.status.off_day': 'Ngày nghỉ theo ca',
  'attendance.status.leave': 'Nghỉ phép đã duyệt',
  'attendance.status.data_incomplete': 'Thiếu dữ liệu',
  'attendance.status.not_configured': 'Chưa cấu hình ca',

  'attendance.status.help.on_time':
    'Có mặt trong khung giờ của ca, tính cả thời gian ân hạn của ca đó.',
  'attendance.status.help.late':
    'Hoạt động đầu tiên ghi nhận được muộn hơn giờ bắt đầu ca cộng thời gian ân hạn.',
  'attendance.status.help.early_leave':
    'Hoạt động cuối cùng ghi nhận được sớm hơn giờ kết thúc ca trừ thời gian ân hạn.',
  'attendance.status.help.late_and_early': 'Cả hai mốc đều nằm ngoài khung giờ của ca.',
  'attendance.status.help.absent':
    'Thiết bị báo cáo đủ cả ngày và không có thời gian nào được tính. Đây là kết luận duy nhất nói về con người, và chỉ được đưa ra khi dữ liệu đầy đủ.',
  'attendance.status.help.off_day': 'Theo lịch ca, ngày này không ai được xếp làm việc.',
  'attendance.status.help.leave': 'Một giải trình nghỉ phép đã được duyệt cho ngày này.',
  'attendance.status.help.data_incomplete':
    'Hệ thống KHÔNG kết luận được gì về ngày này vì dữ liệu chưa đủ. Đây là tình trạng của dữ liệu, không phải của nhân viên.',
  'attendance.status.help.not_configured':
    'Không có ca nào được cấu hình cho nhân viên này trong ngày này, nên không có gì để đối chiếu. Cần sửa cấu hình ca, không phải gửi giải trình.',

  'attendance.reason.no_shift_configured': 'chưa cấu hình ca cho ngày này',
  'attendance.reason.not_a_working_day': 'không phải ngày làm việc theo ca',
  'attendance.reason.no_session_row': 'chưa có bản ghi phiên làm việc nào cho ngày này',
  'attendance.reason.agent_data_missing': 'có thiết bị được kỳ vọng nhưng không gửi dữ liệu về',
  'attendance.reason.no_enrolled_device':
    'không có thiết bị nào đăng ký, nên không quan sát được gì',
  'attendance.reason.incomplete_data': 'dữ liệu quan sát được có phần hỏng nhận diện được',
  'attendance.reason.day_in_progress': 'ca làm việc của ngày này chưa kết thúc',
  'attendance.reason.no_arrival_observed':
    'không quan sát được thời điểm bắt đầu để đối chiếu với ca',
  'attendance.reason.no_departure_observed':
    'không quan sát được thời điểm kết thúc để đối chiếu với ca',
  'attendance.reason.overtime_requires_manual_claim':
    'ca này chỉ ghi nhận ngoài giờ khi có yêu cầu của người dùng',
  'attendance.reason.overtime_not_credited_on_incomplete_data':
    'không cộng ngoài giờ trên một ngày thiếu dữ liệu',
  'attendance.reason.overtime_rule_unrecognised': 'quy tắc ngoài giờ của ca này chưa được hỗ trợ',
  'attendance.reason.stale_source_version':
    'bản ghi được tính bằng phiên bản thuật toán khác với bản đang chạy',

  'attendance.completeness.complete': 'Đầy đủ — quan sát được và không có gì sai.',
  'attendance.completeness.partial': 'Một phần — quan sát được, nhưng có phần hỏng nhận diện được.',
  'attendance.completeness.missing':
    'Thiếu — có thiết bị được kỳ vọng và không có gì được gửi về. Điều này KHÔNG có nghĩa là nhân viên không làm việc.',
  'attendance.completeness.no_data':
    'Không có thiết bị — không máy nào được đăng ký, nên không có gì để quan sát.',
  'attendance.completeness.no_session_row': 'Chưa có bản ghi phiên làm việc nào cho ngày này.',

  'k3.window': 'Đang xem {month}, ngày {date} — {timezone}',
  'k3.filters.legend': 'Bộ lọc',
  'k3.filter.date': 'Ngày',
  'k3.filter.department': 'Phòng ban',
  'k3.filter.employee': 'Nhân viên',
  'k3.filter.status': 'Trạng thái',
  'k3.filter.all': 'Tất cả',
  'k3.filter.apply': 'Áp dụng',
  'k3.filter.note':
    'Bộ lọc phòng ban và nhân viên được gửi cho máy chủ. Bộ lọc trạng thái chỉ lọc những dòng đang hiển thị trong trang này. Không bộ lọc nào ở đây là kiểm quyền: máy chủ quyết định phạm vi trên mọi lần gọi.',

  'k3.weekday.1': 'T2',
  'k3.weekday.2': 'T3',
  'k3.weekday.3': 'T4',
  'k3.weekday.4': 'T5',
  'k3.weekday.5': 'T6',
  'k3.weekday.6': 'T7',
  'k3.weekday.7': 'CN',

  'k3.month.heading': 'Lịch {month}',
  'k3.month.of': 'Lịch {month} — {employee}',
  'k3.month.prev': 'Tháng trước',
  'k3.month.next': 'Tháng sau',
  'k3.month.loading': 'Đang tải lịch tháng',
  'k3.month.subject': 'lịch chấm công theo tháng',
  'k3.month.pickEmployee':
    'Chọn một nhân viên trong danh sách bên dưới để xem lịch tháng của người đó.',
  'k3.month.future': 'chưa đến',
  'k3.month.today': 'Hôm nay',
  'k3.month.allFuture': 'Cả tháng này vẫn chưa đến, nên không có gì để hỏi máy chủ.',
  'k3.month.noCell': 'Ngày này không có ô nào trong trang đã tải.',
  'k3.month.open': 'Xem chi tiết ngày {date}',
  'k3.month.futureNote':
    'Những ngày sau hôm nay không được hỏi máy chủ. Một ngày chưa xảy ra không có bản ghi phiên nào, và vẽ nó thành "thiếu dữ liệu" là biến một ngày chưa đến thành một lỗ hổng dữ liệu.',

  'k3.roster.heading': 'Ngày {date}',
  'k3.roster.loading': 'Đang tải danh sách chấm công theo ngày',
  'k3.roster.subject': 'danh sách chấm công theo ngày',
  'k3.roster.empty.title': 'Phạm vi của bạn không có nhân viên nào cho ngày này',
  'k3.roster.empty.body':
    'Máy chủ đã trả lời và không có nhân viên nào trong phạm vi của tài khoản này. Đây là câu trả lời, không phải sự cố.',
  'k3.roster.col.employee': 'Nhân viên',
  'k3.roster.col.status': 'Trạng thái',
  'k3.roster.col.first': 'Vào',
  'k3.roster.col.last': 'Ra',
  'k3.roster.col.online': 'Trực tuyến',
  'k3.roster.col.missing': 'Thiếu so với ca',
  'k3.roster.select': 'Xem lịch tháng của {name}',
  'k3.roster.open': 'Mở chi tiết ngày {date} của {name}',
  'k3.roster.truncated': '{shown} trong tổng số {total} ô của khoảng thời gian này',
  'k3.roster.statusFilter':
    'Đang lọc theo trạng thái "{status}". Bộ lọc chạy trên {shown} dòng đã tải, không phải trên toàn bộ {total} ô của khoảng thời gian.',
  'k3.roster.noneMatchStatus':
    'Không dòng nào trong trang này ở trạng thái đã chọn. Trang khác vẫn có thể có.',

  'k3.drawer.title': '{employee} · {date}',
  'k3.drawer.close': 'Đóng',
  'k3.drawer.loading': 'Đang tải chi tiết ngày',
  'k3.drawer.subject': 'chi tiết chấm công của ngày này',
  'k3.drawer.window': 'Vào {first} · Ra {last}',
  'k3.drawer.shift': 'Ca làm việc',
  'k3.drawer.shift.assigned': 'Ca {code}, gán theo {scope} ({key}), ưu tiên {priority}',
  'k3.drawer.shift.system_default':
    'Ca {code} — ca mặc định của hệ thống; không có phân công nào khớp',
  'k3.drawer.shift.off_duty': 'Không ai được xếp làm việc ngày này',
  'k3.drawer.shift.unconfigured':
    'Không có ca nào được cấu hình cho ngày này, nên không có gì để đối chiếu',
  'k3.drawer.shift.window': '{start} – {end} ({timezone}), dài {elapsed}',
  'k3.drawer.shift.grace': 'Ân hạn: đi trễ {late}, về sớm {early}',
  'k3.drawer.metrics': 'Số liệu theo ca',
  'k3.metric.late_minutes': 'Đi trễ',
  'k3.metric.early_leave_minutes': 'Về sớm',
  'k3.metric.missing_minutes': 'Thiếu so với ca',
  'k3.metric.overtime_minutes': 'Ngoài giờ',
  'k3.metric.withheld': 'không công bố — {reason}',
  'k3.metric.unexplained':
    'không có số liệu, và máy chủ không nêu lý do. Đây vẫn là một khoảng trống, không phải số 0.',
  'k3.drawer.source': 'Nguồn dữ liệu',
  'k3.drawer.source.help':
    'Khối này cho biết con số ở trên đến từ đâu và có bao nhiêu phần của ngày quan sát được. Nó là cách phân biệt "người này vắng mặt" với "chúng ta không nhìn thấy người này".',
  'k3.drawer.source.rowPresent': 'Có bản ghi phiên làm việc cho ngày này.',
  'k3.drawer.source.rowAbsent':
    'Không có bản ghi phiên làm việc nào cho ngày này, nên các số liệu theo ca ở trên bị giữ lại — chúng không bằng 0.',
  'k3.drawer.source.version':
    'Phiên bản thuật toán của bản ghi: {stored}; bản đang chạy: {current}.',
  'k3.drawer.source.stale':
    'Bản ghi này do một phiên bản thuật toán khác tạo ra. Kết luận "vắng mặt" được giữ lại cho tới khi tính lại; các số liệu khác vẫn đứng.',
  'k3.drawer.source.calculated': 'Tính lúc {when}.',
  'k3.drawer.source.calculatedNever': 'Chưa từng được tính.',
  'k3.drawer.source.devices': 'Thiết bị đóng góp: {devices}.',
  'k3.drawer.source.noDevices': 'Không thiết bị nào đóng góp dữ liệu cho ngày này.',
  'k3.drawer.source.policy': 'Chính sách nhiều thiết bị: {policy}.',
  'k3.drawer.source.lastEvent': 'Sự kiện cuối nhận lúc {when}.',
  'k3.drawer.source.lastEventNever': 'Chưa nhận được sự kiện nào cho ngày này.',
  'k3.drawer.completeness': 'Độ đầy đủ dữ liệu',
  'k3.drawer.completeness.help':
    'Độ đầy đủ cho biết bao nhiêu phần của ngày quan sát được. Con số chấm công không kèm độ đầy đủ là con số nói dối.',
  'k3.drawer.completeness.score': 'Điểm đầy đủ: {percent}%.',
  'k3.drawer.completeness.devices': '{count} thiết bị · {events} sự kiện.',
  'k3.drawer.completeness.gap': 'Khoảng trống dữ liệu: {gap}.',
  'k3.drawer.completeness.clockAnomaly':
    'Phát hiện bất thường về đồng hồ trên thiết bị trong ngày này.',
  'k3.drawer.credited':
    'Ngày này đang được cộng {minutes} ngoại tuyến đã duyệt, nghĩa là có ít nhất một giải trình đã được duyệt.',
  'k3.drawer.correctionSelfOnly':
    'Giải trình là lời khai của chính nhân viên về ngày của họ. Máy chủ từ chối gửi hộ người khác với mọi quyền, nên màn hình này không có nút gửi hộ; nhân viên gửi từ màn hình Hoạt động của tôi.',

  'k3.queue.heading': 'Hàng đợi giải trình',
  'k3.queue.noList':
    'Nhập mã giải trình để mở một bản. Mã nằm trong phản hồi lúc nhân viên gửi và trong màn hình Nhật ký kiểm toán.',
  'k3.queue.twoLevel':
    'Quy trình hai cấp: quản lý quyết định trước, HR quyết định sau nếu bản ghi được chụp lại là cần HR vào lúc gửi.',
  'k3.queue.idLabel': 'Mã giải trình',
  'k3.queue.idHint':
    'Mã nằm trong phản hồi nhân viên nhận được lúc gửi, và ở màn Nhật ký kiểm toán.',
  'k3.queue.open': 'Mở',
  'k3.queue.subject': 'giải trình chấm công',
  'k3.queue.requested': 'Yêu cầu {minutes}, từ {start} đến {end}.',
  'k3.queue.requestedLabel': 'Yêu cầu',
  'k3.queue.reasonLabel': 'Lý do',
  'k3.queue.hrLabel': 'Cấp HR',
  'k3.queue.decidedAtLabel': 'Thời điểm',
  'k3.queue.approvedMinutesOut': 'Đã duyệt {minutes}.',
  'k3.queue.reasonCode': 'Lý do: {reason}',
  'k3.queue.note': 'Ghi chú của nhân viên: {note}',
  'k3.queue.noNote': 'Nhân viên không ghi chú gì.',
  'k3.queue.evidence': '{count} tệp bằng chứng đính kèm.',
  'k3.queue.requiresHr.true':
    'Cần HR duyệt. Yêu cầu này được chụp lại lúc gửi và không đọc lại từ chính sách hiện tại của công ty.',
  'k3.queue.requiresHr.false':
    'Không cần HR duyệt. Yêu cầu này được chụp lại lúc gửi và không đọc lại từ chính sách hiện tại của công ty.',
  'k3.queue.noteLabel': 'Ghi chú (bắt buộc)',
  'k3.queue.noteHelp':
    'Bắt buộc ở cả duyệt lẫn từ chối, và được ghi vào nhật ký kiểm toán. Một lần từ chối không nêu lý do là câu trả lời mà nhân viên đáng được nhận nhưng không nhận được.',
  'k3.queue.approvedMinutes': 'Số phút duyệt',
  'k3.queue.approvedMinutesHelp':
    'Để trống là duyệt đúng số đã yêu cầu. Có thể duyệt ÍT hơn, không bao giờ nhiều hơn.',
  'k3.queue.approve': 'Duyệt',
  'k3.queue.confirmApprove': 'Xác nhận duyệt',
  'k3.queue.confirmApprove.help':
    'Duyệt sẽ ghi thẳng vào giờ công đã lưu của nhân viên và xếp hàng tính lại; màn hình này không hoàn tác được. Kiểm tra lại mã giải trình rồi xác nhận.',
  'k3.queue.reject': 'Từ chối',
  'k3.queue.deciding': 'Đang gửi quyết định…',
  'k3.queue.decided': 'Đã ghi nhận. Trạng thái hiện tại: {status}.',
  'k3.queue.decidedAt': 'Quyết định lúc {when}.',
  'k3.queue.selfDecision':
    'Đây là giải trình của chính bạn. Không ai tự duyệt giải trình của mình, ở cả hai cấp và với mọi quyền. Luật này đặt trên NGƯỜI ĐƯỢC GIẢI TRÌNH, nên không lách được bằng cách nhờ người khác gửi hộ — và gửi hộ cũng đã bị từ chối.',
  'k3.queue.outOfScope':
    'Giải trình này thuộc về một nhân viên ngoài phạm vi của bạn. Máy chủ quyết định phạm vi; màn hình này không lọc gì trước, nên câu trả lời bạn đang đọc là của máy chủ.',
  'k3.queue.notFound':
    'Không có giải trình nào mang mã này trong công ty của bạn. Một mã không tồn tại và một mã thuộc công ty khác được trả lời giống hệt nhau, và đó là chủ ý.',
  'k3.queue.invalidState':
    'Giải trình này đã được quyết định. Các chuyển trạng thái còn hợp lệ: {allowed}.',
  'k3.queue.invalidStateNoList': 'Giải trình này đã được quyết định.',

  'k3.approval.pending_manager': 'Chờ quản lý duyệt',
  'k3.approval.pending_hr': 'Chờ HR duyệt',
  'k3.approval.approved': 'Đã duyệt',
  'k3.approval.rejected': 'Đã từ chối',
  'k3.approval.withdrawn': 'Đã rút lại',

  'k3.reasonCode.meeting': 'Họp',
  'k3.reasonCode.phone_call': 'Gọi điện',
  'k3.reasonCode.reading_document': 'Đọc tài liệu',
  'k3.reasonCode.paperwork': 'Giấy tờ',
  'k3.reasonCode.client_visit': 'Đi gặp khách hàng',
  'k3.reasonCode.break': 'Nghỉ giữa giờ',
  'k3.reasonCode.offsite_work': 'Làm ngoài văn phòng',
  'k3.reasonCode.agent_down': 'Agent không chạy',
  'k3.reasonCode.device_issue': 'Sự cố thiết bị',
  'k3.reasonCode.power_outage': 'Mất điện',
  'k3.reasonCode.approved_leave': 'Nghỉ phép đã duyệt',
  'k3.reasonCode.other': 'Khác',

  'k3.export.heading': 'Xuất báo cáo',
  'k3.export.unavailable':
    'Màn hình này chưa có nút xuất tệp. Tạo bản xuất chấm công ở màn hình Báo cáo; việc đó cần quyền xuất báo cáo.',
  'k3.export.willSend':
    'Báo cáo chấm công của bộ lọc đang xem ({from} đến {to}) sẽ được tạo ở màn hình Báo cáo.',
  'k3.export.noLocalFile':
    'Cũng không có bản xuất tải thẳng từ trình duyệt: chỉ tệp tạo ở màn hình Báo cáo mới ghi được ai đã xuất, xuất lúc nào và với bộ lọc nào.',

  'timeline.heading': 'Dòng thời gian theo giờ',
  'timeline.hours.toggle': 'Từng giờ trong ngày ({count} giờ)',
  'timeline.empty.title': 'Chưa có dữ liệu hoạt động cho ngày này',
  'timeline.empty.body': 'Dữ liệu xuất hiện sau khi máy trạm gửi nhịp đầu tiên.',
  'timeline.intro':
    'Số liệu đọc từ bảng tổng hợp theo giờ. Giờ chưa tới không được vẽ; "chưa tính" và "không có dữ liệu" là hai trạng thái khác nhau, và không trạng thái nào trong số đó là số 0.',
  'timeline.loading': 'Đang tải dòng thời gian',
  'timeline.subject': 'dòng thời gian theo giờ',
  'timeline.caption': 'Hoạt động theo giờ, ngày {date}',
  'timeline.col.hour': 'Giờ',
  'timeline.col.shape': 'Tỷ lệ',
  'timeline.col.detail': 'Chi tiết',
  'timeline.col.state': 'Trạng thái dữ liệu',
  'timeline.detail': 'Hoạt động {active} · Chờ máy {idle}',
  'timeline.private': 'Riêng tư {duration}',
  'timeline.private.help':
    'Khoảng thời gian này ĐÃ được tính là thời gian làm việc. Theo chính sách của công ty, tên ứng dụng hoặc tên miền không được thu thập, nên khoảng này không thuộc nhóm phân loại nào — khác với "chưa có quy tắc khớp". Bạn không cần giải trình khoảng thời gian này.',
  'timeline.kind.complete': 'Đủ dữ liệu',
  'timeline.kind.complete.help': 'Giờ này đã được tính và dữ liệu đầy đủ.',
  'timeline.kind.partial': 'Thiếu một phần',
  'timeline.kind.partial.help': 'Giờ này đã được tính và có phần dữ liệu không đầy đủ.',
  'timeline.kind.missing': 'Không ghi nhận hoạt động',
  'timeline.kind.missing.help':
    'Giờ này đã được tính và không quan sát được hoạt động nào. Đây KHÔNG phải kết luận rằng bạn không làm việc.',
  'timeline.kind.notComputed': 'Chưa tính',
  'timeline.kind.notComputed.help':
    'Ngày này có dữ liệu thô nhưng công việc tổng hợp chưa chạy tới giờ này. Số đang hiển thị là mức tối thiểu, chưa phải tổng.',
  'timeline.kind.noData': 'Không có dữ liệu',
  'timeline.kind.noData.help':
    'Không có bản ghi nào cho giờ này. Đây KHÔNG phải kết luận rằng bạn không làm việc.',
  'timeline.notCaughtUp': 'Công việc tổng hợp chưa chạy hết khoảng thời gian đang xem.',
  'timeline.notStarted': 'Ngày được chọn chưa bắt đầu, nên chưa có giờ nào để hiển thị.',

  'catalog.heading': 'Đang thu thập gì về tôi',
  'catalog.intro':
    'Quy tắc phân loại đang áp dụng cho riêng bạn, tính theo ngày {date}. Trang này không hiển thị quy tắc của phòng ban khác.',
  'catalog.loading': 'Đang tải quy tắc phân loại',
  'catalog.subject': 'quy tắc phân loại',
  'catalog.empty.title': 'Công ty chưa đặt quy tắc phân loại nào',
  'catalog.empty.body':
    'Chưa có ứng dụng hoặc tên miền nào được xếp nhóm. Đây là trạng thái cấu hình của công ty.',
  'catalog.private.heading': 'Tên miền riêng tư — không ghi tên',
  'catalog.private.none': 'Công ty chưa đánh dấu tên miền nào là riêng tư.',
  'catalog.private.badge': 'Riêng tư',
  'catalog.private.help':
    'Tên miền này không được ghi tên. Thời gian trên đó vẫn được tính là thời gian làm việc.',
  'catalog.private.body':
    'Thời gian trên các tên miền này vẫn được tính vào thời gian trực tuyến và thời gian hoạt động của bạn; chỉ có tên là không được ghi lại.',
  'catalog.apps.heading': 'Ứng dụng',
  'catalog.apps.none': 'Chưa có quy tắc nào cho ứng dụng.',
  'catalog.sites.heading': 'Tên miền',
  'catalog.sites.none': 'Chưa có quy tắc nào cho tên miền.',
  'catalog.blacklisted': 'Bị chặn',
  'catalog.blacklisted.help': 'Công ty xếp tên miền này vào danh sách chặn.',
  'catalog.scope.company': 'Toàn công ty',
  'catalog.scope.department': 'Phòng ban',
  'catalog.scope.jobTitle': 'Chức danh',
  'catalog.scope.help':
    'Quy tắc cụ thể hơn được ưu tiên: chức danh trước phòng ban, phòng ban trước toàn công ty.',
  'catalog.class.productive': 'Năng suất',
  'catalog.class.neutral': 'Trung tính',
  'catalog.class.unproductive': 'Không năng suất',
  'catalog.class.unclassified': 'Chưa xếp loại',
  'catalog.class.none': 'Không xếp loại năng suất',
  'catalog.category.productive': 'Năng suất',
  'catalog.category.neutral': 'Trung tính',
  'catalog.category.unproductive': 'Không năng suất',
  'catalog.category.communication': 'Liên lạc',
  'catalog.category.meeting': 'Họp',
  'catalog.category.development': 'Phát triển',
  'catalog.category.design': 'Thiết kế',
  'catalog.category.accounting': 'Kế toán',
  'catalog.category.sales': 'Kinh doanh',
  'catalog.category.marketing': 'Tiếp thị',
  'catalog.category.research': 'Nghiên cứu',
  'catalog.category.training': 'Đào tạo',
  'catalog.category.system': 'Hệ thống',
  'catalog.category.blocked': 'Bị chặn',
  'catalog.category.uncategorized': 'Chưa có quy tắc khớp',

  'policy.heading': 'Chính sách đang áp dụng và việc xác nhận',
  'policy.body':
    'Phiên bản chính sách, ngày hiệu lực và việc xác nhận do Agent trên máy trạm thực hiện; Agent hiển thị chúng trong khay hệ thống.',
  'policy.endpoints':
    'Với giám sát máy trạm, phiên bản chính sách và việc xác nhận do Agent xử lý bằng danh tính của chính thiết bị, không qua phiên đăng nhập web — nên chúng hiển thị trong Agent chứ không phải trang này.',
  'policy.lawfulBasis':
    'Cơ sở pháp lý của việc giám sát máy trạm và định vị GPS là các căn cứ được LIỆT KÊ trong Luật Bảo vệ dữ liệu cá nhân (Luật 91/2025/QH15) và Nghị định 356/2025/NĐ-CP: thực hiện hợp đồng lao động, và tuân thủ nghĩa vụ chấm công, lưu trữ chứng từ tiền lương theo Bộ luật Lao động.',
  'policy.consentRecord':
    'Việc bạn đã được thông báo được ghi lại theo từng phiên bản thông báo, và bản ghi đó vẫn còn. Với dữ liệu vị trí, bạn có thể xác nhận đã đọc thông báo và dừng thu thập bất cứ lúc nào; hệ thống sẽ ngừng lưu toạ độ ngay cả khi ứng dụng vẫn gửi. Văn bản đầy đủ: “Cơ sở pháp lý của việc giám sát”.',
  'policy.self': 'Trang này chỉ đọc dữ liệu của chính bạn.',

  'score.heading': 'Điểm năng suất và cách tính',
  'score.loading': 'Đang tải giải thích điểm',
  'score.subject': 'giải thích điểm năng suất',
  'score.notScored':
    'Kỳ này chưa được chấm điểm. Khi thiếu dữ liệu, hệ thống giảm độ tin cậy chứ không gán điểm 0 — nên "chưa có điểm" nghĩa là chưa có điểm, không phải điểm thấp.',
  'score.value': 'Điểm',
  'score.period': 'Kỳ {from} → {to}',
  'score.formula': 'Công thức phiên bản {version}',
  'score.confidence': 'Độ tin cậy {value}',
  'score.confidence.help':
    'Độ tin cậy: mức độ hệ thống tin vào dữ liệu dùng để tính điểm. Thiếu dữ liệu làm giảm độ tin cậy, không làm điểm thành 0.',
  'score.completeness': 'Độ đầy đủ dữ liệu {value}',
  'score.completeness.help':
    'Độ đầy đủ dữ liệu: phần dữ liệu thực sự thu thập được trong kỳ so với phần đáng lẽ có.',
  'score.unknown': 'chưa rõ',
  'score.lowConfidence.title': 'Điểm này dựa trên dữ liệu chưa đầy đủ',
  'score.lowConfidence.body':
    'Điểm vẫn được hiển thị đầy đủ để bạn đối chiếu được. Độ tin cậy thấp là nhận định về dữ liệu, không phải về bạn.',
  'score.components': 'Các thành phần',
  'score.col.component': 'Thành phần',
  'score.col.weight': 'Trọng số',
  'score.col.contribution': 'Đóng góp',
  'score.noData': 'không đủ dữ liệu',
  'score.positive': 'Yếu tố làm tăng điểm',
  'score.negative': 'Yếu tố làm giảm điểm',
  'score.limitations.heading': 'Giới hạn sử dụng',
  'score.limitation.default':
    'Điểm năng suất không được dùng làm căn cứ duy nhất cho bất kỳ quyết định kỷ luật hay lương thưởng nào.',

  'correction.heading': 'Yêu cầu chỉnh sửa ngày {date}',
  'correction.intro':
    'Đây là một yêu cầu, không phải một chỉnh sửa. Dữ liệu gốc không bị sửa; nếu được duyệt, hệ thống ghi một điều chỉnh riêng.',
  'correction.reason': 'Loại lý do',
  'correction.reason.meeting': 'Họp',
  'correction.reason.phone_call': 'Gọi điện',
  'correction.reason.reading_document': 'Đọc tài liệu',
  'correction.reason.paperwork': 'Xử lý giấy tờ',
  'correction.reason.client_visit': 'Đi gặp khách hàng',
  'correction.reason.break': 'Nghỉ giữa giờ',
  'correction.reason.offsite_work': 'Làm việc ngoài văn phòng',
  'correction.reason.agent_down': 'Agent không chạy',
  'correction.reason.device_issue': 'Sự cố thiết bị',
  'correction.reason.power_outage': 'Mất điện',
  'correction.reason.approved_leave': 'Nghỉ đã được duyệt',
  'correction.reason.other': 'Khác',
  'correction.start': 'Từ',
  'correction.end': 'Đến',
  'correction.note': 'Mô tả',
  'correction.note.hint': 'Bắt buộc, tối thiểu {min} ký tự.',
  'correction.note.other': 'Với lý do "Khác", mô tả cần ít nhất 10 ký tự.',
  'correction.resolved': 'Khoảng thời gian sẽ gửi đi: {from} → {to}',
  'correction.submit': 'Gửi yêu cầu',
  'correction.submitting': 'Đang gửi…',
  'correction.cancel': 'Huỷ',
  'correction.subject': 'yêu cầu chỉnh sửa',
  'correction.filed.title': 'Đã ghi nhận yêu cầu',
  'correction.filed.body':
    'Yêu cầu đã được ghi lại kèm nhật ký. Dữ liệu gốc của ngày này không thay đổi.',
  'correction.filed.status': 'Trạng thái: {status}',
  'correction.filed.hr': 'Cần thêm bước duyệt của Nhân sự',
  'correction.status.pending_manager': 'Chờ quản lý duyệt',
  'correction.status.pending_hr': 'Chờ Nhân sự duyệt',
  'correction.status.approved': 'Đã duyệt',
  'correction.status.rejected': 'Bị từ chối',
  'correction.status.withdrawn': 'Đã rút lại',

  'timer.heading': 'Hẹn giờ',
  'timer.loading': 'Đang đọc hẹn giờ',
  'timer.subject': 'hẹn giờ đang chạy',
  'timer.subject.action': 'thao tác hẹn giờ',
  'timer.elapsed': 'Thời gian đã tính',
  'timer.startedAt': 'Bắt đầu lúc {when}',
  'timer.pause': 'Tạm dừng',
  'timer.resume': 'Tiếp tục',
  'timer.stop': 'Dừng',
  'timer.task.unnamed': 'Công việc chưa có tên',
  'timer.empty.title': 'Chưa có hẹn giờ nào đang chạy',
  'timer.empty.body': 'Tìm công việc ở mục bên dưới rồi bấm Bắt đầu.',
  'timer.switched.title': 'Đã chuyển công việc',
  'timer.switched.body': 'Hẹn giờ trước đó cho "{task}" đã được dừng và ghi lại đầy đủ.',
  'timer.longRunning.title': 'Hẹn giờ đã chạy rất lâu',
  'timer.longRunning.body':
    'Hẹn giờ này đã chạy liên tục hơn {hours} giờ. Nếu công việc đã kết thúc, hãy bấm Dừng. Đây là ghi chú về thời gian đã trôi, không phải quy định của công ty.',
  'timer.longRunning.short': 'Đã chạy rất lâu',
  'timer.mini.loading': 'Đang đọc hẹn giờ…',
  'timer.mini.unreadable': 'Không đọc được hẹn giờ',
  'timer.mini.none': 'Chưa có hẹn giờ',
  'timer.state.running': 'Đang chạy',
  'timer.state.paused': 'Đang tạm dừng',
  'timer.state.stopped': 'Đã dừng',
  'timer.state.pendingApproval': 'Chờ duyệt',
  'timer.state.approved': 'Đã duyệt',
  'timer.state.rejected': 'Bị từ chối',
  'timer.source.agent': 'Agent ghi nhận',
  'timer.source.web': 'Ghi từ web',
  'timer.source.manual': 'Nhập tay',
  'timer.source.api': 'Từ hệ thống khác',
  'timer.source.manual.help':
    'Thời gian do người nhập. Mọi báo cáo và điểm số tách riêng phần này khỏi thời gian do Agent quan sát.',
  'timer.source.observed.help': 'Thời gian do hệ thống ghi nhận, không phải người nhập.',
  'timer.validation.validated': 'Đã đối chiếu achau',
  'timer.validation.validated.help': 'Liên kết công việc đã được đối chiếu với achau.',
  'timer.validation.pending': 'Chưa đối chiếu được với achau',
  'timer.validation.pending.help':
    'Hệ thống chưa liên lạc được với achau để đối chiếu công việc. Thời gian của bạn vẫn được ghi đầy đủ.',
  'timer.validation.pending.body':
    'Không liên lạc được với achau lúc bắt đầu, nên chưa đối chiếu được liên kết công việc. Đây là tình trạng của kết nối, không phải của bản ghi: thời gian vẫn được ghi đầy đủ, không có gì bị xoá, và hệ thống sẽ tự đối chiếu lại khi achau trở lại. Bạn không cần làm gì.',
  'timer.validation.pending.checkedAt':
    'Chưa có thời điểm đối chiếu, vì chưa lần nào hỏi được achau.',
  'timer.validation.failed': 'achau trả lời liên kết không hợp lệ',
  'timer.validation.failed.help':
    'Đã hỏi achau và nhận câu trả lời phủ định. Bản ghi được giữ nguyên.',
  'timer.validation.failed.body':
    'Đã hỏi achau và nhận câu trả lời phủ định về liên kết công việc. Bản ghi thời gian được giữ nguyên, không có gì bị xoá; quản lý sẽ xem lại.',
  'timer.reason.achau_unreachable': 'Lý do: không liên lạc được với achau.',
  'timer.reason.task_closed': 'Lý do: công việc đã đóng trên achau.',
  'timer.reason.task_not_found': 'Lý do: không tìm thấy công việc trên achau.',
  'timer.reason.no_permission': 'Lý do: tài khoản không có quyền với công việc này trên achau.',
  'timer.reason.unknown': 'Lý do không được nêu.',

  'tasks.heading': 'Tìm công việc',
  'tasks.searchLabel': 'Tên hoặc mã công việc',
  'tasks.searchHint':
    'Nhập ít nhất {min} ký tự. Kết quả đọc từ bộ nhớ đệm công việc được đồng bộ từ achau.',
  'tasks.loading': 'Đang tìm công việc',
  'tasks.subject': 'kết quả tìm công việc',
  'tasks.empty.title': 'Không tìm thấy công việc nào khớp',
  'tasks.empty.body':
    'Kết quả đọc từ bộ nhớ đệm, nên công việc vừa tạo trên achau có thể chưa xuất hiện.',
  'tasks.start': 'Bắt đầu',
  'tasks.switch': 'Chuyển sang công việc này',
  'tasks.stale': 'Có thể cũ',
  'tasks.stale.help': 'Dòng này được đồng bộ cách đây lâu hơn hai lần chu kỳ đồng bộ.',
  'tasks.stale.since': 'Đồng bộ lần cuối: {when}.',
  'tasks.closed': 'Đã đóng',
  'tasks.closed.help':
    'Công việc đã đóng. Máy chủ quyết định có cho ghi thời gian hay không, nên nút vẫn bật để bạn nhận được câu trả lời của máy chủ.',
  'tasks.source.cache': 'Từ bộ nhớ đệm',
  'tasks.source.achau': 'Đọc trực tiếp achau',
  'tasks.degraded.detail': 'Không xác nhận được kết quả này với achau trong lần gọi vừa rồi.',
  'tasks.cache.stale': 'Lần đồng bộ công việc gần nhất của công ty đã quá hạn.',
  'tasks.cache.neverSynced':
    'Công ty chưa có lần đồng bộ công việc nào thành công. Đây là việc của bộ phận vận hành, không phải một danh sách công việc rỗng.',

  'entries.heading': 'Bản ghi thời gian',
  'entries.loading': 'Đang tải bản ghi thời gian',
  'entries.subject': 'bản ghi thời gian',
  'entries.empty.title': 'Không có bản ghi nào trong khoảng này',
  'entries.empty.body': 'Khoảng đang hiển thị là {days} ngày gần nhất.',
  'entries.excludes': 'các bản ghi cũ hơn trong {days} ngày này — danh sách dừng ở {limit} dòng',
  'entries.running': 'đang chạy',
  'entries.stillOpen': 'chưa kết thúc',
  'entries.reopened': 'Đã mở lại để sửa',
  'entries.reopened.help':
    'Bản ghi này được chủ sở hữu xin sửa — khác với một bản ghi nhập tay đang chờ duyệt.',

  'manual.heading': 'Nhập thời gian bằng tay',
  'manual.intro':
    'Dùng khi thời gian không được ghi tự động. Mọi bản ghi nhập tay đều có nhật ký và được tách riêng khỏi thời gian do Agent quan sát.',
  'manual.open': 'Mở biểu mẫu nhập tay',
  'manual.close': 'Đóng biểu mẫu',
  'manual.task': 'Mã công việc achau',
  'manual.start': 'Từ',
  'manual.end': 'Đến',
  'manual.note': 'Lý do',
  'manual.note.hint': 'Bắt buộc, tối thiểu {min} ký tự.',
  'manual.resolved': 'Khoảng thời gian sẽ gửi đi: {from} → {to}',
  'manual.submit': 'Ghi nhận',
  'manual.submitting': 'Đang ghi nhận…',
  'manual.subject': 'bản ghi nhập tay',
  'manual.recorded.title': 'Đã ghi nhận',
  'manual.recorded.counted': 'Bản ghi được tính ngay.',
  'manual.recorded.pending':
    'Bản ghi đang chờ quản lý duyệt, theo chính sách của công ty tại thời điểm gửi.',
  'manual.window.title': 'Ngoài khoảng được phép nhập lùi',
  'manual.window.body':
    'Chính sách của công ty chỉ cho nhập lùi tới {earliest}. Giá trị này do máy chủ trả về; giao diện không tự đoán khoảng cho phép.',
  'manual.overlap.title': 'Trùng với một bản ghi khác',
  'manual.overlap.body':
    'Khoảng thời gian này trùng với bản ghi {id}. Hai bản ghi chạm nhau ở đúng một điểm thì không tính là trùng.',

  'state.agentOffline.heartbeatUnavailable':
    'Màn hình này không đọc dữ liệu thiết bị, nên không hiển thị thời điểm tín hiệu gần nhất; thông tin đó nằm ở màn hình Thiết bị và cần quyền quản trị thiết bị.',

  /* ── WP-K4 (Task 44) — /overview. `web/shapes/overview.md` is the shape file. ── */

  'overview.loading': 'Đang tải bảng tổng quan',
  'overview.scope.company': 'Phạm vi: toàn công ty',
  'overview.scope.department': 'Phạm vi: phòng ban của bạn',
  'overview.scope.filtered': 'Đang lọc theo mã phòng ban {id}',
  'overview.range': 'Từ {from} đến {to}',
  'overview.window.label': 'Xem, kết thúc {date}',
  'overview.window.today': 'Hôm nay',
  'overview.window.7d': '7 ngày',
  'overview.window.30d': '30 ngày',
  'overview.department.label': 'Phòng ban',
  'overview.department.apply': 'Áp dụng',
  'overview.department.clear': 'Bỏ lọc',
  'overview.refresh': 'Đọc lại ngay',
  'overview.updatedAt': 'Số liệu đọc lúc {at}',
  'overview.updating': 'Đang đọc lại…',
  'overview.neverUpdated': 'Chưa đọc được lần nào',
  'overview.pollNote':
    'Bảng này tự đọc lại mỗi {aggregate} phút cho số liệu tổng hợp và mỗi {live} giây cho hẹn giờ đang chạy, và dừng khi bạn chuyển sang tab khác.',
  'overview.staleAfterError':
    'Các con số dưới đây là của lần đọc thành công gần nhất ({at}). Lần đọc lại vừa rồi thất bại.',
  'overview.completeness':
    '{complete} ngày-nhân-viên đủ · {partial} một phần · {missing} đã tính nhưng không ghi nhận hoạt động · {notComputed} chưa được tính',
  'overview.truncated':
    'Một trang chỉ chứa {limit} dòng và khoảng này nhiều hơn thế, nên các con số trên chỉ tính đến hết ngày {to} — không phải một tổng bị cắt bớt.',
  'overview.nothingCovered': 'Chưa phủ hết một ngày nào trong khoảng này',
  'overview.hours.uncovered.next':
    'Trang dữ liệu đầu tiên chưa phủ hết một ngày, nên không có tổng nào để hiển thị. Chọn khoảng ngắn hơn hoặc lọc theo phòng ban.',

  'overview.presence.title': 'Có mặt hôm nay',
  'overview.presence.headcount': '{count} nhân viên trong phạm vi',
  'overview.presence.headcountUnit': 'nhân viên trong phạm vi',
  'overview.presence.active': 'Đang hoạt động',
  'overview.presence.active.help':
    'Bản tổng hợp theo ngày của hôm nay ghi nhận có thời gian hoạt động. Số liệu này được tính theo giờ, nên có thể trễ tới một giờ.',
  'overview.presence.idle': 'Trực tuyến nhưng không hoạt động',
  'overview.presence.idle.help':
    'Có thời gian trực tuyến, chưa ghi nhận thời gian hoạt động nào trong hôm nay.',
  'overview.presence.observed_none': 'Đã tính, không ghi nhận hoạt động',
  'overview.presence.observed_none.help':
    'Bản tổng hợp cho hôm nay đã chạy và không thấy hoạt động nào. Đây không phải là kết luận rằng người này không làm việc.',
  'overview.presence.not_computed': 'Chưa tính xong',
  'overview.presence.not_computed.help':
    'Dữ liệu đã về nhưng bản tổng hợp theo ngày chưa chạy tới. Con số sẽ xuất hiện, không phải là số không.',
  'overview.presence.no_activity_data': 'Không có dữ liệu hoạt động',
  'overview.presence.no_activity_data.help':
    'Hôm nay chưa nhận được dữ liệu nào cho người này. Có thể là ngày nghỉ theo ca, có thể là máy không gửi được — hai việc khác nhau, và cột chấm công bên dưới là chỗ phân biệt.',
  'overview.presence.noOffline':
    'Không có mục "ngoại tuyến" ở đây. Ngoại tuyến là nhận định về một máy, và chỉ màn hình Thiết bị công bố nó (cần quyền quản trị thiết bị). Bảng này nói về dữ liệu đã quan sát được, không nói về máy.',
  'overview.presence.empty': 'Không có nhân viên nào trong phạm vi bạn được phép xem',
  'overview.presence.empty.next':
    'Bỏ lọc phòng ban để xem rộng hơn, hoặc liên hệ quản trị viên nếu bạn cần xem thêm người.',
  'overview.presence.noRows':
    'Chưa có bản tổng hợp nào cho hôm nay, nên bảng này chưa nói được gì về hiện diện.',

  'overview.hours.title': 'Giờ trực tuyến quan sát được',
  'overview.hours.over': 'Trên {days} ngày, {rows} ngày-nhân-viên, {employees} người',
  'overview.hours.active': 'Trong đó thời gian hoạt động: {value}',
  'overview.hours.none': 'Chưa quan sát được giờ trực tuyến nào trong khoảng này',
  'overview.hours.none.next':
    'Đây là "chưa quan sát được", không phải "không giờ nào". Con số xuất hiện khi máy trạm gửi nhịp cho khoảng này.',
  'overview.hours.excludes': 'những ngày-nhân-viên chưa được tính hoặc chỉ quan sát được một phần',

  'overview.ratio.title': 'Tỷ lệ hoạt động',
  'overview.ratio.terms': '{active} hoạt động trên {online} trực tuyến',
  'overview.ratio.none': 'Không có tỷ lệ cho khoảng này',
  'overview.ratio.none.next':
    'Chưa quan sát được giây trực tuyến nào, và 0 trên 0 không phải là 0%.',
  'overview.ratio.notProductive':
    'Đây không phải tỉ lệ làm việc hiệu quả. Con số duy nhất đo được ở đây là phần thời gian có thao tác trên tổng thời gian trực tuyến, và nó không nói thời gian đó có hiệu quả hay không.',
  'overview.ratio.partial':
    'Tử số và mẫu số lấy từ cùng những dòng dữ liệu, nên một ngày chỉ quan sát được một phần làm thiếu cả hai như nhau: cái mà dữ liệu thiếu làm lệch là TỔNG, không phải tỷ lệ. Cái nó làm hẹp đi là mẫu quan sát, và mẫu đó ghi ngay bên cạnh.',

  'overview.task.title': 'Dữ liệu công việc từ achau',
  'overview.task.cache.fresh': 'Dữ liệu công việc: mới',
  'overview.task.cache.stale': 'Dữ liệu công việc: cũ — đợt đồng bộ đang trễ',
  'overview.task.cache.never_synced': 'Dữ liệu công việc: chưa đồng bộ lần nào',
  'overview.task.degraded': 'Không liên lạc được với achau trong lần đọc này',
  'overview.task.syncedAt': 'Bản ghi mới nhất được đồng bộ lúc {at}',
  'overview.task.noSyncedAt': 'Không có bản ghi nào để lấy thời điểm đồng bộ.',
  'overview.task.probe':
    'Đây là trạng thái đồng bộ công việc của cả công ty. Thời điểm ở trên là bản ghi mới nhất màn hình này nhìn thấy, không phải lần quét gần nhất.',
  'overview.task.rest': 'Phần còn lại của bảng tổng quan không phụ thuộc vào achau và vẫn chạy.',
  'overview.task.overdue.title': 'Số công việc quá hạn: chưa có nguồn dữ liệu',
  'overview.task.overdue.body':
    'Chưa đếm được số công việc quá hạn: công việc chỉ tra cứu được bằng từ khoá, không lọc được theo hạn chót. Một con số dựng từ đó sẽ sai, nên ở đây không có con số nào.',
  'overview.task.overdue.needs':
    'Khi tra cứu được công việc theo hạn chót, ô này sẽ thành một con số và không có gì khác trên màn hình phải đổi.',

  'overview.trend.title': 'Xu hướng {days} ngày',
  'overview.trend.subtitle':
    'Giờ trực tuyến và giờ hoạt động theo ngày, cho đúng phạm vi đang xem.',
  'overview.trend.noDepartments':
    'Không tách được theo phòng ban: số liệu tổng hợp ở đây không kèm phòng ban. Đây là xu hướng của phạm vi bạn đang xem, không phải nhiều phòng ban chồng lên nhau.',
  'overview.trend.caption':
    'Ngày {date}: {online} trực tuyến, {active} hoạt động, {employees} người có dữ liệu.',
  'overview.trend.gap':
    'Ngày {date}: chưa có bản tổng hợp nào — vẽ thành khoảng trống, không phải cột bằng không.',
  'overview.trend.partial': 'Ngày {date}: có {partial} dòng chỉ quan sát được một phần.',
  'overview.trend.tooShort': 'Cần ít nhất hai ngày có dữ liệu mới vẽ được xu hướng.',
  'overview.trend.axis': 'Cột cao nhất tương ứng {value}.',
  'overview.trend.drill': 'Mở màn hình Chấm công cho khoảng này',

  'overview.reports.title': 'Ai cần giúp',
  'overview.reports.subject': 'danh sách nhân sự',
  'overview.reports.col.name': 'Nhân viên',
  'overview.reports.col.attendance': 'Chấm công hôm nay',
  'overview.reports.col.presence': 'Có mặt',
  'overview.reports.col.task': 'Việc đang chạy',
  'overview.reports.col.hours': 'Giờ quan sát được',
  'overview.reports.col.drill': 'Xem chi tiết',
  'overview.reports.noTask': 'Không có hẹn giờ nào đang chạy',
  'overview.reports.taskUnavailable': 'Chưa đọc được hẹn giờ',
  'overview.reports.hoursUnknown': 'Chưa tính',
  'overview.reports.drill': 'Xem chấm công của {name}',
  'overview.reports.truncated':
    'Danh sách hiển thị {shown} trong {total} nhân viên thuộc phạm vi. Các ô tổng hợp phía trên tính trên toàn phạm vi, không chỉ trên trang này.',

  'overview.unassigned.title': 'Chưa gắn việc ({count})',
  'overview.unassigned.body':
    'Những người không có hẹn giờ nào đang chạy. Đây là nhận định về hẹn giờ, không phải về việc họ có đang làm việc hay không.',
  'overview.unassigned.none': 'Mọi người trong phạm vi đều đang chạy một hẹn giờ.',
  'overview.unassigned.unavailable':
    'Chưa đọc được danh sách hẹn giờ đang chạy, nên phần này không liệt kê ai — liệt kê tất cả mọi người là kết luận sai.',

  'overview.devices.title': 'Tình trạng thiết bị',
  'overview.devices.note':
    'Đây là số máy, không phải số người. Một người có thể có nhiều máy hoặc không có máy nào.',
  'overview.devices.online': 'Trực tuyến',
  'overview.devices.stale': 'Tín hiệu cũ',
  'overview.devices.offline': 'Ngoại tuyến',
  'overview.devices.never': 'Chưa từng gửi tín hiệu',
  'overview.devices.staleHelp':
    'Tín hiệu cũ và ngoại tuyến là hai mục riêng và không gộp. Trạng thái này được tính lại từ tín hiệu gần nhất trong mỗi lần đọc, không lưu trong bảng, và nó trễ theo hướng bi quan: một máy bị gọi là ngoại tuyến hơi sớm chứ không bao giờ muộn.',
  'overview.devices.denied':
    'Phần này cần quyền quản trị thiết bị (workforce.manage_devices), tài khoản này không có. Không hiển thị con số nào ở đây — số không sẽ là một lời nói dối.',
  'overview.devices.truncated': 'Đọc được {known} trong {total} thiết bị.',
  'overview.devices.subject': 'tình trạng thiết bị',

  'overview.alerts.title': 'Cảnh báo',
  'overview.alerts.empty': 'Chưa có bảng tóm tắt cảnh báo theo mức độ',
  'overview.alerts.body': 'Xem toàn bộ cảnh báo của hôm nay ở màn hình Cảnh báo.',

  /* ── the day sheet (Wave 1, group A) ───────────────────────────────────── */
  'overview.day.isToday': 'Hôm nay',
  'overview.day.previous': 'Ngày trước',
  'overview.day.next': 'Ngày sau',
  'overview.day.backToToday': 'Về hôm nay',
  'overview.filters.label': 'Bộ lọc của bảng tổng quan',
  'overview.figures.title': 'Ba con số của ngày',
  'overview.staleAfterErrorShort':
    'Lần đọc lại gần nhất thất bại. Những con số dưới đây là lần đọc thành công cuối cùng, không phải số của lúc này.',

  'overview.exception.title': 'Hôm nay cần xử lý',
  'overview.exception.uncounted': 'chưa đọc được',
  'overview.exception.late': 'Đi muộn',
  'overview.exception.late.body':
    'Những người mà bản chấm công của ngày này ghi nhận đi muộn. Con số lấy từ đúng trạng thái hiển thị ở cột Chấm công bên dưới, không tính lại ở trình duyệt.',
  'overview.exception.late.none': 'Không ai bị ghi nhận đi muộn trong ngày này.',
  'overview.exception.absent': 'Vắng mặt',
  'overview.exception.absent.body':
    'Những người mà bản chấm công của ngày này ghi nhận vắng mặt. Vắng mặt là kết luận của máy chủ, không phải suy ra từ việc thiếu dữ liệu.',
  'overview.exception.absent.none': 'Không ai bị ghi nhận vắng mặt trong ngày này.',
  'overview.exception.noActivity': 'Không có dữ liệu hoạt động',
  'overview.exception.noActivity.body':
    'Không có dòng tổng hợp nào cho những người này trong ngày. Đây là nhận định về dữ liệu, không phải về con người: có thể agent ngừng gửi tín hiệu, có thể chưa tính xong. Không bao giờ hiển thị thành số không.',
  'overview.exception.noActivity.none':
    'Mọi người trong phạm vi đều có dữ liệu hoạt động cho ngày này.',
  'overview.exception.unassigned': 'Không có hẹn giờ đang chạy',
  'overview.reports.ordered':
    'Xếp theo mức cần giúp: vắng mặt, đi muộn, dữ liệu chấm công chưa đủ, không có dữ liệu hoạt động, không có hẹn giờ. Thứ tự chỉ dựa trên chính các cột hiển thị trong bảng.',

  /* ═══════════════ WP-K5 (Task 45) — Thiết bị ═══════════════ */

  'devices.intro':
    'Đây là màn hình thu hồi quyền của một máy. Thu hồi là vĩnh viễn và không hoàn tác được; mã đăng ký chỉ hiện đúng một lần.',
  'devices.subject': 'danh sách thiết bị',

  'devices.census.title': 'Tình trạng nhịp tim',
  'devices.census.scope': 'Đếm trên {counted} thiết bị của trang này. Toàn công ty có {total}.',
  'devices.census.help':
    'Trực tuyến, chậm nhịp và ngoại tuyến là ba câu trả lời khác nhau, không bao giờ gộp. Tình trạng nhịp tim được tính lại từ nhịp cuối trên mỗi lần đọc, không lưu vào cột nào, và luôn lệch về phía thận trọng: một máy bị gọi là ngoại tuyến hơi sớm chứ không bao giờ muộn.',
  'devices.reading.online': 'Trực tuyến',
  'devices.reading.stale': 'Chậm nhịp',
  'devices.reading.offline': 'Ngoại tuyến',
  'devices.reading.never_reported': 'Chưa từng báo cáo',
  'devices.reading.neverHelp':
    'Chưa từng gửi nhịp nào kể từ khi đăng ký. Khác với ngoại tuyến: không có khoảng im lặng nào để đo, nên số phút ngoại tuyến để trống thay vì đếm từ lúc đăng ký.',

  'devices.status.pending': 'Chờ duyệt',
  'devices.status.active': 'Đang hoạt động',
  'devices.status.offline': 'Đã đánh dấu ngoại tuyến',
  'devices.status.suspended': 'Tạm ngưng',
  'devices.status.revoked': 'Đã thu hồi',
  'devices.status.uninstalled': 'Đã gỡ cài đặt',
  'devices.status.agent_outdated': 'Agent quá cũ',
  'devices.status.policy_error': 'Lỗi áp chính sách',
  'devices.status.sync_error': 'Lỗi đồng bộ',
  'devices.status.help':
    'Trạng thái vòng đời do quản trị viên đặt, hoặc do hệ thống tự chuyển sang ngoại tuyến. Nó là trục riêng, không phải tín hiệu sống: một máy có thể vừa đang hoạt động vừa ngoại tuyến trong cùng một câu trả lời.',

  'devices.filter.legend': 'Lọc thiết bị',
  'devices.filter.status': 'Trạng thái vòng đời',
  'devices.filter.statusAny': 'Tất cả trạng thái',
  'devices.filter.q': 'Tên máy',
  'devices.filter.qHint':
    'Khớp một phần tên máy. Không tìm theo vân tay máy: hệ thống không công bố giá trị đó.',
  'devices.filter.employee': 'Nhân sự',
  'devices.filter.employeeHint':
    'Đây là bộ lọc, không phải phạm vi: kết quả vẫn nằm trong phạm vi bạn được phép xem.',
  'devices.filter.department': 'Phòng ban',
  'devices.filter.departmentHint': 'Khớp phòng ban hiện tại của nhân sự đang được gán máy.',
  'devices.filter.apply': 'Áp dụng',
  'devices.filter.clear': 'Xoá lọc',
  'devices.filter.reload': 'Đọc lại',
  'devices.filter.noOnline':
    'Không có bộ lọc trực tuyến ở đây: một bộ lọc như vậy sẽ gộp chậm nhịp, ngoại tuyến và chưa từng báo cáo vào cùng một nhóm. Lọc theo trạng thái vòng đời ở trên.',

  'devices.table.hostname': 'Tên máy',
  'devices.table.employee': 'Nhân sự',
  'devices.table.status': 'Vòng đời',
  'devices.table.liveness': 'Nhịp tim',
  'devices.table.heartbeat': 'Nhịp cuối',
  'devices.table.open': 'Xem chi tiết',
  'devices.table.openLabel': 'Mở chi tiết thiết bị {name}',
  'devices.table.noHostname': 'Chưa khai báo tên máy',
  'devices.table.unassigned': 'Chưa gán nhân sự',
  'devices.table.unnamed': 'Chưa gán tên',
  'devices.table.employeeCode': 'Nhân sự #{id}',
  'devices.employeePickHint': 'Chọn từ danh sách; mã sẽ được gắn tự động.',
  'devices.table.truncated':
    'Đang hiển thị {shown} trên tổng {total} thiết bị. Các con số phía trên đếm trang này, không phải toàn công ty.',

  'devices.empty.title': 'Chưa có thiết bị nào đăng ký',
  'devices.empty.body':
    'Công ty này chưa có máy nào ghi danh. Tạo một mã đăng ký ở mục bên dưới rồi đưa cho người cài đặt.',
  'devices.emptyFiltered.title': 'Không có thiết bị nào khớp bộ lọc',
  'devices.emptyFiltered.body': 'Bộ lọc hiện tại không khớp máy nào. Xoá lọc để xem toàn bộ.',

  'devices.pending.title': 'Máy này đang chờ duyệt',
  'devices.pending.body':
    'Thiết bị chờ duyệt đã có khoá hợp lệ nên vẫn gửi được tín hiệu sống — vì vậy nhịp bên cạnh là thật. Nhưng mọi dữ liệu nó gửi đều bị từ chối và dồn lại trong hàng đợi trên máy. Duyệt máy thì hàng đợi mới được nhận.',

  'devices.detail.title': 'Chi tiết thiết bị',
  'devices.detail.select': 'Chọn một máy trong bảng để xem chi tiết, sức khoẻ và các thao tác.',
  'devices.detail.subject': 'chi tiết thiết bị',
  'devices.detail.identity': 'Nhận dạng',
  'devices.detail.os': 'Hệ điều hành',
  'devices.detail.agentVersion': 'Phiên bản Agent',
  'devices.detail.ip': 'Địa chỉ IP gần nhất',
  'devices.detail.department': 'Phòng ban (ảnh chụp lúc đăng ký)',
  'devices.detail.branch': 'Chi nhánh (ảnh chụp lúc đăng ký)',
  'devices.detail.policy': 'Phiên bản chính sách máy đang giữ',
  'devices.detail.trust': 'Mức tin cậy',
  'devices.detail.unknown': 'Không có dữ liệu',
  'devices.detail.audit': 'Dấu vết kiểm toán trên chính bản ghi này',
  'devices.detail.registered': 'Đăng ký lúc',
  'devices.detail.approved': 'Duyệt lúc',
  'devices.detail.approvedBy': 'Người duyệt (mã achau)',
  'devices.detail.revoked': 'Thu hồi lúc',
  'devices.detail.revokeReason': 'Lý do thu hồi',
  'devices.detail.statusReason': 'Ghi chú trạng thái',
  'devices.detail.auditNote':
    'Đây là những gì nhật ký kiểm toán ghi lại cho các chuyển trạng thái này, đọc thẳng từ bản ghi thiết bị. Nhật ký đầy đủ nằm ở màn hình Nhật ký kiểm toán.',
  'devices.detail.noReassign':
    'Gán lại nhân sự không nằm trên màn hình này. Gán lại là một thao tác riêng, không phải thu hồi rồi đăng ký lại — khoá xác thực cái máy, không phải con người.',

  'devices.health.title': 'Sức khoẻ vận hành',
  'devices.health.subject': 'sức khoẻ thiết bị',
  'devices.health.computed':
    'Toàn bộ mục này được tính khi đọc, không đọc từ cột nào. Vì vậy nếu job quét ngừng chạy thì câu trả lời xấu đi chứ không đóng băng.',
  'devices.health.offlineMinutes': 'Im lặng đã {minutes} phút',
  'devices.health.offlineNone': 'Đang trực tuyến nên không có khoảng im lặng để đo.',
  'devices.health.offlineNever': 'Chưa từng báo cáo, nên không có mốc nào để đo khoảng im lặng.',
  'devices.health.agentState': 'Trạng thái Agent lần nhịp cuối',
  'devices.health.agentOutdated': 'Agent bị đánh dấu quá cũ',
  'devices.health.agentOutdatedHelp':
    'Đây là kết luận đã lưu, không phải so sánh phiên bản: màn hình này chưa biết phiên bản hiện hành là bao nhiêu, nên một câu trả lời tự tính sẽ là phỏng đoán.',
  'devices.health.policyVersion': 'Phiên bản chính sách',
  'devices.health.policyAck': 'Máy xác nhận chính sách lúc',
  'devices.health.lastEvent': 'Sự kiện cuối nhận được',
  'devices.health.sequence': 'Số thứ tự sự kiện cuối',
  'devices.health.queue': 'Hàng đợi cục bộ của Agent: {events} sự kiện',
  'devices.health.queueHelp':
    'Con số Agent tự khai trong nhịp gần nhất, không phải phép đo của hệ thống.',
  'devices.health.queueUnknown':
    'Chưa biết độ sâu hàng đợi: máy chưa từng báo cáo, nên để trống thay vì ghi 0.',
  'devices.health.rejected': 'Sự kiện bị từ chối trong 24 giờ: {count}',
  'devices.health.rejectedHelp':
    'Sự kiện đã vào kho rồi mới bị từ chối. Sự kiện bị chặn ngay ở cửa không thành bản ghi và không được đếm ở đây.',
  'devices.health.flags': 'Cờ cảnh báo',
  'devices.health.flagsNone': 'Không có cờ nào.',
  'devices.health.flag.clock_skew': 'Đồng hồ máy lệch ngoài ngưỡng cho phép',
  'devices.health.flag.sequence_gap': 'Thiếu số thứ tự sự kiện',
  'devices.health.flag.sync_error': 'Lỗi đồng bộ (kết luận đã lưu)',
  'devices.health.flag.policy_error': 'Lỗi áp chính sách (kết luận đã lưu)',
  'devices.health.flagsNever':
    'Hai dấu hiệu — hàng đợi ùn tắc và tới hạn xoay khoá — chưa bao giờ được phát ra vì chưa ai đặt ngưỡng cho chúng. Một cờ dựng trên phỏng đoán còn tệ hơn không có cờ, vì sẽ có người hành động theo nó.',

  'devices.approve.title': 'Duyệt thiết bị',
  'devices.approve.body':
    'Chỉ máy đang chờ duyệt mới duyệt được. Máy ở trạng thái ngoại tuyến do đồng hồ chuyển sang, hoặc đang mang một chẩn đoán sức khoẻ, thì duyệt sẽ là ghi đè lên kết luận đó.',
  'devices.approve.employee': 'Nhân sự (bỏ trống nếu mã đăng ký đã gắn sẵn)',
  'devices.approve.reason': 'Ghi chú (tuỳ chọn, được ghi vào nhật ký kiểm toán)',
  'devices.approve.submit': 'Duyệt thiết bị',
  'devices.approve.submitting': 'Đang duyệt…',
  'devices.approve.done':
    'Đã duyệt. Máy chuyển sang active; nhịp tim vẫn giữ nguyên câu trả lời cũ cho tới nhịp kế tiếp.',

  'devices.revoke.open': 'Thu hồi thiết bị…',
  'devices.revoke.title': 'Thu hồi {name} — đây là những thứ sẽ mất',
  'devices.revoke.lead':
    'Đọc hết danh sách dưới đây trước khi xác nhận. Không có bước hoàn tác nào sau bước này.',
  'devices.revoke.queueKnown':
    'Hàng đợi cục bộ bị huỷ: {events} sự kiện Agent khai trong nhịp gần nhất, cộng thêm mọi thứ nó xếp hàng kể từ lúc đó. Số này không lấy lại được.',
  'devices.revoke.queueEmpty':
    'Hàng đợi cục bộ bị huỷ. Nhịp gần nhất Agent khai 0 sự kiện, nhưng mọi thứ nó xếp hàng kể từ lúc đó cũng mất.',
  'devices.revoke.queueUnknown':
    'Hàng đợi cục bộ bị huỷ và không biết nó lớn bao nhiêu: máy chưa từng báo cáo độ sâu hàng đợi, nên ở đây để trống thay vì ghi 0.',
  'devices.revoke.credentials':
    'Mọi khoá của máy bị đóng trong cùng một giao dịch, kể cả khoá đang trong thời gian ân hạn xoay vòng — chính khoá được ân hạn đó là khoá mà kẻ lấy máy trước lần xoay cuối đang giữ.',
  'devices.revoke.terminal':
    'Thu hồi là trạng thái cuối. Không duyệt lại được, không thu hồi lần hai được. Máy muốn quay lại phải ghi danh như một thiết bị MỚI bằng một mã đăng ký mới, và sẽ mang mã thiết bị khác.',
  'devices.revoke.keepsIngested':
    'Những sự kiện đã vào kho thì KHÔNG bị xoá. Thu hồi chỉ cắt quyền của máy từ lúc này trở đi.',
  'devices.revoke.silent':
    'Máy sẽ không được báo. Lần gọi kế tiếp của nó nhận đúng một câu 401 không phân biệt được với máy lạ hay công ty bị tắt.',
  'devices.revoke.reason': 'Lý do thu hồi (bắt buộc, tối thiểu {min} ký tự)',
  'devices.revoke.reasonHint':
    'Được ghi vào bản ghi thiết bị, vào từng khoá bị đóng và vào nhật ký kiểm toán. Hai ràng buộc trong cơ sở dữ liệu đều đòi nó.',
  'devices.revoke.handle': 'Gõ lại {handle} để xác nhận',
  'devices.revoke.handleHint': 'Ô thứ hai này là để một cú nhấp nhầm không đủ để huỷ một máy.',
  'devices.revoke.handleFallback':
    'Máy này chưa khai tên nên phải gõ lại mã thiết bị — và đó cũng là loại máy dễ bị thu hồi nhầm nhất.',
  'devices.revoke.confirm': 'Thu hồi vĩnh viễn',
  'devices.revoke.confirming': 'Đang thu hồi…',
  'devices.revoke.cancel': 'Huỷ bỏ',
  'devices.revoke.blocked': 'Cần cả lý do lẫn tên máy gõ đúng thì nút mới bật.',
  'devices.revoke.done':
    'Đã thu hồi. Mọi khoá của máy đã đóng, hàng đợi cục bộ của nó đã mất, và không có đường duyệt lại.',

  'devices.tokens.title': 'Mã đăng ký',
  'devices.tokens.subject': 'danh sách mã đăng ký',
  'devices.tokens.intro':
    'Mã đăng ký là thứ người cài đặt nhập vào Agent. Bản rõ chỉ hiện đúng một lần, ngay sau khi tạo: hệ thống chỉ giữ bản mã hoá một chiều nên không có lần đọc thứ hai, mất thì tạo mã khác.',
  'devices.tokens.deadRows':
    'Danh sách này gồm cả mã đã thu hồi, hết hạn và dùng hết. Giấu các dòng chết đi cũng là giấu luôn việc thu hồi khỏi mắt người vừa thực hiện nó.',
  'devices.tokens.create': 'Tạo mã đăng ký',
  'devices.tokens.creating': 'Đang tạo…',
  'devices.tokens.expires': 'Hiệu lực (giờ)',
  'devices.tokens.expiresHint': 'Tối đa 24 giờ. Máy chủ nhận số giây; ô này quy đổi giúp bạn.',
  'devices.tokens.maxUses': 'Số lần dùng tối đa',
  'devices.tokens.employee': 'Nhân sự (tuỳ chọn)',
  'devices.tokens.note': 'Nhãn ghi nhớ (tuỳ chọn)',
  'devices.tokens.autoApprove': 'Bỏ qua bước duyệt thiết bị',
  'devices.tokens.autoApproveHelp':
    'Bỏ qua lớp bảo vệ dành cho mã bị lộ trên đường tới bộ phận IT, nên máy chủ chỉ nhận khi có mã nhân sự, số lần dùng bằng 1 và hiệu lực không quá 24 giờ.',

  'devices.tokens.reveal.title': 'Mã đăng ký — đây là lần duy nhất bạn thấy nó',
  'devices.tokens.reveal.warning':
    'Sao chép ngay bây giờ. Đóng khung này, chuyển màn hình hay tải lại trang đều làm mất bản rõ vĩnh viễn: hệ thống chỉ giữ bản mã hoá một chiều và không có cách nào đọc lại.',
  'devices.tokens.reveal.token': 'Mã (bản rõ)',
  'devices.tokens.reveal.url': 'Địa chỉ ghi danh',
  'devices.tokens.reveal.urlMissing':
    'Chưa có địa chỉ ghi danh để hiển thị vì địa chỉ công khai của hệ thống chưa được cấu hình; hãy hỏi quản trị viên. Người cài đặt cần địa chỉ này như một ô nhập riêng — địa chỉ cố ý không chứa mã, vì một đường dẫn mang khoá sẽ nằm lại trong lịch sử trình duyệt và trong nhật ký của các máy trung gian.',
  'devices.tokens.reveal.copy': 'Sao chép mã',
  'devices.tokens.reveal.copied': 'Đã sao chép vào bộ nhớ tạm.',
  'devices.tokens.reveal.copyFailed':
    'KHÔNG sao chép được: trình duyệt từ chối. Mã vẫn đang hiển thị ngay đây — hãy bôi đen và chép tay ngay bây giờ, vì nó sẽ không hiện lại lần nữa.',
  'devices.tokens.reveal.copyUnavailable':
    'KHÔNG sao chép được: trang này không được phép sao chép tự động, thường là do kết nối chưa được bảo mật. Mã vẫn đang hiển thị ngay đây — hãy bôi đen và chép tay ngay bây giờ, vì nó sẽ không hiện lại lần nữa.',
  'devices.tokens.reveal.dismiss': 'Tôi đã lưu mã — đóng và xoá khỏi màn hình',
  'devices.tokens.spent.title': 'Mã {prefix} đã hiển thị xong',
  'devices.tokens.spent.body':
    'Bản rõ không còn ở bất kỳ đâu trong trình duyệt này và không có yêu cầu nào lấy lại được nó. Nếu chưa kịp lưu, hãy tạo một mã mới và thu hồi mã này.',

  'devices.tokens.col.prefix': 'Tiền tố',
  'devices.tokens.col.label': 'Nhãn',
  'devices.tokens.col.expires': 'Hết hạn',
  'devices.tokens.col.uses': 'Đã dùng',
  'devices.tokens.col.state': 'Tình trạng',
  'devices.tokens.col.action': 'Thao tác',
  'devices.tokens.prefixHelp':
    'Tám ký tự này được sinh từ nguồn ngẫu nhiên riêng lúc tạo mã, không phải phần đầu của bí mật, nên hiển thị được.',
  'devices.tokens.uses': '{used}/{max}',
  'devices.tokens.state.usable': 'Còn dùng được',
  'devices.tokens.state.revoked': 'Đã thu hồi',
  'devices.tokens.state.exhausted': 'Đã dùng hết',
  'devices.tokens.empty': 'Chưa có mã đăng ký nào được tạo.',
  'devices.tokens.revoke': 'Thu hồi mã',
  'devices.tokens.revokeReason': 'Lý do thu hồi mã {prefix}',
  'devices.tokens.revokeSubmit': 'Thu hồi mã này',
  'devices.tokens.revokeDone': 'Đã thu hồi mã. Nó không đổi được thành thiết bị nữa.',
  'devices.tokens.revokeUsedNote':
    'Mã đã dùng một phần vẫn thu hồi được, và đó là trường hợp thường gặp với mã nhiều lượt: sau thao tác này nó không đổi được nữa, bất kể số lượt đã dùng.',

  'devices.offboard.title': 'Nhân sự đã nghỉ việc — máy của họ đã được thu hồi tự động',
  'devices.offboard.body':
    'Khi đồng bộ achau thấy một người không còn trong công ty, hệ thống xếp lịch thu hồi toàn bộ thiết bị của người đó: mọi máy bị thu hồi, mọi khoá bị đóng, và mỗi máy có một dòng nhật ký. Nếu người đó được nhận lại trước khi việc này chạy thì không máy nào bị đụng tới. Bảng dưới đây là chỗ đối chiếu.',
  'devices.offboard.cannotObserve':
    'Màn hình này không tự biết ai đã nghỉ: danh sách thiết bị không cho biết nhân sự còn làm việc hay không. Vì vậy ô dưới đây trả lời câu hỏi “người này đang giữ những máy nào”, chứ không khẳng định người đó đã nghỉ.',
  'devices.offboard.input': 'Nhân sự đã nghỉ',
  'devices.offboard.apply': 'Liệt kê máy của người này',
  'devices.offboard.none': 'Không có máy nào đang gán cho mã nhân sự này.',
  'devices.offboard.result':
    'Đang lọc danh sách thiết bị theo mã nhân sự {employee}. Mỗi máy mở ra chi tiết, nơi thu hồi vẫn phải đi qua hai bước xác nhận của nó.',
  'devices.offboard.noButton':
    'Bảng này cố ý không có nút thu hồi của riêng nó: quyết định thuộc về người, và nút ở đây sẽ biến một danh sách thành một mệnh lệnh.',

  /* ═══════════════ WP-K5 (Task 45) — Chính sách ═══════════════ */

  'policies.intro':
    'Đây là màn hình quyết định nhân viên bị theo dõi chặt tới đâu. Mỗi thay đổi tạo một phiên bản mới, và bản tóm tắt bên dưới nói rõ thay đổi đó siết chặt hay nới lỏng việc giám sát.',
  'policies.subject': 'danh sách luật website',

  'policies.rules.title': 'Tên miền riêng tư và phân loại website',
  'policies.rules.intro':
    'Đây là phần duy nhất của cấu hình giám sát vừa xem vừa sửa được ở màn hình này. Tên miền đánh dấu riêng tư thì thời gian vẫn được tính là có làm việc, nhưng tên miền không bị ghi lại.',
  'policies.rules.visible':
    'Nhân viên cũng đọc được danh sách riêng tư này về chính mình, và màn hình Hoạt động của tôi đặt nó lên đầu. Đó là chủ ý: danh mục luật cảnh báo được giấu khỏi nhân viên vì nó nói cho người bị theo dõi biết cái gì kích hoạt cảnh báo; danh sách riêng tư nói cho họ biết cái gì KHÔNG bị ghi, và một lời hứa mà người được hứa không kiểm tra được thì không phải lời hứa.',
  'policies.rules.col.host': 'Tên miền',
  'policies.rules.col.privacy': 'Riêng tư',
  'policies.rules.col.category': 'Phân loại',
  'policies.rules.col.scope': 'Phạm vi',
  'policies.rules.col.version': 'Phiên bản',
  'policies.rules.private': 'Riêng tư',
  'policies.rules.named': 'Ghi lại tên miền',
  'policies.rules.blacklisted': 'Bị chặn',
  'policies.rules.empty.title': 'Công ty chưa đặt luật website nào',
  'policies.rules.empty.body':
    'Đây là một trạng thái giám sát, không phải một chỗ trống: khi chưa có luật nào, không tên miền nào được đánh dấu riêng tư và mọi tên miền đều được ghi lại theo mức thu thập mặc định.',
  'policies.rules.truncated':
    'Đọc được {shown} trên tổng {total} luật. Bản tóm tắt chỉ so với những luật đang hiển thị.',

  'policies.editor.title': 'Soạn một thay đổi',
  'policies.editor.host': 'Tên miền',
  'policies.editor.hostHint':
    'Chỉ tên máy chủ trần: không có scheme, đường dẫn, query hay fragment. Muốn phủ cả tên miền con thì chọn kiểu khớp hậu tố.',
  'policies.editor.matchField': 'Kiểu khớp',
  'policies.editor.matchField.domain': 'Đúng tên miền',
  'policies.editor.matchField.domain_suffix': 'Tên miền và mọi tên miền con',
  'policies.editor.category': 'Phân loại hoạt động',
  'policies.editor.private': 'Đánh dấu riêng tư (không ghi lại tên miền)',
  'policies.editor.privateHelp':
    'Thời gian trên tên miền riêng tư vẫn được tính vào tổng thời gian làm việc và không vào cột phân loại nào, để không ai bị trừ điểm vì dùng một quyền riêng tư.',
  'policies.editor.blacklist': 'Đánh dấu bị chặn',
  'policies.editor.scope': 'Phạm vi áp dụng',
  'policies.editor.scopeKey': 'Mã phòng ban hoặc chức danh',
  'policies.editor.stage': 'Đưa vào bản tóm tắt',
  'policies.editor.reset': 'Xoá bản nháp',

  'policies.diff.title': 'Tóm tắt thay đổi',
  'policies.diff.unsaved': 'Chưa lưu. Chưa có yêu cầu nào được gửi đi.',
  'policies.diff.none':
    'Bản nháp không thay đổi gì so với luật đang có, nên không có phiên bản mới nào để tạo.',
  'policies.diff.updating': 'Sẽ cập nhật luật đang có: phiên bản {from} thành {to}.',
  'policies.diff.creating': 'Sẽ tạo một luật mới cho tên miền này (phiên bản 1).',
  'policies.direction.tightens': 'Siết chặt giám sát',
  'policies.direction.loosens': 'Nới lỏng giám sát',
  'policies.direction.judgement': 'Đổi cách đánh giá, không đổi thứ được thu thập',

  'policies.change.private.on':
    '{host}: bật riêng tư — từ nay tên miền này KHÔNG còn được ghi lại, dù thời gian vẫn được tính là có làm việc.',
  'policies.change.private.off':
    '{host}: tắt riêng tư — từ nay tên miền này BẮT ĐẦU được ghi lại cho mọi nhân sự trong phạm vi.',
  'policies.change.blacklist.on': '{host}: đánh dấu bị chặn cho mọi nhân sự trong phạm vi.',
  'policies.change.blacklist.off': '{host}: bỏ đánh dấu bị chặn.',
  'policies.change.category': '{host}: phân loại đổi từ {from} sang {to}.',
  'policies.change.class': '{host}: xếp loại năng suất đổi từ {from} sang {to}.',
  'policies.change.new.private':
    '{host}: luật mới, đánh dấu riêng tư — tên miền này đang được ghi lại theo mặc định và từ nay thì không.',
  'policies.change.new.named':
    '{host}: luật mới, không đánh dấu riêng tư. Tên miền này vốn đã được ghi lại theo mức thu thập mặc định, nên luật này chỉ đổi cách đánh giá chứ không thu thập thêm gì.',

  'policies.ack.label': 'Tôi hiểu thay đổi này khiến hệ thống ghi lại nhiều hơn hiện nay.',
  'policies.ack.why':
    'Ô xác nhận này chỉ xuất hiện khi bản tóm tắt có mục siết chặt. Một cảnh báo nổi lên cả với việc đúng thì người bị chặn sẽ học cách bấm qua nó.',
  'policies.save': 'Lưu và tạo phiên bản mới',
  'policies.saving': 'Đang lưu…',
  'policies.saved': 'Đã lưu. Luật {host} nay ở phiên bản {version}.',
  'policies.refused.title': 'Máy chủ từ chối lưu — chưa có gì thay đổi',
  'policies.refused.kept':
    'Phiên bản cũ vẫn nguyên vẹn: màn hình không áp thay đổi trước khi máy chủ nhận, nên không có gì phải hoàn tác. Bản tóm tắt bên dưới vẫn là thứ bạn định gửi.',
  'policies.refused.field': 'Máy chủ chỉ đích danh trường: {fields}',

  'policies.posture.title': 'Trạng thái giám sát — sửa được, ở màn hình Chính sách thu thập',
  'policies.posture.body':
    'Cấu hình thu thập nay sửa được từ trong sản phẩm: xem danh sách, tạo bản nháp, sửa và kích hoạt, với quyền quản lý chính sách.',
  'policies.posture.where':
    'Năm trong bảy mục trên sửa được ở màn hình Chính sách thu thập, cùng với số máy đã xác nhận phiên bản đang áp dụng:',
  'policies.posture.notEditable':
    'Hai mục còn lại thì KHÔNG sửa được ở bất cứ đâu. Ghi phím bị từ chối ở mọi tầng, nên không màn hình nào bật được. Biểu thức che tiêu đề cần một công cụ riêng, vì lý do ghi ngay dưới mục đó.',
  'policies.posture.noControl':
    'Bảng này vẫn không có ô nhập nào, và cố ý cũng không có nút bị làm mờ: nơi sửa là màn hình kia, và một bản sao thứ hai của cùng một biểu mẫu là một bản sao thứ hai để lệch pha.',
  'policies.posture.noValues':
    'Giá trị hiện hành vẫn không in ở đây, và đó là quyết định chứ không phải thiếu sót: bảng này không đọc chính sách, còn màn hình Chính sách thu thập thì đọc từ máy chủ — in một giá trị mặc định ở đây sẽ là màn hình này tự bịa ra trạng thái giám sát của công ty.',
  'policies.posture.field.level': 'Mức thu thập',
  'policies.posture.field.idle': 'Ngưỡng nghỉ ngắn và nghỉ kéo dài',
  'policies.posture.field.windowTitle': 'Có thu thập tiêu đề cửa sổ hay không',
  'policies.posture.field.offHours': 'Có thu thập hoạt động ngoài giờ hay không',
  'policies.posture.field.screenshot': 'Chụp ảnh màn hình',
  'policies.posture.field.keylog': 'Ghi phím',
  'policies.posture.field.redaction': 'Biểu thức che tiêu đề',
  'policies.posture.redaction':
    'Biểu thức che tiêu đề do quản trị viên của công ty tự viết và chạy trên mọi tiêu đề thu về, nên mỗi biểu thức đều được kiểm tra trước: tối đa 200 ký tự và phải viết đúng cú pháp.',
  'policies.posture.failClosed':
    'Việc kiểm tra này thất bại theo hướng đóng: chỉ cần MỘT biểu thức không an toàn thì chính sách đó không giữ lại tiêu đề cửa sổ hay tiêu đề trang nào nữa, cho toàn công ty. Đó là lý do mục này cần một công cụ riêng.',
  'policies.posture.lawfulBasis.title': 'Cơ sở pháp lý của việc giám sát',
  'policies.posture.lawfulBasis.body':
    'Việc giám sát máy trạm và định vị GPS dựa trên các căn cứ được LIỆT KÊ trong Luật Bảo vệ dữ liệu cá nhân (Luật 91/2025/QH15, hiệu lực 01/01/2026) và Nghị định 356/2025/NĐ-CP: thực hiện hợp đồng lao động, và tuân thủ nghĩa vụ chấm công, lưu trữ chứng từ tiền lương theo Bộ luật Lao động. Mục đích phải cụ thể cho từng hoạt động giám sát; một lần đồng ý chung cho cả “chính sách giám sát” là không đủ.',
  'policies.posture.lawfulBasis.location':
    'Dữ liệu vị trí là dữ liệu cá nhân nhạy cảm và có quyền từ chối bắt buộc: nhân viên tự bật quyền từ chối ở màn hình Dữ liệu của tôi, và hệ thống ngừng lưu toạ độ ngay lập tức, không phụ thuộc vào ứng dụng di động.',
  'policies.posture.lawfulBasis.document':
    'Văn bản đầy đủ: “Cơ sở pháp lý của việc giám sát”; đánh giá tác động: “Đánh giá tác động bảo vệ dữ liệu”.',

  'policies.version.title': 'Phiên bản chính sách và xác nhận của thiết bị',
  'policies.version.body':
    'Chính sách có phiên bản, và mỗi máy xác nhận đã áp phiên bản nào. Sự kiện đến từ một máy còn ở phiên bản cũ vẫn được nhận và được xét lại theo chính sách hiện hành — từ chối chúng sẽ là xoá dữ liệu của mọi máy chưa kịp hỏi, còn nhận mà không xét lại sẽ làm lớp che phía máy chủ vô dụng đúng trong trường hợp nó tồn tại vì nó.',
  'policies.version.where':
    'Phiên bản mỗi máy đang giữ và thời điểm nó xác nhận nằm ở màn hình Thiết bị, trong mục sức khoẻ của từng máy.',
  'policies.version.link': 'Mở màn hình Thiết bị',

  /* ─── G9 / D-37: is this score still the number its inputs imply? ─────── */

  'score.freshness.current': 'Số liệu còn khớp',
  'score.freshness.current.help':
    'Mọi dữ liệu điểm này được tính từ đều cũ hơn chính điểm. Không có gì thay đổi sau khi tính.',
  'score.freshness.stillReal':
    'Con số bên dưới là số thật và vẫn là số của bạn — nó không bị ẩn, không bị đưa về 0 và bạn vẫn có quyền chất vấn nó. Đây KHÔNG phải lỗi và cũng KHÔNG phải thiếu dữ liệu: độ tin cậy và độ đầy đủ nói về những gì lần tính đã nhìn thấy, còn mục này nói rằng dữ liệu đầu vào đã thay đổi SAU khi tính.',
  'score.freshness.scheduled.title': 'Dữ liệu đầu vào đã thay đổi — sẽ được tính lại tự động',
  'score.freshness.scheduled.body':
    'Kỳ này nằm trong khoảng mà tác vụ quét theo giờ còn xét, nên nó sẽ được tính lại mà không cần ai yêu cầu. Không cần làm gì thêm.',
  'score.freshness.manual.title': 'Dữ liệu đầu vào đã thay đổi — sẽ KHÔNG tự tính lại',
  'score.freshness.manual.body':
    'Kỳ này đã cũ hơn khoảng thời gian mà đợt tính lại theo giờ xét tới, nên con số sẽ giữ nguyên cho tới khi có người yêu cầu tính lại kỳ này.',
  'score.freshness.manual.nobody':
    'Nói rõ vì đây là chỗ dễ hiểu nhầm nhất: KHÔNG có lần chạy nào đang đến. Chờ sẽ không làm con số này đổi. Hãy đề nghị quản lý hoặc bộ phận vận hành yêu cầu tính lại.',
  'score.freshness.scheduled.tag': 'Sẽ tự tính lại',
  'score.freshness.manual.tag': 'Không có lần chạy nào đang đến',
  'score.freshness.inputs.heading': 'Những nguồn dữ liệu đã thay đổi:',
  'score.freshness.inputs.unnamed':
    'Có một nguồn dữ liệu đã thay đổi nhưng màn hình này chưa có tên cho nó, nên nó được báo là có thay đổi thay vì bị bỏ qua.',
  'score.freshness.input.algorithm':
    'Phiên bản cách tính điểm (bản ghi này được tính bằng một phiên bản khác)',
  'score.freshness.input.dailyAggregate': 'Bảng tổng hợp theo ngày',
  'score.freshness.input.attendanceSession':
    'Phiên chấm công (một mục chấm công nhập tay, hoặc một yêu cầu điều chỉnh được duyệt hay bị từ chối, đều dựng lại phiên của ngày đó)',
  'score.freshness.input.timeEntry': 'Bản ghi thời gian trong kỳ',
  'score.freshness.unknown.title': 'Không xác định được số liệu còn khớp hay không',
  'score.freshness.unknown.absent':
    'Không nhận được thông tin về độ mới của số liệu, mà thông tin này là bắt buộc. Màn hình cố ý KHÔNG đọc sự vắng mặt đó thành “còn khớp”: đọc như vậy chính là cái lỗi mà thông tin này sinh ra để chặn.',
  'score.freshness.unknown.malformed':
    'Thông tin về độ mới của số liệu có dạng màn hình này không đọc được. Màn hình không đoán.',
  'score.freshness.unknown.contradictory':
    'Thông tin về độ mới của số liệu tự mâu thuẫn (ví dụ báo đã cũ nhưng lại nói không cần tính lại). Chọn tin nửa nào cũng là màn hình tự bịa ra một câu trả lời về điểm của bạn.',

  /* ══════════════════ WP-L5 (Task 53) — alerts, incidents, data risk ═══════ */

  'common.cancel': 'Huỷ',
  'filters.all': 'Tất cả',
  'filters.selected': '(đang chọn)',

  'access.title': 'Lý do truy cập',
  'access.why':
    'Đọc {subject} là truy cập dữ liệu nhạy cảm. Mỗi lần đọc được ghi lại kèm tên bạn, những nhân viên có trong trang và lý do bạn nhập dưới đây.',
  'access.recorded':
    'Lý do truy cập là bắt buộc và không tắt được bằng bất kỳ cấu hình nào. Vì vậy màn hình này không gửi yêu cầu nào trước khi bạn nhập lý do.',
  'access.label': 'Lý do',
  'access.rule': 'Ít nhất {min} ký tự sau khi bỏ khoảng trắng thừa.',
  'access.latin1':
    'Lý do này có ký tự tiếng Việt có dấu, mà hệ thống chưa nhận được dấu ở ô này, nên yêu cầu không được gửi đi. Xin nhập lại không dấu. Đây là một thiếu sót đã được báo cho bộ phận kỹ thuật.',
  'access.open': 'Mở {subject}',
  'access.change': 'Đổi lý do và đọc lại',
  'access.current': 'Lý do đang dùng: “{reason}”',
  'access.closed.title': 'Chưa đọc gì cả',
  'access.closed.body':
    'Chưa có yêu cầu nào được gửi tới máy chủ. Nhập lý do truy cập ở trên để mở {subject}.',

  'alerts.intro':
    'Hàng đợi cảnh báo: cái gì đã bắn mà chưa ai ký tên vào, và cái nào nặng nhất. Đây không phải sổ nhật ký.',
  'alerts.subjectWord': 'hộp thư cảnh báo',
  'alerts.scopeNote':
    'Quyền xử lý sự cố cho bạn vào màn hình này nhưng không mở rộng tầm nhìn: bạn vẫn chỉ thấy phạm vi mình được phép xem. Người ở mức phòng ban không thấy cảnh báo nào không gắn với nhân viên trong nhánh của mình, kể cả cảnh báo về máy móc. Một hàng đợi ngắn không có nghĩa là một tuần yên ả.',
  'alerts.filter.severity': 'Mức độ',
  'alerts.filter.status': 'Trạng thái',
  'alerts.filter.serverSide':
    'Mọi bộ lọc ở đây được áp dụng trên toàn bộ dữ liệu, mỗi ô chọn một giá trị. Danh sách tải theo từng trang và không có tổng số, nên lọc ngay trên trang đang xem sẽ cho một con số không đối chiếu được với gì.',
  'alerts.status.open': 'Chưa xác nhận',
  'alerts.status.acknowledged': 'Đã xác nhận',
  'alerts.status.suppressed': 'Bị gộp trùng',
  'alerts.subject': 'Đối tượng: {key}',
  'alerts.subject.withheld':
    'Đối tượng: bị giữ lại (khoá gộp có thành phần bạn không được xem — cả khoá bị thay, không phải bị cắt)',
  'alerts.occurrences': '{count} lần bắn',
  'alerts.measured': 'Đo được: {value}',
  'alerts.measured.withheld': 'Đo được: không có số — máy chủ không công bố giá trị này',
  'alerts.threshold': 'Ngưỡng: {value}',
  'alerts.threshold.withheld': 'Ngưỡng: không có số',
  'alerts.redacted':
    'Có ít nhất một khoá trong chi tiết bị giữ lại với tài khoản này. Dòng vẫn được trả về nguyên vẹn để số đếm không bị sai.',
  'alerts.hasIncident': 'Đã mở sự cố #{id} từ cảnh báo này.',
  'alerts.read.newest': 'Cảnh báo mới nhất trong những gì đã đọc: {when}',
  'alerts.read.none': 'Chưa đọc được cảnh báo nào để nói về thời điểm.',
  'alerts.partial.excludes':
    'những cảnh báo cũ hơn {count} dòng đã đọc — luồng còn tiếp, trang này chưa phải toàn bộ',
  'alerts.empty.title': 'Không có cảnh báo nào khớp bộ lọc này',
  'alerts.empty.body':
    'Bộ lọc đang là mức độ “{severity}”, trạng thái “{status}”. Đây là câu trả lời cho bộ lọc đó, không phải cho toàn công ty.',
  'alerts.ack.open': 'Xác nhận',
  'alerts.ack.title': 'Xác nhận cảnh báo #{id}',
  'alerts.ack.fact.irreversible':
    'Không hoàn tác được: không bỏ xác nhận, không chuyển cho người khác, không sửa lại ghi chú.',
  'alerts.ack.fact.noop':
    'Bấm lại lần nữa là no-op 200, trả về đúng người và đúng thời điểm của lần đầu. Nên nút này an toàn khi bấm hai lần, và lần thứ hai không đè lên chữ ký của người đầu tiên.',
  'alerts.ack.fact.cooldown':
    'Việc xác nhận khởi động cooldown_minutes của rule, tính từ acknowledged_at. Những lần bắn trong khoảng đó được ghi thành dòng bị gộp trùng — gộp KHÔNG phải là xoá.',
  'alerts.ack.fact.recurrence':
    'Sau cooldown, lần bắn tiếp theo là một cảnh báo MỚI với id riêng và lần xác nhận riêng, không phải mở lại cảnh báo này.',
  'alerts.ack.fact.audit':
    'Ghi chú được lưu nguyên văn cùng cảnh báo và làm lý do của dòng nhật ký kiểm toán.',
  'alerts.ack.note': 'Ghi chú (tuỳ chọn — máy chủ cho phép để trống)',
  'alerts.ack.submit': 'Xác nhận',
  'alerts.ack.submitting': 'Đang gửi…',
  'alerts.ack.ok': 'Đã xác nhận lúc {when}.',
  'alerts.ack.okBody': 'Không thể hoàn tác. Lần bắn tiếp theo sau cooldown sẽ là một cảnh báo mới.',
  'alerts.ack.done': 'Đã xác nhận bởi {who} lúc {when}.',
  'alerts.ack.unknownActor': 'không rõ ai',
  'alerts.ack.suppressed':
    'Đây là bản ghi trùng của một cảnh báo khác. Xác nhận nó sẽ bị từ chối và để cảnh báo thật vẫn mở — hãy xác nhận cảnh báo mà nó trùng.',
  'alerts.ack.readOnly':
    'Cần quyền resolve_incidents để xác nhận. Tài khoản này đọc được nhưng không quyết định được.',

  'incidents.intro': 'Sự cố đang mở: bạn được phép làm gì tiếp theo, và còn bao lâu.',
  'incidents.subjectWord': 'danh sách sự cố',
  'incidents.detail.subject': 'chi tiết sự cố',
  'incidents.scopeNote':
    'Quyền xử lý sự cố cho bạn vào màn hình này nhưng không mở rộng tầm nhìn: bạn vẫn chỉ thấy phạm vi mình được phép xem. Người ở mức phòng ban không thấy sự cố nào không gắn nhân viên trong nhánh của mình.',
  'incidents.filter.state': 'Trạng thái',
  'incidents.open': 'Mở',
  'incidents.empty.title': 'Không có sự cố nào khớp bộ lọc',
  'incidents.empty.body':
    'Bộ lọc trạng thái đang là “{state}”. Đây là câu trả lời cho bộ lọc đó, không phải “công ty không có sự cố nào”.',
  'incidents.partial.excludes': 'phần còn lại của hàng đợi — đang hiện {shown} trên {total}',
  'incidents.row.breached': 'ĐÃ QUÁ HẠN SLA',
  'incidents.row.due': 'Hạn SLA: {when}',
  'incidents.row.noSla': 'Không có hạn SLA cho mức độ này',
  'incidents.detail.title': 'Sự cố #{number} — {title}',

  'incidents.state.new': 'Mới',
  'incidents.state.acknowledged': 'Đã tiếp nhận',
  'incidents.state.investigating': 'Đang điều tra',
  'incidents.state.waitingForEmployee': 'Chờ nhân viên',
  'incidents.state.resolved': 'Đã xử lý',
  'incidents.state.falsePositive': 'Báo động giả',
  'incidents.state.escalated': 'Đã leo thang',
  'incidents.state.closed': 'Đã đóng',

  'incidents.move.new': 'Đưa về Mới',
  'incidents.move.acknowledged': 'Tiếp nhận',
  'incidents.move.investigating': 'Chuyển sang điều tra',
  'incidents.move.waitingForEmployee': 'Chờ nhân viên trả lời',
  'incidents.move.resolved': 'Kết luận đã xử lý',
  'incidents.move.falsePositive': 'Kết luận báo động giả',
  'incidents.move.escalated': 'Leo thang',
  'incidents.move.closed': 'Đóng hồ sơ',

  'incidents.transition.title': 'Chuyển trạng thái',
  'incidents.transition.derived':
    'Đây là toàn bộ {count} bước hợp lệ từ trạng thái “{state}”, lấy từ chính bảng transition của máy chủ. Những bước không hợp lệ KHÔNG được vẽ ra dưới dạng nút mờ — chúng không tồn tại trên màn hình này.',
  'incidents.transition.terminal.title': 'Đã đóng — không còn bước nào',
  'incidents.transition.terminal.body':
    'closed là trạng thái cuối và không mở lại được. Một hàng đợi mà trạng thái cuối không phải là cuối thì không ai đếm được. Nếu điều kiện tái diễn, hãy mở một sự cố MỚI.',
  'incidents.transition.form': 'Chuyển sang: {to}',
  'incidents.transition.noteLabel': 'Ghi chú (bắt buộc)',
  'incidents.transition.noteRule':
    'Ít nhất {min} ký tự. Ghi chú trở thành nội dung dòng lịch sử của bước này — một bước không có lời giải thích là một bước không ai giải thích.',
  'incidents.transition.resolutionLabel': 'Kết luận (bắt buộc)',
  'incidents.transition.resolutionRule':
    'Ba trạng thái kết thúc đều đòi một kết luận. Không có kết luận thì hệ thống từ chối lưu.',
  'incidents.transition.submit': 'Gửi',
  'incidents.transition.submitting': 'Đang gửi…',
  'incidents.transition.ok': 'Đã chuyển sang “{state}”.',
  'incidents.transition.serverAllowed': 'Máy chủ nói những bước hợp lệ lúc này là: {list}',

  'incidents.reopen.title': 'Mở lại — hai điều cần biết trước',
  'incidents.reopen.clears':
    'Mở lại XOÁ kết luận hiện tại (resolution, resolution_code, resolved_at, resolved_by, verified_risk). Kết luận cũ không mất: nó nằm trong dòng lịch sử đã ghi lúc kết luận.',
  'incidents.reopen.sla':
    'Mở lại KHÔNG cấp hạn SLA mới. Hạn cũ và dấu vi phạm được giữ nguyên — nếu không, ai đó chỉ cần kết luận rồi mở lại là biến một hạn đã lỡ thành một hạn đạt.',

  'incidents.sla.due.title': 'SLA — còn hạn',
  'incidents.sla.breached.title': 'SLA — ĐÃ QUÁ HẠN',
  'incidents.sla.concludedLate.title': 'SLA — kết thúc sau hạn',
  'incidents.sla.none.title': 'SLA — không có hạn cho mức độ này',
  'incidents.sla.none.body':
    'Chính sách của công ty không đặt hạn xử lý cho mức độ này. “Không có hạn” không phải là “đã kịp hạn”, và càng không phải hạn bằng không.',
  'incidents.sla.due': 'Hạn: {when}',
  'incidents.sla.remaining': 'Còn lại: {interval}',
  'incidents.sla.overdue': 'Quá hạn: {interval}',
  'incidents.sla.concludedLate': 'Kết thúc sau hạn {interval} — máy chủ không tính đây là vi phạm.',
  'incidents.sla.interval': '{hours} giờ {minutes} phút',
  'incidents.sla.interval.unknown': 'không đo được',
  'incidents.sla.notRestarted':
    'Đồng hồ KHÔNG chạy lại. Hạn ở trên là hạn gốc: mở lại giữ nguyên sla_due_at và giữ nguyên dấu vi phạm, nên sự cố này quá hạn ngay từ lúc được mở lại.',
  'incidents.sla.arithmetic':
    'Hạn = created_at + số giờ theo mức độ, lấy từ chính sách mặc định đang hiệu lực. Đổi mức độ tính lại từ created_at chứ không từ lúc đổi, nên hạ hay nâng mức độ đều không mua thêm thời gian. Màn hình này in lại con số của máy chủ, không tự tính.',

  'incidents.assign.title': 'Phụ trách và mức độ',
  'incidents.assign.owner': 'Người phụ trách (mã nhân viên)',
  'incidents.assign.ownerNote':
    'Mã nhân viên trong Workforce Pulse — có ở cột đầu màn Nhân sự, không phải mã tài khoản achau.',
  'incidents.assign.severity': 'Mức độ',
  'incidents.assign.regrade':
    'Đổi mức độ sẽ tính lại hạn SLA TỪ created_at, không phải từ bây giờ. Một sự cố mở đã lâu, khi được nâng lên critical, quá hạn ngay lập tức — và đó là câu trả lời đúng.',
  'incidents.assign.save': 'Lưu',
  'incidents.assign.saving': 'Đang lưu…',
  'incidents.assign.saved': 'Đã lưu.',
  'incidents.assign.kept':
    'Không có gì được áp trước khi máy chủ chấp nhận, nên không có gì phải hoàn tác. Nội dung bạn nhập vẫn còn nguyên ở trên.',
  'incidents.assign.conflict':
    'Người khác vừa sửa sự cố này trong lúc bạn đang nhập (phiên bản hiện tại: {etag}). Màn hình đã đọc lại; hãy xem thay đổi của họ rồi quyết định. Màn hình cố ý không tự gửi lại, vì làm vậy sẽ đè mất thay đổi vừa lưu.',
  'incidents.assign.noEtag.title': 'Chưa gửi được thay đổi',
  'incidents.assign.noEtag.body':
    'Lần đọc này chưa kèm phiên bản của bản ghi, mà không có phiên bản thì việc lưu có thể đè mất thay đổi của người khác. Vì vậy ở đây không có nút; đây không phải vấn đề quyền của bạn. Hãy tải lại sự cố.',

  'incidents.evidence.title': 'Bằng chứng',
  'incidents.evidence.refsOnly':
    'Chỉ là tham chiếu. Bằng chứng nằm trong kho lưu trữ của công ty và chưa mở được từ trình duyệt, nên ở đây in mã tham chiếu chứ không tạo một liên kết chết.',
  'incidents.evidence.redacted':
    'Có mục bằng chứng bị giữ lại với tài khoản này — cụ thể là khoá gộp của cảnh báo, khi nó nêu tên một ứng dụng hoặc tên miền mà bạn không có quyền view_apps / view_websites. Cả mục bị bỏ chứ không bị cắt bớt.',
  'incidents.evidence.noCapturedAt': 'không có thời điểm thu thập',
  'incidents.evidence.empty.title': 'Chưa có mục bằng chứng nào',
  'incidents.evidence.empty.body':
    'Sự cố mở tay chưa gắn tham chiếu nào. Sự cố mở tự động từ cảnh báo mang hai mục: chính cảnh báo, và khoá gộp mô tả đối tượng.',
  'incidents.evidence.kind.activityEvent': 'Sự kiện hoạt động',
  'incidents.evidence.kind.timeEntry': 'Bản ghi thời gian',
  'incidents.evidence.kind.attendanceDay': 'Ngày chấm công',
  'incidents.evidence.kind.alert': 'Cảnh báo',
  'incidents.evidence.kind.file': 'Tệp',
  'incidents.evidence.kind.note': 'Ghi chú',

  'incidents.timeline.title': 'Dòng thời gian',
  'incidents.timeline.intro':
    'Bản ghi các ghi chú có chữ ký, theo thứ tự thời gian, kèm bước chuyển mà mỗi dòng ghi lại. Một CONSTRAINT TRIGGER từ chối commit một lần đổi trạng thái mà không ghi cặp from/to, nên lịch sử ở đây là đầy đủ.',
  'incidents.timeline.transition': 'Chuyển: {from} → {to}',
  'incidents.timeline.transitionUnknown':
    'Chuyển: {from} → {to} (màn hình chưa có tên cho một trong hai trạng thái)',
  'incidents.timeline.noTransition': 'Dòng này không ghi lần đổi trạng thái nào.',
  'incidents.timeline.halfPair':
    'Dòng này thiếu một nửa của bước chuyển (từ {from} sang {to}). Không đọc được là một ghi chú, cũng không đọc được là một bước chuyển — hãy báo cho quản trị viên.',
  'incidents.timeline.notRead': 'Chưa đọc dòng thời gian — bản danh sách không mang comments.',
  'incidents.timeline.empty.title': 'Chưa có dòng nào',
  'incidents.timeline.empty.body': 'Chưa ai ghi gì và chưa có bước chuyển nào được thực hiện.',
  'incidents.timeline.system': 'hệ thống',
  'incidents.timeline.author': 'nhân viên #{id}',

  'incidents.noDelete.title': 'Không có xoá — và đó là ràng buộc của CSDL',
  'incidents.noDelete.body':
    'Không có cách nào xoá một sự cố, ở bất kỳ mức độ nào và qua bất kỳ màn hình nào. Sự cố chỉ rời khỏi hệ thống khi tới hạn lưu trữ.',
  'incidents.noDelete.instead':
    'Vì vậy ở đây không có nút xoá, và cũng không có nút xoá bị mờ. Sự cố mở nhầm thì chuyển sang “Báo động giả” kèm kết luận, và nó ở lại trong hồ sơ.',
  'incidents.unknownState.title': 'Trạng thái này chưa được màn hình nhận ra',
  'incidents.unknownState.body':
    'Máy chủ trả về trạng thái “{state}”, mà bảng transition của client không có dòng nào cho nó. Màn hình không đoán: không nút nào được vẽ ra. Hãy cập nhật client.',

  'dataRisk.intro':
    'Dữ liệu công ty đã ra ngoài — hoặc suýt ra ngoài — chưa, và đã có ai nhận việc chưa.',
  'dataRisk.subjectWord': 'trang rủi ro dữ liệu',
  'dataRisk.tiles.title': 'Bốn loại cảnh báo rủi ro dữ liệu',
  'dataRisk.tiles.counted':
    'Mỗi ô đếm những gì trang này đọc được, không phải tổng của công ty: danh sách cảnh báo tải theo từng trang và không có tổng số.',
  'dataRisk.tile.read': 'Đã đọc {count} cảnh báo',
  'dataRisk.tile.unacknowledged': 'Chưa ai xác nhận: {count}',
  'dataRisk.tile.noneRead': 'Không có cảnh báo nào trong những gì đã đọc.',
  'dataRisk.tile.link': 'Mở {label} trong Cảnh báo',
  'dataRisk.partial.excludes': 'phần còn lại của luồng cảnh báo — trang này chưa đọc hết',

  'dataRisk.domains.title': 'Tên miền bị chặn',
  'dataRisk.domains.subject': 'thống kê tên miền',
  'dataRisk.domains.window': 'Khoảng ngày: {from} → {to}',
  'dataRisk.domains.counted':
    'Bộ lọc chặn chỉ áp dụng trên trang đang xem: khớp {matched} trong {page} dòng đã đọc, trên tổng {total} dòng.',
  'dataRisk.domains.col.domain': 'Tên miền',
  'dataRisk.domains.col.seconds': 'Giây',
  'dataRisk.domains.col.visits': 'Lượt',
  'dataRisk.domains.col.employees': 'Số nhân viên',
  'dataRisk.domains.withheld': 'bị giữ lại',
  'dataRisk.domains.noEmployeeCount': 'không công bố',
  'dataRisk.domains.empty.title': 'Không có tên miền bị chặn nào trong trang đã đọc',
  'dataRisk.domains.empty.body':
    'Đây là câu trả lời cho trang đang xem, không phải cho toàn bộ khoảng ngày.',

  'dataRisk.absent.title': 'Những gì trang này chưa nói được',
  'dataRisk.absent.body':
    'Những mục dưới đây là nội dung một trang Rủi ro dữ liệu cần có mà hiện chưa có dữ liệu. Chúng được nêu tên chứ không thay bằng số giả, vì một trang im lặng sẽ bị đọc thành “không có gì xảy ra”.',
  'dataRisk.absent.usb':
    'Sự kiện USB: chưa có nguồn dữ liệu riêng cho loại sự kiện này. Tín hiệu USB duy nhất trang này thấy được là cảnh báo về thiết bị USB chưa được duyệt.',
  'dataRisk.absent.file':
    'Thao tác tệp: cũng chưa có nguồn dữ liệu riêng. Tín hiệu duy nhất là cảnh báo về thao tác hàng loạt trên tệp.',
  'dataRisk.absent.blockedSeconds':
    'Thời gian bị chặn theo từng nhân viên: số liệu này chưa được công bố cho màn hình nào.',
  'dataRisk.absent.visits':
    'Lượt truy cập bị chặn theo từng nhân viên: số liệu về trang web chỉ gộp theo tên miền và phân loại, kèm số người, chứ không nêu từng người.',
  'dataRisk.absent.category':
    'Phân loại của sự cố: sự cố chưa mang nhãn rủi ro dữ liệu, nên không lọc riêng được. Trang này chỉ tới được sự cố qua chính cảnh báo đã mở ra nó.',
  'dataRisk.absent.noControls':
    'Mục này không có nút nào, và cố ý cũng không có nút bị làm mờ: một nút xám nói “việc này làm được, nhưng không phải bởi bạn”, còn ở đây thì chưa ai làm được.',

  'trigger.extendedIdle': 'Rảnh kéo dài',
  'trigger.noActiveTask': 'Không có việc đang chạy',
  'trigger.estimateExceeded': 'Vượt ước lượng',
  'trigger.productivityDrop': 'Năng suất giảm',
  'trigger.workloadOverload': 'Quá tải',
  'trigger.workloadUnderload': 'Dưới tải',
  'trigger.lateArrival': 'Đi muộn',
  'trigger.earlyLeave': 'Về sớm',
  'trigger.missingHours': 'Thiếu giờ',
  'trigger.agentOffline': 'Agent ngoại tuyến',
  'trigger.agentStopped': 'Agent bị dừng',
  'trigger.agentUninstalled': 'Agent bị gỡ',
  'trigger.unknownDevice': 'Thiết bị lạ',
  'trigger.usbUnapproved': 'USB chưa được duyệt',
  'trigger.bulkFileOperation': 'Thao tác tệp hàng loạt',
  'trigger.blockedApp': 'Ứng dụng bị chặn',
  'trigger.blockedWebsite': 'Website bị chặn',
  'trigger.afterHoursActivity': 'Hoạt động ngoài giờ',

  /* ── WP-K8 (Task 48) — Reports ────────────────────────────────────────── */

  'k8.reports.intro':
    'Xuất dữ liệu thành tệp. Mỗi yêu cầu chạy nền; trang này cho biết khi nào xong và cấp liên kết tải khi có tệp.',

  'k8.reports.create.title': 'Yêu cầu báo cáo mới',
  'k8.reports.create.type': 'Loại báo cáo',
  'k8.reports.create.format': 'Định dạng',
  'k8.reports.create.from': 'Từ ngày',
  'k8.reports.create.to': 'Đến ngày',
  'k8.reports.create.employees': 'Giới hạn theo nhân viên (mã nhân viên, cách nhau bởi dấu phẩy)',
  'k8.reports.create.employeesHint':
    'Để trống nghĩa là toàn bộ phạm vi bạn được phép xem — khác hẳn với một danh sách rỗng. Mã nhân viên có ở cột đầu màn Nhân sự.',
  'k8.reports.create.department': 'Giới hạn theo phòng ban (mã phòng ban)',
  'k8.reports.create.submit': 'Tạo báo cáo',
  'k8.reports.create.submitting': 'Đang gửi yêu cầu…',
  'k8.reports.create.builderNote':
    '5 trong 13 loại báo cáo đã tạo được tệp. 8 loại còn lại bị từ chối thay vì sinh ra tệp rỗng: một tệp chấm công rỗng đưa cho kế toán còn tệ hơn một lời từ chối.',
  'k8.reports.create.unbuilt': 'Loại báo cáo này chưa tạo được tệp — yêu cầu sẽ bị từ chối.',
  'k8.reports.create.productivityNote':
    'Số liệu năng suất trong tệp là điểm ĐÃ LƯU tại thời điểm bảng nguồn ghi, không tính lại lúc xuất. Một tệp tạo ngay sau khi duyệt sửa vẫn mang con số trước khi sửa.',
  'k8.reports.create.periodInvalid':
    'Cần cả ngày bắt đầu và ngày kết thúc, và bắt đầu không sau kết thúc.',

  'k8.reports.type.attendance': 'Chấm công',
  'k8.reports.type.shift_compliance': 'Tuân thủ ca',
  'k8.reports.type.active_idle': 'Hoạt động / rảnh',
  'k8.reports.type.app_usage': 'Sử dụng ứng dụng',
  'k8.reports.type.website_usage': 'Sử dụng website',
  'k8.reports.type.task_timer': 'Hẹn giờ theo việc',
  'k8.reports.type.estimate_vs_actual': 'Ước lượng so với thực tế',
  'k8.reports.type.productivity': 'Năng suất',
  'k8.reports.type.workload': 'Khối lượng công việc',
  'k8.reports.type.usb_file_events': 'Sự kiện USB và tệp',
  'k8.reports.type.alerts_incidents': 'Cảnh báo và sự cố',
  'k8.reports.type.device_health': 'Sức khoẻ thiết bị',
  'k8.reports.type.audit_log': 'Nhật ký kiểm toán',

  'k8.reports.list.title': 'Báo cáo công ty đã yêu cầu',
  'k8.reports.list.companyWide':
    'Đây là hàng đợi xuất của cả công ty, không phải riêng những báo cáo bạn tạo. Mỗi lần tải xuống vẫn được kiểm lại theo phạm vi hiện tại của bạn, nên một dòng hiện ở đây vẫn có thể bị từ chối khi bấm tải.',
  'k8.reports.list.empty.title': 'Công ty chưa có báo cáo nào',
  'k8.reports.list.empty.body': 'Không có báo cáo nào khớp bộ lọc đang áp dụng.',
  'k8.reports.list.partial': 'Đang hiện {shown} trong tổng số {total} báo cáo.',

  'k8.reports.status.pending': 'Đang chờ',
  'k8.reports.status.running': 'Đang tạo',
  'k8.reports.status.done': 'Đã xong',
  'k8.reports.status.failed': 'Thất bại',
  'k8.reports.status.expired': 'Đã hết hạn lưu trữ',

  'k8.reports.row.period': 'Kỳ',
  'k8.reports.row.requested': 'Yêu cầu lúc',
  'k8.reports.row.completed': 'Hoàn tất lúc',
  'k8.reports.row.rows': 'Số dòng',
  'k8.reports.row.size': 'Kích thước (byte)',
  'k8.reports.row.scope': 'Số nhân viên trong phạm vi đã đóng băng',
  'k8.reports.row.downloads': 'Số lần đã cấp liên kết tải',
  'k8.reports.row.failure': 'Mã lỗi',
  'k8.reports.row.heading': 'Báo cáo {id} — {type} ({format})',
  'k8.reports.row.periodAbsent': 'máy chủ không ghi kỳ dữ liệu cho dòng này',

  'k8.reports.download': 'Tải xuống',
  'k8.reports.downloading': 'Đang xin liên kết…',
  'k8.reports.download.granted':
    'Đã cấp liên kết cho {filename}. Liên kết đó hết hiệu lực lúc {expiresAt} và đã được dùng ngay khi cấp.',
  'k8.reports.download.grantedNoName':
    'Đã cấp liên kết. Liên kết đó hết hiệu lực lúc {expiresAt} và đã được dùng ngay khi cấp.',
  'k8.reports.download.ttlNote':
    'Liên kết tải là một chứng chỉ mang danh: S3 không kiểm lại quyền, chỉ kiểm chữ ký và hạn 180 giây (SEC-48). Trang này KHÔNG giữ liên kết nào — mỗi lần bấm là một lần chạy lại đủ chuỗi kiểm quyền và cấp liên kết mới, đồng thời cộng vào download_count. Vì vậy không có liên kết nào có thể hết hạn khi bạn đang mở trang.',
  'k8.reports.download.stale':
    'Trạng thái trên màn hình đã cũ so với máy chủ. Dòng này vừa được đọc lại.',
  'k8.reports.download.reasonNote':
    'Mỗi lần tải xuống đều được ghi lại kèm số dòng thật của tệp và danh sách nhân viên trong phạm vi đã đóng băng. Thao tác này không đòi lý do truy cập, nên màn hình không hỏi: thu một lý do không cần thiết là thu dưới danh nghĩa sai.',

  'k8.reports.noControl.pending':
    'Chưa có tệp nên chưa có nút tải. Trang tự đọc lại cho tới khi tệp sẵn sàng.',
  'k8.reports.noControl.failed':
    'Không sinh được tệp, nên không có gì để tải. Hãy tạo lại yêu cầu ở biểu mẫu phía trên.',
  'k8.reports.noControl.expired':
    'Tệp đã bị xoá theo lịch lưu trữ. Không thao tác nào lấy lại được; hãy tạo yêu cầu mới.',

  'k8.reports.absence.title': 'Ô trống không tồn tại trong tệp',
  'k8.reports.absence.body':
    'Bảng tính đọc một ô rỗng thành số 0. Nên chỗ nào không có số đo, tệp in một trong bốn nhãn dưới đây dưới dạng chữ, để phép cộng bỏ qua thay vì cộng thêm 0. Gặp nhãn nào, đừng thay bằng số.',
  'k8.reports.absence.not_computed':
    'Không có dòng tổng hợp cho nhân viên và ngày đó. KHÔNG phải một phép đo bằng 0.',
  'k8.reports.absence.withheld_private':
    'Thời gian ĐÃ được làm và ĐÃ nằm trong tổng. Nội dung làm gì bị chính sách riêng tư giữ lại, nên không hiện tên.',
  'k8.reports.absence.data_incomplete':
    'Ngày đó tồn tại nhưng không xác thực được (agent ngoại tuyến, dữ liệu thiếu). Không phải vắng mặt, cũng không phải một ca đầy đủ.',
  'k8.reports.absence.not_applicable':
    'Cột không có ý nghĩa với dòng này — không ca, không công thức, hoặc không hẹn giờ.',
  'k8.reports.figure.absent': '{sentinel} — {meaning}',

  'k8.reports.absent.title': 'Những gì màn hình này chưa làm được',
  'k8.reports.absent.body':
    'Hai mục dưới đây vẫn còn thiếu, nên chúng được nêu tên thay vì dựng giả.',
  'k8.reports.absent.noScheduleList':
    'Chưa có danh sách lịch báo cáo: một lịch chỉ mở lại được nếu bạn đã ghi mã của nó, nên chưa duyệt được hết các lịch đang chạy của công ty.',
  'k8.reports.absent.noDirectory':
    'Nhân viên, phòng ban và người nhận trên trang này vẫn nhập bằng mã: một báo cáo nhận nhiều mã cùng lúc, và ô chọn từng người không diễn đạt được điều đó. Mã có ở cột đầu màn Nhân sự.',
  'k8.reports.absent.noControls':
    'Khối này không có nút nào, và cũng không có nút bị làm mờ: một nút xám nói “được đấy, nhưng không phải cho bạn”, còn sự thật là chưa ai làm được việc này.',

  'k8.sched.title': 'Lịch gửi báo cáo định kỳ',
  'k8.sched.intro':
    'Một lịch tạo tệp theo chu kỳ và gửi thư cho người nhận. Thư chỉ mang liên kết về màn hình này, không đính kèm tệp: hòm thư là bản sao vĩnh viễn không thu hồi được.',
  'k8.sched.name': 'Tên lịch',
  'k8.sched.type': 'Loại báo cáo',
  'k8.sched.formats': 'Định dạng (chọn ít nhất một)',
  'k8.sched.frequency': 'Tần suất',
  'k8.sched.dayOfWeek': 'Thứ trong tuần',
  'k8.sched.dayOfMonth': 'Ngày trong tháng',
  'k8.sched.dayOfMonthHint':
    'Tối đa 28. Ngày 29–31 bị TỪ CHỐI chứ không bị kéo về: "hằng tháng ngày 31" mà tự lùi xuống 28 trong tháng Hai sẽ làm báo cáo lương lệch tới ba ngày, bốn lần mỗi năm, trong khi dòng dữ liệu vẫn ghi 31.',
  'k8.sched.hour': 'Giờ',
  'k8.sched.minute': 'Phút',
  'k8.sched.timezone': 'Múi giờ (để trống là dùng múi giờ công ty)',
  'k8.sched.localTime':
    'Giờ chạy là giờ ĐỊA PHƯƠNG của múi giờ trên, kể cả hai ngày đổi giờ: lịch 08:00 vẫn chạy 08:00 địa phương ở cả hai phía, dù hai lần cách nhau 23 hay 25 giờ.',
  'k8.sched.period': 'Kỳ dữ liệu mỗi lần chạy',
  'k8.sched.recipients': 'Người nhận (mã nhân viên, cách nhau bởi dấu phẩy)',
  'k8.sched.recipientsHint':
    'Là mã nhân viên trong chính công ty này; không nhận địa chỉ email tự do, vì như vậy một lịch sẽ thành đường xuất dữ liệu thường trực tới hòm thư bất kỳ. Nhân viên đã nghỉ cũng bị từ chối.',
  'k8.sched.enabled': 'Bật ngay sau khi tạo',
  'k8.sched.submit': 'Tạo lịch',
  'k8.sched.submitting': 'Đang tạo lịch…',
  'k8.sched.created': 'Đã tạo lịch "{name}". Lần chạy kế tiếp: {nextRunAt}.',
  'k8.sched.createdId':
    'Lịch vừa tạo đã được mở ở khối “Quản lý một lịch” bên dưới. Vẫn nên ghi lại mã {id}: chưa có danh sách lịch, nên mã là đường duy nhất quay lại bản ghi này sau khi tải lại trang.',

  /* ── Task U19 — quản lý một lịch qua GET/PATCH/DELETE /report-schedules/{id} ── */

  'k8.sched.manage.title': 'Quản lý một lịch',
  'k8.sched.manage.intro':
    'Chưa có danh sách lịch, nên khối này làm việc theo một mã: mã của lịch bạn vừa tạo, hoặc mã bạn đã ghi lại.',
  'k8.sched.manage.idLabel': 'Id lịch',
  'k8.sched.manage.read': 'Đọc lịch',
  'k8.sched.manage.reading': 'Đang đọc…',
  'k8.sched.manage.subject': 'lịch báo cáo định kỳ',
  'k8.sched.manage.state.enabled': 'Đang bật — sẽ chạy vào lần kế tiếp',
  'k8.sched.manage.state.paused': 'Đang tạm dừng — bản ghi vẫn còn, không chạy',
  'k8.sched.manage.nextRun': 'Lần chạy kế tiếp',
  'k8.sched.manage.lastRun': 'Lần chạy gần nhất',
  'k8.sched.manage.lastStatus': 'Kết quả lần gần nhất',
  'k8.sched.manage.failures': 'Số lần hỏng liên tiếp',
  'k8.sched.manage.recipients': 'Người nhận',
  'k8.sched.manage.neverRun': 'chưa chạy lần nào',
  'k8.sched.manage.inFlight':
    'đang chạy — cột kết quả được xoá lúc xếp hàng nên một lần chạy lại không còn hiện kết quả của tuần trước',
  'k8.sched.manage.status.success': 'thành công',
  'k8.sched.manage.status.failed': 'thất bại',
  'k8.sched.manage.status.skipped': 'bỏ qua',
  'k8.sched.manage.pause': 'Tạm dừng lịch',
  'k8.sched.manage.resume': 'Bật lại lịch',
  'k8.sched.manage.toggling': 'Đang gửi PATCH…',
  'k8.sched.manage.pauseNote':
    'Tạm dừng giữ nguyên bản ghi: người nhận, giờ chạy và lần chạy kế tiếp đều còn, và bật lại được.',
  'k8.sched.manage.noEtag':
    'Lần đọc này chưa kèm phiên bản của bản ghi, mà không có phiên bản thì việc lưu có thể đè mất thay đổi của người khác. Nên ở đây chưa có nút bật/tắt; hãy đọc lại lịch.',
  'k8.sched.manage.delete': 'Gỡ lịch này',
  'k8.sched.manage.deleting': 'Đang gửi DELETE…',
  'k8.sched.manage.deleteNote':
    'Cảnh báo: xoá lịch là xoá hẳn, không phải đánh dấu “đã kết thúc”, và không có gì để bật lại. Các tệp lịch này đã sinh ra vẫn tải được, nhưng mất đường liên kết ngược về lịch; dấu vết còn lại nằm trong nhật ký kiểm toán. Muốn dừng tạm thời thì dùng “Tạm dừng lịch”.',
  'k8.sched.manage.deleted.title': 'Lịch {id} đã bị xoá',
  'k8.sched.manage.deleted.body':
    'Lịch đã được xoá và màn hình đã đọc lại để xác nhận: không còn bản ghi nào. Đây là xoá hẳn chứ không phải kết thúc — bản ghi không quay lại được. Các báo cáo lịch này đã sinh vẫn tải được như cũ.',
  'k8.sched.manage.deleted.pending': 'Đang đọc lại để xác nhận…',
  'k8.sched.manage.deleted.unconfirmed':
    'Lịch đã được nhận lệnh xoá, nhưng lần đọc lại vẫn thấy bản ghi, nên màn hình này không xác nhận được là nó đã biến mất. Hãy bấm “Đọc lịch” lại với đúng mã này.',

  'k8.sched.freq.daily': 'Hằng ngày',
  'k8.sched.freq.weekly': 'Hằng tuần',
  'k8.sched.freq.monthly': 'Hằng tháng',
  'k8.sched.dow.1': 'Thứ Hai',
  'k8.sched.dow.2': 'Thứ Ba',
  'k8.sched.dow.3': 'Thứ Tư',
  'k8.sched.dow.4': 'Thứ Năm',
  'k8.sched.dow.5': 'Thứ Sáu',
  'k8.sched.dow.6': 'Thứ Bảy',
  'k8.sched.dow.7': 'Chủ Nhật',
  'k8.sched.period.previous_day': 'Ngày hôm trước',
  'k8.sched.period.previous_week': 'Tuần trước',
  'k8.sched.period.previous_month': 'Tháng trước',
  'k8.sched.period.rolling_7d': '7 ngày gần nhất',
  'k8.sched.period.rolling_30d': '30 ngày gần nhất',
  'k8.sched.periodNote':
    'Mọi kỳ đều kết thúc ở NGÀY HÔM QUA. Một báo cáo bao gồm cả ngày đang chạy sẽ trình bày một ngày dở dang như một ngày trọn vẹn.',

  'k8.sched.problem.name.length': 'Tên phải có từ 1 đến 120 ký tự.',
  'k8.sched.problem.name.line_break':
    'Tên không được xuống dòng: nó mở đầu tiêu đề thư, và một tiêu đề thư kết thúc ở chỗ xuống dòng.',
  'k8.sched.problem.formats.count': 'Chọn từ 1 đến 3 định dạng.',
  'k8.sched.problem.formats.duplicate':
    'Không chọn lặp một định dạng: hai bản cùng định dạng sẽ đè lên nhau.',
  'k8.sched.problem.day_of_week.range': 'Chọn một thứ trong tuần (Thứ Hai đến Chủ Nhật).',
  'k8.sched.problem.day_of_month.range': 'Ngày trong tháng phải từ 1 đến 28.',
  'k8.sched.problem.hour_of_day.range': 'Giờ phải từ 0 đến 23.',
  'k8.sched.problem.minute_of_hour.range': 'Phút phải từ 0 đến 59.',
  'k8.sched.problem.recipients.count': 'Cần từ 1 đến 50 mã nhân viên.',

  'k8.sched.scopeNote.title': 'Lịch định kỳ là hành vi quản trị ở phạm vi toàn công ty',
  'k8.sched.scopeNote.body':
    'Tạo lịch đòi phạm vi toàn công ty, chặt hơn tạo một báo cáo lẻ, vì một lịch ghi tên người nhận: ai chỉ xem được dữ liệu của chính mình mà tạo được lịch thì sắp xếp được việc dữ liệu bị gửi định kỳ cho một danh sách đồng nghiệp. Phiên này đang ở phạm vi {level}, nên biểu mẫu tạo lịch và khối quản lý lịch không hiện.',

  /* ── WP-K8 (Task 48) — Audit Log ──────────────────────────────────────── */

  'k8.audit.intro':
    'Hai loại bản ghi chỉ ghi thêm: hành động quản trị, và lượt đọc dữ liệu nhạy cảm. Không xoá được và không sửa được — đó là chủ ý, không phải thiếu sót.',
  'k8.audit.subjectWord': 'nhật ký kiểm toán',
  'k8.audit.selfLogged':
    'Chính lần đọc này cũng là một truy cập nhạy cảm: nó được ghi lại kèm lý do bạn khai ở trên, và người kiểm toán tiếp theo sẽ đọc được.',

  'k8.audit.filters.title': 'Bộ lọc',
  'k8.audit.filters.serverSide':
    'Mọi ô dưới đây lọc trên toàn bộ nhật ký, không phải lọc lại trên trang đang xem. Nhật ký tải theo từng trang và không có tổng số, nên lọc ngay trên trang sẽ cho một con số không đối chiếu được với gì.',
  'k8.audit.filters.logType': 'Bảng',
  'k8.audit.filters.logType.any': 'Cả hai',
  'k8.audit.filters.logType.audit': 'Hành động quản trị',
  'k8.audit.filters.logType.data_access': 'Lượt đọc dữ liệu',
  'k8.audit.filters.actor': 'Người thực hiện (mã nhân viên)',
  'k8.audit.filters.action': 'Hành động',
  'k8.audit.filters.actionHint':
    'Khớp CHÍNH XÁC động từ, ví dụ device.revoke — không phải tìm theo chuỗi con. Gõ "device" sẽ không ra dòng nào, và điều đó không có nghĩa là không có hành động nào trên thiết bị.',
  'k8.audit.filters.resourceType': 'Loại tài nguyên',
  'k8.audit.filters.resourceId': 'Id tài nguyên',
  'k8.audit.filters.from': 'Từ (RFC3339 UTC)',
  'k8.audit.filters.to': 'Đến (RFC3339 UTC)',
  'k8.audit.filters.success': 'Kết quả',
  'k8.audit.filters.success.any': 'Cả hai',
  'k8.audit.filters.success.true': 'Chỉ thành công',
  'k8.audit.filters.success.false': 'Chỉ thất bại',
  'k8.audit.filters.apply': 'Áp dụng bộ lọc',
  'k8.audit.filters.clear': 'Xoá bộ lọc',

  'k8.audit.results.title': 'Kết quả',
  'k8.audit.results.count': 'Đã đọc {count} dòng ở trang này.',
  'k8.audit.results.noTotal':
    'Nhật ký tải theo từng trang và không có tổng số, nên con số ở trên là “những gì trang này đã đọc”, không phải “tổng số sự kiện của công ty”.',
  'k8.audit.results.next': 'Trang tiếp',
  'k8.audit.empty.narrowed.title': 'Không có dòng nào khớp bộ lọc này',
  'k8.audit.empty.narrowed.body':
    'Đây là câu về bộ lọc đang đặt, không phải về nhật ký. Hãy nới bộ lọc rồi đọc lại.',
  'k8.audit.empty.wide.title': 'Không có dòng nào trong trang này',
  'k8.audit.empty.wide.body': 'Không đặt bộ lọc nào và máy chủ vẫn trả về trang rỗng.',

  'k8.audit.col.when': 'Thời điểm',
  'k8.audit.col.type': 'Bảng',
  'k8.audit.col.actor': 'Người thực hiện',
  'k8.audit.col.action': 'Hành động',
  'k8.audit.col.resource': 'Tài nguyên',
  'k8.audit.col.result': 'Kết quả',
  'k8.audit.col.reason': 'Lý do',
  'k8.audit.type.audit': 'Quản trị',
  'k8.audit.type.data_access': 'Đọc dữ liệu',
  'k8.audit.result.success': 'Thành công',
  'k8.audit.result.failure': 'Thất bại',
  'k8.audit.actor.noName': 'không có tên đăng nhập trên dòng này',
  'k8.audit.resource.none': 'không gắn tài nguyên nào',

  'k8.audit.reason.access': 'Lý do người đọc đã khai: "{text}"',
  'k8.audit.reason.accessNotGated':
    'Chính sách không bắt khai lý do cho lần đọc này — không phải người đọc từ chối nói.',
  'k8.audit.reason.action': 'Lý do kèm hành động: "{text}"',
  'k8.audit.reason.actionNeedsNone': 'Hành động này không đòi lý do.',

  'k8.audit.detail.show': 'Xem chi tiết dòng {id}',
  'k8.audit.detail.hide': 'Ẩn chi tiết dòng {id}',
  'k8.audit.detail.title': 'Chi tiết',
  'k8.audit.detail.before': 'Trước',
  'k8.audit.detail.after': 'Sau',
  'k8.audit.detail.metadataOnly':
    'Phần trước và sau chỉ ghi lại thông tin mô tả, không bao giờ chứa bí mật hay nội dung riêng tư. Một mục vắng mặt ở đây nghĩa là nó không được ghi lại, KHÔNG phải là nó đã bị xoá.',
  'k8.audit.detail.absent': 'không ghi lại',
  'k8.audit.detail.runId': 'Mã đợt đồng bộ achau',
  'k8.audit.detail.runIdNote':
    'Có mặt khi hành động cũng thay đổi dữ liệu bên achau. Cùng giá trị này nằm trong AuditLog bên achau, nên hai phía khớp được bằng chứng.',
  'k8.audit.detail.correlationId': 'Mã tra cứu',
  'k8.audit.detail.ip': 'Địa chỉ IP',
  'k8.audit.detail.userAgent': 'Trình duyệt và hệ điều hành',

  'k8.audit.immutable.title': 'Nhật ký này không sửa và không xoá được',
  'k8.audit.immutable.body':
    'Nhật ký chỉ ghi thêm. Trang này vì thế không có nút xoá, không có nút sửa, và cũng không có nút bị làm mờ: một nút xám sẽ nói rằng ai đó làm được việc này còn bạn thì không, trong khi sự thật là không ai làm được, qua bất kỳ màn hình nào.',

  'k8.audit.export.link': 'Xuất dữ liệu này thành tệp ở trang Báo cáo',
  'k8.audit.export.hint':
    'Cần quyền xuất báo cáo. Nhật ký kiểm toán là một trong 13 loại báo cáo tạo được ở màn hình Báo cáo.',

  /* ═══════════════ WP-M4 (Task 57) — Phân tích AI ═══════════════ */

  'm4.intro':
    'Mỗi bản phân tích ở đây là một câu do mô hình viết về công việc của người khác. Màn hình này luôn hiển thị kèm độ tin cậy và phần giới hạn, không ẩn được và không thu gọn được.',

  'm4.disclaimer.title': 'Kết quả phân tích của AI không được dùng làm căn cứ kỷ luật duy nhất.',
  'm4.disclaimer.body':
    'AI không được tự sa thải, kỷ luật, trừ lương hay kết luận gian lận. Phần “Khuyến nghị” là tư vấn, không phải quyết định. Trước khi hành động với một con người, hãy đọc phần Giới hạn của chính bản phân tích đó và mở bằng chứng.',
  'm4.disclaimer.fixed':
    'Câu trên luôn hiển thị: không đóng được, không thu gọn được, và nó nằm trên mọi kết quả AI của trang này.',

  'm4.subject.insights': 'danh sách phân tích AI',
  'm4.subject.evidence': 'bằng chứng của bản phân tích',
  'm4.subject.answer': 'câu trả lời của AI',

  'm4.list.title': 'Các phân tích đã lưu',
  'm4.list.caption':
    'Các bản phân tích đã lưu, mới nhất trước. Danh sách này không gọi mô hình nào, nên vẫn đọc được khi dịch vụ AI gián đoạn.',
  'm4.list.empty.title': 'Chưa có phân tích nào khớp bộ lọc này',
  'm4.list.empty.body':
    'Đây là câu về BỘ LỌC, không phải về công ty và không phải về ngân sách: không có dòng nào trong khoảng thời gian và loại phân tích đang chọn. Nếu công ty đã hết hạn mức tháng, màn hình sẽ nói riêng điều đó ở ô bên dưới chứ không hiện ô rỗng này.',
  'm4.list.readCount': 'Đã đọc {count} bản ghi.',
  'm4.list.noTotal':
    'Danh sách tải theo từng trang và không có tổng số, nên mọi con số trên màn hình này là “đã đọc được bao nhiêu”, không phải “công ty có bao nhiêu”.',
  'm4.list.more': 'Đọc tiếp trang sau',
  'm4.filter.type': 'Loại phân tích',
  'm4.filter.type.all': 'Tất cả',
  'm4.filter.from': 'Từ ngày',
  'm4.filter.to': 'Đến ngày',
  'm4.filter.apply': 'Áp dụng',
  'm4.filter.serverSide':
    'Cả ba bộ lọc được áp dụng trên toàn bộ danh sách, không phải lọc lại trên trang đang xem: lọc trên một trang sẽ chỉ trả lời “trong số vừa đọc được”, mà đó không phải câu hỏi ai đó gõ ra.',

  'm4.card.summaryLabel': 'Kết luận',
  'm4.card.verbatim':
    'Kết luận, giới hạn, nguyên nhân và khuyến nghị là văn bản của máy chủ, hiển thị nguyên văn — không dịch, không cắt, không tóm tắt lại.',
  'm4.card.limitations.title': 'Giới hạn của phân tích này',
  'm4.card.limitations.why':
    'Đọc hết phần này trước khi hành động. Một câu điểm số cũ được nối vào đây sau khi validate, kể cả khi mô hình không nhắc — nên đây là chỗ duy nhất cảnh báo đó chắc chắn còn.',
  'm4.card.limitations.sentinel':
    'Máy chủ không gửi giới hạn nào cho dòng này. #DATA_INCOMPLETE là chữ do màn hình điền vào — nó KHÔNG có nghĩa là "phân tích này không có giới hạn nào".',
  'm4.card.confidence.label': 'Độ tin cậy',
  'm4.card.confidence.help':
    'Độ tin cậy nói lần chạy đó NHÌN THẤY được bao nhiêu, không nói kết luận đúng hay sai. Bản tóm tắt số học luôn có độ tin cậy 0 vì nó không suy diễn gì.',
  'm4.card.confidence.absent.not_published':
    'Máy chủ không công bố độ tin cậy cho dòng này. Đây không phải 0.',
  'm4.card.confidence.absent.not_a_number':
    'Độ tin cậy nhận được không phải một con số đọc được. Đây không phải 0.',
  'm4.card.confidence.absent.out_of_range':
    'Độ tin cậy nhận được nằm ngoài khoảng cho phép, nên màn hình không hiển thị nó như một số đo.',
  'm4.card.causes.title': 'Nguyên nhân có thể',
  'm4.card.causes.none': 'Mô hình không nêu nguyên nhân nào cho kết luận này.',
  'm4.card.actions.title': 'Khuyến nghị',
  'm4.card.actions.none': 'Mô hình không nêu khuyến nghị nào.',
  'm4.card.actions.advisory':
    'Chỉ là tư vấn. Không có hành động nhân sự nào được thực hiện tự động.',
  'm4.card.evidence.title': 'Bằng chứng',
  'm4.card.evidence.none':
    'Bản phân tích này không trích dẫn số liệu nào. Mọi kết luận phải truy ngược được về số liệu — một đoạn văn về con người mà không nêu cơ sở thì đừng hành động theo.',
  'm4.card.evidence.open': 'Mở bằng chứng',
  'm4.card.evidence.close': 'Đóng bằng chứng',
  'm4.card.generatedAt': 'Sinh lúc',
  'm4.card.model': 'Mô hình',
  'm4.card.question': 'Câu hỏi',
  'm4.card.period': 'Kỳ dữ liệu',
  'm4.card.jobId': 'Mã yêu cầu',

  'm4.status.published': 'Mô hình đã trả lời',
  'm4.status.fallback': 'Tóm tắt số học, không có AI',
  'm4.status.budget': 'Không chạy AI — hết hạn mức tháng',
  'm4.status.superseded': 'Đã bị thay thế',
  'm4.status.unreadable': 'Không đọc được',

  'm4.type.summary': 'Tóm tắt',
  'm4.type.trend_comparison': 'So sánh xu hướng',
  'm4.type.anomaly': 'Bất thường',
  'm4.type.cause_hypothesis': 'Giả thuyết nguyên nhân',
  'm4.type.recommendation': 'Khuyến nghị',
  'm4.type.workload_analysis': 'Phân tích khối lượng công việc',
  'm4.type.scoped_query': 'Truy vấn theo quyền',
  'm4.type.unknown': 'Loại phân tích chưa được đặt tên',

  'm4.card.fallback.title': 'Đây là bản tóm tắt số học, không phải phân tích của AI',
  'm4.card.fallback.body':
    'Mô hình không trả lời được, hoặc câu trả lời của nó trượt validate hai lần. Những con số dưới đây được dựng bằng số học từ chính dữ liệu đã lấy về — chúng có thật. Cái không có là phần suy diễn.',
  'm4.card.fallback.markers':
    'Phân biệt được ở bốn chỗ độc lập: trạng thái, mã chẩn đoán, một câu trong phần Giới hạn, và hai mục Nguyên nhân/Khuyến nghị đều trống.',
  'm4.card.fallback.diagnosis': 'Mã chẩn đoán',
  'm4.card.fallback.noDiagnosis':
    'Không có lý do kèm theo, dù lẽ ra phải có. Ghi lại vì đây là dấu hiệu của một bản ghi bất thường.',

  'm4.card.budget.title': 'Không có phân tích vì công ty đã dùng hết hạn mức AI của tháng',
  'm4.card.budget.body':
    'Yêu cầu này đã bị từ chối TRƯỚC KHI gửi đi. Không có lời gọi mô hình nào được thực hiện, nên cũng không có kết quả nào để tìm.',
  'm4.card.budget.notOutage':
    'Đây KHÔNG phải sự cố hệ thống: hạn mức là chuyện chi phí, không phải chuyện hệ thống có chạy hay không, và báo lỗi hệ thống lúc này sẽ đánh thức nhầm người.',
  'm4.card.budget.notEmpty':
    'Đây cũng KHÔNG phải "không tìm thấy gì". AI chưa từng chạy cho yêu cầu này.',
  'm4.card.budget.fix':
    'Chờ không có tác dụng trong tháng này. Hạn mức chỉ mở lại khi sang tháng mới, hoặc khi người vận hành nâng WP_AI_SOFT_LIMIT.',

  'm4.card.superseded.title': 'Dòng này đã bị thay thế',
  'm4.card.superseded.body':
    'Bản phân tích này được đánh dấu là đã bị thay thế, nhưng quy trình sinh phân tích không bao giờ đặt dấu đó. Hãy đọc nó như một bản ghi cũ chứ đừng hành động theo.',

  'm4.card.unreadable.title': 'Không đọc được bản phân tích đó',
  'm4.card.unreadable.no_status': 'Bản ghi không có trạng thái.',
  'm4.card.unreadable.unknown_status':
    'Trạng thái là “{status}”, mà màn hình này chưa có tên cho giá trị đó, nên nó KHÔNG được đọc như một bản phân tích đã công bố. Đọc một trạng thái lạ thành trạng thái tốt chính là khuyết tật, không phải phiên bản nhẹ hơn của nó.',
  'm4.card.unreadable.no_summary':
    'Dòng không mang kết luận nào. Một đoạn trống ở đây sẽ đọc thành "mô hình đã xem và không thấy gì đáng nói", mà không ai nói câu đó cả.',
  'm4.card.unreadable.caveatsKept':
    'Phần Giới hạn vẫn được hiển thị bên dưới: đó là thứ phải sống sót qua mọi nhánh.',

  'm4.card.disagree.title': 'Hai dấu hiệu trên dòng này mâu thuẫn nhau',
  'm4.card.disagree.body':
    'Hợp đồng nói ai_unavailable đúng bằng (status === "fallback"). Dòng này không như vậy. Màn hình không tự chọn tin nửa nào — nó hiển thị theo status và báo mâu thuẫn ở đây để người đọc biết mà nghi ngờ dòng trước mặt.',

  'm4.ask.title': 'Hỏi AI',
  'm4.ask.provider.label': 'Ai trả lời',
  'm4.ask.provider.litellm': 'Phân tích nội bộ',
  'm4.ask.provider.brain': 'BrainAI',
  'm4.ask.provider.litellm.note':
    'Câu trả lời do chính dịch vụ này dựng: lấy số liệu tổng hợp trong phạm vi của bạn, kèm độ tin cậy, giới hạn và bằng chứng.',
  'm4.ask.provider.brain.note':
    'Câu hỏi được chuyển tới BrainAI của công ty qua Control Plane. BrainAI tự truy cập dữ liệu bằng quyền của chính bạn; dịch vụ này chuyển nguyên văn câu trả lời và không diễn giải.',
  'm4.ask.provider.brain.help':
    'BrainAI của công ty. Không dùng hạn mức AI chung và không lưu lại bản phân tích nào.',
  'm4.ask.provider.brain.off':
    'Bản triển khai này chưa cấu hình Control Plane, nên không có BrainAI để gọi. Người quản trị cần đặt CONTROL_PLANE_URL rồi khởi động lại dịch vụ.',
  'm4.ask.asked': 'Đã hỏi',
  'm4.ask.brain.runId': 'Mã lần chạy',
  'm4.ask.brain.passthrough':
    'Nội dung trên là nguyên văn của BrainAI. Dịch vụ này không kiểm tra lại, không tính độ tin cậy và không lưu thành bản phân tích.',
  'm4.ask.body':
    'Đây là lời gọi AI duy nhất được trả lời đồng bộ, và nó chiếm một trong số ít slot của tiến trình trong tối đa 60 giây.',
  'm4.ask.suggest.title': 'Câu hỏi gợi ý',
  'm4.ask.suggest.note':
    'Bấm một gợi ý chỉ điền vào ô bên dưới, KHÔNG gửi đi. Bạn vẫn phải bấm Hỏi — một câu hỏi chưa đọc là câu hỏi bạn không nhận trách nhiệm được, khi câu trả lời là một câu về đồng nghiệp.',
  'm4.ask.suggest.hours.label': 'Xu hướng giờ làm',
  'm4.ask.suggest.hours.question':
    'Trong kỳ đang chọn, tổng giờ hoạt động của phạm vi tôi được xem thay đổi thế nào so với kỳ trước, và những ngày nào lệch nhiều nhất?',
  'm4.ask.suggest.workload.label': 'Cân bằng khối lượng',
  'm4.ask.suggest.workload.question':
    'Khối lượng công việc trong kỳ này phân bố ra sao giữa những người trong phạm vi tôi được xem, và có ai lệch rõ so với phần còn lại không?',
  'm4.ask.suggest.completeness.label': 'Độ đầy đủ dữ liệu',
  'm4.ask.suggest.completeness.question':
    'Trong kỳ này có bao nhiêu ngày dữ liệu không đầy đủ, và điều đó ảnh hưởng thế nào tới các con số tổng hợp?',
  'm4.ask.suggest.alerts.label': 'Mẫu cảnh báo',
  'm4.ask.suggest.alerts.question':
    'Những cảnh báo phát sinh trong kỳ này tập trung vào loại nào, và chúng có đi kèm thay đổi nào trong số liệu tổng hợp không?',
  'm4.ask.label': 'Câu hỏi của bạn',
  'm4.ask.rule': 'Từ 1 đến {max} ký tự.',
  'm4.ask.submit': 'Hỏi',
  'm4.ask.submitting': 'Đang hỏi…',
  'm4.ask.answer.title': 'Câu trả lời',
  'm4.ask.empty':
    'Chưa có câu hỏi nào trong tab này. Đây là câu về tab đang mở, không phải về công ty.',
  'm4.ask.notPersisted':
    'Cuộc hội thoại này không được lưu. Nó mất khi bạn đóng thẻ trình duyệt hoặc đăng xuất, nên hãy chép lại phần bạn cần giữ.',
  'm4.ask.noStreaming': 'Câu trả lời xuất hiện một lần, khi đã xong, chứ không hiện dần từng chữ.',
  'm4.ask.refused.title': 'Mô hình đã từ chối trả lời câu hỏi này',
  'm4.ask.refused.body':
    'Máy chủ đánh dấu refused = true: câu hỏi đòi một kết luận mà dữ liệu không đỡ nổi, hoặc đòi dữ liệu ngoài phạm vi bạn được xem. Đây là câu trả lời, không phải lỗi.',

  'm4.refusal.serverSentence': 'Máy chủ nói:',
  'm4.refusal.serverSentence.withheld':
    'Có một câu giải thích kèm theo nhưng không được hiển thị nguyên văn cho loại lỗi này. Mã lỗi và mã tra cứu ở dưới.',
  'm4.refusal.budget.title': 'Không hỏi được: công ty đã dùng hết hạn mức AI của tháng',
  'm4.refusal.budget.body':
    'Yêu cầu bị từ chối trước khi gửi tới mô hình, nên không có gì được tiêu thêm và cũng không có kết quả nào.',
  'm4.refusal.budget.notOutage':
    'Đây không phải sự cố hệ thống: các tính năng AI còn lại và toàn bộ màn hình khác vẫn hoạt động bình thường.',
  'm4.refusal.budget.notEmpty':
    'Đây cũng KHÔNG phải "không tìm thấy gì": AI chưa chạy lần nào cho câu hỏi này.',
  'm4.refusal.budget.noRetry':
    'Không có nút thử lại ở đây, và cũng không có nút xám: bấm lại chỉ tạo thêm một lần từ chối. Hạn mức mở lại khi sang tháng, hoặc khi người vận hành nâng WP_AI_SOFT_LIMIT.',
  'm4.refusal.budget.period': 'Tháng tính phí',
  'm4.refusal.budget.cost': 'Đã tiêu (USD)',
  'm4.refusal.budget.limit': 'Hạn mức (USD)',
  'm4.refusal.budget.unpublished':
    'Máy chủ không gửi con số này kèm lời từ chối. Không hiển thị 0 ở đây — 0 sẽ là một lời nói dối về chi tiêu của công ty.',
  'm4.refusal.budget.noEndpoint':
    'Màn hình chưa cho biết trước hạn mức AI còn lại bao nhiêu. Ba con số trên là tất cả những gì hệ thống nói về chi tiêu AI, và chỉ xuất hiện đúng lúc bị từ chối.',
  'm4.refusal.busy.title': 'Tất cả slot hỏi đồng bộ đang bận',
  'm4.refusal.busy.body':
    'Tiến trình này chỉ cho phép vài lượt chat cùng lúc, mỗi lượt giữ kết nối tới 60 giây. Máy chủ từ chối ngay thay vì xếp hàng, vì hàng đợi sâu ở đây nghĩa là chờ vài phút trên một kết nối đã bỏ.',
  'm4.refusal.busy.wait':
    'Một slot sẽ trống trở lại. Đây là điều khác hẳn hết hạn mức: chờ ở đây CÓ tác dụng.',
  'm4.refusal.busy.slots': 'Đang chạy {inFlight} trên {limit} slot.',
  'm4.refusal.busy.slotsUnknown': 'Máy chủ không gửi số slot kèm lời từ chối.',
  'm4.refusal.unavailable.title': 'Không gọi được tới mô hình',
  'm4.refusal.unavailable.body':
    'Bình thường một lượt hỏi vẫn trả về bản tóm tắt số học khi dịch vụ AI gián đoạn, nên lỗi này nghĩa là từ chối đến từ chỗ khác. Các màn hình còn lại vẫn chạy.',

  'm4.evidence.title': 'Bằng chứng của bản phân tích {id}',
  'm4.evidence.body':
    'Đây là truy cập nhạy cảm: mỗi lần mở được ghi lại kèm số bản ghi thật sự trao ra và danh sách người thật sự bị đọc.',
  'm4.evidence.count': 'Xem được {shown} trong {total} mục được trích dẫn.',
  'm4.evidence.col.ref': 'Tham chiếu',
  'm4.evidence.col.value': 'Giá trị',
  'm4.evidence.col.where': 'Đọc lại ở đâu',
  'm4.evidence.scopeNote':
    'Thấy được bản phân tích không có nghĩa là thấy được bản ghi: mọi tham chiếu được kiểm lại theo quyền của bạn HÔM NAY, vì một bản phân tích sống lâu hơn quyền đã tạo ra nó.',
  'm4.evidence.threeCauses':
    'Bịa ra, đã bị xoá, và ngoài quyền của bạn là ba nguyên nhân cho cùng một kết quả. Bạn không được cho biết đó là nguyên nhân nào.',
  'm4.evidence.withheld.not_accessible':
    'Không trao mục này. Máy chủ giữ nguyên chỗ của nó và KHÔNG gửi khoá value — vắng mặt, chứ không phải một giá trị rỗng.',
  'm4.evidence.withheld.no_value_published':
    'Máy chủ nói mục này xem được nhưng không gửi kèm giá trị nào. Đây là một dòng bất thường chứ không phải một lần từ chối, nên nó không được đếm vào số mục đã trao.',
  'm4.evidence.malformed':
    'Một mục trong danh sách bằng chứng không đúng dạng và đã bị bỏ qua. Ghi ra ở đây thay vì bỏ đi im lặng.',
  'm4.evidence.target.reachable':
    'Bản ghi gốc chưa mở được từ màn hình nào; mã tham chiếu ở trên là đường tra cứu.',
  'm4.evidence.target.noEndpoint':
    'Bản ghi gốc chưa mở được từ màn hình nào. Màn hình in mã tham chiếu chứ không tạo liên kết — một liên kết chết trên hồ sơ điều tra đọc thành “bằng chứng đã mất”.',
  'm4.evidence.target.notEmitted':
    'Loại bằng chứng này chưa được hỗ trợ. Nếu bạn đang nhìn thấy nó, hãy báo cho quản trị viên.',
  'm4.evidence.freshness.title': 'Tình trạng cập nhật của điểm này, tính ở thời điểm HIỆN TẠI',
  'm4.evidence.freshness.body':
    'Khối này nói về BÂY GIỜ vì đây là một lần đọc trực tiếp. Phần Giới hạn của bản phân tích nói về lúc nó được sinh ra. Hai câu hỏi khác nhau, không cái nào thay cái nào.',
  'm4.evidence.empty': 'Bản phân tích này không trích dẫn bản ghi nào, nên không có gì để mở.',

  'm4.analyze.title': 'Yêu cầu một phân tích mới',
  'm4.analyze.body':
    'Bản phân tích được chạy nền: yêu cầu được nhận ngay, và kết quả xuất hiện trong danh sách khi chạy xong.',
  'm4.analyze.type': 'Loại phân tích',
  'm4.analyze.from': 'Từ ngày',
  'm4.analyze.to': 'Đến ngày',
  'm4.analyze.submit': 'Xếp hàng phân tích',
  'm4.analyze.submitting': 'Đang gửi…',
  'm4.analyze.queued': 'Đã xếp hàng. Mã yêu cầu: {jobId}',
  'm4.analyze.poll': 'Kiểm tra kết quả',
  'm4.analyze.polling': 'Đang kiểm tra…',
  'm4.analyze.pollEmpty':
    'Yêu cầu này chưa có kết quả nào. Trang rỗng ở đây nghĩa là “chưa xong”, KHÔNG phải “không có kết quả”.',
  'm4.analyze.noJobStatus':
    'Chưa có trạng thái tiến độ, nên màn hình không phân biệt được “đang xếp hàng” với “đang chạy”. Kết quả xuất hiện trong danh sách khi xong.',
  'm4.analyze.pollResult': 'Kết quả của yêu cầu này',
  'm4.analyze.employeeIds': 'Nhân viên',
  'm4.analyze.employeeIds.hint':
    'Để trống nghĩa là không giới hạn theo nhân viên nào — khác hẳn với một danh sách rỗng.',

  'm4.absent.title': 'Giới hạn của bản phân tích',
  'm4.absent.body':
    'Mỗi mục dưới đây là một câu hỏi màn hình này chưa trả lời được. Chúng được nêu tên chứ không thay bằng số giả.',
  'm4.absent.budget':
    'Hạn mức AI còn lại: đây là thứ quyết định câu hỏi có được trả lời hay không, và hiện bạn chỉ biết còn bao nhiêu đúng vào lúc bị từ chối.',
  'm4.absent.insightById':
    'Mở một bản phân tích theo mã: hiện chỉ tới được nó khi nó còn nằm trong trang danh sách.',
  'm4.absent.alertById':
    'Mở một cảnh báo theo mã: bằng chứng dạng cảnh báo hiện chỉ in ra mã, chưa mở thẳng được.',
  'm4.absent.dailyById':
    'Mở một bản ghi tổng hợp theo ngày theo mã: hiện chỉ tra được theo khoảng ngày.',
  'm4.absent.jobStatus':
    'Tiến độ của một yêu cầu phân tích: hiện chỉ biết nó xong khi kết quả xuất hiện trong danh sách.',
  'm4.absent.conversation':
    'Không có bảng hội thoại, theo thiết kế. Transcript do trình duyệt giữ và chết cùng phiên đăng nhập.',
  'm4.absent.streaming':
    'Câu trả lời hiện dần từng chữ: câu trả lời hiện xuất hiện một lần, khi đã xong.',

  'm4.overview.title': 'Phân tích AI',
  'm4.overview.body':
    'Bản phân tích mới nhất trong phạm vi bạn được xem, hiển thị bằng đúng thẻ của trang Phân tích AI — nên phần Giới hạn đi cùng nó tới đây, không bị cắt đi cho vừa ô.',
  'm4.overview.link': 'Mở trang Phân tích AI',
  'm4.overview.denied':
    'Ô này cần quyền workforce.use_ai_analytics, tài khoản này không có. Không hiển thị kết quả AI nào ở đây.',
  'm4.overview.empty':
    'Chưa có phân tích nào trong khoảng thời gian đang lọc. Đây là câu về bộ lọc, không phải về công ty.',

  // ── Recruiting (Phase 1 ATS, WP-W1) ────────────────────────────────────────
  'nav.recruitingJobs': 'Tin tuyển dụng',
  'nav.candidates': 'Ứng viên',
  'nav.pipeline': 'Quy trình tuyển',
  'nav.interviews': 'Phỏng vấn',
  'nav.offers': 'Đề nghị tuyển',
  'nav.outreach': 'Tiếp cận ứng viên',

  // ── HR / people-ops (Phase 3a/3b/3c, WP-W6/WP-W7/WP-W8) ────────────────────
  'nav.hrDocuments': 'Hồ sơ & hợp đồng',
  'nav.onboarding': 'Nhập việc',
  'nav.hrLeave': 'Nghỉ phép',
  'nav.hrPerformance': 'Đánh giá hiệu suất',

  'recruiting.jobs.intro':
    'Danh sách tin tuyển dụng của công ty. Mở một tin để xem, sửa, chuyển trạng thái và quản lý kênh đăng.',
  'recruiting.jobs.scopeNote':
    'Chỉ hiển thị tin tuyển dụng thuộc công ty đang xem. Phạm vi do máy chủ quyết định, không lấy từ tham số.',
  'recruiting.jobs.subjectWord': 'tin tuyển dụng',
  'recruiting.jobs.open': 'Mở',

  'recruiting.jobs.filter.status': 'Trạng thái',
  'recruiting.jobs.filter.ownerMe': 'Chỉ tin của tôi',
  'recruiting.jobs.filter.ownerMeHint':
    'Lọc theo tin có recruiter là bạn. Đây là bộ lọc trên dòng dữ liệu, không phải mức phân quyền.',

  'recruiting.jobs.empty.title': 'Không có tin tuyển dụng nào khớp bộ lọc',
  'recruiting.jobs.empty.body':
    'Không có tin nào ở trạng thái {status}. Đây là câu về bộ lọc, không phải về công ty.',
  'recruiting.jobs.partial.excludes': 'các tin cũ hơn {shown} tin mới nhất đang hiển thị',
  'recruiting.jobs.row.meta': 'Cần {headcount} người · {type} · {location}',

  'recruiting.jobs.status.draft': 'Nháp',
  'recruiting.jobs.status.open': 'Đang tuyển',
  'recruiting.jobs.status.onHold': 'Tạm dừng',
  'recruiting.jobs.status.filled': 'Đã tuyển đủ',
  'recruiting.jobs.status.cancelled': 'Đã huỷ',
  'recruiting.jobs.status.closed': 'Đã đóng',

  'recruiting.jobs.move.draft': 'Chuyển về nháp',
  'recruiting.jobs.move.open': 'Mở tuyển',
  'recruiting.jobs.move.onHold': 'Tạm dừng',
  'recruiting.jobs.move.filled': 'Đánh dấu đã tuyển đủ',
  'recruiting.jobs.move.cancelled': 'Huỷ tin',
  'recruiting.jobs.move.closed': 'Đóng tin',

  'recruiting.jobs.employmentType.none': 'Chưa xác định',
  'recruiting.jobs.employmentType.full_time': 'Toàn thời gian',
  'recruiting.jobs.employmentType.part_time': 'Bán thời gian',
  'recruiting.jobs.employmentType.contract': 'Hợp đồng',
  'recruiting.jobs.employmentType.intern': 'Thực tập',

  'recruiting.jobs.create.title': 'Mở tin tuyển dụng mới',
  'recruiting.jobs.create.intro':
    'Tin mới luôn được tạo ở trạng thái nháp. Máy chủ tự ghi người tạo; bạn chỉ cần đặt tên vị trí.',
  'recruiting.jobs.create.titleLabel': 'Tên vị trí',
  'recruiting.jobs.create.titleRule': 'Bắt buộc. Không quá 500 ký tự.',
  'recruiting.jobs.create.employmentType': 'Hình thức làm việc',
  'recruiting.jobs.create.headcount': 'Số lượng cần tuyển',
  'recruiting.jobs.create.location': 'Địa điểm',
  'recruiting.jobs.create.department': 'Mã phòng ban',
  'recruiting.jobs.create.hiringManager': 'Mã người quản lý tuyển dụng',
  'recruiting.jobs.create.recruiter': 'Mã recruiter phụ trách',
  'recruiting.jobs.create.idHint':
    'Mã nhân viên có ở cột đầu màn Nhân sự; mã phòng ban ở màn Cấu hình công ty.',
  'recruiting.jobs.create.description': 'Mô tả công việc',
  'recruiting.jobs.create.submit': 'Tạo tin',
  'recruiting.jobs.create.submitting': 'Đang tạo…',
  'recruiting.jobs.create.ok': 'Đã tạo tin "{title}" ở trạng thái nháp.',

  'recruiting.jobs.detail.subject': 'chi tiết tin tuyển dụng',
  'recruiting.jobs.detail.meta': 'Cần {headcount} người · Recruiter: {recruiter}',
  'recruiting.jobs.detail.unassigned': 'chưa phân công',
  'recruiting.jobs.detail.noLocation': 'chưa có địa điểm',
  'recruiting.jobs.detail.notOpened': 'Chưa từng mở tuyển',
  'recruiting.jobs.detail.opened': 'Mở tuyển: {when}',
  'recruiting.jobs.detail.closed': 'Đóng: {when}',
  'recruiting.jobs.lastUpdated': 'Cập nhật gần nhất: {when}',

  'recruiting.jobs.transition.title': 'Chuyển trạng thái',
  'recruiting.jobs.transition.derived':
    'Chỉ hiện các bước hợp lệ từ trạng thái {status} — {count} bước. Bước không hợp lệ không xuất hiện.',
  'recruiting.jobs.transition.terminal.title': 'Trạng thái cuối',
  'recruiting.jobs.transition.terminal.body':
    'Tin ở trạng thái cuối (đã tuyển đủ, đã huỷ hoặc đã đóng) không còn bước chuyển nào.',
  'recruiting.jobs.transition.serverAllowed': 'Máy chủ cho phép: {list}',

  'recruiting.jobs.edit.title': 'Sửa thông tin tin tuyển dụng',
  'recruiting.jobs.edit.titleLabel': 'Tên vị trí',
  'recruiting.jobs.edit.headcount': 'Số lượng cần tuyển',
  'recruiting.jobs.edit.location': 'Địa điểm',
  'recruiting.jobs.edit.employmentType': 'Hình thức làm việc',
  'recruiting.jobs.edit.save': 'Lưu thay đổi',
  'recruiting.jobs.edit.saving': 'Đang lưu…',
  'recruiting.jobs.edit.ok': 'Đã lưu thay đổi.',
  'recruiting.jobs.edit.noChange': 'Chưa có thay đổi nào để lưu.',

  'recruiting.jobs.postings.title': 'Kênh đăng tin',
  'recruiting.jobs.postings.intro':
    'Mỗi kênh đăng được tạo ở trạng thái nháp. Máy chủ quyết định trạng thái.',
  'recruiting.jobs.postings.subject': 'kênh đăng tin',
  'recruiting.jobs.postings.empty.title': 'Chưa có kênh đăng nào',
  'recruiting.jobs.postings.empty.body':
    'Tin này chưa được đăng lên kênh nào. Tạo một kênh đăng bên dưới.',
  'recruiting.jobs.postings.channel': 'Kênh',
  'recruiting.jobs.postings.channel.internal': 'Nội bộ',
  'recruiting.jobs.postings.channel.career_site': 'Trang tuyển dụng',
  'recruiting.jobs.postings.channel.referral': 'Giới thiệu',
  'recruiting.jobs.postings.channel.agency': 'Đơn vị tuyển dụng',
  'recruiting.jobs.postings.noChannel': 'Chưa chọn kênh',
  'recruiting.jobs.postings.slug': 'Đường dẫn công khai',
  'recruiting.jobs.postings.slugRule': 'Tuỳ chọn. Chỉ gồm chữ thường, số và dấu gạch ngang.',
  'recruiting.jobs.postings.slugInvalid':
    'Đường dẫn chỉ được gồm chữ thường không dấu, số và dấu gạch ngang.',
  'recruiting.jobs.postings.expires': 'Hết hạn',
  'recruiting.jobs.postings.create': 'Tạo kênh đăng',
  'recruiting.jobs.postings.creating': 'Đang tạo…',
  'recruiting.jobs.postings.ok': 'Đã tạo kênh đăng ở trạng thái nháp.',
  'recruiting.jobs.postings.status.draft': 'Nháp',
  'recruiting.jobs.postings.status.published': 'Đã đăng',
  'recruiting.jobs.postings.status.unpublished': 'Đã gỡ',
  'recruiting.jobs.postings.status.expired': 'Hết hạn',
  'recruiting.jobs.postings.publishedAt': 'Đăng lúc {when}',
  'recruiting.jobs.postings.notPublished': 'Chưa đăng',
  'recruiting.jobs.postings.expiresAt': 'hết hạn {when}',
  'recruiting.jobs.postings.noSlug': 'chưa có đường dẫn',

  // ── Recruiting — Candidates (Phase 1 ATS, WP-W2) ───────────────────────────
  'recruiting.candidates.intro':
    'Ứng viên của công ty. Mở một hồ sơ để xem, sửa, ghi nhận đồng ý, quản lý tài liệu CV, hoặc ẩn hồ sơ theo yêu cầu xoá.',
  'recruiting.candidates.scopeNote':
    'Chỉ hiển thị ứng viên thuộc công ty đang xem, và không gồm hồ sơ đã ẩn. Phạm vi do máy chủ quyết định, không lấy từ tham số.',
  'recruiting.candidates.subjectWord': 'ứng viên',
  'recruiting.candidates.open': 'Mở',

  'recruiting.candidates.filter.source': 'Nguồn',
  'recruiting.candidates.filter.q': 'Tìm theo tên hoặc email',
  'recruiting.candidates.filter.qHint':
    'Khớp một phần, không phân biệt hoa thường, trên tên và email. Bộ lọc chạy ở máy chủ.',

  'recruiting.candidates.empty.title': 'Không có ứng viên nào khớp bộ lọc',
  'recruiting.candidates.empty.body':
    'Không có ứng viên nào khớp {filter}. Đây là câu về bộ lọc, không phải về công ty. Hồ sơ đã ẩn không xuất hiện ở đây.',
  'recruiting.candidates.partial.excludes': 'các ứng viên cũ hơn {shown} người đang hiển thị',
  'recruiting.candidates.row.meta': '{source} · {title} · {location}',
  'recruiting.candidates.filterAll': 'tất cả nguồn',

  'recruiting.candidates.source.career_site': 'Trang tuyển dụng',
  'recruiting.candidates.source.referral': 'Giới thiệu',
  'recruiting.candidates.source.agency': 'Đơn vị tuyển dụng',
  'recruiting.candidates.source.manual': 'Nhập tay',
  'recruiting.candidates.source.none': 'Không rõ nguồn',

  'recruiting.candidates.redacted.badge': 'Đã ẩn',
  'recruiting.candidates.redacted.note':
    'Hồ sơ này đã được ẩn theo yêu cầu xoá dữ liệu. Thông tin cá nhân đã bị xoá vĩnh viễn và không thể khôi phục; không còn gì để sửa hay ẩn thêm.',
  'recruiting.candidates.redacted.at': 'Đã ẩn lúc {when}',

  'recruiting.candidates.create.title': 'Thêm ứng viên',
  'recruiting.candidates.create.intro':
    'Chỉ bắt buộc họ tên. Máy chủ tự ghi người tạo. Trùng email với một ứng viên chưa ẩn sẽ bị từ chối.',
  'recruiting.candidates.create.fullName': 'Họ và tên',
  'recruiting.candidates.create.fullNameRule': 'Bắt buộc. Không quá 500 ký tự.',
  'recruiting.candidates.create.email': 'Email',
  'recruiting.candidates.create.phone': 'Số điện thoại',
  'recruiting.candidates.create.source': 'Nguồn',
  'recruiting.candidates.create.currentTitle': 'Chức danh hiện tại',
  'recruiting.candidates.create.currentCompany': 'Công ty hiện tại',
  'recruiting.candidates.create.location': 'Địa điểm',
  'recruiting.candidates.create.linkedin': 'LinkedIn',
  'recruiting.candidates.create.consent':
    'Ứng viên đã đồng ý cho lưu trữ dữ liệu (ghi nhận bây giờ)',
  'recruiting.candidates.create.submit': 'Thêm ứng viên',
  'recruiting.candidates.create.submitting': 'Đang thêm…',
  'recruiting.candidates.create.ok': 'Đã thêm ứng viên "{name}".',

  'recruiting.candidates.detail.subject': 'chi tiết ứng viên',
  'recruiting.candidates.detail.email': 'Email',
  'recruiting.candidates.detail.phone': 'Điện thoại',
  'recruiting.candidates.detail.source': 'Nguồn',
  'recruiting.candidates.detail.currentTitle': 'Chức danh',
  'recruiting.candidates.detail.currentCompany': 'Công ty',
  'recruiting.candidates.detail.location': 'Địa điểm',
  'recruiting.candidates.detail.linkedin': 'LinkedIn',
  'recruiting.candidates.detail.none': '—',
  'recruiting.candidates.detail.consent': 'Đồng ý lưu trữ dữ liệu: {when}',
  'recruiting.candidates.detail.noConsent': 'Chưa ghi nhận đồng ý lưu trữ dữ liệu.',
  'recruiting.candidates.lastUpdated': 'Cập nhật gần nhất: {when}',

  'recruiting.candidates.edit.title': 'Sửa thông tin ứng viên',
  'recruiting.candidates.edit.fullName': 'Họ và tên',
  'recruiting.candidates.edit.email': 'Email',
  'recruiting.candidates.edit.phone': 'Số điện thoại',
  'recruiting.candidates.edit.source': 'Nguồn',
  'recruiting.candidates.edit.currentTitle': 'Chức danh hiện tại',
  'recruiting.candidates.edit.currentCompany': 'Công ty hiện tại',
  'recruiting.candidates.edit.location': 'Địa điểm',
  'recruiting.candidates.edit.linkedin': 'LinkedIn',
  'recruiting.candidates.edit.save': 'Lưu thay đổi',
  'recruiting.candidates.edit.saving': 'Đang lưu…',
  'recruiting.candidates.edit.ok': 'Đã lưu thay đổi.',
  'recruiting.candidates.edit.noChange': 'Chưa có thay đổi nào để lưu.',

  'recruiting.candidates.consent.title': 'Đồng ý lưu trữ dữ liệu',
  'recruiting.candidates.consent.record': 'Ghi nhận đồng ý (bây giờ)',
  'recruiting.candidates.consent.clear': 'Xoá đồng ý',
  'recruiting.candidates.consent.saving': 'Đang lưu…',

  'recruiting.candidates.redact.title': 'Ẩn hồ sơ (quyền được xoá)',
  'recruiting.candidates.redact.warning':
    'Ẩn hồ sơ sẽ xoá vĩnh viễn thông tin cá nhân của ứng viên (tên, email, điện thoại, LinkedIn). Thao tác này KHÔNG THỂ hoàn tác.',
  'recruiting.candidates.redact.start': 'Ẩn ứng viên này',
  'recruiting.candidates.redact.confirmQuestion':
    'Xác nhận ẩn vĩnh viễn? Thông tin cá nhân sẽ bị xoá và không khôi phục được.',
  'recruiting.candidates.redact.confirm': 'Xác nhận ẩn vĩnh viễn',
  'recruiting.candidates.redact.cancel': 'Huỷ',
  'recruiting.candidates.redact.pending': 'Đang ẩn…',
  'recruiting.candidates.redact.ok': 'Đã ẩn hồ sơ ứng viên. Thông tin cá nhân đã bị xoá.',

  'recruiting.candidates.docType.cv': 'CV',
  'recruiting.candidates.docType.cover_letter': 'Thư xin việc',
  'recruiting.candidates.docType.portfolio': 'Hồ sơ năng lực',
  'recruiting.candidates.docType.other': 'Khác',
  'recruiting.candidates.docType.none': 'Không rõ loại',

  'recruiting.candidates.documents.title': 'Tài liệu',
  'recruiting.candidates.documents.subject': 'tài liệu',
  'recruiting.candidates.documents.subjectAccess': 'CV của ứng viên',
  'recruiting.candidates.documents.intro':
    'Metadata của tài liệu — không phải nội dung. Tải nội dung xuống là một lần đọc dữ liệu nhạy cảm, có ghi nhật ký, và cần lý do truy cập.',
  'recruiting.candidates.documents.empty.title': 'Chưa có tài liệu nào',
  'recruiting.candidates.documents.empty.body':
    'Ứng viên này chưa có tài liệu CV nào. Đăng ký và tải lên một tài liệu bên dưới.',
  'recruiting.candidates.documents.uploadedAt': 'Tải lên {when}',
  'recruiting.candidates.documents.sizeKb': '{kb} KB',
  'recruiting.candidates.documents.sizeUnknown': 'chưa rõ dung lượng',
  'recruiting.candidates.documents.meta': '{type} · {size}',

  'recruiting.candidates.documents.register.title': 'Tải lên tài liệu CV',
  'recruiting.candidates.documents.register.intro':
    'Hệ thống ghi nhận thông tin tệp rồi cấp một đường tải lên có hiệu lực ngắn; trình duyệt tải thẳng nội dung lên kho lưu trữ.',
  'recruiting.candidates.documents.register.file': 'Tệp',
  'recruiting.candidates.documents.register.docType': 'Loại tài liệu',
  'recruiting.candidates.documents.register.submit': 'Đăng ký và tải lên',
  'recruiting.candidates.documents.register.submitting': 'Đang tải lên…',
  'recruiting.candidates.documents.register.ok': 'Đã tải lên "{filename}".',
  'recruiting.candidates.documents.register.noFile': 'Chọn một tệp để tải lên.',

  'recruiting.candidates.documents.download': 'Tải xuống',
  'recruiting.candidates.documents.downloading': 'Đang tải…',
  'recruiting.candidates.documents.delivered': 'Đã tải "{filename}".',
  'recruiting.candidates.documents.reasonNeeded':
    'Nhập lý do truy cập ở trên (ít nhất {min} ký tự, không dấu) để bật nút tải xuống.',

  // ── Recruiting — Pipeline (Phase 1 ATS, WP-W3) ─────────────────────────────
  'recruiting.pipeline.intro':
    'Bảng quy trình tuyển: mỗi cột là một giai đoạn, mỗi thẻ là một hồ sơ ứng tuyển. Chuyển hồ sơ theo đúng máy trạng thái mà máy chủ cho phép.',
  'recruiting.pipeline.scopeNote':
    'Chỉ hiển thị hồ sơ ứng tuyển thuộc công ty đang xem. Phạm vi do máy chủ quyết định, không lấy từ tham số.',
  'recruiting.pipeline.subjectWord': 'hồ sơ ứng tuyển',

  'recruiting.pipeline.filter.requisition': 'Lọc theo tin tuyển dụng',
  'recruiting.pipeline.filter.requisitionIdHint':
    'Không đọc được danh sách tin tuyển dụng (cần quyền recruiting.view_jobs), nên đây là ô nhập mã tin.',

  'recruiting.pipeline.empty.title': 'Chưa có hồ sơ ứng tuyển nào',
  'recruiting.pipeline.empty.body':
    'Không có hồ sơ nào khớp bộ lọc hiện tại. Đây là câu về bộ lọc, không phải về công ty. Thêm một hồ sơ bằng biểu mẫu bên trên.',
  'recruiting.pipeline.partial.excludes': 'các hồ sơ cũ hơn {shown} hồ sơ mới nhất đang hiển thị',

  'recruiting.pipeline.column.count': '{count} hồ sơ',
  'recruiting.pipeline.column.empty': 'Trống',
  'recruiting.pipeline.card.requisition': 'Tin: {requisition}',
  'recruiting.pipeline.card.open': 'Xem lịch sử',

  'recruiting.pipeline.stage.applied': 'Đã nộp',
  'recruiting.pipeline.stage.screening': 'Sàng lọc',
  'recruiting.pipeline.stage.interview': 'Phỏng vấn',
  'recruiting.pipeline.stage.offer': 'Đề nghị',
  'recruiting.pipeline.stage.hired': 'Đã tuyển',
  'recruiting.pipeline.stage.rejected': 'Từ chối',
  'recruiting.pipeline.stage.withdrawn': 'Đã rút',

  'recruiting.pipeline.move.applied': 'Chuyển về đã nộp',
  'recruiting.pipeline.move.screening': 'Chuyển sang sàng lọc',
  'recruiting.pipeline.move.interview': 'Chuyển sang phỏng vấn',
  'recruiting.pipeline.move.offer': 'Chuyển sang đề nghị',
  'recruiting.pipeline.move.hired': 'Tuyển dụng',
  'recruiting.pipeline.move.rejected': 'Từ chối',
  'recruiting.pipeline.move.withdrawn': 'Rút hồ sơ',

  'recruiting.pipeline.transition.terminal':
    'Hồ sơ ở giai đoạn cuối (đã tuyển, từ chối hoặc đã rút) không còn bước chuyển nào.',
  'recruiting.pipeline.transition.serverAllowed': 'Máy chủ cho phép: {list}',

  'recruiting.pipeline.hire.label': 'Tuyển dụng',
  'recruiting.pipeline.hire.tooltip':
    'Tuyển dụng là một hành động riêng ở Giai đoạn 2, không thực hiện ở màn hình này. Bước chuyển sang "đã tuyển" bị máy chủ từ chối tại đây.',

  'recruiting.pipeline.history.title': 'Lịch sử giai đoạn',
  'recruiting.pipeline.history.subject': 'lịch sử giai đoạn',
  'recruiting.pipeline.history.origin': 'khởi tạo',
  'recruiting.pipeline.history.system': 'hệ thống',
  'recruiting.pipeline.history.move': '{from} → {to}',
  'recruiting.pipeline.history.meta': 'Người thực hiện: {who} · {when}',
  'recruiting.pipeline.history.note': 'Ghi chú: {note}',
  'recruiting.pipeline.history.empty.title': 'Chưa có lịch sử',
  'recruiting.pipeline.history.empty.body':
    'Hồ sơ này chưa có bước chuyển giai đoạn nào được ghi nhận.',

  'recruiting.pipeline.reject.title': 'Từ chối hồ sơ',
  'recruiting.pipeline.reject.intro':
    'Chuyển hồ sơ sang trạng thái từ chối. Lý do là tuỳ chọn nhưng được ghi lại làm ghi chú lịch sử.',
  'recruiting.pipeline.reject.reason': 'Lý do (tuỳ chọn)',
  'recruiting.pipeline.reject.submit': 'Từ chối hồ sơ',
  'recruiting.pipeline.reject.pending': 'Đang từ chối…',
  'recruiting.pipeline.reject.terminal':
    'Hồ sơ đã ở giai đoạn cuối, không thể từ chối thêm một lần nữa.',
  'recruiting.pipeline.reject.ok': 'Đã từ chối hồ sơ.',

  'recruiting.pipeline.create.title': 'Thêm hồ sơ ứng tuyển',
  'recruiting.pipeline.create.intro':
    'Gắn một ứng viên với một tin tuyển dụng. Hồ sơ luôn được tạo ở giai đoạn "đã nộp". Trùng cặp ứng viên–tin sẽ bị từ chối.',
  'recruiting.pipeline.create.candidate': 'Ứng viên',
  'recruiting.pipeline.create.candidatePlaceholder': '— Chọn ứng viên —',
  'recruiting.pipeline.create.requisition': 'Tin tuyển dụng',
  'recruiting.pipeline.create.requisitionPlaceholder': '— Chọn tin tuyển dụng —',
  'recruiting.pipeline.create.requisitionIdHint':
    'Không đọc được danh sách tin tuyển dụng (cần quyền recruiting.view_jobs), nên đây là ô nhập mã tin.',
  'recruiting.pipeline.create.recruiter': 'Recruiter phụ trách',
  'recruiting.pipeline.create.recruiterHint':
    'Tuỳ chọn. Gõ tên hoặc email rồi chọn trong danh sách; để trống nếu chưa phân công.',
  'recruiting.pipeline.create.submit': 'Thêm hồ sơ',
  'recruiting.pipeline.create.submitting': 'Đang thêm…',
  'recruiting.pipeline.create.ok': 'Đã thêm hồ sơ ứng tuyển ở giai đoạn "đã nộp".',

  // ── Interviews (WP-W4) ──────────────────────────────────────────────────────
  'recruiting.interviews.intro':
    'Lên lịch phỏng vấn cho một hồ sơ ứng tuyển, quản lý hội đồng phỏng vấn, cập nhật trạng thái và nộp phiếu đánh giá.',
  'recruiting.interviews.scopeNote':
    'Chỉ hiển thị phỏng vấn thuộc công ty đang xem. Phạm vi do máy chủ quyết định, không lấy từ tham số.',
  'recruiting.interviews.subjectWord': 'phỏng vấn',
  'recruiting.interviews.open': 'Xem chi tiết',

  'recruiting.interviews.filter.application': 'Lọc theo hồ sơ ứng tuyển',
  'recruiting.interviews.filter.applicationIdHint':
    'Không đọc được danh sách hồ sơ (cần quyền recruiting.view_candidates), nên đây là ô nhập mã hồ sơ.',
  'recruiting.interviews.filter.mine': 'Chỉ phỏng vấn của tôi',
  'recruiting.interviews.filter.mineHint':
    'Khi bật, chỉ hiển thị các buổi phỏng vấn mà bạn có trong hội đồng.',

  'recruiting.interviews.empty.title': 'Chưa có buổi phỏng vấn nào',
  'recruiting.interviews.empty.body':
    'Không có buổi phỏng vấn nào khớp bộ lọc hiện tại. Lên lịch một buổi bằng biểu mẫu bên trên.',
  'recruiting.interviews.partial.excludes':
    'các buổi phỏng vấn cũ hơn {shown} buổi mới nhất đang hiển thị',

  'recruiting.interviews.row.meta': 'Hình thức: {kind} · {when}',
  'recruiting.interviews.row.unscheduled': 'chưa định thời gian',

  'recruiting.interviews.kind.phone': 'Điện thoại',
  'recruiting.interviews.kind.onsite': 'Trực tiếp',
  'recruiting.interviews.kind.technical': 'Kỹ thuật',
  'recruiting.interviews.kind.panel': 'Hội đồng',
  'recruiting.interviews.kind.culture': 'Văn hoá',
  'recruiting.interviews.kind.none': 'Chưa xác định',

  'recruiting.interviews.status.scheduled': 'Đã lên lịch',
  'recruiting.interviews.status.completed': 'Đã hoàn thành',
  'recruiting.interviews.status.cancelled': 'Đã huỷ',
  'recruiting.interviews.status.no_show': 'Vắng mặt',
  'recruiting.interviews.status.title': 'Trạng thái phỏng vấn',
  'recruiting.interviews.status.terminal':
    'Buổi phỏng vấn ở trạng thái cuối (hoàn thành, đã huỷ hoặc vắng mặt) không còn bước chuyển nào.',
  'recruiting.interviews.status.serverAllowed': 'Máy chủ cho phép: {list}',

  'recruiting.interviews.move.scheduled': 'Đặt lại lịch',
  'recruiting.interviews.move.completed': 'Đánh dấu hoàn thành',
  'recruiting.interviews.move.cancelled': 'Huỷ buổi phỏng vấn',
  'recruiting.interviews.move.no_show': 'Đánh dấu vắng mặt',

  'recruiting.interviews.role.lead': 'Trưởng hội đồng',
  'recruiting.interviews.role.panelist': 'Thành viên',

  'recruiting.interviews.recommendation.strong_yes': 'Rất nên tuyển',
  'recruiting.interviews.recommendation.yes': 'Nên tuyển',
  'recruiting.interviews.recommendation.no': 'Không nên tuyển',
  'recruiting.interviews.recommendation.strong_no': 'Rất không nên tuyển',

  'recruiting.interviews.detail.status': 'Trạng thái',
  'recruiting.interviews.detail.kind': 'Hình thức',
  'recruiting.interviews.detail.start': 'Bắt đầu',
  'recruiting.interviews.detail.end': 'Kết thúc',
  'recruiting.interviews.detail.timezone': 'Múi giờ',
  'recruiting.interviews.detail.location': 'Địa điểm',
  'recruiting.interviews.detail.meetingUrl': 'Liên kết họp',
  'recruiting.interviews.detail.none': '—',

  'recruiting.interviews.schedule.title': 'Lên lịch phỏng vấn',
  'recruiting.interviews.schedule.intro':
    'Tạo một buổi phỏng vấn cho một hồ sơ ứng tuyển. Chỉ hồ sơ là bắt buộc; các trường còn lại là tuỳ chọn. Buổi phỏng vấn luôn mở ở trạng thái "đã lên lịch".',
  'recruiting.interviews.schedule.application': 'Hồ sơ ứng tuyển',
  'recruiting.interviews.schedule.applicationIdHint':
    'Không đọc được danh sách hồ sơ (cần quyền recruiting.view_candidates), nên đây là ô nhập mã hồ sơ.',
  'recruiting.interviews.schedule.applicationPlaceholder': '— Chọn hồ sơ ứng tuyển —',
  'recruiting.interviews.schedule.kind': 'Hình thức',
  'recruiting.interviews.schedule.kindPlaceholder': '— Không chọn —',
  'recruiting.interviews.schedule.start': 'Thời gian bắt đầu',
  'recruiting.interviews.schedule.end': 'Thời gian kết thúc',
  'recruiting.interviews.schedule.timezone': 'Múi giờ',
  'recruiting.interviews.schedule.location': 'Địa điểm',
  'recruiting.interviews.schedule.meetingUrl': 'Liên kết họp',
  'recruiting.interviews.schedule.panel': 'Hội đồng phỏng vấn',
  'recruiting.interviews.schedule.panelHint':
    'Tuỳ chọn. Thêm người phỏng vấn bằng mã nhân viên — mã có ở cột đầu màn Nhân sự.',
  'recruiting.interviews.schedule.panelistId': 'Mã nhân viên',
  'recruiting.interviews.schedule.panelistRole': 'Vai trò',
  'recruiting.interviews.schedule.panelistRemove': 'Bỏ',
  'recruiting.interviews.schedule.panelistAdd': 'Thêm người phỏng vấn',
  'recruiting.interviews.schedule.submit': 'Lên lịch',
  'recruiting.interviews.schedule.submitting': 'Đang lên lịch…',
  'recruiting.interviews.schedule.ok': 'Đã lên lịch buổi phỏng vấn ở trạng thái "đã lên lịch".',

  'recruiting.interviews.panel.title': 'Hội đồng phỏng vấn',
  'recruiting.interviews.panel.intro':
    'Thêm hoặc bỏ người phỏng vấn. Danh sách hiển thị lại sau mỗi lần thay đổi.',
  'recruiting.interviews.panel.employeeId': 'Mã nhân viên',
  'recruiting.interviews.panel.employeeIdHint':
    'Mã nhân viên có ở cột đầu màn Nhân sự, không phải mã tài khoản achau.',
  'recruiting.interviews.panel.role': 'Vai trò',
  'recruiting.interviews.panel.add': 'Thêm vào hội đồng',
  'recruiting.interviews.panel.adding': 'Đang thêm…',
  'recruiting.interviews.panel.subject': 'hội đồng phỏng vấn',
  'recruiting.interviews.panel.unknown':
    'Chưa có thay đổi nào trong phiên này, nên danh sách hội đồng hiện tại chưa được tải. Thêm hoặc bỏ một người để xem danh sách.',
  'recruiting.interviews.panel.empty': 'Hội đồng đang trống.',
  'recruiting.interviews.panel.roleNone': 'Chưa gán vai trò',
  'recruiting.interviews.panel.remove': 'Bỏ khỏi hội đồng',

  'recruiting.interviews.scorecard.title': 'Phiếu đánh giá',
  'recruiting.interviews.scorecard.interviewer':
    'Người đánh giá: {name} (chính là bạn — máy chủ tự ghi).',
  'recruiting.interviews.scorecard.ownState': 'Phiếu đánh giá của bạn đã được ghi nhận.',
  'recruiting.interviews.scorecard.noRecommendation': 'Chưa có đề xuất',
  'recruiting.interviews.scorecard.overallShort': 'Điểm tổng: {score}/5',
  'recruiting.interviews.scorecard.submittedAt': 'Nộp lúc {when}',
  'recruiting.interviews.scorecard.recommendation': 'Đề xuất',
  'recruiting.interviews.scorecard.recommendationPlaceholder': '— Chọn đề xuất —',
  'recruiting.interviews.scorecard.overall': 'Điểm tổng (1–5)',
  'recruiting.interviews.scorecard.overallPlaceholder': '— Chọn điểm —',
  'recruiting.interviews.scorecard.competencies': 'Năng lực (tuỳ chọn)',
  'recruiting.interviews.scorecard.competenciesHint':
    'Thêm từng năng lực và điểm 1–5. Để trống nếu không dùng.',
  'recruiting.interviews.scorecard.competencyName': 'Tên năng lực',
  'recruiting.interviews.scorecard.competencyScore': 'Điểm',
  'recruiting.interviews.scorecard.competencyRemove': 'Bỏ',
  'recruiting.interviews.scorecard.competencyAdd': 'Thêm năng lực',
  'recruiting.interviews.scorecard.comments': 'Nhận xét (tuỳ chọn)',
  'recruiting.interviews.scorecard.submit': 'Nộp phiếu đánh giá',
  'recruiting.interviews.scorecard.resubmit': 'Cập nhật phiếu đánh giá',
  'recruiting.interviews.scorecard.submitting': 'Đang nộp…',
  'recruiting.interviews.scorecard.subject': 'phiếu đánh giá',
  'recruiting.interviews.scorecard.notOnPanel.title':
    'Bạn không có trong hội đồng buổi phỏng vấn này',
  'recruiting.interviews.scorecard.notOnPanel.body':
    'Chỉ người phỏng vấn có trong hội đồng mới nộp được phiếu đánh giá. Nếu bạn cần đánh giá, hãy đề nghị điều phối viên thêm bạn vào hội đồng.',
  'recruiting.interviews.scorecard.allTitle': 'Tất cả phiếu đánh giá',
  'recruiting.interviews.scorecard.allEmpty.title': 'Chưa có phiếu đánh giá',
  'recruiting.interviews.scorecard.allEmpty.body':
    'Chưa có người phỏng vấn nào nộp phiếu đánh giá cho buổi này.',
  'recruiting.interviews.scorecard.allHidden':
    'Cần quyền recruiting.view_candidates để xem phiếu đánh giá của cả hội đồng.',
  'recruiting.interviews.scorecard.rowInterviewer': 'Người phỏng vấn {who}',
  'recruiting.interviews.scorecard.noOverall': 'Chưa có điểm tổng',

  // ── Offers (WP-W5) ──────────────────────────────────────────────────────────
  'recruiting.offers.intro':
    'Tạo và điều phối đề nghị tuyển cho một hồ sơ ứng tuyển: soạn bản nháp, phê duyệt, gửi cho ứng viên và ghi nhận phản hồi.',
  'recruiting.offers.scopeNote':
    'Chỉ hiển thị đề nghị tuyển thuộc công ty đang xem. Phạm vi do máy chủ quyết định, không lấy từ tham số.',
  'recruiting.offers.subjectWord': 'đề nghị tuyển',

  'recruiting.offers.filter.application': 'Hồ sơ ứng tuyển',
  'recruiting.offers.filter.applicationPlaceholder': '— Chọn hồ sơ ứng tuyển —',
  'recruiting.offers.filter.applicationIdHint':
    'Không đọc được danh sách hồ sơ (cần quyền recruiting.view_candidates), nên đây là ô nhập mã hồ sơ.',

  'recruiting.offers.pick.title': 'Chọn một hồ sơ ứng tuyển',
  'recruiting.offers.pick.body':
    'Đề nghị tuyển gắn với một hồ sơ ứng tuyển. Chọn một hồ sơ ở trên để xem các đề nghị của hồ sơ đó và tạo bản nháp mới.',

  'recruiting.offers.empty.title': 'Chưa có đề nghị tuyển nào',
  'recruiting.offers.empty.body':
    'Hồ sơ này chưa có đề nghị tuyển. Tạo một bản nháp bằng biểu mẫu bên trên.',

  'recruiting.offers.row.salary': '{amount} {currency}',
  'recruiting.offers.row.noSalary': 'Chưa có mức lương',
  'recruiting.offers.row.meta': 'Ngày bắt đầu: {start} · Hết hạn: {expires}',
  'recruiting.offers.row.noStart': 'chưa đặt',
  'recruiting.offers.row.noExpiry': 'chưa đặt',

  'recruiting.offers.status.draft': 'Bản nháp',
  'recruiting.offers.status.pending_approval': 'Chờ phê duyệt',
  'recruiting.offers.status.approved': 'Đã phê duyệt',
  'recruiting.offers.status.sent': 'Đã gửi',
  'recruiting.offers.status.accepted': 'Đã chấp nhận',
  'recruiting.offers.status.declined': 'Đã từ chối',
  'recruiting.offers.status.rescinded': 'Đã thu hồi',
  'recruiting.offers.status.expired': 'Đã hết hạn',
  'recruiting.offers.status.terminal':
    'Đề nghị tuyển ở trạng thái cuối (đã chấp nhận, đã từ chối, đã thu hồi hoặc đã hết hạn) không còn thao tác nào.',
  'recruiting.offers.status.serverAllowed': 'Máy chủ cho phép: {list}',

  'recruiting.offers.action.approve': 'Phê duyệt',
  'recruiting.offers.action.send': 'Gửi cho ứng viên',
  'recruiting.offers.action.respond_accepted': 'Ghi nhận: chấp nhận',
  'recruiting.offers.action.respond_declined': 'Ghi nhận: từ chối',

  'recruiting.offers.create.title': 'Tạo đề nghị tuyển (bản nháp)',
  'recruiting.offers.create.intro':
    'Tạo một đề nghị tuyển ở trạng thái "bản nháp" cho hồ sơ đã chọn. Mọi trường đều tuỳ chọn; để trống sẽ tạo bản nháp rỗng để điền và phê duyệt sau.',
  'recruiting.offers.create.amount': 'Mức lương',
  'recruiting.offers.create.amountInvalid':
    'Nhập số không âm, tối đa 12 chữ số phần nguyên và 2 chữ số thập phân (ví dụ 85000.00).',
  'recruiting.offers.create.currency': 'Loại tiền (mã ISO)',
  'recruiting.offers.create.startDate': 'Ngày bắt đầu dự kiến',
  'recruiting.offers.create.expiresAt': 'Thời điểm hết hạn',
  'recruiting.offers.create.documentKey': 'Mã tài liệu đề nghị (S3)',
  'recruiting.offers.create.documentKeyHint':
    'Tuỳ chọn. Mã tài liệu đề nghị tuyển đã lưu trong kho; hiện chưa tải tệp lên từ màn hình này được.',
  'recruiting.offers.create.submit': 'Tạo bản nháp',
  'recruiting.offers.create.submitting': 'Đang tạo…',
  'recruiting.offers.create.ok': 'Đã tạo đề nghị tuyển ở trạng thái "bản nháp".',

  // ── HR: employee (shared by both HR screens, WP-W6) ─────────────────────────
  'hr.employee.title': 'Nhân viên',
  'hr.employee.intro':
    'Nhập mã nhân viên để xem hồ sơ của họ. Máy chủ kiểm quyền theo cây quản lý của bạn; nhập mã không mở rộng phạm vi.',
  'hr.employee.idLabel': 'Mã nhân viên',
  'hr.employee.idHint':
    'Mã nhân viên trong Workforce Pulse — có ở cột đầu màn Nhân sự, không phải mã tài khoản achau.',
  // The picker's own pair, used where the id box has been replaced by `EmployeePicker`.
  'hr.employee.pickLabel': 'Nhân viên',
  'hr.employee.pickHint':
    'Gõ tên hoặc email rồi chọn trong danh sách. Bạn chỉ thấy những người trong phạm vi mình được phép xem.',
  'hr.employee.load': 'Xem hồ sơ',
  'hr.employee.none.title': 'Chưa chọn nhân viên',
  'hr.employee.none.body': 'Nhập mã nhân viên phía trên để xem tài liệu và hợp đồng của họ.',

  // ── HR: employee documents (WP-W6) ──────────────────────────────────────────
  'hr.documents.screenIntro':
    'Tài liệu và hợp đồng lao động của một nhân viên. Đọc phần nội dung tệp được ghi nhật ký truy cập.',
  'hr.documents.scopeNote':
    'Danh sách tài liệu và hợp đồng là dữ liệu thường; chỉ thao tác tải tệp mới cần lý do truy cập.',
  'hr.documents.title': 'Tài liệu',
  'hr.documents.intro':
    'Chỉ là thông tin tài liệu — không phải nội dung tệp, không phải liên kết. Nội dung tệp được tải riêng, có ghi nhật ký.',
  'hr.documents.subject': 'tài liệu',
  'hr.documents.subjectAccess': 'tài liệu nhân sự của nhân viên',
  'hr.documents.empty.title': 'Chưa có tài liệu nào',
  'hr.documents.empty.body': 'Nhân viên này chưa có tài liệu nhân sự nào được đăng ký.',
  'hr.documents.uploadedAt': 'Tải lên {when}',
  'hr.documents.sizeKb': '{kb} KB',
  'hr.documents.sizeUnknown': 'chưa rõ dung lượng',
  'hr.documents.meta': '{type} · {size}',
  'hr.documents.register.title': 'Tải lên tài liệu',
  'hr.documents.register.intro':
    'Đăng ký thông tin rồi tải nội dung tệp thẳng lên kho lưu trữ. Máy chủ không trung chuyển nội dung tệp.',
  'hr.documents.register.file': 'Tệp',
  'hr.documents.register.docType': 'Loại tài liệu',
  'hr.documents.register.submit': 'Đăng ký và tải lên',
  'hr.documents.register.submitting': 'Đang tải lên…',
  'hr.documents.register.ok': 'Đã tải lên "{filename}".',
  'hr.documents.register.noFile': 'Chọn một tệp để tải lên.',
  'hr.documents.download': 'Tải xuống',
  'hr.documents.downloading': 'Đang tải…',
  'hr.documents.delivered': 'Đã tải "{filename}".',
  'hr.documents.reasonNeeded': 'Nhập lý do truy cập ít nhất {min} ký tự để bật nút tải xuống.',
  'hr.documents.docType.none': 'không rõ loại',
  'hr.documents.docType.id_card': 'CMND/CCCD',
  'hr.documents.docType.passport': 'Hộ chiếu',
  'hr.documents.docType.contract': 'Hợp đồng',
  'hr.documents.docType.certificate': 'Chứng chỉ',
  'hr.documents.docType.tax': 'Thuế',
  'hr.documents.docType.other': 'Khác',

  // ── HR: contracts (WP-W6) ───────────────────────────────────────────────────
  'hr.contracts.title': 'Hợp đồng lao động',
  'hr.contracts.intro': 'Các hợp đồng lao động của nhân viên, mới nhất trước.',
  'hr.contracts.subject': 'hợp đồng',
  'hr.contracts.empty.title': 'Chưa có hợp đồng nào',
  'hr.contracts.empty.body': 'Nhân viên này chưa có hợp đồng lao động nào được ghi nhận.',
  'hr.contracts.create.title': 'Tạo hợp đồng',
  'hr.contracts.create.type': 'Loại hợp đồng',
  'hr.contracts.create.startDate': 'Ngày bắt đầu',
  'hr.contracts.create.endDate': 'Ngày kết thúc',
  'hr.contracts.create.baseSalary': 'Lương cơ bản',
  'hr.contracts.create.currency': 'Tiền tệ',
  'hr.contracts.create.status': 'Trạng thái',
  'hr.contracts.create.submit': 'Tạo hợp đồng',
  'hr.contracts.create.submitting': 'Đang tạo…',
  'hr.contracts.create.ok': 'Đã tạo hợp đồng "{type}".',
  'hr.contracts.type.none': 'không rõ loại',
  'hr.contracts.type.permanent': 'Không thời hạn',
  'hr.contracts.type.fixed_term': 'Xác định thời hạn',
  'hr.contracts.type.probation': 'Thử việc',
  'hr.contracts.type.intern': 'Thực tập',
  'hr.contracts.status.none': 'không rõ trạng thái',
  'hr.contracts.status.draft': 'Nháp',
  'hr.contracts.status.active': 'Hiệu lực',
  'hr.contracts.status.expired': 'Hết hạn',
  'hr.contracts.status.terminated': 'Chấm dứt',
  'hr.contracts.row.meta': 'Từ {start} đến {end} · {salary}',
  'hr.contracts.row.noEnd': 'không thời hạn',
  'hr.contracts.row.noSalary': 'chưa ghi lương',
  'hr.contracts.row.salary': '{amount} {currency}',
  'hr.contracts.lastUpdated': 'Cập nhật {when}',

  // ── HR: onboarding (WP-W6) ──────────────────────────────────────────────────
  'hr.onboarding.intro':
    'Quy trình nhập việc chuẩn của công ty, và tiến trình nhập việc của từng nhân viên.',
  'hr.onboarding.scopeNote':
    'Mẫu là cấu hình cấp công ty; danh sách nhập việc gắn với từng nhân viên và kiểm quyền theo cây quản lý.',
  'hr.onboarding.category.none': 'không phân loại',
  'hr.onboarding.category.it': 'CNTT',
  'hr.onboarding.category.hr': 'Nhân sự',
  'hr.onboarding.category.manager': 'Quản lý',
  'hr.onboarding.taskStatus.pending': 'Chờ làm',
  'hr.onboarding.taskStatus.done': 'Xong',
  'hr.onboarding.taskStatus.skipped': 'Bỏ qua',
  'hr.onboarding.taskStatus.blocked': 'Bị chặn',
  'hr.onboarding.checklistStatus.in_progress': 'Đang thực hiện',
  'hr.onboarding.checklistStatus.completed': 'Hoàn tất',
  'hr.onboarding.checklistStatus.cancelled': 'Đã huỷ',
  'hr.onboarding.templates.title': 'Mẫu nhập việc',
  'hr.onboarding.templates.intro':
    'Quy trình nhập việc chuẩn của công ty. Đây là cấu hình cấp công ty, không phải dữ liệu của một nhân viên.',
  'hr.onboarding.templates.subject': 'mẫu',
  'hr.onboarding.templates.active': 'Đang dùng',
  'hr.onboarding.templates.inactive': 'Ngừng dùng',
  'hr.onboarding.templates.open': 'Xem bước',
  'hr.onboarding.templates.empty.title': 'Chưa có mẫu nào',
  'hr.onboarding.templates.empty.body':
    'Công ty chưa có mẫu nhập việc nào. Tạo một mẫu để bắt đầu.',
  'hr.onboarding.templates.create.title': 'Tạo mẫu',
  'hr.onboarding.templates.create.name': 'Tên mẫu',
  'hr.onboarding.templates.create.active': 'Kích hoạt ngay',
  'hr.onboarding.templates.create.submit': 'Tạo mẫu',
  'hr.onboarding.templates.create.submitting': 'Đang tạo…',
  'hr.onboarding.templates.create.ok': 'Đã tạo mẫu "{name}".',
  'hr.onboarding.items.title': 'Các bước của mẫu',
  'hr.onboarding.items.intro':
    'Các bước của mẫu, theo thứ tự. Số ngày trễ là số ngày sau khi bắt đầu thì bước tương ứng đến hạn.',
  'hr.onboarding.items.subject': 'bước',
  'hr.onboarding.items.empty.title': 'Chưa có bước nào',
  'hr.onboarding.items.empty.body': 'Mẫu này chưa có bước nào. Thêm một bước ở trên.',
  'hr.onboarding.items.meta': '{category} · đến hạn sau {offset} ngày',
  'hr.onboarding.items.create.title': 'Tiêu đề bước',
  'hr.onboarding.items.create.category': 'Nhóm phụ trách',
  'hr.onboarding.items.create.sortOrder': 'Thứ tự',
  'hr.onboarding.items.create.offsetDays': 'Số ngày đến hạn',
  'hr.onboarding.items.create.offsetHint':
    'Số ngày sau khi bắt đầu nhập việc thì bước này đến hạn.',
  'hr.onboarding.items.create.submit': 'Thêm bước',
  'hr.onboarding.items.create.submitting': 'Đang thêm…',
  'hr.onboarding.items.create.ok': 'Đã thêm bước "{title}".',
  'hr.onboarding.checklists.title': 'Nhập việc theo nhân viên',
  'hr.onboarding.checklists.intro':
    'Nhập mã nhân viên để xem và quản lý tiến trình nhập việc của họ.',
  'hr.onboarding.checklists.subject': 'danh sách nhập việc',
  'hr.onboarding.checklists.load': 'Xem tiến trình',
  'hr.onboarding.checklists.none.title': 'Chưa chọn nhân viên',
  'hr.onboarding.checklists.none.body':
    'Nhập mã nhân viên phía trên để xem các danh sách nhập việc của họ.',
  'hr.onboarding.checklists.empty.title': 'Chưa có danh sách nhập việc',
  'hr.onboarding.checklists.empty.body':
    'Nhân viên này chưa có danh sách nhập việc nào. Tạo một danh sách từ mẫu.',
  'hr.onboarding.checklists.progress': 'Đã xong {done}/{total}',
  'hr.onboarding.checklists.startedAt': 'Bắt đầu {when}',
  'hr.onboarding.checklists.notStarted': 'chưa bắt đầu',
  'hr.onboarding.checklists.create.title': 'Tạo danh sách nhập việc',
  'hr.onboarding.checklists.create.intro':
    'Chọn một mẫu để tạo sẵn các công việc, hoặc để trống để tạo danh sách rỗng.',
  'hr.onboarding.checklists.create.template': 'Mẫu',
  'hr.onboarding.checklists.create.noTemplate': 'Không dùng mẫu (danh sách rỗng)',
  'hr.onboarding.checklists.create.templateHint':
    'Chỉ hiện các mẫu đang dùng. Mỗi bước của mẫu sẽ thành một công việc.',
  'hr.onboarding.checklists.create.submit': 'Tạo danh sách',
  'hr.onboarding.checklists.create.submitting': 'Đang tạo…',
  'hr.onboarding.tasks.subject': 'công việc',
  'hr.onboarding.tasks.none': 'Danh sách này chưa có công việc nào.',
  'hr.onboarding.tasks.meta': '{category} · đến hạn {due}',
  'hr.onboarding.tasks.noDue': 'không hạn',
  'hr.onboarding.tasks.complete': 'Đánh dấu xong',
  'hr.onboarding.tasks.completing': 'Đang lưu…',
  'hr.onboarding.tasks.doneMark': 'Đã xong',

  // ── HR: leave / time-off (WP-W7) ─────────────────────────────────────────────
  'hr.leave.intro':
    'Số ngày phép còn lại, xin nghỉ, và danh sách yêu cầu của bạn — cùng với phần phê duyệt nếu bạn có quyền đó.',
  'hr.leave.scopeNote':
    'Xin nghỉ luôn là cho chính bạn — máy chủ lấy nhân viên từ phiên đăng nhập, biểu mẫu không có ô mã nhân viên.',
  'hr.leave.status.pending': 'Đang chờ',
  'hr.leave.status.approved': 'Đã duyệt',
  'hr.leave.status.rejected': 'Đã từ chối',
  'hr.leave.status.cancelled': 'Đã huỷ',
  'hr.leave.type.unknown': 'Loại nghỉ không xác định',
  'hr.leave.types.subject': 'loại nghỉ phép',

  'hr.leave.my.title': 'Nghỉ phép của tôi',

  'hr.leave.balances.title': 'Số ngày phép còn lại',
  'hr.leave.balances.subject': 'số dư nghỉ phép',
  'hr.leave.balances.empty.title': 'Chưa có số dư nghỉ phép nào',
  'hr.leave.balances.empty.body': 'Bạn chưa có số dư nghỉ phép nào được ghi nhận cho năm nay.',
  'hr.leave.balances.col.type': 'Loại nghỉ phép',
  'hr.leave.balances.col.year': 'Năm',
  'hr.leave.balances.col.accrued': 'Ngày cấp',
  'hr.leave.balances.col.used': 'Đã dùng',
  'hr.leave.balances.col.remaining': 'Còn lại',

  'hr.leave.request.title': 'Xin nghỉ phép',
  'hr.leave.request.intro':
    'Yêu cầu luôn là cho chính bạn. Số ngày nghỉ được máy chủ tính từ ngày làm việc trong khoảng đã chọn.',
  'hr.leave.request.subject': 'yêu cầu nghỉ phép',
  'hr.leave.request.type': 'Loại nghỉ phép',
  'hr.leave.request.typePlaceholder': '— Chọn loại nghỉ —',
  'hr.leave.request.startDate': 'Ngày bắt đầu',
  'hr.leave.request.endDate': 'Ngày kết thúc',
  'hr.leave.request.reason': 'Lý do (không bắt buộc)',
  'hr.leave.request.submit': 'Gửi yêu cầu',
  'hr.leave.request.submitting': 'Đang gửi…',
  'hr.leave.request.ok': 'Đã gửi yêu cầu nghỉ {days} ngày, đang chờ duyệt.',

  'hr.leave.myRequests.title': 'Yêu cầu của tôi',
  'hr.leave.myRequests.subject': 'yêu cầu nghỉ phép',
  'hr.leave.myRequests.empty.title': 'Chưa có yêu cầu nghỉ phép nào',
  'hr.leave.myRequests.empty.body': 'Bạn chưa gửi yêu cầu nghỉ phép nào.',
  'hr.leave.myRequests.row.meta': '{start} → {end} · {days} ngày',
  'hr.leave.myRequests.row.reason': 'Lý do: {reason}',
  'hr.leave.myRequests.row.noReason': 'không ghi lý do',
  'hr.leave.myRequests.cancel': 'Huỷ yêu cầu',
  'hr.leave.myRequests.cancelling': 'Đang huỷ…',

  'hr.leave.approvals.title': 'Phê duyệt nghỉ phép',
  'hr.leave.approvals.intro':
    'Chọn một nhân viên để xem các yêu cầu nghỉ phép đang chờ duyệt của họ và ra quyết định.',
  'hr.leave.approvals.subject': 'yêu cầu chờ duyệt',
  'hr.leave.approvals.load': 'Xem yêu cầu chờ duyệt',
  'hr.leave.approvals.none.title': 'Chưa chọn nhân viên',
  'hr.leave.approvals.none.body':
    'Chọn một nhân viên phía trên để xem các yêu cầu nghỉ phép đang chờ duyệt của họ.',
  'hr.leave.approvals.empty.title': 'Không có yêu cầu đang chờ',
  'hr.leave.approvals.empty.body': 'Nhân viên này không có yêu cầu nghỉ phép nào đang chờ duyệt.',
  'hr.leave.approvals.approve': 'Duyệt',
  'hr.leave.approvals.reject': 'Từ chối',
  'hr.leave.approvals.deciding': 'Đang lưu…',
  'hr.leave.approvals.insufficientBalance':
    'Số ngày phép còn lại không đủ: đã cấp {accrued} ngày, đã dùng {used} ngày, yêu cầu thêm {requested} ngày.',

  'hr.performance.intro':
    'Chu kỳ đánh giá, mục tiêu và đánh giá hiệu suất. Mỗi nhân viên xem đánh giá và mục tiêu của mình, và có thể tự đánh giá; quản lý/nhân sự có thể tạo chu kỳ, giao mục tiêu và đánh giá nhân viên.',
  'hr.performance.scopeNote':
    'Người đánh giá luôn là chính bạn (phiên đăng nhập) — không có trường chọn người đánh giá trên biểu mẫu.',

  'hr.performance.cycleStatus.draft': 'Nháp',
  'hr.performance.cycleStatus.open': 'Đang mở',
  'hr.performance.cycleStatus.closed': 'Đã đóng',

  'hr.performance.goalStatus.active': 'Đang thực hiện',
  'hr.performance.goalStatus.achieved': 'Đã đạt',
  'hr.performance.goalStatus.missed': 'Chưa đạt',
  'hr.performance.goalStatus.cancelled': 'Đã huỷ',

  'hr.performance.reviewKind.self': 'Tự đánh giá',
  'hr.performance.reviewKind.manager': 'Quản lý đánh giá',
  'hr.performance.reviewKind.peer': 'Đồng nghiệp đánh giá',

  'hr.performance.cycleIdLabel': 'Mã chu kỳ đánh giá',
  'hr.performance.cycleIdHint':
    'Nhập mã chu kỳ đánh giá. Chưa có danh mục chu kỳ để chọn ở đây — hỏi bộ phận nhân sự nếu chưa biết mã.',

  'hr.performance.my.title': 'Đánh giá của tôi',

  'hr.performance.myReviews.title': 'Các đánh giá của tôi',
  'hr.performance.myReviews.subject': 'đánh giá',
  'hr.performance.myReviews.empty.title': 'Chưa có đánh giá nào',
  'hr.performance.myReviews.empty.body': 'Bạn chưa có đánh giá hiệu suất nào được ghi nhận.',
  'hr.performance.myReviews.row.overall': 'Điểm chung: {overall}/5',
  'hr.performance.myReviews.row.noOverall': 'chưa có điểm chung',
  'hr.performance.myReviews.row.noNarrative': 'chưa có nhận xét',

  'hr.performance.myGoals.title': 'Mục tiêu của tôi',
  'hr.performance.myGoals.subject': 'mục tiêu',
  'hr.performance.myGoals.empty.title': 'Chưa có mục tiêu nào',
  'hr.performance.myGoals.empty.body': 'Bạn chưa được giao mục tiêu nào.',
  'hr.performance.myGoals.row.noMeasure': 'chưa có thước đo',
  'hr.performance.myGoals.row.weight': 'trọng số {weight}',

  'hr.performance.selfReview.title': 'Viết tự đánh giá',
  'hr.performance.selfReview.intro':
    'Tự đánh giá luôn là về chính bạn — người đánh giá là phiên đăng nhập của bạn, không thể chọn khác.',
  'hr.performance.selfReview.subject': 'tự đánh giá',
  'hr.performance.selfReview.overall': 'Điểm chung (1–5, không bắt buộc)',
  'hr.performance.selfReview.narrative': 'Nhận xét (không bắt buộc)',
  'hr.performance.selfReview.productivityScoreId': 'Mã điểm năng suất tham khảo (không bắt buộc)',
  'hr.performance.selfReview.submit': 'Gửi tự đánh giá',
  'hr.performance.selfReview.submitting': 'Đang gửi…',
  'hr.performance.selfReview.ok': 'Đã gửi tự đánh giá.',

  'hr.performance.manage.title': 'Quản lý chu kỳ & mục tiêu',

  'hr.performance.cycles.title': 'Chu kỳ đánh giá',
  'hr.performance.cycles.intro': 'Lịch đánh giá của công ty — ví dụ "H2 2026".',
  'hr.performance.cycles.subject': 'chu kỳ đánh giá',
  'hr.performance.cycles.empty.title': 'Chưa có chu kỳ nào',
  'hr.performance.cycles.empty.body': 'Công ty chưa có chu kỳ đánh giá nào. Tạo một chu kỳ ở trên.',
  'hr.performance.cycles.row.period': '{start} → {end}',
  'hr.performance.cycles.row.noDate': 'chưa đặt',
  'hr.performance.cycles.create.name': 'Tên chu kỳ',
  'hr.performance.cycles.create.periodStart': 'Ngày bắt đầu',
  'hr.performance.cycles.create.periodEnd': 'Ngày kết thúc',
  'hr.performance.cycles.create.status': 'Trạng thái',
  'hr.performance.cycles.create.statusDefault': '— Mặc định (nháp) —',
  'hr.performance.cycles.create.submit': 'Tạo chu kỳ',
  'hr.performance.cycles.create.submitting': 'Đang tạo…',
  'hr.performance.cycles.create.ok': 'Đã tạo chu kỳ "{name}".',

  'hr.performance.manageGoals.title': 'Mục tiêu theo nhân viên',
  'hr.performance.manageGoals.intro': 'Chọn một nhân viên để xem và giao mục tiêu cho họ.',
  'hr.performance.manageGoals.subject': 'mục tiêu',
  'hr.performance.manageGoals.employeeLabel': 'Nhân viên',
  'hr.performance.manageGoals.none.title': 'Chưa chọn nhân viên',
  'hr.performance.manageGoals.none.body': 'Chọn một nhân viên phía trên để xem và giao mục tiêu.',
  'hr.performance.manageGoals.empty.title': 'Chưa có mục tiêu nào',
  'hr.performance.manageGoals.empty.body': 'Nhân viên này chưa được giao mục tiêu nào.',
  'hr.performance.manageGoals.create.goalTitle': 'Tên mục tiêu',
  'hr.performance.manageGoals.create.description': 'Mô tả (không bắt buộc)',
  'hr.performance.manageGoals.create.measure': 'Thước đo (không bắt buộc)',
  'hr.performance.manageGoals.create.status': 'Trạng thái',
  'hr.performance.manageGoals.create.statusDefault': '— Mặc định (đang thực hiện) —',
  'hr.performance.manageGoals.create.weight': 'Trọng số (0–100, không bắt buộc)',
  'hr.performance.manageGoals.create.submit': 'Giao mục tiêu',
  'hr.performance.manageGoals.create.submitting': 'Đang giao…',
  'hr.performance.manageGoals.create.ok': 'Đã giao mục tiêu "{title}".',

  'hr.performance.review.title': 'Đánh giá theo nhóm',
  'hr.performance.teamReviews.subject': 'đánh giá',
  'hr.performance.teamReviews.load': 'Xem đánh giá',
  'hr.performance.teamReviews.cycleFilter': 'Lọc theo mã chu kỳ (không bắt buộc)',
  'hr.performance.teamReviews.cycleFilterHint': 'Để trống để xem mọi chu kỳ.',
  'hr.performance.teamReviews.none.title': 'Chưa chọn nhân viên',
  'hr.performance.teamReviews.none.body':
    'Nhập mã nhân viên phía trên để xem đánh giá của họ và gửi đánh giá của quản lý.',
  'hr.performance.teamReviews.empty.title': 'Chưa có đánh giá nào',
  'hr.performance.teamReviews.empty.body': 'Nhân viên này chưa có đánh giá nào được ghi nhận.',

  'hr.performance.submitReview.title': 'Gửi đánh giá của quản lý',
  'hr.performance.submitReview.intro':
    'Đánh giá này dành cho nhân viên đã chọn ở trên — người đánh giá là phiên đăng nhập của bạn, không thể chọn khác.',
  'hr.performance.submitReview.subject': 'đánh giá của quản lý',
  'hr.performance.submitReview.submit': 'Gửi đánh giá',
  'hr.performance.submitReview.submitting': 'Đang gửi…',
  'hr.performance.submitReview.ok': 'Đã gửi đánh giá.',

  // ── Payroll (Task 16, Phase 8a, WP-W9) ─────────────────────────────────────
  'nav.payroll': 'Bảng lương',

  'payroll.intro':
    'Phiếu lương của bạn, mẫu bảng lương công ty và các đợt tính lương với hai bước phê duyệt.',
  'payroll.scopeNote':
    'Mỗi người chỉ xem được phiếu lương của chính mình. Xem đợt tính lương của cả nhóm và quản lý mẫu bảng lương cần quyền riêng, được máy chủ kiểm tra trên từng yêu cầu.',

  // CS-3: bộ tính lương chỉ có một bộ luật (VN). Quốc gia khác bị từ chối rõ ràng.
  'payroll.countryEngine.title': 'Bộ tính lương chưa có luật của quốc gia này',
  'payroll.countryEngine.body':
    'Bộ tính lương hiện chỉ có luật Việt Nam. Công ty đang cấu hình quốc gia {country}; đổi ở Cấu hình công ty hoặc chờ bộ luật của {country}.',

  'payroll.periodIdLabel': 'Mã kỳ lương',
  'payroll.periodIdHint':
    'Mã kỳ lương nằm trên phiếu lương đã phát hành của bạn, ở mục Đợt tính lương phía dưới nếu bạn có quyền xem, hoặc do bộ phận nhân sự cung cấp.',
  'payroll.templateIdLabel': 'Mã mẫu bảng lương',

  'payroll.my.title': 'Phiếu lương của tôi',
  'payroll.my.intro': 'Chọn kỳ lương để xem phiếu lương của chính bạn.',
  'payroll.my.load': 'Xem phiếu lương',
  'payroll.my.subject': 'phiếu lương',
  'payroll.my.none.title': 'Chưa chọn kỳ lương',
  'payroll.my.none.body': 'Chọn kỳ lương phía trên để xem phiếu lương của bạn.',
  'payroll.my.empty.title': 'Chưa có phiếu lương cho kỳ này',
  'payroll.my.empty.body':
    'Kỳ lương này chưa có đợt tính lương nào được CEO duyệt, hoặc bạn chưa có phiếu lương trong đợt đó. Phiếu lương chỉ hiển thị sau khi đợt tính lương được duyệt chính thức.',
  'payroll.my.deductionCeiling': 'Trần khấu trừ kỳ này (không phải một khoản trừ riêng): {value}',
  'payroll.my.net': 'Thực lãnh: {value}',
  'payroll.my.ledger.caption':
    'Sổ phiếu lương: mỗi dòng là một khoản mục, cột phải là số tiền theo {currency}.',
  'payroll.my.ledger.col.line': 'Khoản mục',
  'payroll.my.ledger.col.amount': 'Số tiền ({currency})',

  'payroll.manage.title': 'Quản lý mẫu bảng lương',
  'payroll.manage.intro':
    'Mẫu bảng lương của công ty và danh sách thành phần lương (Lương CB, Lương chính, Phụ cấp, BHXH, …).',

  'payroll.templates.title': 'Mẫu bảng lương',
  'payroll.templates.subject': 'mẫu bảng lương',
  'payroll.templates.create.name': 'Tên mẫu',
  'payroll.templates.create.active': 'Đang dùng',
  'payroll.templates.create.seedDefaults': 'Tạo sẵn thành phần mặc định Á Châu',
  'payroll.templates.create.seedDefaultsHint':
    'Tạo sẵn danh sách thành phần và công thức lương phiên bản 1 theo mẫu Á Châu; có thể sửa từng thành phần sau.',
  'payroll.templates.create.submit': 'Tạo mẫu',
  'payroll.templates.create.submitting': 'Đang tạo…',
  'payroll.templates.create.ok': 'Đã tạo mẫu "{name}".',
  'payroll.templates.empty.title': 'Chưa có mẫu bảng lương nào',
  'payroll.templates.empty.body': 'Công ty chưa có mẫu bảng lương nào. Tạo một mẫu ở trên.',
  'payroll.templates.active': 'Đang dùng',
  'payroll.templates.inactive': 'Không dùng',
  'payroll.templates.viewComponents': 'Xem thành phần',

  'payroll.defs.title': 'Thành phần lương',
  'payroll.defs.subject': 'thành phần lương',
  'payroll.defs.none.title': 'Chưa chọn mẫu bảng lương',
  'payroll.defs.none.body': 'Chọn "Xem thành phần" ở một mẫu phía trên để xem và thêm thành phần.',
  'payroll.defs.empty.title': 'Mẫu này chưa có thành phần nào',
  'payroll.defs.empty.body': 'Thêm thành phần đầu tiên ở form bên dưới.',
  'payroll.defs.row.meta': 'Loại: {kind} · Nguồn: {source}',
  'payroll.defs.row.unconfirmed': 'Chưa xác nhận',
  'payroll.defs.create.key': 'Khoá thành phần (key)',
  'payroll.defs.create.name': 'Tên thành phần',
  'payroll.defs.create.kind': 'Loại',
  'payroll.defs.create.source': 'Nguồn',
  'payroll.defs.create.formula': 'Công thức (không bắt buộc)',
  'payroll.defs.create.sortOrder': 'Thứ tự hiển thị (không bắt buộc)',
  'payroll.defs.create.submit': 'Thêm thành phần',
  'payroll.defs.create.submitting': 'Đang thêm…',
  'payroll.defs.create.ok': 'Đã thêm thành phần "{name}".',

  'payroll.runs.title': 'Đợt tính lương',
  'payroll.runs.intro':
    'Kỳ lương, các đợt tính lương và hai bước phê duyệt: quản lý duyệt trước, CEO duyệt chính thức sau.',

  'payroll.periods.title': 'Kỳ lương',
  // The period list this screen has already read, offered as a list. The typed-id
  // labels above stay for the one case the list is out of reach (read refused).
  'payroll.periods.pickLabel': 'Kỳ lương',
  'payroll.periods.choose': '— Chọn kỳ lương —',
  'payroll.periods.any': 'Tất cả kỳ lương',
  'payroll.periods.subject': 'kỳ lương',
  'payroll.periods.create.label': 'Tên kỳ lương (ví dụ: Tháng 01/2026)',
  'payroll.periods.create.start': 'Ngày bắt đầu',
  'payroll.periods.create.end': 'Ngày kết thúc',
  'payroll.periods.create.submit': 'Tạo kỳ lương',
  'payroll.periods.create.submitting': 'Đang tạo…',
  'payroll.periods.create.ok': 'Đã tạo kỳ lương "{label}".',
  'payroll.periods.empty.title': 'Chưa có kỳ lương nào',
  'payroll.periods.empty.body': 'Công ty chưa có kỳ lương nào. Tạo một kỳ lương ở trên.',
  'payroll.periods.empty.bodyNothing':
    'Công ty chưa có kỳ lương nào, nên cũng chưa có đợt tính lương nào. Tạo một kỳ lương ở trên trước.',
  'payroll.periods.row.range': '{start} → {end}',

  'payroll.periodStatus.draft': 'Nháp',
  'payroll.periodStatus.open': 'Đang mở',
  'payroll.periodStatus.closed': 'Đã đóng',

  'payroll.runsList.title': 'Danh sách đợt tính lương',
  'payroll.runsList.subject': 'đợt tính lương',
  'payroll.runsList.filterPeriod': 'Lọc theo kỳ lương (không bắt buộc)',
  'payroll.runsList.filterPeriodHint': 'Để trống để xem mọi kỳ lương.',
  'payroll.runsList.filterApply': 'Lọc',
  'payroll.runsList.create.submit': 'Tạo đợt tính lương',
  'payroll.runsList.create.submitting': 'Đang tạo…',
  'payroll.runsList.create.ok': 'Đã tạo đợt tính lương #{id}.',
  'payroll.runsList.empty.title': 'Chưa có đợt tính lương nào',
  'payroll.runsList.empty.body': 'Chưa có đợt tính lương nào khớp bộ lọc. Tạo một đợt ở trên.',
  'payroll.runsList.row.id': 'Đợt #{id}',
  'payroll.runsList.row.meta': 'Kỳ lương {periodId} · Mẫu {templateId}',
  'payroll.runsList.working': 'Đang xử lý…',
  'payroll.runsList.calculate': 'Nhập liệu & tính lương',
  'payroll.runsList.managerApprove': 'Quản lý duyệt',
  'payroll.runsList.ceoApprove': 'CEO duyệt (chính thức)',
  'payroll.runsList.viewItems': 'Xem bảng lương nhóm',

  'payroll.approval.groupLabel': 'Hai chữ ký duyệt của đợt #{id}',
  'payroll.approval.manager': 'Quản lý duyệt',
  'payroll.approval.ceo': 'CEO duyệt',
  'payroll.approval.unsigned': 'Chưa ký',
  'payroll.runsList.writePending':
    'Đang gửi một thao tác cho đợt #{id}. Nút ghi của các đợt khác tạm ẩn đến khi có kết quả — mỗi lần chỉ một thao tác trên các đợt.',

  'payroll.runStatus.draft': 'Nháp',
  'payroll.runStatus.calculated': 'Đã tính lương',
  'payroll.runStatus.manager_approved': 'Quản lý đã duyệt',
  'payroll.runStatus.ceo_approved': 'CEO đã duyệt',
  'payroll.runStatus.paid': 'Đã chi trả',

  'payroll.runItems.subject': 'bảng lương nhóm',
  'payroll.runItems.empty.title': 'Chưa có phiếu lương nào',
  'payroll.runItems.empty.body':
    'Đợt tính lương này chưa có phiếu lương nào trong phạm vi bạn xem được.',
  'payroll.runItems.row.employee': 'Nhân viên {employeeId}',
  'payroll.runItems.row.net': 'Thực lãnh: {net}',
  'payroll.runItems.close': 'Đóng',

  // ── Roadmap A-6 / B3 — the manual inputs a calculation sends ──────────────
  'payroll.inputs.title': 'Nhập liệu thủ công — đợt #{id}',
  'payroll.inputs.subject': 'phép tính lương',
  'payroll.inputs.intro':
    'Những khoản máy không tự tính được: Lương CB (nền đóng bảo hiểm), lương năng suất theo hợp đồng, tỷ lệ đạt, phụ cấp, thưởng, tạm ứng và số người phụ thuộc đã đăng ký. Mỗi dòng là một người có hợp đồng hiệu lực trong kỳ — đúng những người đợt này sẽ có phiếu lương.',
  'payroll.inputs.zeroRule':
    'Ô để trống không được gửi đi và được tính là 0. Tính lại sẽ thay mọi khoản nhập tay của lần tính trước bằng đúng những gì bảng này gửi — bảng không tự điền lại số cũ.',
  'payroll.inputs.phatLawful':
    'Cột Phạt chỉ nhận các căn cứ mà công ty đã cấu hình trong công thức tính lương đang dùng, và bắt buộc chọn một khi có số tiền, kèm lý do bằng văn bản — một số tiền không có căn cứ, hoặc một căn cứ máy chủ không nhận ra, đều bị từ chối. Tuỳ căn cứ, khoản này hoặc trừ vào lương ròng (có thể có trần — vượt trần thì máy chủ từ chối cả lệnh tính, không tự hạ xuống) hoặc làm giảm một khoản thưởng trước thuế (không bao giờ nhiều hơn khoản thưởng đã ghi). Bảng nhập liệu này không có sẵn danh sách căn cứ để chọn; gõ đúng mã căn cứ theo cấu hình của công ty.',
  'payroll.inputs.penalty.basis.label': 'Căn cứ',
  'payroll.inputs.penalty.basis.hint': 'mã căn cứ theo cấu hình công ty',
  'payroll.inputs.penalty.reason.label': 'Lý do',
  'payroll.inputs.penalty.reason.hint':
    'ít nhất {min} ký tự — ghi rõ sự việc và văn bản làm căn cứ',
  'payroll.inputs.penalty.problem.basis_required': 'Có số tiền thì phải ghi căn cứ.',
  'payroll.inputs.penalty.problem.reason_required': 'Có số tiền thì phải ghi lý do.',
  'payroll.inputs.penalty.problem.reason_too_short': 'Lý do cần ít nhất {min} ký tự.',
  'payroll.inputs.penalty.problem.amount_required': 'Có căn cứ hoặc lý do mà không có số tiền.',
  'payroll.inputs.capExceeded':
    'Khoản khấu trừ {amount} của nhân viên #{employee} vượt trần khấu trừ mà công ty đã cấu hình: tối đa {pct}% tiền lương thực trả hằng tháng còn lại sau bảo hiểm bắt buộc và thuế thu nhập cá nhân — {cap} cho kỳ này. Sửa số tiền hoặc chia khoản này ra nhiều kỳ.',
  'payroll.inputs.netPayExceeded':
    'Khoản khấu trừ {amount} của nhân viên #{employee} sẽ đưa thực lãnh xuống dưới 0 (còn {net}) — máy chủ từ chối cả lệnh tính, không tự đưa về 0. Sửa số tiền, hoặc kiểm tra tạm ứng của kỳ này.',
  'payroll.inputs.bonusExceeded':
    'Khoản giảm trừ {amount} của nhân viên #{employee} vượt quá khoản thưởng {bonus} mà nó trừ vào — máy chủ từ chối cả lệnh tính, không tự giới hạn về mức thưởng. Sửa số tiền, hoặc kiểm tra khoản thưởng của kỳ này.',
  'payroll.inputs.issueRefused': 'Trường `{field}`: {reason}',
  'payroll.inputs.issueRefusedFor': 'Nhân viên #{employee} — mục “{field}”: {reason}',
  'payroll.inputs.roster.subject': 'danh sách người được trả lương',
  'payroll.inputs.roster.empty.title': 'Không ai có hợp đồng hiệu lực trong kỳ này',
  'payroll.inputs.roster.empty.body': 'Tính lương cho đợt này sẽ không tạo phiếu lương nào.',
  'payroll.inputs.csv.title': 'Dán hoặc chọn tệp CSV',
  'payroll.inputs.csv.hint':
    'Dòng đầu là tên cột: {columns} — được bỏ bớt cột, thứ tự tuỳ ý, dán thẳng từ Excel (tách bằng tab) cũng được. Số tiền là số nguyên đồng, chỉ gồm chữ số: 500000 — nếu Excel hiện 500.000, bỏ định dạng ngăn hàng nghìn của cột trước khi sao chép. Tỷ lệ đạt dùng dấu chấm thập phân: 0.9. Ô trống nghĩa là không nhập.',
  'payroll.inputs.csv.paste': 'Nội dung CSV',
  'payroll.inputs.csv.file': 'Hoặc chọn tệp CSV',
  'payroll.inputs.csv.fileError': 'Không đọc được tệp này.',
  'payroll.inputs.csv.apply': 'Áp dụng vào bảng',
  'payroll.inputs.csv.applied': 'Đã áp dụng {rows} dòng vào bảng.',
  'payroll.inputs.csv.issues': 'Đã áp dụng {rows} dòng; những dòng dưới đây thì không:',
  'payroll.inputs.csv.issue.empty': 'Không có dòng tiêu đề nào.',
  'payroll.inputs.csv.issue.unclosed_quote':
    'Dòng {line}: dấu ngoặc kép mở mà không đóng — không áp dụng gì.',
  'payroll.inputs.csv.issue.no_employee_id_column': 'Thiếu cột mã nhân viên — không áp dụng gì.',
  'payroll.inputs.csv.issue.unknown_column':
    'Cột "{detail}" không phải một khoản nhập tay — không áp dụng gì. Cột được nhận: {columns}.',
  'payroll.inputs.csv.issue.nameless_column':
    'Cột thứ {detail} không có tên ở dòng tiêu đề nhưng có dữ liệu — không áp dụng gì. Đặt tên cho cột đó (một trong: {columns}) hoặc bỏ nó khỏi vùng sao chép.',
  'payroll.inputs.csv.issue.duplicate_column':
    'Cột "{detail}" xuất hiện hai lần — không áp dụng gì.',
  'payroll.inputs.csv.issue.too_many_rows':
    'Có {detail} dòng, nhiều hơn {max} người một lần tính nhận — không áp dụng gì.',
  'payroll.inputs.csv.issue.cell_count':
    'Dòng {line}: số ô ({detail}) không khớp dòng tiêu đề — bỏ qua dòng này.',
  'payroll.inputs.csv.issue.bad_employee_id':
    'Dòng {line}: “{detail}” không phải mã nhân viên — bỏ qua dòng này.',
  'payroll.inputs.csv.issue.not_in_roster':
    'Dòng {line}: nhân viên #{detail} không có hợp đồng hiệu lực trong kỳ, nên không có phiếu lương để nhập — bỏ qua dòng này.',
  'payroll.inputs.csv.issue.duplicate_employee':
    'Dòng {line}: nhân viên #{detail} có hơn một dòng — không áp dụng dòng nào của người này.',
  'payroll.inputs.grid.caption': 'Khoản nhập tay của từng người trong đợt',
  'payroll.inputs.grid.employee': 'Nhân viên',
  'payroll.inputs.grid.unnamed': 'Nhân viên không còn trong danh bạ',
  'payroll.inputs.grid.cellLabel': '{field} — {employee}',
  'payroll.inputs.field.so_nguoi_phu_thuoc': 'Số người phụ thuộc',
  'payroll.inputs.hint.money': '{currency} — chỉ chữ số (500000)',
  'payroll.inputs.hint.ty_le_dat': '0.9 = 90%, tối đa {max}',
  'payroll.inputs.hint.dependants': 'số nguyên, tối đa {max}',
  'payroll.inputs.problem.not_a_number':
    'Không đọc được thành số: chỉ dùng chữ số, riêng tỷ lệ đạt có thêm được phần thập phân sau dấu chấm (0.9, không phải 0,9); số người phụ thuộc là số nguyên; một dãy chữ số dài quá cũng không đọc được.',
  'payroll.inputs.problem.not_whole_dong':
    'Số tiền là số nguyên đồng, chỉ gồm chữ số — không dùng dấu chấm hay dấu phẩy để ngăn hàng nghìn (ví dụ 500000, không phải 500.000).',
  'payroll.inputs.problem.below_min': 'Nhỏ nhất là {min}.',
  'payroll.inputs.problem.above_max': 'Lớn nhất là {max}.',
  'payroll.inputs.problem.not_an_integer': 'Phải là số nguyên.',
  'payroll.inputs.summary':
    'Đã nhập {cells} ô cho {people}/{roster} người. Người không có ô nào thì mọi khoản nhập tay bằng 0.',
  'payroll.inputs.calculate': 'Tính lương cho {count} người',
  'payroll.inputs.blocked.problems': 'Còn {count} ô chưa hợp lệ — sửa xong mới tính được.',
  'payroll.inputs.blocked.tooMany': 'Một lần tính nhận khoản nhập tay của tối đa {max} người.',
  'payroll.inputs.blocked.pending':
    'Đang chờ thao tác gửi cho đợt #{id} xong — mỗi lần chỉ một thao tác trên các đợt.',
  'payroll.inputs.notCalculable':
    'Đợt này đã có phê duyệt nên không tính lại được nữa — bảng nhập liệu và nút tính lương không còn. Kết quả của lần tính vừa gửi vẫn ở đây.',
  'payroll.inputs.calculated': 'Đã tính: {count} phiếu lương.',
  'payroll.inputs.refusedReason': 'Lý do máy chủ đưa ra: {reason}',
  'payroll.inputs.clear': 'Xoá hết ô đã nhập',
  'payroll.inputs.close': 'Đóng',

  // ── Roadmap U26 — payslip PDFs ─────────────────────────────────────────────
  'payroll.pdf.subject': 'phiếu lương PDF',
  'payroll.pdf.download': 'Tải phiếu lương PDF',
  'payroll.pdf.downloading': 'Đang tải…',
  'payroll.pdf.delivered': 'Đã tải "{filename}".',
  'payroll.pdf.reasonNeeded': 'Nhập lý do truy cập ít nhất {min} ký tự để bật nút tải PDF.',
  'payroll.pdf.unreleased':
    'PDF phiếu lương chỉ tải được sau khi CEO duyệt đợt này (hoặc sau khi đã chi trả).',
  'payroll.pdf.notFound':
    'Không có phiếu lương đã phát hành để tải: kỳ lương không còn, hoặc phiếu chưa được CEO duyệt.',

  'payroll.component.luong_cb': 'Lương CB',
  'payroll.component.luong_chinh_theo_hd': 'Lương chính theo HD',
  'payroll.component.gio_cong': 'Giờ công',
  'payroll.component.luong_chinh': 'Lương chính',
  'payroll.component.luong_nang_suat_hd': 'Lương năng suất HD',
  'payroll.component.ty_le_dat': 'Tỷ lệ đạt (Hiệu quả)',
  'payroll.component.luong_hieu_qua': 'Lương Hiệu quả',
  'payroll.component.gio_ot': 'Giờ OT',
  'payroll.component.luong_ot': 'Lương OT',
  'payroll.component.luong': 'Lương',
  'payroll.component.phu_cap_dong_phuc': 'Phụ cấp Đồng phục',
  'payroll.component.phu_cap_xang': 'Phụ cấp Tiền Xăng',
  'payroll.component.phu_cap_khac': 'Phụ cấp Khác',
  'payroll.component.tong_phu_cap': 'Tổng phụ cấp',
  'payroll.component.thuong': 'Thưởng khác',
  'payroll.component.phat': 'Phạt vi phạm',
  'payroll.component.khau_tru_boi_thuong': 'Khấu trừ theo căn cứ (trừ vào lương ròng)',
  'payroll.component.giam_thuong': 'Giảm trừ theo căn cứ (trừ vào lương gộp)',
  'payroll.component.khau_tru_toi_da': 'Trần khấu trừ tháng đã cấu hình',
  'payroll.component.bhxh_nv': 'BHXH nhân viên',
  'payroll.component.bhyt_nv': 'BHYT nhân viên',
  'payroll.component.kpcd_nv': 'KPCĐ nhân viên',
  'payroll.component.tong_bhnv': 'Tổng BHNV',
  'payroll.component.bhxh_cty': 'BHXH công ty',
  'payroll.component.bhyt_cty': 'BHYT công ty',
  'payroll.component.kpcd_cty': 'KPCĐ công ty',
  'payroll.component.tong_bh_cty': 'Tổng BH công ty',
  'payroll.component.tong_bh_kpcd': 'Tổng BH/KPCĐ',
  'payroll.component.tong_luong': 'Tổng lương',
  'payroll.component.tong_chi_tra': 'Tổng chi trả',
  'payroll.component.tam_ung': 'Tạm ứng',
  'payroll.component.thuc_lanh': 'Thực lãnh',

  // ── Geo check-in (Task 17 API, WP-W10, Phase 7 SPA) ─────────────────────────
  'nav.geo': 'Chấm công vị trí',

  'geo.intro':
    'Chấm công bằng vị trí GPS của trình duyệt, lịch sử chấm công của bạn, và quản lý văn phòng (nếu có quyền).',

  'geo.checkin.title': 'Chấm công',
  'geo.checkin.intro':
    'Cần đồng ý ghi nhận vị trí trước, sau đó trình duyệt sẽ hỏi quyền truy cập vị trí của bạn.',
  'geo.checkin.consent': 'Tôi đồng ý cho ghi nhận vị trí GPS của tôi cho lượt chấm công này.',
  'geo.checkin.button': 'Chấm công',
  'geo.checkin.locating': 'Đang xác định vị trí…',
  'geo.checkin.submitting': 'Đang gửi…',
  'geo.checkin.subject': 'lượt chấm công',
  'geo.checkin.failure.permission_denied':
    'Trình duyệt từ chối quyền truy cập vị trí. Vui lòng cho phép truy cập vị trí rồi thử lại.',
  'geo.checkin.failure.position_unavailable': 'Không xác định được vị trí. Vui lòng thử lại.',
  'geo.checkin.failure.timeout': 'Xác định vị trí quá lâu. Vui lòng thử lại.',
  'geo.checkin.failure.unsupported': 'Trình duyệt này không hỗ trợ xác định vị trí.',
  'geo.checkin.conflict.geo_checkin_disabled':
    'Công ty của bạn chưa bật tính năng chấm công vị trí.',
  'geo.checkin.conflict.geo_consent_required':
    'Bạn cần đồng ý ghi nhận vị trí trước khi chấm công.',
  'geo.checkin.result.within': 'Trong phạm vi văn phòng "{office}", cách tâm {distance}m.',
  'geo.checkin.result.outside':
    'Ngoài phạm vi văn phòng gần nhất "{office}", cách tâm {distance}m.',
  'geo.checkin.result.noOffice': 'Công ty chưa cấu hình văn phòng nào để so sánh.',
  'geo.checkin.result.noLocation':
    'Bạn đã từ chối thu thập vị trí, nên lượt chấm công này được ghi nhận mà không lưu toạ độ. Máy chủ là nơi thực thi việc đó, không phải ứng dụng.',
  'geo.checkin.outsideGeofence':
    'Bạn đang cách văn phòng gần nhất {distance} m (bán kính {radius} m). Hãy vào trong vùng rồi thử lại.',

  'geo.badge.within': 'Trong phạm vi',
  'geo.badge.outside': 'Ngoài phạm vi',
  'geo.badge.noLocation': 'Không thu thập vị trí',
  'geo.row.noOffice': 'Không có văn phòng',
  'geo.row.noLocation':
    'Nhân viên đã từ chối thu thập vị trí, nên lượt chấm công này không lưu toạ độ nào.',
  'geo.row.distance': 'cách {distance}m',

  'geo.my.title': 'Chấm công của tôi',
  'geo.my.subject': 'lượt chấm công của tôi',
  'geo.my.empty.title': 'Chưa có lượt chấm công nào',
  'geo.my.empty.body': 'Bạn chưa chấm công lần nào.',

  'geo.team.title': 'Chấm công của nhân viên khác',
  'geo.team.intro':
    'Xem lịch sử chấm công của một nhân viên khác — cần nêu lý do truy cập vì đây là dữ liệu vị trí nhạy cảm.',
  'geo.team.employeeIdLabel': 'Mã nhân viên',
  'geo.team.employeeIdHint':
    'Mã nhân viên có ở cột đầu màn Nhân sự, không phải mã tài khoản achau.',
  'geo.team.load': 'Xem lịch sử chấm công',
  'geo.team.subject': 'lịch sử chấm công của nhân viên',
  'geo.team.none.title': 'Chưa chọn nhân viên',
  'geo.team.none.body': 'Nhập mã nhân viên phía trên để xem lịch sử chấm công của họ.',
  'geo.team.empty.title': 'Chưa có lượt chấm công nào',
  'geo.team.empty.body': 'Nhân viên này chưa chấm công lần nào.',
  'geo.team.trustFilter': 'Chỉ hiện nghi ngờ',

  'geo.trust.trusted': 'Đáng tin cậy',
  'geo.trust.suspect': 'Nghi ngờ',
  'geo.trust.unverified': 'Chưa xác minh',
  'geo.trust.speed': 'Tốc độ di chuyển ước tính: {speed} km/h',
  'geo.trust.reason.mock_location': 'Thiết bị báo vị trí giả',
  'geo.trust.reason.low_accuracy': 'Độ chính xác vị trí thấp hơn ngưỡng cho phép',
  'geo.trust.reason.no_accuracy': 'Không có dữ liệu độ chính xác',
  'geo.trust.reason.impossible_travel': 'Tốc độ di chuyển so với lượt trước vượt ngưỡng cho phép',
  'geo.trust.reason.location_opted_out':
    'Nhân viên đã từ chối thu thập vị trí — không có toạ độ để đối chiếu. Đây là quyền theo luật, không phải dấu hiệu nghi ngờ.',

  'geo.offices.title': 'Văn phòng (vùng chấm công)',
  'geo.offices.intro':
    'Danh sách văn phòng dùng để so khớp vị trí chấm công, và thêm văn phòng mới.',
  'geo.offices.subject': 'văn phòng',
  'geo.offices.empty.title': 'Chưa có văn phòng nào',
  'geo.offices.empty.body': 'Công ty chưa có văn phòng nào. Thêm một văn phòng ở trên.',
  'geo.offices.row.meta': '{latitude}, {longitude} · bán kính {radius}m',
  'geo.offices.active': 'Đang dùng',
  'geo.offices.inactive': 'Không dùng',
  'geo.offices.create.name': 'Tên văn phòng',
  'geo.offices.create.latitude': 'Vĩ độ (latitude)',
  'geo.offices.create.longitude': 'Kinh độ (longitude)',
  'geo.offices.create.radius': 'Bán kính (m, mặc định 150)',
  'geo.offices.create.active': 'Đang dùng',
  'geo.offices.create.submit': 'Thêm văn phòng',
  'geo.offices.create.submitting': 'Đang thêm…',
  'geo.offices.create.ok': 'Đã thêm văn phòng "{name}".',
  'geo.offices.radiusHint': 'Tối thiểu 50 m vì sai số GPS; tối đa 3000 m',
  'geo.offices.edit.open': 'Sửa',
  'geo.offices.edit.cancel': 'Hủy',
  'geo.offices.edit.save': 'Lưu',
  'geo.offices.edit.saving': 'Đang lưu…',
  'geo.offices.edit.name': 'Tên văn phòng',
  'geo.offices.edit.latitude': 'Vĩ độ (latitude)',
  'geo.offices.edit.longitude': 'Kinh độ (longitude)',
  'geo.offices.edit.radius': 'Bán kính (m)',
  'geo.offices.deactivate': 'Tắt',
  'geo.offices.activate': 'Bật lại',

  // ── Geo policy admin panel (Task T2-C-web, Tier-2 ops) ──────────────────────
  'geo.policy.title': 'Chính sách chấm công vị trí',
  'geo.policy.subject': 'chính sách chấm công vị trí',
  'geo.policy.enabled': 'Bật chấm công theo vị trí',
  'geo.policy.maxAccuracy': 'Sai số GPS tối đa (m)',
  'geo.policy.maxAccuracy.hint':
    "Vị trí có sai số lớn hơn ngưỡng này được đánh dấu 'chưa xác minh' — không bị từ chối",
  'geo.policy.maxTravel': 'Tốc độ di chuyển tối đa (km/h)',
  'geo.policy.maxTravel.hint':
    "Tốc độ di chuyển cao hơn ngưỡng này so với lần chấm trước được đánh dấu 'nghi ngờ' — không bị từ chối",
  'geo.policy.mode.legend': 'Khi chấm công ngoài vùng mọi văn phòng',
  'geo.policy.mode.allow_flag': 'Cho phép, chỉ đánh dấu ngoài vùng',
  'geo.policy.mode.allow_flag.note': 'Mặc định — khuyến nghị cho văn phòng có tín hiệu GPS yếu.',
  'geo.policy.mode.block': 'Từ chối khi ngoài vùng',
  'geo.policy.mode.block.note': 'Ứng dụng di động sẽ hiển thị khoảng cách đến văn phòng gần nhất.',
  'geo.policy.updated.never': 'Chưa cài đặt (đang dùng mặc định)',
  'geo.policy.updated.at': 'Cập nhật lần cuối: {at} bởi {by}',
  'geo.policy.updated.unknown': 'không rõ',
  'geo.policy.save': 'Lưu chính sách',
  'geo.policy.saving': 'Đang lưu…',
  'geo.policy.saveOk': 'Đã lưu chính sách.',

  // ── Outreach (Task 18 API, WP-W11, Phase 5 SPA) ─────────────────────────────
  'outreach.intro':
    'Kiểm soát tuân thủ cho việc liên hệ ứng viên: kênh liên hệ, sự đồng ý và danh sách chặn.',

  'outreach.policy.title': 'Chính sách kênh liên hệ',
  'outreach.policy.intro':
    'Kênh nào công ty được phép dùng để liên hệ ứng viên, giới hạn số lượng mỗi ngày, và liệu tin nhắn có cần phê duyệt trước khi gửi.',
  'outreach.policy.defaultNote':
    'Mặc định: chưa kênh nào được bật. Một công ty chưa cấu hình gì sẽ thấy cả sáu kênh ở trạng thái tắt.',
  'outreach.policy.subjectWord': 'chính sách kênh',
  'outreach.policy.enabled': 'Đang bật',
  'outreach.policy.disabled': 'Đang tắt',
  'outreach.policy.paused': 'Tạm dừng',
  'outreach.policy.active': 'Đang hoạt động',
  'outreach.policy.approvalRequired': 'cần phê duyệt',
  'outreach.policy.approvalNotRequired': 'không cần phê duyệt',
  'outreach.policy.meta': '{paused} · Giới hạn {limit} tin/ngày · {approval}',
  'outreach.policy.neverConfigured': 'Công ty chưa cấu hình kênh này — đang ở giá trị mặc định.',
  'outreach.policy.updatedAt': 'Cập nhật lần cuối: {when}',
  'outreach.policy.highRisk.title': 'Kênh có rủi ro pháp lý cao',
  'outreach.policy.highRisk.body':
    'Bật kênh này có thể vi phạm Điều khoản dịch vụ của nền tảng và pháp luật Việt Nam hoặc pháp luật địa phương liên quan (bao gồm quy định về sự đồng ý và giới hạn tần suất của Nghị định 91). Việc bật kênh này là lựa chọn của công ty và công ty tự chịu rủi ro.',
  'outreach.policy.form.enabled': 'Bật kênh này',
  'outreach.policy.form.paused': 'Tạm dừng kênh này',
  'outreach.policy.form.dailyRateLimit': 'Giới hạn số tin mỗi ngày',
  'outreach.policy.form.requiresApproval': 'Yêu cầu phê duyệt trước khi gửi',
  'outreach.policy.form.requiresApprovalLocked':
    'Máy chủ luôn yêu cầu phê duyệt cho các kênh rủi ro cao — không thể tắt.',
  'outreach.policy.form.save': 'Lưu chính sách',
  'outreach.policy.form.saving': 'Đang lưu…',
  'outreach.policy.form.ok': 'Đã lưu chính sách kênh.',

  'outreach.channel.email': 'Email',
  'outreach.channel.linkedin_dm': 'LinkedIn — tin nhắn riêng',
  'outreach.channel.linkedin_comment': 'LinkedIn — bình luận',
  'outreach.channel.facebook_dm': 'Facebook — tin nhắn riêng',
  'outreach.channel.facebook_comment': 'Facebook — bình luận',
  'outreach.channel.web_scrape': 'Thu thập dữ liệu web',

  'outreach.messages.title': 'Tin nhắn tiếp cận',
  'outreach.messages.subjectWord': 'tin nhắn tiếp cận',
  'outreach.messages.empty.title': 'Chưa có tin nhắn nào',
  'outreach.messages.empty.body': 'Chưa có tin nhắn tiếp cận nào được tạo.',
  'outreach.messages.row.noConsent': 'Chưa có bản ghi đồng ý',
  'outreach.messages.row.hasConsent': 'Đã có bản ghi đồng ý',
  'outreach.messages.action.approve': 'Phê duyệt',
  'outreach.messages.status.terminal': 'Trạng thái đã kết thúc, không còn hành động nào ở đây.',
  'outreach.messages.status.awaitingDispatch':
    'Đang chờ tiến trình gửi đánh giá các điều kiện còn lại.',

  'outreach.messages.status.draft': 'Bản nháp',
  'outreach.messages.status.pending_approval': 'Chờ phê duyệt',
  'outreach.messages.status.approved': 'Đã phê duyệt',
  'outreach.messages.status.sent': 'Đã gửi',
  'outreach.messages.status.suppressed': 'Đã chặn',
  'outreach.messages.status.failed': 'Thất bại',
  'outreach.messages.status.cancelled': 'Đã huỷ',

  'outreach.messages.create.title': 'Tạo tin nhắn tiếp cận',
  'outreach.messages.create.intro':
    'Gửi tới một ứng viên theo mã định danh — không nhập địa chỉ thủ công. Tin nhắn sẽ ở trạng thái "chờ phê duyệt" và chưa được gửi.',
  'outreach.messages.create.candidate': 'Ứng viên',
  'outreach.messages.create.candidateIdHint':
    'Chưa có danh sách ứng viên ở đây, nên mã ứng viên được nhập trực tiếp.',
  'outreach.messages.create.candidatePlaceholder': '— Chọn ứng viên —',
  'outreach.messages.create.channel': 'Kênh liên hệ',
  'outreach.messages.create.subject': 'Tiêu đề (tuỳ chọn)',
  'outreach.messages.create.body': 'Nội dung',
  'outreach.messages.create.submit': 'Tạo tin nhắn',
  'outreach.messages.create.submitting': 'Đang tạo…',
  'outreach.messages.create.ok': 'Đã tạo tin nhắn ở trạng thái "chờ phê duyệt".',

  'outreach.compliance.title': 'Đồng ý & danh sách chặn',

  'outreach.consent.title': 'Ghi nhận sự đồng ý',
  'outreach.consent.intro':
    'Ghi nhận rằng một ứng viên đã đồng ý được liên hệ qua một kênh cụ thể.',
  'outreach.consent.basis.label': 'Căn cứ đồng ý',
  'outreach.consent.basis.candidate_initiated': 'Ứng viên chủ động liên hệ trước',
  'outreach.consent.basis.explicit_optin': 'Đồng ý rõ ràng',
  'outreach.consent.basis.legitimate_interest': 'Lợi ích hợp pháp',
  'outreach.consent.note': 'Ghi chú (tuỳ chọn)',
  'outreach.consent.submit': 'Ghi nhận đồng ý',
  'outreach.consent.submitting': 'Đang ghi nhận…',
  'outreach.consent.subjectWord': 'bản ghi đồng ý',
  'outreach.consent.ok': 'Đã ghi nhận sự đồng ý.',

  'outreach.suppress.title': 'Thêm vào danh sách chặn',
  'outreach.suppress.intro':
    'Chặn một ứng viên khỏi việc bị liên hệ qua một kênh cụ thể — ví dụ khi họ đã từ chối nhận liên hệ.',
  'outreach.suppress.reason': 'Lý do (tuỳ chọn)',
  'outreach.suppress.submit': 'Thêm vào danh sách chặn',
  'outreach.suppress.submitting': 'Đang thêm…',
  'outreach.suppress.subjectWord': 'danh sách chặn',
  'outreach.suppress.ok': 'Đã thêm vào danh sách chặn.',

  // ── Sourcing (Task 20 API, WP-W12, Phase 6 SPA) ─────────────────────────────
  'nav.sourcing': 'Tìm nguồn ứng viên',
  'sourcing.intro':
    'Các đợt tìm nguồn ứng viên tự động. Chỉ bốn kênh rủi ro thấp được phép kích hoạt từ đây; năm kênh rủi ro cao bị cách ly, luôn hiển thị kèm cảnh báo pháp lý.',

  'sourcing.filter.requisition': 'Vị trí tuyển dụng',
  'sourcing.filter.requisitionIdHint':
    'Chưa có danh sách vị trí tuyển dụng ở đây, nên mã vị trí được nhập trực tiếp.',

  'sourcing.runs.title': 'Các đợt tìm nguồn',
  'sourcing.runs.subjectWord': 'đợt tìm nguồn',
  'sourcing.runs.empty.title': 'Chưa có đợt tìm nguồn nào',
  'sourcing.runs.empty.body': 'Chưa có đợt tìm nguồn ứng viên nào được chạy khớp bộ lọc hiện tại.',
  'sourcing.runs.row.noRequisition': 'Không gắn với vị trí tuyển dụng cụ thể',
  'sourcing.runs.row.leads': 'Tìm thấy {found} hồ sơ · {leadsNew} hồ sơ mới',

  'sourcing.run.status.pending': 'Đang chờ',
  'sourcing.run.status.running': 'Đang chạy',
  'sourcing.run.status.completed': 'Hoàn tất',
  'sourcing.run.status.failed': 'Thất bại',

  'sourcing.channel.career_page': 'Trang tuyển dụng công ty',
  'sourcing.channel.ats_import': 'Nhập từ hệ thống tuyển dụng cũ',
  'sourcing.channel.email': 'Email',
  'sourcing.channel.job_board_api': 'Trang tuyển dụng đối tác',
  'sourcing.channel.linkedin_dm': 'LinkedIn — tin nhắn riêng',
  'sourcing.channel.linkedin_comment': 'LinkedIn — bình luận',
  'sourcing.channel.facebook_dm': 'Facebook — tin nhắn riêng',
  'sourcing.channel.facebook_comment': 'Facebook — bình luận',
  'sourcing.channel.web_scrape': 'Thu thập dữ liệu web',

  'sourcing.trigger.title': 'Kích hoạt một đợt tìm nguồn',
  'sourcing.trigger.intro':
    'Chỉ bốn kênh rủi ro thấp — trang tuyển dụng của công ty, nhập từ hệ thống tuyển dụng cũ, email, và trang tuyển dụng đối tác — có thể chọn ở đây.',
  'sourcing.trigger.requisition': 'Vị trí tuyển dụng',
  'sourcing.trigger.requisitionIdHint':
    'Chưa có danh sách vị trí tuyển dụng ở đây, nên mã vị trí được nhập trực tiếp.',
  'sourcing.trigger.requisitionPlaceholder': '— Chọn vị trí tuyển dụng —',
  'sourcing.trigger.channel': 'Kênh tìm nguồn',
  'sourcing.trigger.submit': 'Kích hoạt đợt tìm nguồn',
  'sourcing.trigger.submitting': 'Đang kích hoạt…',
  'sourcing.trigger.subjectWord': 'đợt tìm nguồn',
  'sourcing.trigger.ok':
    'Đã chạy xong: tìm thấy {leadsFound} hồ sơ, {leadsNew} hồ sơ mới, tạo {candidatesCreated} ứng viên.',

  'sourcing.quarantine.title': 'Kênh rủi ro cao — bị cách ly',
  'sourcing.quarantine.intro':
    'Năm kênh này không bao giờ là lựa chọn ở màn hình này, dù chính sách công ty hay cấu hình máy chủ nói gì — luôn hiển thị, không bao giờ ẩn.',
  'sourcing.quarantine.badge': 'Bị cách ly',
  'sourcing.quarantine.highRisk.title': 'Kênh có rủi ro pháp lý cao',
  'sourcing.quarantine.highRisk.body':
    'Điều khoản dịch vụ của LinkedIn/Facebook cấm thu thập, tổng hợp hoặc nhắn tin hàng loạt; việc liên hệ ứng viên ngoài ý muốn của họ còn có thể vi phạm Nghị định 91/2020 (đồng ý và giới hạn tần suất) và Luật Bảo vệ dữ liệu cá nhân 91/2025/QH15 cùng Nghị định 356/2025 (bảo vệ dữ liệu cá nhân). Kênh này không thể kích hoạt từ màn hình này.',

  // ── Inbox (Task 23 API, WP-W13, Phase 4c SPA) ───────────────────────────────
  'nav.inbox': 'Hộp thư đến',
  'inbox.intro':
    'Email gốc mà ứng viên đã gửi, cùng CV đính kèm — bối cảnh để đối chiếu trước khi duyệt bản nháp trả lời do AI soạn ở màn hình Tiếp cận ứng viên.',
  'inbox.subjectWord': 'email gửi đến',
  'inbox.subjectAccess': 'tệp đính kèm',

  'inbox.filter.status': 'Trạng thái phân tích',
  'inbox.filter.candidate': 'Ứng viên khớp',

  'inbox.list.empty.title': 'Chưa có email nào khớp bộ lọc',
  'inbox.list.empty.body': 'Chưa có email gửi đến nào khớp với bộ lọc hiện tại.',
  'inbox.list.partial.excludes': 'Đang hiển thị {shown} email đầu tiên trong hộp thư',
  'inbox.list.noSubject': '(không có tiêu đề)',
  'inbox.list.noSender': '(không rõ người gửi)',
  'inbox.list.noDate': '(không rõ thời gian)',
  'inbox.list.meta': 'Từ {from} · {when}',
  'inbox.list.unmatched': 'Chưa khớp ứng viên nào — hồ sơ tiềm năng',
  'inbox.list.matched': 'Khớp với ứng viên: {name}',
  'inbox.list.attachments': '{count} tệp đính kèm',
  'inbox.list.open': 'Mở email',

  'inbox.detail.subject': 'email gửi đến',
  'inbox.detail.from': 'Người gửi',
  'inbox.detail.receivedAt': 'Thời gian nhận',
  'inbox.detail.none': '—',
  'inbox.detail.bodyTitle': 'Nội dung email',
  'inbox.detail.noBody': 'Email này không có nội dung văn bản.',
  'inbox.detail.candidateTitle': 'Ứng viên khớp',
  'inbox.detail.unmatched':
    'Người gửi chưa khớp với ứng viên nào — một hồ sơ tiềm năng cần rà soát.',

  'inbox.attachments.title': 'Tệp đính kèm',
  'inbox.attachments.empty.title': 'Email này không có tệp đính kèm',
  'inbox.attachments.unnamed': '(không có tên tệp)',
  'inbox.attachments.meta': '{type} · {size}',
  'inbox.attachments.typeUnknown': 'không rõ định dạng',
  'inbox.attachments.sizeUnknown': 'không rõ dung lượng',
  'inbox.attachments.sizeKb': '{kb} KB',
  'inbox.attachments.rejected': 'Không lưu được tệp này: {reason}',
  'inbox.attachments.rejectedUnknown': 'không rõ lý do',
  'inbox.attachments.download': 'Tải tệp đính kèm',
  'inbox.attachments.downloading': 'Đang tải…',
  'inbox.attachments.reasonNeeded':
    'Nhập lý do truy cập ít nhất {min} ký tự để bật nút tải tệp đính kèm.',
  'inbox.attachments.delivered': 'Đã tải xong: {filename}',

  'inbox.parseStatus.parsed': 'Đã phân tích đầy đủ',
  'inbox.parseStatus.partial': 'Phân tích một phần',
  'inbox.parseStatus.needs_human': 'Cần người rà soát',

  // ── Presence (Task P2 API, PW1 pillar web) ──────────────────────────────────
  'nav.presence': 'Hiện diện',

  'presence.intro':
    'Bảng hiện diện thời gian thực: ai đang làm việc, ở đâu, và đang làm gì — không phải bảng xếp hạng năng suất.',
  'presence.updatedAt': 'Cập nhật lúc {at}',
  'presence.neverUpdated': 'Chưa từng cập nhật',
  'presence.updating': 'Đang cập nhật…',

  'presence.summary.heading': 'Tổng quan theo trạng thái',
  'presence.summary.loading': 'Đang tải tổng quan hiện diện',
  'presence.summary.subject': 'tổng quan hiện diện',
  'presence.summary.total': 'Tổng cộng {count} người trong phạm vi',
  'presence.summary.staleAfterError': 'Lần làm mới gần nhất thất bại; các số trên là số liệu cũ.',

  'presence.filters.legend': 'Bộ lọc',
  'presence.filter.status': 'Trạng thái',
  'presence.filter.department': 'Phòng ban',
  'presence.filter.office': 'Văn phòng',
  'presence.filter.officeHint':
    'Mã văn phòng nằm ở màn Cấu hình công ty, mục địa điểm. Để trống là mọi văn phòng.',
  'presence.filter.apply': 'Áp dụng',
  'presence.filter.reload': 'Đọc lại',
  'presence.filter.all': 'Tất cả',

  'presence.list.heading': 'Danh sách hiện diện',
  'presence.list.loading': 'Đang tải danh sách hiện diện',
  'presence.list.subject': 'danh sách hiện diện',
  'presence.list.empty.title': 'Không có ai trong phạm vi',
  'presence.list.empty.body': 'Phạm vi truy cập của bạn hiện không có nhân viên nào.',
  'presence.list.staleAfterError':
    'Lần làm mới gần nhất thất bại; danh sách dưới đây là dữ liệu cũ.',
  'presence.list.col.employee': 'Nhân viên',
  'presence.list.col.status': 'Trạng thái',
  'presence.list.col.where': 'Vị trí',
  'presence.list.col.onWhat': 'Đang làm',
  'presence.list.col.lastSeen': 'Lần thấy gần nhất',
  'presence.noDepartment': 'Không có phòng ban',

  'presence.where.none': 'Chưa chấm công hôm nay',
  'presence.where.noOffice': 'Không xác định được văn phòng',
  'presence.where.within': 'Trong phạm vi văn phòng',
  'presence.where.outside': 'Ngoài phạm vi văn phòng',
  'presence.onWhat.none': 'Không có công việc đang mở',

  'presence.status.working_onsite': 'Đang làm việc tại văn phòng',
  'presence.status.working_remote': 'Đang làm việc từ xa',
  'presence.status.on_break': 'Đang nghỉ giải lao',
  'presence.status.offline': 'Ngoại tuyến',
  'presence.status.help.working_onsite':
    'Có bộ đếm giờ công việc đang chạy, và lượt chấm công hôm nay nằm trong phạm vi văn phòng.',
  'presence.status.help.working_remote':
    'Có bộ đếm giờ công việc đang chạy, nhưng không có lượt chấm công hôm nay trong phạm vi văn phòng.',
  'presence.status.help.on_break': 'Bộ đếm giờ công việc đang tạm dừng.',
  'presence.status.help.offline': 'Không có bộ đếm giờ công việc nào đang mở.',

  'presence.trust.trusted': 'Đáng tin cậy',
  'presence.trust.suspect': 'Cần xem xét',
  'presence.trust.unverified': 'Chưa xác minh',

  // ── Effectiveness (Task P3 API, PW2 pillar web) ─────────────────────────────
  'nav.effectiveness': 'Hiệu quả công việc',

  'effectiveness.intro':
    'Mục tiêu là số liệu chính; điểm năng suất chỉ là bối cảnh phụ, luôn kèm độ tin cậy; ' +
    'nhóm được xem như một phân bố để phát hiện ai cần hỗ trợ — không phải một bảng xếp hạng.',
  'effectiveness.insufficientData': 'Chưa đủ dữ liệu',
  'effectiveness.noDepartment': 'Không có phòng ban',
  'effectiveness.department': 'Phòng ban {id}',

  'effectiveness.framing.title': 'Không phải chỉ tiêu, không phải xếp hạng',
  'effectiveness.framing.body':
    'Đây là bối cảnh cho một cuộc trao đổi mang tính hỗ trợ giữa quản lý và nhân viên — ' +
    'không bao giờ là một quyết định tự động, một chỉ tiêu, hay một bảng xếp hạng trừng phạt.',

  'effectiveness.list.heading': 'Từng nhân viên',
  'effectiveness.list.loading': 'Đang tải danh sách hiệu quả công việc',
  'effectiveness.list.subject': 'danh sách hiệu quả công việc',
  'effectiveness.list.empty.title': 'Không có ai trong phạm vi',
  'effectiveness.list.empty.body': 'Phạm vi truy cập của bạn hiện không có nhân viên nào.',
  'effectiveness.list.col.employee': 'Nhân viên',
  'effectiveness.list.col.goals': 'Mục tiêu',
  'effectiveness.list.col.productivity': 'Điểm năng suất (bối cảnh phụ)',
  'effectiveness.list.col.attendance': 'Chấm công',

  'effectiveness.goals.counts': '{achieved}/{total} mục tiêu đã đạt',
  'effectiveness.goals.achievementPct': 'Tỉ lệ hoàn thành: {value}',

  'effectiveness.productivity.value': 'Điểm năng suất',
  'effectiveness.productivity.confidence': 'Độ tin cậy: {value}',
  'effectiveness.productivity.confidence.help':
    'Mức độ chắc chắn của điểm này, dựa trên dữ liệu đã quan sát được trong kỳ.',
  'effectiveness.productivity.lowConfidence.title': 'Độ tin cậy thấp',
  'effectiveness.productivity.lowConfidence.body':
    'Điểm này dựa trên dữ liệu chưa đầy đủ. Hãy đọc cùng mục tiêu và chấm công của kỳ này, ' +
    'và không dùng một mình điểm này để đưa ra quyết định kỷ luật.',

  'effectiveness.attendance.days': 'Có mặt {present}/{expected} ngày được kỳ vọng',
  'effectiveness.attendance.onTimeRate': 'Tỉ lệ đúng giờ: {value}',

  'effectiveness.team.heading': 'Phân bố của nhóm',
  'effectiveness.team.loading': 'Đang tải phân bố của nhóm',
  'effectiveness.team.subject': 'phân bố hiệu quả công việc của nhóm',
  'effectiveness.team.notRanking':
    'Đây là một phân bố để phát hiện ai cần hỗ trợ — không phải danh sách xếp hạng từng người.',
  'effectiveness.team.memberCount': 'Số người trong phạm vi: {count}',
  'effectiveness.team.goalAchievement': 'Tỉ lệ hoàn thành mục tiêu (phân bố)',
  'effectiveness.team.productivityScore': 'Điểm năng suất (phân bố)',
  'effectiveness.team.col.min': 'Thấp nhất',
  'effectiveness.team.col.p25': 'Tứ phân vị 25',
  'effectiveness.team.col.median': 'Trung vị',
  'effectiveness.team.col.p75': 'Tứ phân vị 75',
  'effectiveness.team.col.max': 'Cao nhất',
  'effectiveness.team.lowConfidenceCount': 'Số điểm có độ tin cậy thấp: {count}',
  'effectiveness.team.noGoalDataCount': 'Số người chưa có dữ liệu mục tiêu: {count}',
  'effectiveness.team.noProductivityScoreCount': 'Số người chưa có điểm năng suất: {count}',

  // ── People directory (Task T2-A API, T2-B pillar web, manage-people pillar) ──
  'nav.people': 'Nhân sự',

  'people.intro':
    'Danh bạ nhân viên trong phạm vi bạn được phép xem. Bộ lọc nằm trên đường dẫn (URL) nên ' +
    'có thể chia sẻ hoặc lưu lại — khác với bộ lọc đã lưu theo từng người của một số sản phẩm khác.',
  'people.noDepartment': 'Không có phòng ban',
  'people.noManager': 'Không có quản lý',

  'people.picker.loading': 'Đang tìm nhân viên',
  'people.picker.subject': 'danh sách nhân viên',
  'people.picker.empty': 'Không tìm thấy nhân viên',

  'people.filters.legend': 'Bộ lọc',
  'people.filter.search': 'Tìm theo tên hoặc email',
  'people.filter.status': 'Trạng thái',
  'people.filter.status.active': 'Đang làm việc',
  'people.filter.status.offboarded': 'Đã nghỉ',
  'people.filter.status.all': 'Tất cả',
  'people.filter.department': 'Phòng ban',
  'people.filter.department.all': 'Tất cả phòng ban',
  'people.filter.department.option': '{name} ({count})',
  'people.filter.role': 'Vai trò',
  'people.filter.role.all': 'Tất cả vai trò',

  'people.list.heading': 'Danh sách nhân viên',
  'people.list.caption':
    'Danh bạ nhân viên trong phạm vi của bạn, có thể sắp xếp theo họ tên, phòng ban hoặc ' +
    'thời điểm đồng bộ.',
  'people.list.loading': 'Đang tải danh sách nhân viên',
  'people.list.subject': 'danh sách nhân viên',
  'people.list.empty.title': 'Không có ai trong phạm vi',
  'people.list.empty.body':
    'Phạm vi truy cập của bạn hiện không có nhân viên nào phù hợp với bộ lọc.',
  'people.list.col.fullName': 'Họ tên',
  'people.list.col.email': 'Email',
  'people.list.col.role': 'Vai trò',
  'people.list.col.department': 'Phòng ban',
  'people.list.col.jobTitle': 'Chức danh',
  'people.list.col.manager': 'Quản lý',
  'people.list.col.status': 'Trạng thái',
  'people.list.col.syncedAt': 'Đồng bộ lúc',

  'people.status.active': 'Đang làm việc',
  'people.status.offboarded': 'Đã nghỉ',

  'people.pagination.range': 'Hiển thị {from}–{to} / {total}',
  'people.pagination.prev': 'Trước',
  'people.pagination.next': 'Sau',

  'people.drawer.title': 'Hồ sơ: {name}',
  'people.drawer.close': 'Đóng',
  'people.drawer.loading': 'Đang tải hồ sơ nhân viên',
  'people.drawer.subject': 'hồ sơ nhân viên',
  'people.drawer.field.email': 'Email',
  'people.drawer.field.role': 'Vai trò',
  'people.drawer.field.jobTitle': 'Chức danh',
  'people.drawer.field.department': 'Phòng ban',
  'people.drawer.field.branch': 'Chi nhánh',
  'people.drawer.field.manager': 'Quản lý',
  'people.drawer.field.directReports': 'Số nhân viên trực tiếp báo cáo',
  'people.drawer.field.status': 'Trạng thái',
  'people.drawer.field.syncedAt': 'Đồng bộ lúc',
  'people.drawer.field.offboardedAt': 'Ngày nghỉ việc',
  'people.drawer.quickLinks.heading': 'Xem nhanh',
  'people.drawer.quickLinks.attendance': 'Xem chấm công của nhân viên này',
  'people.drawer.quickLinks.note':
    'Chấm công vị trí, Hiện diện và Hiệu quả công việc chưa mở được với bộ lọc sẵn theo một nhân viên.',

  // ── Permission grants admin (Task T2-D, Tier-2 ops) ─────────────────────────
  'nav.adminPermissions': 'Phân quyền',

  'adminPermissions.subject': 'danh sách quyền đã cấp',

  'adminPermissions.intro':
    'Mỗi vai trò (Ban điều hành, Quản lý, Nhân viên) có một tập quyền mặc định ngầm định, ' +
    'không hiển thị thành dòng ở đây. Một quyền cấp bên dưới chỉ ghi lại phần khác biệt so ' +
    'với mặc định đó: hiệu lực "Cho phép" cộng thêm một mã quyền, "Từ chối" gỡ bớt một mã ' +
    'quyền — và khi cả hai cùng áp dụng cho một đối tượng, "Từ chối" luôn thắng "Cho phép". ' +
    'Dòng đã hết hạn hoặc đã thu hồi vẫn được giữ lại trong danh sách để phục vụ tra soát, ' +
    'không bị xoá.',

  'adminPermissions.create.open': 'Cấp quyền',

  'adminPermissions.list.heading': 'Danh sách quyền đã cấp',
  'adminPermissions.list.caption':
    'Mọi quyền đã cấp trong công ty đang xem, kể cả đã hết hạn hoặc đã thu hồi.',
  'adminPermissions.list.loading': 'Đang tải danh sách quyền đã cấp',
  'adminPermissions.list.empty.title': 'Chưa có quyền nào được cấp',
  'adminPermissions.list.empty.body':
    'Không có dòng nào khớp với bộ lọc hiện tại. Vai trò mặc định vẫn áp dụng như bình thường.',
  'adminPermissions.list.col.subject': 'Đối tượng',
  'adminPermissions.list.col.code': 'Mã quyền',
  'adminPermissions.list.col.effect': 'Hiệu lực',
  'adminPermissions.list.col.reason': 'Lý do',
  'adminPermissions.list.col.grantedBy': 'Cấp bởi/lúc',
  'adminPermissions.list.col.expiresAt': 'Hết hạn',
  'adminPermissions.list.col.status': 'Trạng thái',
  'adminPermissions.list.col.actions': 'Thao tác',
  'adminPermissions.noExpiry': 'Không hết hạn',
  'adminPermissions.noReason': '—',
  'adminPermissions.codeCell': '{group} · {code}',
  'adminPermissions.grantedCell': '{by} lúc {at}',
  'adminPermissions.grantedByUnknown': 'không rõ',

  'adminPermissions.subjectType.employee': 'Nhân viên',
  'adminPermissions.subjectType.role': 'Vai trò',
  'adminPermissions.subjectType.department': 'Phòng ban',
  'adminPermissions.subjectType.jobTitle': 'Chức danh',

  'adminPermissions.effect.allow': 'Cho phép',
  'adminPermissions.effect.deny': 'Từ chối',

  'adminPermissions.status.active': 'Đang hiệu lực',
  'adminPermissions.status.expired': 'Hết hạn',
  'adminPermissions.status.revoked': 'Đã thu hồi',

  'adminPermissions.group.workforce': 'Workforce',
  'adminPermissions.group.recruiting': 'Tuyển dụng',
  'adminPermissions.group.hr': 'Nhân sự',
  'adminPermissions.group.payroll': 'Lương',

  'adminPermissions.filters.legend': 'Bộ lọc',
  'adminPermissions.filter.subjectType': 'Đối tượng',
  'adminPermissions.filter.subjectType.all': 'Tất cả đối tượng',
  'adminPermissions.filter.group': 'Nhóm quyền',
  'adminPermissions.filter.group.all': 'Tất cả nhóm',
  'adminPermissions.filter.effect': 'Hiệu lực',
  'adminPermissions.filter.effect.all': 'Tất cả',
  'adminPermissions.filter.status': 'Trạng thái',
  'adminPermissions.filter.status.all': 'Tất cả trạng thái',

  'adminPermissions.form.heading': 'Cấp quyền',
  'adminPermissions.form.subjectType.label': 'Loại đối tượng',
  'adminPermissions.form.employee.label': 'Nhân viên',
  'adminPermissions.form.role.label': 'Vai trò',
  'adminPermissions.form.role.placeholder': 'Chọn vai trò',
  'adminPermissions.form.department.label': 'Phòng ban',
  'adminPermissions.form.department.placeholder': 'Chọn phòng ban',
  'adminPermissions.form.jobTitle.label': 'Chức danh',
  'adminPermissions.form.jobTitle.placeholder': 'Ví dụ: Trưởng phòng Nhân sự',
  'adminPermissions.form.permissionCode.label': 'Mã quyền',
  'adminPermissions.form.permissionCode.placeholder': '— Chọn mã quyền —',
  'adminPermissions.form.effect.label': 'Hiệu lực',
  'adminPermissions.form.reason.label': 'Lý do',
  'adminPermissions.form.reason.hint': 'Bắt buộc trên màn hình này để phục vụ tra soát sau này.',
  'adminPermissions.form.expiresAt.label': 'Hết hạn (tuỳ chọn)',
  'adminPermissions.form.submit': 'Cấp quyền',
  'adminPermissions.form.submitting': 'Đang cấp quyền…',
  'adminPermissions.form.cancel': 'Huỷ',
  'adminPermissions.form.created': 'Đã cấp quyền',
  'adminPermissions.form.updated': 'Đã cập nhật quyền',

  'adminPermissions.revoke.action': 'Thu hồi',
  'adminPermissions.revoke.title': 'Thu hồi quyền "{code}" của {subject}?',
  'adminPermissions.revoke.body':
    'Dòng sẽ chuyển sang trạng thái "Đã thu hồi" và vẫn được giữ lại để phục vụ tra soát; ' +
    'hiệu lực bị gỡ ngay khi xác nhận.',
  'adminPermissions.revoke.confirm': 'Xác nhận thu hồi',
  'adminPermissions.revoke.confirming': 'Đang thu hồi…',
  'adminPermissions.revoke.cancel': 'Huỷ',
  'adminPermissions.revoke.done': 'Đã thu hồi quyền.',

  'adminPermissions.code.workforce.view_self':
    'Xem dữ liệu của chính mình (chấm công, hoạt động, năng suất).',
  'adminPermissions.code.workforce.view_department': 'Xem dữ liệu của phòng ban mình quản lý.',
  'adminPermissions.code.workforce.view_workspace': 'Xem dữ liệu của toàn công ty.',
  'adminPermissions.code.workforce.manage_shifts':
    'Quản lý ca làm việc; duyệt chấm công và giờ làm.',
  'adminPermissions.code.workforce.manage_policies':
    'Quản lý chính sách giám sát, quyền truy cập và cấu hình công ty.',
  'adminPermissions.code.workforce.manage_devices': 'Quản lý thiết bị và mã đăng ký Agent.',
  'adminPermissions.code.workforce.view_apps': 'Xem dữ liệu sử dụng ứng dụng.',
  'adminPermissions.code.workforce.view_websites': 'Xem dữ liệu truy cập website.',
  'adminPermissions.code.workforce.view_file_events':
    'Xem sự kiện thao tác tệp (chưa có màn hình tương ứng).',
  'adminPermissions.code.workforce.view_usb_events':
    'Xem sự kiện thiết bị USB (chưa có màn hình tương ứng).',
  'adminPermissions.code.workforce.view_screenshots':
    'Xem ảnh chụp màn hình (chưa có màn hình tương ứng).',
  'adminPermissions.code.workforce.export_reports':
    'Xuất báo cáo và xem lịch xuất báo cáo định kỳ.',
  'adminPermissions.code.workforce.manage_alerts': 'Quản lý quy tắc cảnh báo.',
  'adminPermissions.code.workforce.resolve_incidents':
    'Xem và xử lý sự cố; xác nhận đã đọc cảnh báo.',
  'adminPermissions.code.workforce.view_audit_logs': 'Xem nhật ký kiểm toán.',
  'adminPermissions.code.workforce.manage_retention':
    'Quản lý chính sách lưu trữ dữ liệu (chưa có màn hình tương ứng).',
  'adminPermissions.code.workforce.use_ai_analytics': 'Sử dụng phân tích AI.',
  'adminPermissions.code.recruiting.view_jobs': 'Xem tin tuyển dụng.',
  'adminPermissions.code.recruiting.manage_jobs': 'Tạo và quản lý tin tuyển dụng.',
  'adminPermissions.code.recruiting.view_candidates': 'Xem hồ sơ ứng viên.',
  'adminPermissions.code.recruiting.manage_candidates':
    'Quản lý ứng viên, liên hệ và pipeline tuyển dụng.',
  'adminPermissions.code.recruiting.view_candidate_documents': 'Xem tài liệu/CV của ứng viên.',
  'adminPermissions.code.recruiting.submit_feedback': 'Nộp đánh giá phỏng vấn.',
  'adminPermissions.code.recruiting.manage_offers': 'Tạo và quản lý thư mời làm việc.',
  'adminPermissions.code.recruiting.hire': 'Chuyển ứng viên trúng tuyển thành nhân viên.',
  'adminPermissions.code.hr.view_employee_hr':
    'Xem hồ sơ nhân sự (hợp đồng, tài liệu) của nhân viên.',
  'adminPermissions.code.hr.view_employee_documents':
    'Xem nội dung tài liệu nhân sự của nhân viên.',
  'adminPermissions.code.hr.manage_employee_documents': 'Quản lý (tải lên, xoá) tài liệu nhân sự.',
  'adminPermissions.code.hr.manage_onboarding': 'Quản lý mẫu và tiến trình hội nhập nhân viên mới.',
  'adminPermissions.code.hr.view_leave': 'Xem đơn và số dư ngày nghỉ phép của người khác.',
  'adminPermissions.code.hr.request_leave': 'Nộp đơn xin nghỉ phép của bản thân.',
  'adminPermissions.code.hr.approve_leave': 'Duyệt đơn xin nghỉ phép.',
  'adminPermissions.code.hr.manage_leave_types': 'Quản lý loại ngày nghỉ phép.',
  'adminPermissions.code.hr.view_reviews': 'Xem đánh giá hiệu suất của người khác.',
  'adminPermissions.code.hr.manage_review_cycles': 'Quản lý chu kỳ đánh giá và mục tiêu.',
  'adminPermissions.code.hr.submit_review':
    'Nộp đánh giá hiệu suất (của bản thân hoặc người khác).',
  'adminPermissions.code.hr.view_own_review': 'Xem đánh giá hiệu suất của bản thân.',
  'adminPermissions.code.payroll.view_own_payslip': 'Xem phiếu lương của bản thân.',
  'adminPermissions.code.payroll.view_payroll': 'Xem kỳ lương, đợt chạy lương và các khoản mục.',
  'adminPermissions.code.payroll.run_payroll': 'Tạo kỳ lương và tính lương.',
  'adminPermissions.code.payroll.approve_payroll': 'Duyệt đợt chạy lương (cấp quản lý).',
  'adminPermissions.code.payroll.ceo_approve': 'Duyệt đợt chạy lương (cấp Ban điều hành).',
  'adminPermissions.code.payroll.manage_templates': 'Quản lý mẫu thành phần lương.',
  'adminPermissions.code.payroll.manage_regulations': 'Quản lý công thức và quy định tính lương.',

  // ── Roster grid (Task T2-E API, T2-E-web, location-attendance pillar) ──────
  'nav.roster': 'Lịch ca',
  'nav.myData': 'Dữ liệu của tôi',

  // Tier-3 U8 — bản sao dữ liệu cá nhân của chính mình (PDPL, Luật 91/2025/QH15).
  // Ba mốc thời gian là BA con số khác nhau và không câu nào được gộp chúng lại:
  // đường dẫn sống vài phút, tệp sống bảy ngày, bản ghi thì giữ lại.
  'myData.intro':
    'Bạn có quyền nhận một bản sao dữ liệu cá nhân của mình theo Luật Bảo vệ dữ liệu cá nhân (Luật 91/2025/QH15).',
  'myData.export.title': 'Bản sao dữ liệu',
  'myData.export.policy':
    'Tối đa {cap} yêu cầu mỗi ngày. Chúng tôi cố gắng hoàn tất trong {hours} giờ. Tệp được giữ {days} ngày kể từ lúc lắp xong.',
  'myData.export.cta': 'Yêu cầu bản sao dữ liệu',
  'myData.export.requesting': 'Đang gửi yêu cầu…',
  'myData.export.subject': 'yêu cầu bản sao dữ liệu',
  'myData.export.loading': 'Đang tải danh sách yêu cầu',
  'myData.export.empty.title': 'Bạn chưa yêu cầu bản sao nào.',
  'myData.export.empty.body':
    'Khi bạn yêu cầu, chúng tôi lắp một tệp gồm ngày công, lần chấm công có toạ độ, dòng thời gian hoạt động và phiếu lương đã phát hành của riêng bạn.',
  'myData.export.history': 'Các yêu cầu trước đó',
  'myData.status.pending': 'Đang chờ xử lý',
  'myData.status.running': 'Đang chuẩn bị',
  'myData.status.done': 'Sẵn sàng',
  'myData.status.failed': 'Thất bại',
  'myData.requestedAt': 'Gửi lúc {at}',
  'myData.fileUntil': 'Còn tải được đến {at}',
  'myData.fileGone': 'Tệp đã hết hạn tải. Hãy tạo yêu cầu mới.',
  'myData.download': 'Tải xuống',
  'myData.failed.title': 'Lắp tệp không thành công',
  'myData.failed.body': 'Hãy tạo một yêu cầu mới. Yêu cầu này vẫn được giữ lại trong hồ sơ.',

  // Tier-3 U9 — REGION 2 of /my-data, "ai đã xem dữ liệu của tôi".
  'myData.access.title': 'Ai đã xem dữ liệu của tôi',
  'myData.access.intro':
    'Mỗi lần ai đó đọc dữ liệu nhạy cảm về bạn đều được ghi lại kèm lý do. Những lần đọc trên ' +
    'phạm vi toàn công ty cũng hiện ở đây, có nhãn riêng — chúng không nhắm vào cá nhân bạn.',
  'myData.access.subject': 'nhật ký truy cập của bạn',
  'myData.access.loading': 'Đang tải nhật ký truy cập',
  'myData.access.empty.title': 'Chưa có ai đọc dữ liệu của bạn.',
  'myData.access.empty.body':
    'Khi có người đọc, mỗi lần sẽ hiện ở đây: ai đọc, đọc gì, lúc nào và vì sao.',
  'myData.access.companyWide': 'toàn công ty',
  'myData.access.col.actor': 'Ai đọc',
  'myData.access.col.what': 'Đọc gì',
  'myData.access.col.at': 'Lúc nào',
  'myData.access.col.reason': 'Lý do',
  'myData.access.at': 'Lúc {at}',
  'myData.access.what': '{verb} · {kind}',
  'myData.access.reason': 'Lý do: {reason}',
  'myData.access.more': 'Xem thêm',
  'myData.access.moreLoading': 'Đang tải…',
  'myData.access.verb.view': 'Đã xem',
  'myData.access.verb.list': 'Đã xem danh sách',
  'myData.access.verb.export': 'Đã xuất dữ liệu',
  'myData.access.verb.download': 'Đã tải tệp',
  'myData.access.verb.ai_query': 'Đã hỏi trợ lý AI',
  'myData.access.kind.screenshot': 'Ảnh màn hình',
  'myData.access.kind.usb_event': 'Sự kiện USB',
  'myData.access.kind.file_event': 'Sự kiện tệp',
  'myData.access.kind.audit_log': 'Nhật ký kiểm toán',
  'myData.access.kind.employee_activity': 'Hoạt động làm việc',
  'myData.access.kind.employee_attendance': 'Dữ liệu chấm công',
  'myData.access.kind.report': 'Báo cáo',
  'myData.access.kind.incident_evidence': 'Bằng chứng sự cố',
  'myData.access.kind.ai_insight': 'Nhận định của trợ lý AI',
  'myData.access.kind.candidate_document': 'Hồ sơ ứng viên',
  'myData.access.kind.employee_document': 'Hồ sơ nhân sự',
  'myData.access.kind.employee_location': 'Vị trí check-in',
  'myData.access.kind.dsar_export': 'Bản sao dữ liệu cá nhân',

  'roster.intro':
    'Lưới ca làm việc theo tuần, xếp theo cùng thứ tự ưu tiên dùng cho từng ngày. Cảnh báo Bộ luật Lao động là thông tin tham khảo, không chặn xếp ca.',
  'roster.subject': 'lịch ca theo tuần',

  'roster.filters.legend': 'Bộ lọc',
  'roster.filter.department': 'Phòng ban',
  'roster.filter.department.all': 'Tất cả phòng ban',

  'roster.week.prev': 'Tuần trước',
  'roster.week.next': 'Tuần sau',
  'roster.week.thisWeek': 'Tuần này',
  'roster.week.range': 'Tuần {from} – {to}',

  'roster.grid.loading': 'Đang tải lịch ca',
  'roster.grid.empty.title': 'Không có nhân sự trong phạm vi',
  'roster.grid.empty.body':
    'Phạm vi truy cập của bạn hiện không có nhân viên nào phù hợp với bộ lọc tuần này.',
  'roster.grid.col.employee': 'Nhân viên',
  'roster.grid.col.weekTotal': 'Tổng tuần',
  'roster.grid.caption':
    'Lịch ca theo tuần: mỗi hàng là một nhân viên, mỗi cột là một ngày; cột cuối là tổng số giờ ' +
    'đã xếp trong tuần.',
  'roster.grid.noDepartment': 'Không có phòng ban',

  'roster.cell.dayOff': 'Nghỉ theo lịch ca',
  'roster.cell.unconfigured': 'Chưa cấu hình',
  'roster.cell.open': 'Xem/xếp ca cho {name} ngày {date}',

  'roster.via.job_title': 'theo chức danh',
  'roster.via.department': 'theo phòng ban',
  'roster.via.branch': 'theo chi nhánh',
  'roster.via.company': 'theo công ty',
  'roster.via.default': 'mặc định',

  'roster.scope.employee': 'Nhân viên',
  'roster.scope.job_title': 'Chức danh',
  'roster.scope.department': 'Phòng ban',
  'roster.scope.branch': 'Chi nhánh',
  'roster.scope.company': 'Toàn công ty',

  'roster.warning.daily_over_8h': 'Quá 8 giờ/ngày',
  'roster.warning.weekly_over_48h': 'Quá 48 giờ/tuần',
  'roster.warning.days_over_6_per_week': 'Quá 6 ngày/tuần',
  'roster.warning.rest_under_12h': 'Nghỉ dưới 12 giờ',
  'roster.warning.night_work': 'Có ca đêm (22:00–06:00)',
  'roster.warning.overlap': 'Trùng ca',
  'roster.warning.unknown': 'Có cảnh báo (mã chưa xác định)',
  'roster.warning.detail.minutes': '{minutes} phút đã xếp',
  'roster.warning.detail.days': '{days} ngày đã làm',

  'roster.drawer.title': 'Xếp ca: {name}',
  'roster.drawer.close': 'Đóng',
  'roster.drawer.loading': 'Đang tải danh sách phân ca',
  'roster.drawer.subject': 'danh sách phân ca',
  'roster.drawer.empty': 'Nhân viên này chưa có phân ca nào.',
  'roster.drawer.list.heading': 'Các lần phân ca',
  'roster.drawer.list.note':
    'Các dòng theo phòng ban/chi nhánh/chức danh/toàn công ty là chính sách áp dụng cho nhiều ' +
    'người, không riêng nhân viên này.',
  'roster.drawer.list.col.scope': 'Phạm vi',
  'roster.drawer.list.col.shift': 'Ca',
  'roster.drawer.list.col.effective': 'Hiệu lực',
  'roster.drawer.list.col.warnings': 'Cảnh báo',
  'roster.drawer.list.col.actions': 'Thao tác',
  'roster.drawer.list.ended': 'Đã kết thúc {date}',
  'roster.drawer.list.effective.openEnded': 'Từ {from}',
  'roster.drawer.list.effective.ranged': '{from} – {to}',

  'roster.assign.heading': 'Gán ca',
  'roster.assign.shift': 'Ca làm việc',
  'roster.assign.shift.placeholder': '— Chọn ca —',
  'roster.assign.effectiveFrom': 'Hiệu lực từ',
  'roster.assign.effectiveTo': 'Hiệu lực đến (tuỳ chọn)',
  'roster.assign.submit': 'Gán ca',
  'roster.assign.submitting': 'Đang gán ca…',
  'roster.assign.done': 'Ca đã được gán. Cảnh báo không chặn — bạn có thể điều chỉnh lại.',
  'roster.assign.noShifts': 'Chưa có ca làm việc nào được cấu hình đang hoạt động để gán.',
  'roster.assign.skipped.employee_not_in_company':
    'Nhân viên này không còn thuộc công ty nên chưa được gán ca.',
  'roster.assign.skipped.unknown':
    'Không thể gán ca cho nhân viên này. Vui lòng thử lại hoặc báo cho quản trị viên.',

  'roster.end.action': 'Kết thúc',
  'roster.end.title': 'Kết thúc phân ca "{shift}" của {subject}?',
  'roster.end.body':
    'Phân ca sẽ dừng hiệu lực từ ngày kết thúc bên dưới; dữ liệu chấm công đã tính trước đó vẫn ' +
    'được giữ nguyên, không bị xoá.',
  'roster.end.effectiveTo': 'Ngày kết thúc (bỏ trống = hôm nay)',
  'roster.end.confirm': 'Xác nhận kết thúc',
  'roster.end.confirming': 'Đang kết thúc…',
  'roster.end.cancel': 'Huỷ',
  'roster.end.alreadyEnded': 'Đã kết thúc trước đó.',

  // ── DSAR queue and erasure (Task U10, Tier-3 live-tenant readiness) ──────
  'nav.dsarQueue': 'Yêu cầu dữ liệu cá nhân',

  'dsar.queue.intro':
    'Mọi yêu cầu của chủ thể dữ liệu trong công ty. Thực thi một yêu cầu XOÁ sẽ xoá dữ liệu ' +
    'của người đó vĩnh viễn, ngay lập tức và KHÔNG khôi phục được. Những gì luật buộc phải ' +
    'giữ lại (phiếu lương, hồ sơ nhân sự, hợp đồng) và nhật ký kiểm toán vẫn được giữ, và ' +
    'kết quả sẽ liệt kê từng bản ghi đó.',
  'dsar.queue.subject': 'hàng đợi yêu cầu dữ liệu cá nhân',
  'dsar.queue.empty': 'Chưa có yêu cầu nào.',
  'dsar.queue.table.caption': 'Yêu cầu dữ liệu cá nhân của công ty',

  'dsar.queue.column.id': 'Mã yêu cầu',
  'dsar.queue.column.employee': 'Nhân viên',
  'dsar.queue.column.kind': 'Loại',
  'dsar.queue.column.status': 'Trạng thái',
  'dsar.queue.column.requested': 'Yêu cầu lúc',
  'dsar.queue.column.result': 'Kết quả',

  'dsar.kind.export': 'Bản sao dữ liệu',
  'dsar.kind.erasure': 'Xoá dữ liệu',

  'dsar.queue.progress': 'đã chạy xong:',
  'dsar.queue.retained': 'Giữ lại theo nghĩa vụ pháp lý:',
  'dsar.queue.evidence': 'Giữ lại làm bằng chứng xử lý:',

  'dsar.queue.execute': 'Thực thi xoá…',
  'dsar.queue.executing': 'Đang xoá…',
  'dsar.queue.confirm.warning':
    'Hành động này xoá dữ liệu thô và dữ liệu dẫn xuất của nhân viên này ngay bây giờ, và ' +
    'KHÔNG có cách nào hoàn lại. Không có bản nháp, không có thùng rác.',
  'dsar.queue.confirm.remains':
    'Vẫn được giữ: phiếu lương và hồ sơ nhân sự trong thời hạn lưu trữ bắt buộc, các dòng ' +
    'đang bị legal hold, và nhật ký kiểm toán ghi lại chính lần xoá này.',
  'dsar.queue.confirm.typeId': 'Gõ lại mã yêu cầu để xác nhận',
  'dsar.queue.confirm.execute': 'Xoá vĩnh viễn',
  'dsar.queue.error.notOffboarded.title': 'Nhân viên chưa nghỉ việc',
  'dsar.queue.error.notOffboarded':
    'Chỉ có thể xoá dữ liệu của nhân viên đã nghỉ việc. Hãy hoàn tất offboarding trước — ' +
    'bước đó thu hồi thiết bị và huỷ phiên đăng nhập, nếu không thì agent của họ vẫn đang ' +
    'ghi thêm dữ liệu trong lúc xoá.',

  // ── Retention settings (Task U11, Tier-3 live-tenant readiness) ──────────
  'nav.retention': 'Thời hạn lưu trữ',

  'retention.intro':
    'Mỗi loại dữ liệu một dòng, mỗi dòng một phiên bản riêng. Hạ số ngày KHÔNG xóa gì ngay: ' +
    'lần dọn dẹp kế tiếp mới xoá, và xoá hồi tố — không có ân hạn, không khôi phục được.',
  'retention.subject': 'thời hạn lưu trữ',
  'retention.table.caption': 'Thời hạn lưu trữ theo loại dữ liệu',

  'retention.column.dataClass': 'Loại dữ liệu',
  'retention.column.days': 'Số ngày giữ',
  'retention.column.floor': 'Sàn tối thiểu',
  'retention.column.updated': 'Đổi lần cuối',
  'retention.column.state': 'Trạng thái',

  'retention.floor.range': 'Cho phép {min}–{max} ngày',
  'retention.floor.recordKeeping': 'Sàn {days} ngày — nghĩa vụ lưu trữ hồ sơ',
  'retention.floor.bounds': 'Sàn {days} ngày — biên vận hành',
  'retention.version': 'Phiên bản {version}',
  'retention.awaitingMigration': 'Chưa có bảng để dọn',
  'retention.awaitingMigration.hint':
    'Loại dữ liệu này chưa có gì để dọn. Đây là một sự vắng mặt được khai báo, không phải “đã xoá 0 dòng”.',
  'retention.enabled': 'Đang bật dọn dẹp',
  'retention.disabled': 'Đang tắt dọn dẹp',

  'retention.reason': 'Lý do (lưu vào nhật ký kiểm toán)',
  'retention.reason.hint':
    'Tối thiểu {min} ký tự. Mỗi loại được áp dụng đều ghi một dòng nhật ký kiểm toán.',
  'retention.acknowledge': 'Tôi hiểu lần dọn kế tiếp sẽ xoá dữ liệu đang nằm trong cửa sổ hiện tại',
  'retention.acknowledge.required':
    'Loại này đang bị rút ngắn từ {from} xuống {to} ngày. Phải xác nhận trước khi lưu.',
  'retention.save': 'Lưu thay đổi',
  'retention.saving': 'Đang lưu…',
  'retention.dirty': 'Đang sửa {count} loại',
  'retention.saveOk': 'Đã lưu {count} loại.',

  'retention.applied': 'Đã lưu — phiên bản {version}',
  'retention.conflict.title': 'Người khác vừa đổi loại này',
  'retention.conflict.body':
    'Bạn gửi phiên bản {expected}, dòng hiện là phiên bản {actual} với {stored} ngày. ' +
    'Thay đổi của bạn cho loại này KHÔNG được áp dụng — ô bên trên vẫn giữ {yours} ngày ' +
    'bạn vừa nhập, chưa lưu.',
  'retention.conflict.overwrite': 'Ghi đè {stored} ngày mà người kia vừa lưu',
  'retention.range.required':
    '{dataClass} phải nằm trong khoảng {floor}–{max} ngày. Sửa lại trước khi lưu.',

  'retention.class.raw_activity': 'Sự kiện hoạt động thô',
  'retention.class.heartbeat': 'Tín hiệu sống của Agent',
  'retention.class.aggregate_hourly': 'Tổng hợp theo giờ',
  'retention.class.aggregate_daily': 'Tổng hợp theo ngày',
  'retention.class.derived_daily': 'Phiên làm việc và thời gian ghi nhận',
  'retention.class.screenshot': 'Ảnh màn hình',
  'retention.class.audit': 'Nhật ký kiểm toán',
  'retention.class.report': 'Báo cáo đã xuất',
  'retention.class.alert': 'Cảnh báo',
  'retention.class.incident': 'Sự vụ',
  'retention.class.ai_insight': 'Phân tích AI',
  'retention.class.cache': 'Bộ nhớ đệm công việc',
  'retention.class.jobs': 'Tác vụ nền đã xong',
  'retention.class.short_ops': 'Vận hành ngắn hạn',
  'retention.class.recruiting_pii': 'Dữ liệu ứng viên',
  'retention.class.hr_records': 'Hồ sơ nhân sự và hợp đồng',
  'retention.class.payroll': 'Phiếu lương',
  'retention.class.geo_checkin': 'Chấm công vị trí',

  // ── Cấu hình công ty: quốc gia và tiền tệ (Task CS-2) ────────────────────
  'companySettings.title': 'Cấu hình công ty',
  'companySettings.subject': 'cấu hình công ty',
  'companySettings.region.title': 'Quốc gia và tiền tệ',
  'companySettings.country': 'Quốc gia',
  'companySettings.currency': 'Đơn vị tiền tệ',
  'companySettings.currency.hint':
    'Chọn quốc gia sẽ gợi ý sẵn đơn vị tiền tệ thường dùng; bạn đổi lại được.',
  'companySettings.engineNote':
    'Bộ tính lương hiện áp dụng quy định của Việt Nam (thuế thu nhập cá nhân, bảo hiểm bắt buộc). ' +
    'Quốc gia và tiền tệ ở đây quyết định cách hiển thị số tiền và các gợi ý chính sách của AI. ' +
    'Đặt quốc gia khác Việt Nam sẽ KHÔNG tính lương theo luật nước đó: bộ tính lương từ chối đợt tính lương cho tới khi có bộ luật của nước đó (CS-3).',
  'companySettings.timezone': 'Múi giờ',
  'companySettings.locale': 'Ngôn ngữ',
  'companySettings.updated': 'Đổi lần cuối',
  'companySettings.facts.hint':
    'Múi giờ và ngôn ngữ được đồng bộ từ achau, không sửa ở màn hình này.',
  'companySettings.save': 'Lưu cấu hình',
  'companySettings.saving': 'Đang lưu…',
  'companySettings.saved': 'Đã lưu. Áp dụng cho phiếu lương và gợi ý của AI từ bây giờ.',
  'companySettings.unsaved': 'Có thay đổi chưa lưu',

  /* Task U14 — the shared versioned-config lifecycle (KTD13). One vocabulary for
     the four configuration screens U15–U18, so a stale version reads the same on
     every one of them. */
  'config.table.caption': 'Các phiên bản cấu hình, bản đang áp dụng và thiết lập của từng bản',
  'config.column.version': 'Phiên bản',
  'config.column.state': 'Trạng thái',
  'config.column.settings': 'Thiết lập',
  'config.column.actions': 'Thao tác',
  'config.active': 'Đang áp dụng',
  'config.inactive': 'Chưa áp dụng',
  'config.token': 'Mã đối chiếu {token}',
  'config.token.none':
    'Bản đọc này không kèm mã đối chiếu nên không sửa được — đây không phải vấn đề quyền của bạn.',
  'config.token.unversioned':
    'Bề mặt này không kiểm tra xung đột: nếu hai người cùng sửa, bản lưu sau sẽ thắng.',
  'config.pending': 'Đang gửi thay đổi…',
  'config.saved': 'Đã lưu.',
  'config.diff.title': 'Thay đổi chưa lưu, so với bản đang áp dụng',
  'config.diff.empty': 'Chưa có thay đổi nào. Chỉ những trường bạn thực sự sửa mới được gửi đi.',
  'config.diff.change': '{key}: {from} → {to}',
  'config.diff.absent': '(bản đang áp dụng không có trường này)',
  'config.conflict.title': 'Ai đó vừa thay đổi cấu hình này',
  'config.conflict.token':
    'Máy chủ cho biết bản đang lưu ở {token}. Thay đổi của bạn KHÔNG được áp dụng.',
  'config.conflict.token.unknown':
    'Máy chủ không cho biết mã của bản đang lưu. Thay đổi của bạn KHÔNG được áp dụng.',
  'config.conflict.kept': 'Bản nháp của bạn được giữ nguyên — không có gì bị mất.',
  'config.conflict.compare': '{key} — đang lưu: {stored} · bạn nhập: {yours}',
  'config.conflict.stored.unknown': 'chưa đọc lại',
  'config.conflict.loadStored': 'Đọc lại giá trị đang lưu',
  'config.conflict.overwrite': 'Tôi hiểu và vẫn muốn ghi đè thay đổi của người kia',

  /* Task U15 — the collection policy editor and its rollout. The most sensitive
     configuration in the product, and until this screen it could only be changed
     with a database client. */
  'policyEditor.title': 'Chính sách thu thập',
  'policyEditor.intro':
    'Chính sách thu thập quyết định Agent ghi lại những gì trên máy của từng người. Đây là cấu hình nhạy cảm nhất của sản phẩm; hãy đọc kỹ trước khi đổi.',
  'policyEditor.panel.title': 'Các phiên bản chính sách',
  'policyEditor.subject': 'chính sách thu thập',
  'policyEditor.empty.title': 'Công ty này chưa có chính sách thu thập nào',
  'policyEditor.empty.body':
    'Chưa có bản nào được ghi, nên các máy đang chạy theo mặc định dựng sẵn. Đặt tên rồi tạo một bản nháp. Kích hoạt sẽ XUẤT BẢN bản nháp đó, nhưng không đặt nó làm mặc định của công ty — xem ghi chú ngay dưới bảng phiên bản.',
  'policyEditor.create.name': 'Tên phiên bản mới',
  'policyEditor.create.hint': 'Nhập tên trước; máy chủ từ chối một bản không có tên.',
  'policyEditor.create.submit': 'Tạo bản nháp',
  'policyEditor.row.edit': 'Sửa bản này',
  'policyEditor.row.activate': 'Kích hoạt',
  'policyEditor.activate.note':
    'Kích hoạt là XUẤT BẢN một bản nháp, chứ không đặt nó làm mặc định của công ty. Một chính sách chỉ thực sự điều khiển máy khi nó là mặc định của công ty hoặc có một phân công trỏ tới nó, và việc đặt mặc định chưa làm được từ màn hình này.',
  'policyEditor.form.title': 'Đang sửa: {name} ({version})',
  'policyEditor.form.active':
    'Bản này ĐANG áp dụng. Lưu sẽ tạo một chuỗi phiên bản mới và mọi máy phải xác nhận lại.',
  'policyEditor.form.draft':
    'Bản này là bản nháp: chưa máy nào được phục vụ nó, nên lưu không đổi số phiên bản.',
  'policyEditor.level.legend': 'Mức thu thập',
  'policyEditor.level.1': 'Mức 1 — chỉ thời lượng ứng dụng và tên miền',
  'policyEditor.level.2': 'Mức 2 — thêm tiêu đề cửa sổ và trang',
  'policyEditor.level.3': 'Mức 3 — thêm các sự kiện chi tiết nhất hiện có',
  'policyEditor.idleShort': 'Ngưỡng nghỉ ngắn (giây)',
  'policyEditor.idleExtended': 'Ngưỡng nghỉ kéo dài (giây)',
  'policyEditor.idle.hint':
    'Ngưỡng kéo dài phải LỚN HƠN ngưỡng ngắn. Nếu sai, hệ thống sẽ từ chối lưu và gọi tên ô nhập sai.',
  'policyEditor.windowTitle': 'Thu thập tiêu đề cửa sổ',
  'policyEditor.offHours': 'Thu thập hoạt động ngoài giờ',
  'policyEditor.usb': 'Theo dõi thiết bị USB',
  'policyEditor.file': 'Theo dõi sự kiện tệp',
  'policyEditor.screenshot': 'Chụp ảnh màn hình',
  'policyEditor.basis': 'Cơ sở pháp lý cho việc chụp ảnh màn hình',
  'policyEditor.basis.hint':
    'Bật chụp màn hình mà không ghi cơ sở pháp lý sẽ bị từ chối, theo quy định bảo vệ dữ liệu cá nhân. Viết bằng lời của chính công ty, cho đúng mục đích cụ thể.',
  'policyEditor.noListEditor':
    'Danh sách riêng tư, danh sách chặn và biểu thức che tiêu đề chưa sửa được ở đây. Một biểu thức sai một ký tự sẽ khiến cả công ty không giữ lại tiêu đề cửa sổ nào, nên việc sửa chúng cần một công cụ riêng.',
  'policyEditor.noKeylog':
    'Không có công tắc ghi phím, và sẽ không bao giờ có: hệ thống từ chối bật nó ở mọi tầng.',
  'policyEditor.save': 'Lưu vào bản này',
  'policyEditor.save.nothing': 'Chưa sửa gì. Chỉ những trường bạn thực sự sửa mới được gửi đi.',
  'policyEditor.save.blocked':
    'Chưa gửi được: một lần ghi đang chạy, hoặc có xung đột đang chờ bạn xác nhận ở phần trên.',
  'policyEditor.discard': 'Bỏ các thay đổi chưa lưu',
  'policyEditor.rollout.title': 'Mức độ phủ của bản đang áp dụng',
  'policyEditor.rollout.subject': 'mức độ phủ của chính sách',
  'policyEditor.rollout.noActive.title': 'Chưa có bản nào đang áp dụng',
  'policyEditor.rollout.noActive.body':
    'Chưa bản nào được kích hoạt LÀM MẶC ĐỊNH của công ty, nên không có phiên bản nào để các máy xác nhận. Một công ty có thể có vài bản đã kích hoạt mà vẫn chưa có mặc định: kích hoạt là một quyết định, đặt mặc định là một quyết định khác, và màn hình này chỉ gửi được quyết định thứ nhất.',
  'policyEditor.rollout.against':
    'Đếm theo phiên bản {version} — đúng phiên bản mà Agent xác nhận.',
  'policyEditor.rollout.caption': 'Số máy theo từng nhóm, so với phiên bản đang áp dụng',
  'policyEditor.rollout.column.bucket': 'Nhóm',
  'policyEditor.rollout.column.count': 'Số máy',
  'policyEditor.rollout.acknowledged': 'Đã xác nhận',
  'policyEditor.rollout.pending': 'Đã nhận, chưa xác nhận (và đang online)',
  'policyEditor.rollout.stale': 'Im lặng (stale)',
  'policyEditor.rollout.offline': 'Ngoại tuyến',
  'policyEditor.rollout.online': 'Trực tuyến',
  'policyEditor.rollout.total': 'Tổng số máy được kỳ vọng báo về',
  'policyEditor.rollout.overlap':
    'Bốn con số trên là bốn câu hỏi, không phải bốn phần của một cái bánh, nên chúng không nhất thiết cộng lại bằng tổng: một máy đã xác nhận rồi mới im lặng thì vừa nằm ở nhóm đã xác nhận vừa nằm ở nhóm im lặng. Riêng nhóm đã nhận nhưng chưa xác nhận đòi máy phải đang trực tuyến — một Agent đã im lặng không phải là một đợt triển khai đang chạy.',
  'policyEditor.rollout.asOf': 'Đọc lúc {at}.',
  'policyEditor.rollout.devices.subject': 'danh sách máy chưa xác nhận',
  'policyEditor.rollout.devices.empty.title': 'Mọi máy đều đã xác nhận phiên bản này',
  'policyEditor.rollout.devices.empty.body':
    'Không còn máy nào trong danh sách kỳ vọng báo về mà chưa xác nhận chuỗi phiên bản đang áp dụng.',
  'policyEditor.rollout.devices.caption': 'Các máy chưa xác nhận phiên bản đang áp dụng',
  'policyEditor.rollout.devices.host': 'Tên máy',
  'policyEditor.rollout.devices.liveness': 'Tín hiệu sống',
  'policyEditor.rollout.devices.served': 'Đã được phục vụ',
  'policyEditor.rollout.devices.acked': 'Đã xác nhận',
  'policyEditor.rollout.devices.never': 'chưa từng',
  'policyEditor.rollout.devices.showing': 'Đang hiện {shown} trong {total} máy chưa xác nhận.',
  'policyEditor.rollout.devices.truncated':
    'Danh sách dừng ở trang đầu tiên nên phần còn lại không hiện ở đây; các con số phía trên vẫn đếm đủ toàn bộ.',
  'policyEditor.rollout.devices.withheld':
    'Danh sách tên máy cần thêm quyền workforce.manage_devices. Hai mã quyền này độc lập với nhau: quản trị chính sách thu thập không đương nhiên là quản trị đội máy.',

  /* ── Task U16 — quy tắc cảnh báo và hành động tự động ─────────────────────── */
  'alertRules.title': 'Quy tắc cảnh báo',
  'alertRules.intro':
    'Danh mục quy tắc cảnh báo của công ty: điều kiện nào tạo ra cảnh báo, gom nhóm ra sao, và hành động tự động nào chạy theo. Mỗi lần sửa nội dung quy tắc sẽ tạo một phiên bản mới; bật hoặc tắt một quy tắc thì không.',
  'alertRules.noQuota':
    'Biểu mẫu này không dựng được quy tắc dạng định mức: không có trường nào là điểm năng suất, chỉ tiêu hay thứ hạng, và danh sách trường là danh sách đóng theo từng loại kích hoạt. Cảnh báo là để phát hiện sự cố, không phải để đặt hạn mức cho một con người.',
  'alertRules.subject': 'quy tắc cảnh báo',
  'alertRules.panel.title': 'Danh mục quy tắc',
  'alertRules.panel.lineage': 'Các phiên bản của quy tắc {key}',
  'alertRules.empty.title': 'Công ty này chưa có quy tắc cảnh báo nào',
  'alertRules.empty.body':
    'Chưa có quy tắc nào được tạo hoặc gieo sẵn cho công ty này. Tạo một quy tắc để bắt đầu.',
  'alertRules.back': 'Quay lại danh mục',
  'alertRules.activate.note':
    'Quy tắc cảnh báo không có nút Kích hoạt riêng: trạng thái là một trường của biểu mẫu bên dưới và được lưu cùng với nút Lưu.',
  'alertRules.row.edit': 'Sửa quy tắc này',
  'alertRules.row.select': 'Chọn phiên bản này',
  'alertRules.create.open': 'Tạo quy tắc mới',
  'alertRules.create.cancel': 'Huỷ tạo mới',
  'alertRules.create.title': 'Quy tắc mới',
  'alertRules.create.keyHint':
    'Mã quy tắc được sinh tự động từ tên. Nếu trùng với một quy tắc đang hoạt động, hệ thống từ chối thay vì tạo bản thứ hai cùng mã.',
  'alertRules.create.statusHint':
    'Quy tắc mới mặc định là bản nháp: một quy tắc bắt đầu bắn cảnh báo về những người có tên ngay khi vừa bấm nút không phải là mặc định nên tự chọn thay cho quản trị viên.',
  'alertRules.create.submit': 'Tạo quy tắc',
  'alertRules.create.blocked':
    'Cần tên, ít nhất một điều kiện và ít nhất một hành động thì mới có yêu cầu để gửi đi.',
  'alertRules.field.name': 'Tên quy tắc',
  'alertRules.field.trigger': 'Loại kích hoạt',
  'alertRules.field.severity': 'Mức nghiêm trọng',
  'alertRules.field.window': 'Cửa sổ quan sát (phút)',
  'alertRules.field.dedup': 'Cửa sổ gộp trùng (phút)',
  'alertRules.field.cooldown': 'Thời gian nghỉ giữa hai lần bắn (phút)',
  'alertRules.field.groupBy': 'Gom nhóm theo',
  'alertRules.field.status': 'Trạng thái',
  'alertRules.trigger.noFactSource':
    'Chưa có nguồn dữ liệu cho loại kích hoạt vừa chọn. Một quy tắc trên nó vẫn lưu được nhưng sẽ không bao giờ chạy, nên biểu mẫu không dựng điều kiện và không mời tạo mới.',
  'alertRules.condition.title': 'Điều kiện',
  'alertRules.condition.hint':
    'Tất cả các điều kiện phải cùng đúng. Tên trường là danh sách đóng do nguồn dữ liệu của loại kích hoạt công bố — không có ô nhập tự do cho tên trường.',
  'alertRules.condition.field': 'Trường',
  'alertRules.condition.operator': 'Toán tử',
  'alertRules.condition.value': 'Giá trị',
  'alertRules.condition.add': 'Thêm điều kiện',
  'alertRules.condition.remove': 'Bỏ điều kiện',
  'alertRules.condition.noFields': 'Loại kích hoạt này chưa có trường nào để so sánh.',
  'alertRules.actions.legend': 'Hành động tự động',
  'alertRules.actions.hint':
    'Chọn loại hành động từ danh sách đóng mà máy chủ lưu được. Mỗi loại tự hiện các tham số của riêng nó; loại nào không có tham số thì không hiện gì thêm.',
  'alertRules.action.notify_manager': 'Báo quản lý',
  'alertRules.action.notify_it': 'Báo IT',
  'alertRules.action.notify_hr': 'Báo nhân sự',
  'alertRules.action.create_incident': 'Mở sự cố',
  'alertRules.action.create_achau_task': 'Tạo công việc trong achau',
  'alertRules.action.note.notify_manager':
    'Hành động này có chạy. Người nhận lấy từ quản lý trực tiếp trong hồ sơ nhân sự, không phải một tham số của quy tắc.',
  'alertRules.action.note.notify_it':
    'Hành động này có chạy. Người nhận lấy từ nhóm CNTT của công ty, không phải một tham số của quy tắc.',
  'alertRules.action.note.notify_hr':
    'Hành động này chưa gửi gì cả: chưa có kênh thông báo tới bộ phận nhân sự. Quy tắc mang hành động này vẫn lưu được — hãy đọc là “chưa ai được báo”, không phải “nhân sự đã được báo”.',
  'alertRules.action.note.create_incident':
    'Sự cố đã được mở ở nơi khác: hệ thống mở nó cùng lúc ghi cảnh báo, theo ngưỡng mức độ của công ty. Hành động này không mở thêm lần thứ hai.',
  'alertRules.action.note.create_achau_task':
    'Hành động này có chạy. Nội dung công việc và mức ưu tiên suy ra từ cảnh báo và từ mức nghiêm trọng của chính quy tắc, không phải một tham số của quy tắc.',
  'alertRules.severity.informational': 'Thông tin',
  'alertRules.severity.low': 'Thấp',
  'alertRules.severity.medium': 'Trung bình',
  'alertRules.severity.high': 'Cao',
  'alertRules.severity.critical': 'Nghiêm trọng',
  'alertRules.form.title': 'Sửa {name} (phiên bản {version})',
  'alertRules.form.trigger': 'Loại kích hoạt {trigger} · trạng thái {status}',
  'alertRules.form.retired':
    'Phiên bản này đã bị thay thế nên là lịch sử: máy chủ từ chối sửa nó. Chọn phiên bản đang có hiệu lực để sửa.',
  'alertRules.form.noTriggerEdit':
    'Không sửa được loại kích hoạt của một quy tắc đã tạo: loại kích hoạt quyết định những trường nào tồn tại, nên đổi nó sẽ biến mọi điều kiện thành trường không có thật. Một loại kích hoạt khác là một quy tắc khác — hãy tạo mới.',
  'alertRules.status.hint':
    'Chỉ đổi mỗi trạng thái thì máy chủ dời trạng thái trên chính hàng đó và không tạo phiên bản mới. Đổi trạng thái cùng với một trường khác thì tạo phiên bản mới mang trạng thái đó.',
  'alertRules.save': 'Lưu thay đổi',
  'alertRules.save.nothing': 'Chưa có thay đổi nào để lưu.',
  'alertRules.save.blocked':
    'Có một xung đột phiên bản chưa được xác nhận trên phiên bản này, hoặc một yêu cầu đang chạy.',
  'alertRules.discard': 'Bỏ thay đổi',
  'alertRules.noActive':
    'Quy tắc này chưa có phiên bản nào đang áp dụng — nó mới chỉ là bản nháp, hoặc đang tắt — nên phần so sánh bên dưới không có gì để đối chiếu. Những gì bạn sửa ở đây sẽ được so với chính phiên bản đang chọn, không phải với một phiên bản đang chạy.',

  /* ── Task U17 — công thức chấm điểm năng suất ──────────────────────── */
  'nav.productivityFormulas': 'Công thức năng suất',
  'formulas.title': 'Công thức chấm điểm năng suất',
  'formulas.intro':
    'Trọng số của từng thành phần quyết định điểm năng suất được tính ra sao. Mỗi lần sửa là một phiên bản mới: một phiên bản đã có điểm tham chiếu thì bị đóng băng vĩnh viễn, để một điểm chấm tháng Ba vẫn giải thích được sau khi công thức đã đổi.',
  'formulas.noQuota':
    'Điểm sinh ra từ công thức này luôn đi kèm lời giải thích từng thành phần và một độ tin cậy; thiếu dữ liệu làm giảm độ tin cậy chứ không biến điểm thành 0. Nó là bối cảnh cho một cuộc trao đổi mang tính hỗ trợ giữa quản lý và nhân viên — không bao giờ là một chỉ tiêu, một định mức hay một bảng xếp hạng. Chín thành phần là một danh sách đóng: ở đây không có ô nhập tự do nào để đặt tên cho một định mức.',
  'formulas.noConcurrencyCheck':
    'Màn hình này không phát hiện được hai người cùng sửa một công thức: lần lưu sau sẽ thắng và không ai được cảnh báo. Hãy thống nhất với nhau trước khi sửa.',
  'formulas.subject': 'công thức năng suất',
  'formulas.panel.title': 'Danh mục công thức',
  'formulas.panel.lineage': 'Các phiên bản của công thức {key}',
  'formulas.empty.title': 'Công ty này chưa có công thức năng suất nào',
  'formulas.empty.body':
    'Chưa có công thức nào, nên hiện chưa ai được chấm điểm. Công thức mẫu là: kết quả công việc 25, thời gian hiệu quả 20, đúng hạn 15, tập trung 15, chuyên cần 15, chất lượng 10 — tổng trọng số dương 100,00 — và trừ điểm rủi ro đã xác minh 10 nằm ngoài tổng đó. Nút bên dưới dựng đúng công thức đó ở dạng bản nháp.',
  'formulas.create.default': 'Tạo công thức mặc định',
  'formulas.close': 'Đóng biểu mẫu',
  'formulas.back': 'Quay lại danh mục',
  'formulas.row.edit': 'Sửa bản nháp này',
  'formulas.row.activate': 'Kích hoạt phiên bản này',
  'formulas.row.branch': 'Tạo phiên bản mới từ bản này',
  'formulas.immutable.note':
    'Chỉ một bản nháp chưa chấm điểm cho ai mới sửa tại chỗ được. Với mọi phiên bản khác, cách sửa là tạo phiên bản mới từ nó.',
  'formulas.retire.note':
    'Màn hình này cố ý không có nút ngừng một phiên bản: ngừng phiên bản duy nhất đang áp dụng sẽ khiến không ai được chấm điểm nữa. Cách đổi công thức đang áp dụng là tạo phiên bản mới rồi kích hoạt nó.',
  'formulas.composer.patch': 'Sửa bản nháp {name}',
  'formulas.composer.from': 'Phiên bản mới dựa trên {name} (v{version})',
  'formulas.composer.first': 'Công thức đầu tiên của công ty',
  'formulas.field.name': 'Tên công thức',
  'formulas.field.description': 'Mô tả',
  'formulas.field.weight': 'Trọng số (điểm phần trăm)',
  'formulas.components.legend': 'Thành phần và trọng số',
  'formulas.components.hint':
    'Bỏ chọn một thành phần là loại nó khỏi công thức. Một thành phần cộng điểm không được để trọng số 0 — hãy bỏ chọn nó, vì một thành phần được liệt kê mà không mang trọng số thì không ai giải thích được nó có mặt để làm gì.',
  'formulas.direction.positive': 'cộng điểm, tính vào tổng 100',
  'formulas.direction.negative': 'trừ điểm, nằm ngoài tổng 100',
  'formulas.weights.total': 'Tổng trọng số dương: {total} / {required}',
  'formulas.weights.empty': 'Một công thức phải có ít nhất một thành phần.',
  'formulas.weights.badWeight':
    'Trọng số của {key} không hợp lệ ({problem}): phải là một số hữu hạn từ 0 đến 100 và có tối đa 2 chữ số thập phân.',
  'formulas.weights.zeroPositive':
    '{key} là thành phần cộng điểm nên không được mang trọng số 0 — hãy bỏ chọn nó thay vì để 0.',
  'formulas.weights.sum':
    'Tổng trọng số dương đang là {total}, phải đúng {required}. Đây chỉ là lần kiểm tra sớm; hệ thống cũng từ chối lưu một tổng sai.',
  'formulas.weights.total.unknown':
    'Tổng trọng số dương: chưa tính được — có trọng số chưa hợp lệ nên không có phép cộng nào để nói, đích vẫn là {required}.',
  'formulas.weights.problem.not_finite': 'không phải một số',
  'formulas.weights.problem.negative': 'số âm',
  'formulas.weights.problem.above_100': 'lớn hơn 100',
  'formulas.weights.problem.too_many_decimals': 'quá 2 chữ số thập phân',
  'formulas.dropped':
    'Phiên bản gốc mang thành phần {keys} mà danh sách chín thành phần hiện tại không còn có (máy chủ đánh dấu known: false). Enum trên wire là một danh sách đóng nên không gửi lại được: phiên bản mới sẽ không có thành phần đó, và trọng số của nó rời khỏi tổng cùng với nó. Hãy chia lại phần trọng số ấy cho các thành phần còn lại trước khi lưu — nếu không, tổng sẽ thiếu và lời từ chối bên dưới sẽ nói về những trọng số bạn chưa hề chạm tới.',
  'formulas.activateNow': 'Kích hoạt ngay khi tạo',
  'formulas.activateNow.hint':
    'Gửi activate: true trong cùng một request, nên việc tạo phiên bản và việc chuyển hiệu lực nằm trong một transaction — không có khoảnh khắc nào công thức đã tồn tại mà chưa áp dụng cho ai. Để trống thì phiên bản mới là bản nháp và bạn kích hoạt sau bằng nút trên dòng của nó.',
  'formulas.submit.create': 'Tạo phiên bản mới',
  'formulas.submit.patch': 'Lưu vào bản nháp',
  'formulas.submit.blocked.weights':
    'Chưa gửi được: trọng số chưa hợp lệ — xem danh sách phía trên.',
  'formulas.submit.blocked.name': 'Chưa gửi được: công thức cần một tên.',
  'formulas.submit.blocked.pending': 'Chưa gửi được: đang có một yêu cầu chạy dở.',
  'formulas.discard': 'Bỏ thay đổi',
  'formulas.help.title': 'Mỗi thành phần đo cái gì',
  'formulas.help.intro':
    'Một trọng số là một quyết định về con người, nên trước khi nâng một con số hãy đọc nó là tỉ lệ của cái gì. Những câu dưới đây mô tả đúng phép đo đang chạy, kể cả những chỗ phép đo nói thẳng là không đo được.',
  'formulas.component.task_result': 'Kết quả công việc',
  'formulas.component.on_time': 'Đúng hạn',
  'formulas.component.quality': 'Chất lượng',
  'formulas.component.productive_time': 'Thời gian hiệu quả',
  'formulas.component.focus': 'Tập trung',
  'formulas.component.attendance': 'Chuyên cần',
  'formulas.component.workload_balance': 'Cân bằng khối lượng',
  'formulas.component.verified_risk_penalty': 'Trừ điểm rủi ro đã xác minh',
  'formulas.component.approved_manual_adjustment': 'Điều chỉnh thủ công đã duyệt',
  'formulas.component.help.task_result':
    'Mức hoàn thành trung bình của các công việc có hạn chót hoặc thời điểm hoàn thành rơi vào kỳ. Một công việc đã đóng mà không ghi phần trăm được tính là hoàn thành. Không có công việc nào trong kỳ thì thành phần này không đo được — đó là thiếu công việc chứ không phải thiếu việc — và điểm không vì thế mà thành 0.',
  'formulas.component.help.on_time':
    'Số công việc hoàn thành đúng hoặc trước hạn, trên tổng số công việc có hạn chót đã hoàn thành trong kỳ. Không có công việc nào mang hạn chót thì không có mẫu số, và điều đó không nói rằng có hạn chót nào bị trễ.',
  'formulas.component.help.quality':
    'Chất lượng hiện KHÔNG đo được: hệ thống chưa có đánh giá, chưa đếm lỗi và chưa có xếp hạng nào. Một công thức có thành phần này sẽ làm giảm độ tin cậy của mọi điểm mà không làm điểm dịch chuyển. Chọn nó chỉ khi bạn muốn ghi nhận rằng phần này đang thiếu tín hiệu.',
  'formulas.component.help.productive_time':
    'Số giây được xếp là hiệu quả, trên tổng số giây thuộc các nhóm hoạt động có đưa ra tuyên bố về năng suất. Thời gian riêng tư bị loại khỏi cả tử số lẫn mẫu số, nên không ai bị trừ điểm vì dùng quyền riêng tư; các nhóm không tuyên bố gì — họp, đào tạo, phát triển — cũng không nằm dưới mẫu.',
  'formulas.component.help.focus':
    'Số giây có thao tác trên số giây trực tuyến quan sát được. Thời gian nghỉ đã được giải trình và duyệt được cộng trở lại vào tử số, tối đa bằng đúng lượng nghỉ đo được, nên một lời duyệt rộng tay không tạo ra thời gian chưa từng trực tuyến. Không có giây trực tuyến nào thì không có mẫu số, và điều đó không nói rằng người đó ngồi không.',
  'formulas.component.help.attendance':
    'Số phút được ghi nhận trên số phút theo lịch ca, tính trên những ngày có kết luận chấm công. Thời gian làm ngoài hệ thống đã được duyệt vẫn được tính là ghi nhận; một ngày mà dữ liệu không đáng tin thì không nằm ở cả hai vế của phân số.',
  'formulas.component.help.workload_balance':
    'Thời gian đã ghi nhận so với thời gian theo lịch ca, phạt cả hai chiều: 60 giờ trên một lịch 40 giờ cũng là một phát hiện như 20 giờ. Giờ nhập tay được cộng vào và được báo cáo riêng. Không có ca, hoặc công ty không dùng hẹn giờ, thì không đo được — đó là sự thật về cách công ty vận hành chứ không phải một phép đo về người.',
  'formulas.component.help.verified_risk_penalty':
    'Điểm phạt từ những sự cố đã xử lý xong và mang dấu đã xác minh; không có gì khác đóng góp vào đây. Đây là thành phần trừ điểm: trọng số của nó nằm ngoài tổng 100 và được trừ thêm lên trên. Trọng số 0 là hợp lệ, và đó là cách một công ty nói rằng họ không phạt rủi ro.',
  'formulas.component.help.approved_manual_adjustment':
    'Số điểm điều chỉnh thủ công đã được duyệt, đưa vào cùng một danh sách thành phần để một lần chấm giải thích được đủ mọi thứ đã cộng vào điểm, kể cả phần do con người quyết định. Là thành phần cộng điểm và tính vào tổng 100.',

  /* Task U18 — công thức tính lương (gross-to-net); roadmap E0-4 — lịch sử, xác nhận, bản nháp. */
  'payrollFormulas.title': 'Công thức tính lương',
  'payrollFormulas.intro':
    'Toàn bộ tham số mà máy tính lương đọc để ra lương thực lãnh: giờ công chuẩn, hệ số tăng ca, các tỷ lệ bảo hiểm, trần đóng và biểu thuế TNCN. Đây là dữ liệu của công ty, không phải hằng số trong mã nguồn.',
  'payrollFormulas.subject': 'công thức tính lương',
  'payrollFormulas.history':
    'Bảng dưới là toàn bộ lịch sử, mới nhất trước: mọi phiên bản của công ty, kể cả bản nháp đang chờ xác nhận, bản đã ngừng và bộ tham số Việt Nam 2026 được cấp sẵn ở trạng thái chưa kích hoạt.',
  'payrollFormulas.immutable':
    'Không phiên bản nào sửa được số: không có thao tác sửa và không có thao tác xoá. Muốn đổi số thì soạn một bản nháp mới; kỳ lương đã tính vẫn giữ nguyên phiên bản của nó. Điều duy nhất đổi được trên một phiên bản là trạng thái: được xác nhận, được kích hoạt, hoặc bị một phiên bản khác thay.',
  'payrollFormulas.twoPerson':
    'Người soạn không tự kích hoạt được bản mình soạn. Màn hình này chỉ lưu bản nháp; một người khác có quyền quản lý tham số lương phải đối chiếu từng con số rồi bấm "Xác nhận & kích hoạt" kèm một câu xác nhận — câu đó được lưu cùng tên người xác nhận và thời điểm.',
  'payrollFormulas.concurrency':
    'Dòng "không kiểm tra xung đột" dưới mỗi phiên bản chỉ nói về việc lưu bản nháp — và ở đây không lần lưu nào thắng lần nào: hai người cùng lưu thì có hai bản nháp. Xác nhận thì có kiểm tra: máy chủ xử lý lần lượt, và lần xác nhận thứ hai cho cùng một phiên bản bị từ chối.',
  'payrollFormulas.inForce.note':
    '"Đang áp dụng" là phiên bản máy chủ trả về cho hôm nay. Nhiều phiên bản có thể cùng được kích hoạt, mỗi bản cho một khoảng ngày; một đợt tính lương dùng bản được kích hoạt có ngày hiệu lực muộn nhất không sau ngày đầu kỳ.',
  'payrollFormulas.inForce.none':
    'Chưa có phiên bản nào được kích hoạt, nên chưa tạo được đợt tính lương. Một bản nháp phải được người thứ hai xác nhận và kích hoạt trước.',
  'payrollFormulas.inForce.subject': 'phiên bản đang áp dụng',
  'payrollFormulas.unconfirmed.title': 'Phiên bản {version} đang áp dụng nhưng chưa được xác nhận',
  'payrollFormulas.unconfirmed.badge': 'Chưa xác nhận',
  'payrollFormulas.unconfirmed.noWarning': 'Chưa có người thứ hai xác nhận các con số này.',
  'payrollFormulas.unconfirmed.howTo':
    'Một người khác người soạn đối chiếu từng con số với văn bản pháp quy, rồi bấm "Xác nhận bộ số" trên dòng của phiên bản này ở bảng dưới.',
  'payrollFormulas.confirmed.badge': 'Đã xác nhận',
  'payrollFormulas.confirmed.by': 'bởi {who}, lúc {when}:',
  'payrollFormulas.confirmed.warningKept': 'Cảnh báo lúc cấp (giữ lại làm lịch sử): {note}',
  'payrollFormulas.person.you': 'bạn',
  'payrollFormulas.person.id': 'nhân viên #{id}',
  'payrollFormulas.person.unknown': 'một người không còn trong hệ thống',
  'payrollFormulas.when.unknown': 'không rõ thời điểm',
  'payrollFormulas.panel.title': 'Các phiên bản tham số',
  'payrollFormulas.empty.title': 'Công ty này chưa có phiên bản công thức nào',
  'payrollFormulas.empty.body':
    'Chưa có bộ tham số nào, và màn hình này không tự nghĩ ra số: mọi con số phải do người nhập hoặc do lệnh cấp bộ tham số Việt Nam 2026 ghi vào. Nhập đủ tám tham số bắt buộc để lưu bản nháp đầu tiên; một người khác sẽ xác nhận và kích hoạt nó.',
  'payrollFormulas.row.label': 'Phiên bản {version} · hiệu lực từ {from}',
  'payrollFormulas.row.notInForce': 'Không phải bản đang áp dụng',
  'payrollFormulas.row.inForceUnknown': 'Chưa biết có phải bản đang áp dụng không',
  'payrollFormulas.row.warning': 'Cảnh báo: {note}',
  'payrollFormulas.row.composeFrom': 'Soạn bản nháp từ bản này',
  'payrollFormulas.lifecycle.active': 'Đang kích hoạt',
  'payrollFormulas.lifecycle.inactive': 'Không kích hoạt',
  'payrollFormulas.author.seed': 'Do lệnh cấp dữ liệu ghi — không có người soạn',
  'payrollFormulas.author.you': 'Bạn soạn phiên bản này',
  'payrollFormulas.author.other': 'Người soạn: nhân viên #{id}',
  'payrollFormulas.close': 'Đóng',
  'payrollFormulas.create.first': 'Soạn bản nháp đầu tiên',
  'payrollFormulas.create.draft': 'Soạn bản nháp mới',
  'payrollFormulas.params.title': 'Tham số của phiên bản {version}',
  'payrollFormulas.params.caption': 'Danh sách tham số của phiên bản {version}',
  'payrollFormulas.params.column.name': 'Tham số',
  'payrollFormulas.params.column.value': 'Giá trị',
  'payrollFormulas.effectiveFrom': 'Hiệu lực từ {from}.',
  'payrollFormulas.usage.unknown':
    'Chưa biết kỳ lương nào đang dùng phiên bản này — tài khoản này không đọc được danh sách kỳ lương.',
  'payrollFormulas.usage.none': 'Chưa kỳ lương nào tham chiếu phiên bản này.',
  'payrollFormulas.usage.count': 'Đang được {count} kỳ lương tham chiếu.',
  'payrollFormulas.param.gio_chuan': 'Giờ công chuẩn/tháng',
  'payrollFormulas.param.ot_coefficient': 'Hệ số tăng ca',
  'payrollFormulas.param.bhxh_nv_pct': 'BHXH — nhân viên',
  'payrollFormulas.param.bhyt_nv_pct': 'BHYT — nhân viên',
  'payrollFormulas.param.kpcd_nv_pct': 'KPCĐ — nhân viên',
  'payrollFormulas.param.bhxh_cty_pct': 'BHXH — công ty',
  'payrollFormulas.param.bhyt_cty_pct': 'BHYT — công ty',
  'payrollFormulas.param.kpcd_cty_pct': 'KPCĐ — công ty',
  'payrollFormulas.param.bhtn_nv_pct': 'BHTN — nhân viên',
  'payrollFormulas.param.bhtn_cty_pct': 'BHTN — công ty',
  'payrollFormulas.param.bhxh_bhyt_cap_vnd': 'Trần đóng BHXH/BHYT',
  'payrollFormulas.param.bhtn_cap_vnd': 'Trần đóng BHTN',
  'payrollFormulas.param.pit_personal_deduction_vnd': 'Giảm trừ bản thân',
  'payrollFormulas.param.pit_dependent_deduction_vnd': 'Giảm trừ người phụ thuộc',
  'payrollFormulas.param.absent': 'phiên bản này không mang tham số đó',
  'payrollFormulas.value.fraction': '{raw} ({percent}%)',
  'payrollFormulas.value.vnd': '{value} ₫/tháng',
  'payrollFormulas.value.hours': '{value} giờ',
  'payrollFormulas.grounds.title': 'Căn cứ khấu trừ của công ty',
  'payrollFormulas.grounds.hint':
    'Đây là danh sách mã căn cứ hợp lệ khi nhập khoản Phạt cho đợt lương dùng phiên bản này. Danh sách và trần khấu trừ là cấu hình của công ty, không phải hằng số trong mã nguồn.',
  'payrollFormulas.grounds.caption': 'Căn cứ khấu trừ của phiên bản {version}',
  'payrollFormulas.grounds.column.code': 'Mã',
  'payrollFormulas.grounds.column.name': 'Tên căn cứ',
  'payrollFormulas.grounds.column.appliesTo': 'Trừ vào',
  'payrollFormulas.grounds.column.capped': 'Chịu trần',
  'payrollFormulas.grounds.appliesTo.gross_before_tax': 'Lương gộp, trước thuế',
  'payrollFormulas.grounds.appliesTo.net_after_tax': 'Lương ròng, sau thuế',
  'payrollFormulas.grounds.capped.yes': 'Có',
  'payrollFormulas.grounds.capped.no': 'Không',
  'payrollFormulas.grounds.absent':
    'Phiên bản này không cấu hình căn cứ khấu trừ nào — mọi khoản Phạt trên đợt lương dùng phiên bản này đều bị từ chối.',
  'payrollFormulas.cap.value':
    'Trần khấu trừ deduction_cap_pct: {raw} ({percent}% tiền lương còn lại sau bảo hiểm bắt buộc và thuế TNCN).',
  'payrollFormulas.cap.absent':
    'Trần khấu trừ deduction_cap_pct: phiên bản này không đặt trần — một căn cứ có đánh dấu chịu trần sẽ bị từ chối.',
  'payrollFormulas.brackets.title': 'Biểu thuế TNCN lũy tiến',
  'payrollFormulas.brackets.absent': 'Phiên bản này không mang biểu thuế.',
  'payrollFormulas.brackets.open': 'Phần vượt trên bậc cuối: {percent}%',
  'payrollFormulas.brackets.upTo': 'Đến {bound} ₫/tháng: {percent}%',
  'payrollFormulas.brackets.hint':
    'Bậc xếp từ thấp lên cao. Để trống mức trần của bậc cuối để đó là bậc mở. Thuế suất nhập theo phân số: 0.05 là 5%.',
  'payrollFormulas.brackets.bound': 'Bậc {index} — mức trần (VNĐ/tháng)',
  'payrollFormulas.brackets.rate': 'Bậc {index} — thuế suất',
  'payrollFormulas.brackets.remove': 'Bỏ bậc này',
  'payrollFormulas.brackets.add': 'Thêm một bậc',
  'payrollFormulas.brackets.invalid': 'Có bậc chưa nhập đủ hoặc không phải là số.',
  'payrollFormulas.composer.first': 'Bản nháp đầu tiên của công ty',
  'payrollFormulas.composer.from':
    'Bản nháp mới, bắt đầu từ số của phiên bản {version} — chỉ lưu được khi khác phiên bản này',
  'payrollFormulas.composer.draft':
    'Lưu xong, đây là một bản nháp: chưa được kích hoạt, chưa được xác nhận, và không đổi gì trong bảng lương cho tới khi một người khác bấm "Xác nhận & kích hoạt".',
  'payrollFormulas.composer.blank':
    'Các ô để trống vì chưa có phiên bản nào để lấy số. Màn hình này không điền sẵn một con số pháp quy nào.',
  'payrollFormulas.composer.noteLost.title': 'Bản nháp sẽ không mang cảnh báo của phiên bản gốc',
  'payrollFormulas.composer.noteLost.body':
    'Phiên bản gốc mang cảnh báo chưa xác nhận do lệnh cấp dữ liệu ghi. Bản nháp mới không mang cảnh báo đó, nên người xác nhận bản nháp sẽ không đọc thấy nó. Nếu bạn chỉ muốn xác nhận chính các con số ấy, hãy dùng "Xác nhận & kích hoạt" trên dòng của phiên bản gốc.',
  'payrollFormulas.composer.effectiveFrom.label': 'Hiệu lực từ ngày',
  'payrollFormulas.composer.effectiveFrom.hint':
    'Ngày đầu tiên các con số này được áp dụng. Bắt buộc — màn hình không tự chọn ngày.',
  'payrollFormulas.composer.fields': 'Tham số',
  'payrollFormulas.composer.optional': '(không bắt buộc)',
  'payrollFormulas.composer.notANumber': 'Giá trị này không phải là số.',
  'payrollFormulas.composer.changes.title': 'Bản nháp này thay đổi gì so với phiên bản {version}',
  'payrollFormulas.composer.changes.none':
    'Chưa thay đổi tham số nào. Mọi tham số của phiên bản {version} — kể cả những tham số không có ô nhập ở trên, như căn cứ khấu trừ và trần khấu trừ — được gửi lại nguyên vẹn.',
  'payrollFormulas.composer.changes.entry': '{key}: {from} → {to}',
  'payrollFormulas.composer.changes.absent': '(phiên bản gốc không có tham số này)',
  'payrollFormulas.submit': 'Lưu bản nháp để người khác xác nhận',
  'payrollFormulas.blocked.invalid': 'Còn ô chưa phải là số. Sửa xong mới lưu được.',
  'payrollFormulas.blocked.missing': 'Còn thiếu tham số bắt buộc: {keys}.',
  'payrollFormulas.blocked.date': 'Chưa chọn ngày hiệu lực.',
  'payrollFormulas.blocked.unchanged':
    'Không có gì khác so với phiên bản gốc, nên không có gì để lưu. Muốn xác nhận các con số này thì dùng "Xác nhận & kích hoạt" trên chính phiên bản đó — một bản sao giống hệt chỉ làm mất cảnh báo.',
  'payrollFormulas.blocked.pending': 'Đang gửi yêu cầu…',
  'payrollFormulas.discard': 'Bỏ các thay đổi',
  'payrollFormulas.confirm.openInactive': 'Xác nhận & kích hoạt',
  'payrollFormulas.confirm.openActive': 'Xác nhận bộ số',
  'payrollFormulas.confirm.self': 'Bạn soạn phiên bản này, nên một người khác phải xác nhận.',
  'payrollFormulas.confirm.already': 'Phiên bản này đã được xác nhận.',
  'payrollFormulas.confirm.titleInactive':
    'Xác nhận và kích hoạt phiên bản {version} (hiệu lực từ {from})',
  'payrollFormulas.confirm.titleActive':
    'Xác nhận bộ số của phiên bản {version} (hiệu lực từ {from})',
  'payrollFormulas.confirm.effectInactive':
    'Khi bạn xác nhận, phiên bản này được kích hoạt, và mọi phiên bản đang kích hoạt có số nhỏ hơn mà ngày hiệu lực từ {from} trở đi sẽ ngừng. Máy chủ từ chối nếu một phiên bản số lớn hơn đang kích hoạt đã có hiệu lực từ ngày này trở về trước. Đợt tính lương đã tạo — kể cả đợt còn nháp — vẫn giữ phiên bản cũ.',
  'payrollFormulas.confirm.effectActive':
    'Phiên bản này đang được kích hoạt. Xác nhận chỉ ghi lại câu của bạn cùng tên và thời điểm; không phiên bản nào bị ngừng.',
  'payrollFormulas.confirm.warningTitle': 'Cảnh báo đi kèm phiên bản này',
  'payrollFormulas.confirm.label': 'Bạn đã đối chiếu những gì, với văn bản nào?',
  'payrollFormulas.confirm.rule':
    'Từ {min} đến {max} ký tự, không tính khoảng trắng ở đầu và cuối. Câu này được lưu nguyên văn cùng tên bạn và thời điểm xác nhận.',
  'payrollFormulas.confirm.length':
    'Câu xác nhận cần từ {min} đến {max} ký tự, không tính khoảng trắng ở đầu và cuối.',
  'payrollFormulas.confirm.submitInactive': 'Xác nhận & kích hoạt phiên bản {version}',
  'payrollFormulas.confirm.submitActive': 'Xác nhận phiên bản {version}',
  'payrollFormulas.confirm.cancel': 'Huỷ',
  'payrollFormulas.confirm.refused': 'Máy chủ từ chối ({code}). Lý do máy chủ đưa ra: {reason}',
  'payrollFormulas.confirm.refused.noReason':
    'không kèm lý do — xem thông báo ở phần các phiên bản.',
} as const;

export type MessageKey = keyof typeof vi;

/**
 * English. `Partial` on purpose — see the module comment. A missing key falls back
 * to Vietnamese at run time rather than rendering the key or an empty string,
 * because a raw key on screen is worse for the reader than the wrong language.
 */
export const en: Partial<Record<MessageKey, string>> = {
  'app.subtitle': 'Sign in with your achau account',

  'common.retry': 'Retry',
  'common.retrying': 'Retrying…',
  'common.signOut': 'Sign out',
  'common.errorCode': 'Error code',
  'common.correlationId': 'Reference id',
  'common.contactAdmin': 'Contact your company administrator.',
  'common.never': 'never',
  'common.unknownError':
    'Something went wrong. Try again, or quote the reference id to your support team.',
  'common.networkError': 'Could not reach the server.',
  'common.retryAfter': 'Try again in {seconds} seconds.',
  'common.moreDetail': 'Detail',

  'renderError.title': 'This screen hit an error and stopped',
  'renderError.body':
    'Your data is unaffected. Try reloading; if it keeps failing, give the reference id to your administrator.',

  'department.label': 'Department',
  'department.all': 'All departments',
  'department.unreadable': 'The department list could not be read. Reload the page to try again.',

  'locale.label': 'Language',
  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  'login.title': 'Sign in',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.failed': 'Incorrect username or password.',

  'shell.mainNav': 'Main navigation',
  'shell.skipToContent': 'Skip navigation, go to main content',
  'shell.mainLandmark': 'Main content',
  'shell.loadingSession': 'Working out what you may see',
  'shell.sessionFailed': 'Could not read your session',
  'shell.signedInAs': 'Signed in as {name}',
  'shell.role.ceo': 'Executive',
  'shell.role.manager': 'Manager',
  'shell.role.employee': 'Employee',
  'shell.timerSlot': 'Timer',
  'shell.timerSlotEmpty': 'No timer is wired into this shell',
  'shell.company': 'Active company',
  'shell.companyOnly': 'This account belongs to one company',
  'shell.companyCount': 'Member of {count} companies',
  'shell.statesReference': 'UI state reference',
  'shell.topbarMore': 'Settings and account',

  'nav.empty.title': 'No menu items available',
  'nav.empty.body':
    'This account holds no permission for any Workforce Pulse screen. Permissions are granted on the Policies screen and are audited.',

  'nav.group.daily': 'Daily',
  'nav.group.people': 'People',
  'nav.group.recruiting': 'Recruiting',
  'nav.group.payroll': 'Payroll',
  'nav.group.operations': 'Operations',
  'nav.group.analytics': 'Analytics',
  'nav.group.admin': 'Administration',
  'nav.group.standingHint': 'This group is open because you are inside it',

  'nav.overview': 'Overview',
  'nav.liveActivity': 'Live Activity',
  'nav.attendance': 'Attendance',
  'nav.timeTracking': 'Time Tracking',
  'nav.productivity': 'Productivity',
  'nav.applications': 'Applications',
  'nav.websites': 'Websites',
  'nav.devices': 'Devices',
  'nav.alerts': 'Alerts',
  'nav.alertRules': 'Alert rules',
  'nav.incidents': 'Incidents',
  'nav.dataRisk': 'Data Risk',
  'nav.reports': 'Reports',
  'nav.aiInsights': 'AI Insights',
  'nav.policies': 'Policies',
  'nav.policyCollection': 'Collection policy',
  'nav.companySettings': 'Company settings',
  'nav.auditLog': 'Audit Log',
  'nav.myActivity': 'My Activity',

  'severity.critical': 'Critical',
  'severity.high': 'High',
  'severity.medium': 'Medium',
  'severity.low': 'Low',
  'severity.info': 'Info',

  'state.skeleton.label': 'Loading',
  'state.empty.title': 'Nothing to show yet',

  'state.permissionDenied.title': 'Access refused',
  'state.permissionDenied.WORKFORCE_PERMISSION_DENIED':
    'Your account does not hold the permission this screen needs.',
  'state.permissionDenied.WORKFORCE_SCOPE_DENIED':
    'You may see this kind of data, but the record requested is outside your scope.',
  'state.permissionDenied.WORKFORCE_SENSITIVE_DATA_DENIED':
    'This screen carries sensitive data and needs its own permission.',
  'state.permissionDenied.WORKFORCE_ACCESS_REASON_REQUIRED':
    'Company policy requires a stated reason to read this data.',

  'state.companyDisabled.company.title': 'Company not activated',
  'state.companyDisabled.company.body':
    'Company {company} has not been enabled for Workforce Pulse. There is nothing wrong with your account.',
  'state.companyDisabled.service.title': 'Service switched off',
  'state.companyDisabled.service.body':
    'Workforce Pulse is switched off for this whole instance. That is a configuration state, not a fault with your account.',
  'state.companyDisabled.service.contact': 'Contact the platform operator.',

  'state.dataDelayed.title': 'Data is late',
  'state.dataDelayed.agent': 'Data from the desktop Agent is arriving late.',
  'state.dataDelayed.achau': 'Task data from achau could not be synchronised.',
  'state.dataDelayed.processing': 'The figures are calculated, but events are still landing.',
  'state.dataDelayed.since': 'Last updated: {when}.',
  'state.dataDelayed.sinceNever': 'No successful update yet.',

  'state.partialData.title': 'Data is incomplete',
  'state.partialData.body': 'The figure shown excludes {excludes}.',
  'state.partialData.score': 'Data completeness: {percent}%.',
  'state.partialData.devices': 'Devices contributing data: {count}.',

  'state.agentOffline.stale.title': 'Agent has gone quiet',
  'state.agentOffline.offline.title': 'Agent offline',
  'state.agentOffline.stale.body':
    'Device {device} has missed several heartbeats. A leading indicator, not yet an incident.',
  'state.agentOffline.offline.body':
    'Device {device} has stopped reporting. This period does NOT mean the employee did no work.',
  'state.agentOffline.lastHeartbeat': 'Last heartbeat: {when}.',
  'state.agentOffline.neverHeard': 'This device has never been heard from.',
  'state.agentOffline.device': 'this device',

  'state.lastSync.title': 'Last successful sync',

  'companyBlocked.retryHint': 'If an administrator has just enabled it, press Retry.',

  'selectCompany.title': 'Choose a company',
  'selectCompany.body':
    'This account belongs to {count} companies. One session reads exactly one company.',
  'selectCompany.unavailable':
    'You cannot switch companies from this screen yet. Contact an administrator if you need to work in a different company.',
  'selectCompany.enabled': 'Active',
  'selectCompany.disabled': 'Not activated',

  'placeholder.title': '{screen}',
  'placeholder.body':
    'Navigation, permissions and the shared states are in place. This screen’s content is built by task {task}.',

  'states.title': 'UI state reference',
  'states.intro':
    'A reference page for interface reviewers: the ten required states, rendered from fixed sample values. This page reads no company data.',
  'states.driver': 'Driven by',
  'states.name.skeleton': '1. Skeleton loading',
  'states.name.empty': '2. Empty',
  'states.name.permissionDenied': '3. Permission denied',
  'states.name.companyDisabled': '4. Company not activated',
  'states.name.dataDelayed': '5. Data delayed',
  'states.name.partialData': '6. Partial data',
  'states.name.agentOffline': '7. Agent offline',
  'states.name.retry': '8. Retry action',
  'states.name.lastSync': '9. Last successful sync',
  'states.name.renderError': '10. Render error',
  'states.sample.throw': 'Trigger a render error',
  'states.sample.throwHint':
    'Press to make a child throw during render. Only this cell turns into the error note — the rail, the top bar and the rest of the page stay usable, exactly as they do when a real screen breaks.',
  'states.sample.throwOk': 'The child is rendering normally.',
  'states.sample.emptyTitle': 'No devices enrolled yet',
  'states.sample.emptyBody':
    'Issue a registration code on the Devices screen, then install the Agent on the workstation.',
  'states.sample.company': 'Sample Company',
  'states.sample.excludes': 'roughly 09:00-11:20, while the second device reported nothing',

  /* ── the reference page's primitive rack (Wave 1, group A) ─────────────── */
  'states.nine.title': 'The ten states',
  'states.prim.title': 'Primitives and tokens',
  'states.prim.intro':
    'Every sample below is the real class and the real component, never a redrawn one. A reference page that hand-rolled its own button would still look right on the day the real button broke.',
  'states.prim.buttons': 'Buttons',
  'states.prim.button.primary': 'Primary action',
  'states.prim.button.secondary': 'Secondary action',
  'states.prim.button.quiet': 'Quiet',
  'states.prim.button.danger': 'Danger',
  'states.prim.button.small': 'Small (34px)',
  'states.prim.button.disabled': 'Disabled',
  'states.prim.button.linklike': 'Link-like',
  'states.prim.fields': 'Fields',
  'states.prim.field.label': 'Department id',
  'states.prim.field.hint': 'Enter the numeric id, not the department name.',
  'states.prim.field.error': 'A department id is digits only.',
  'states.prim.badges': 'Severity badges',
  'states.prim.badge.neutral': 'Neutral',
  'states.prim.toggles': 'Segmented control',
  'states.prim.rows': 'List rows (44px)',
  'states.prim.row.plain': 'Plain row',
  'states.prim.row.selected': 'Selected row',
  'states.prim.table': 'Data table (36px)',
  'states.prim.table.col.name': 'Employee',
  'states.prim.table.col.value': 'Observed hours',
  'states.prim.table.col.when': 'Started',
  'states.prim.tile': 'Figure tile',
  'states.prim.tile.title': 'Active ratio',
  'states.prim.tile.detail':
    'A figure is printed with its label and its staleness, never with a trend line.',
  'states.prim.mono': 'Identifiers and code',
  'states.prim.flag': 'Verified',
  'states.prim.type': 'The type ramp',
  'states.prim.type.display': 'Display 26px / 700 — numbers only',
  'states.prim.type.headline': 'Headline 19px / 700 — one per page',
  'states.prim.type.title': 'Title 15px / 600 — section heading',
  'states.prim.type.body': 'Body 13px / 400',
  'states.prim.type.small': 'Small 12px — the functional floor',
  'states.prim.type.label': 'Label 11px uppercase — labels only',
  'states.prim.type.sample': 'A manager reads this table eight hours a day',
  'states.prim.colour': 'Grounds and hues',
  'states.prim.colour.bg': 'Paper',
  'states.prim.colour.surface': 'Sunken surface',
  'states.prim.colour.accent': 'Accent (pressable)',
  'states.prim.colour.cta': 'Primary button',
  'states.prim.colour.rail': 'Desk edge',

  /* ── WP-K2 (Task 42) ────────────────────────────────────────────────────── */

  'common.hourShort': 'h',
  'common.minuteShort': 'm',

  'day.label': 'Date',
  'day.previous': 'Previous day',
  'day.next': 'Next day',
  'date.chosen': 'Selected: {date}',
  'date.none': 'No day chosen yet',

  'error.failed.title': 'Could not load {subject}',
  'error.unavailable.title': 'No {subject} yet',
  'error.unavailable.body':
    'There is nothing to show here yet. This is not a problem with your account.',

  'attendance.heading': 'Attendance for {date}',
  'attendance.loading': 'Loading attendance',
  'attendance.subject': 'attendance data',
  'attendance.window': 'First {first} · Last {last}',
  'attendance.noFirst': 'nothing recorded',
  'attendance.noLast': 'nothing recorded',
  'attendance.online': 'Online',
  'attendance.active': 'Active',
  'attendance.idle': 'Idle',
  'attendance.idle.help':
    'Idle: time with no keyboard or mouse input, against the threshold your company set. It is not a conclusion that you were not working.',
  'attendance.break': 'Break',
  'attendance.approvedOffline': 'Approved offline',
  'attendance.overtime': 'Overtime',
  'attendance.notReported': 'not reported',
  'attendance.empty.title': 'No attendance record for this day',
  'attendance.empty.body':
    'No work session was recorded for this day. If the workstation was off or the Agent was not running, the system had nothing to record — which is why the correction request is offered here.',
  'attendance.correct': 'Request a correction',
  'attendance.excludes.minutes': 'about {minutes} with no data from the workstation',
  'attendance.excludes.agent': 'the period during which the workstation reported nothing',
  'attendance.unsynced': 'The figures are calculated; some of this day’s events are still landing.',
  'attendance.adjustments':
    'This day is credited {minutes} of approved offline time, so at least one explanation has been approved. The number of adjustments is not shown here. The original data is not edited; adjustments are recorded separately.',
  'attendance.agentDevice': 'your workstation',

  /* ── WP-K3 (Task 43) ─────────────────────────────────────────────────────── */

  'attendance.status.on_time': 'On time',
  'attendance.status.late': 'Late',
  'attendance.status.early_leave': 'Left early',
  'attendance.status.late_and_early': 'Late and left early',
  'attendance.status.absent': 'Absent',
  'attendance.status.off_day': 'Non-working day',
  'attendance.status.leave': 'Approved leave',
  'attendance.status.data_incomplete': 'Data incomplete',
  'attendance.status.not_configured': 'No shift configured',

  'attendance.status.help.on_time': 'Present within the shift window, grace period included.',
  'attendance.status.help.late':
    'The first observed activity is after the shift start plus its grace period.',
  'attendance.status.help.early_leave':
    'The last observed activity is before the shift end minus its grace period.',
  'attendance.status.help.late_and_early': 'Both ends fall outside the shift window.',
  'attendance.status.help.absent':
    'A device reported all day and no time was credited. This is the only verdict that says something about the person, and it is only reached on a complete day.',
  'attendance.status.help.off_day': 'The roster expected nobody in on this day.',
  'attendance.status.help.leave': 'An approved leave explanation covers this day.',
  'attendance.status.help.data_incomplete':
    'The system reached NO conclusion about this day because the data is not complete. This is a statement about the data, not about the employee.',
  'attendance.status.help.not_configured':
    'No shift is configured for this employee on this day, so there is nothing to measure against. The remedy is a configuration change, not a correction request.',

  'attendance.reason.no_shift_configured': 'no shift is configured for this day',
  'attendance.reason.not_a_working_day': 'not a working day on the roster',
  'attendance.reason.no_session_row': 'there is no work-session record for this day at all',
  'attendance.reason.agent_data_missing': 'a device was expected and reported nothing',
  'attendance.reason.no_enrolled_device': 'no device is enrolled, so nothing was observable',
  'attendance.reason.incomplete_data': 'the observed data has an identifiable hole in it',
  'attendance.reason.no_arrival_observed':
    'no arrival was observed, so there is nothing to compare with the shift start',
  'attendance.reason.no_departure_observed':
    'no departure was observed, so there is nothing to compare with the shift end',
  'attendance.reason.overtime_requires_manual_claim':
    'this shift only recognises overtime that somebody claims',
  'attendance.reason.overtime_not_credited_on_incomplete_data':
    'overtime is not credited on a day with incomplete data',
  'attendance.reason.overtime_rule_unrecognised': 'this shift’s overtime rule is not supported yet',
  'attendance.reason.stale_source_version':
    'the record was built by a different algorithm version than the one running',

  'attendance.completeness.complete': 'Complete — observed, and nothing wrong with it.',
  'attendance.completeness.partial':
    'Partial — observed, with an identifiable hole in what arrived.',
  'attendance.completeness.missing':
    'Missing — a device was expected and nothing arrived. This does NOT mean the employee did no work.',
  'attendance.completeness.no_data':
    'No device — nothing is enrolled, so there was nothing to observe.',
  'attendance.completeness.no_session_row': 'There is no work-session record for this day.',

  'k3.window': 'Viewing {month}, day {date} — {timezone}',
  'k3.filters.legend': 'Filters',
  'k3.filter.date': 'Day',
  'k3.filter.department': 'Department',
  'k3.filter.employee': 'Employee',
  'k3.filter.status': 'Status',
  'k3.filter.all': 'All',
  'k3.filter.apply': 'Apply',
  'k3.filter.note':
    'The department and employee filters are sent to the server. The status filter narrows only the rows on this page. None of them is a permission check: the server decides scope on every call.',

  'k3.weekday.1': 'Mon',
  'k3.weekday.2': 'Tue',
  'k3.weekday.3': 'Wed',
  'k3.weekday.4': 'Thu',
  'k3.weekday.5': 'Fri',
  'k3.weekday.6': 'Sat',
  'k3.weekday.7': 'Sun',

  'k3.month.heading': 'Month of {month}',
  'k3.month.of': 'Month of {month} — {employee}',
  'k3.month.prev': 'Previous month',
  'k3.month.next': 'Next month',
  'k3.month.loading': 'Loading the month',
  'k3.month.subject': 'the monthly attendance calendar',
  'k3.month.pickEmployee': 'Pick an employee in the list below to see their month.',
  'k3.month.future': 'not yet',
  'k3.month.today': 'Today',
  'k3.month.allFuture': 'This whole month is still ahead, so there is nothing to ask the server.',
  'k3.month.noCell': 'This day has no cell on the page that was loaded.',
  'k3.month.open': 'Open the detail for {date}',
  'k3.month.futureNote':
    'Days after today are not requested. A day that has not happened has no session record, and drawing it as “data incomplete” turns a day that has not arrived into a hole in the data.',

  'k3.roster.heading': 'Day {date}',
  'k3.roster.loading': 'Loading the day’s roster',
  'k3.roster.subject': 'the daily attendance list',
  'k3.roster.empty.title': 'Your scope reaches nobody on this day',
  'k3.roster.empty.body':
    'The server answered, and no employee is inside this account’s scope. That is the answer, not a failure.',
  'k3.roster.col.employee': 'Employee',
  'k3.roster.col.status': 'Status',
  'k3.roster.col.first': 'In',
  'k3.roster.col.last': 'Out',
  'k3.roster.col.online': 'Online',
  'k3.roster.col.missing': 'Short of shift',
  'k3.roster.select': 'Show {name}’s month',
  'k3.roster.open': 'Open {name}’s detail for {date}',
  'k3.roster.truncated': '{shown} of {total} cells in this window',
  'k3.roster.statusFilter':
    'Filtering on “{status}”. The filter runs over the {shown} rows that were loaded, not over all {total} cells in the window.',
  'k3.roster.noneMatchStatus':
    'No row on this page has the selected status. Another page still might.',

  'k3.drawer.title': '{employee} · {date}',
  'k3.drawer.close': 'Close',
  'k3.drawer.loading': 'Loading the day’s detail',
  'k3.drawer.subject': 'this day’s attendance detail',
  'k3.drawer.window': 'In {first} · Out {last}',
  'k3.drawer.shift': 'Shift',
  'k3.drawer.shift.assigned': 'Shift {code}, assigned by {scope} ({key}), priority {priority}',
  'k3.drawer.shift.system_default': 'Shift {code} — the system default; no assignment matched',
  'k3.drawer.shift.off_duty': 'Nobody was rostered on this day',
  'k3.drawer.shift.unconfigured':
    'No shift is configured for this day, so there is nothing to measure against',
  'k3.drawer.shift.window': '{start} – {end} ({timezone}), {elapsed} long',
  'k3.drawer.shift.grace': 'Grace: {late} late, {early} early leave',
  'k3.drawer.metrics': 'Shift-relative figures',
  'k3.metric.late_minutes': 'Late',
  'k3.metric.early_leave_minutes': 'Left early',
  'k3.metric.missing_minutes': 'Short of shift',
  'k3.metric.overtime_minutes': 'Overtime',
  'k3.metric.withheld': 'withheld — {reason}',
  'k3.metric.unexplained':
    'no figure, and the server gave no reason. It is still an absence and it is still not a zero.',
  'k3.drawer.source': 'Where the numbers came from',
  'k3.drawer.source.help':
    'This block says where the figures above came from and how much of the day was observable. It is how you tell “this person was absent” from “we could not see this person”.',
  'k3.drawer.source.rowPresent': 'There is a work-session record for this day.',
  'k3.drawer.source.rowAbsent':
    'There is no work-session record for this day, so the shift-relative figures above are withheld — they are not zero.',
  'k3.drawer.source.version': 'Record algorithm version: {stored}; running version: {current}.',
  'k3.drawer.source.stale':
    'This record was built by a different algorithm version. The “absent” verdict is withheld until it is recalculated; every other figure stands.',
  'k3.drawer.source.calculated': 'Calculated at {when}.',
  'k3.drawer.source.calculatedNever': 'Never calculated.',
  'k3.drawer.source.devices': 'Contributing devices: {devices}.',
  'k3.drawer.source.noDevices': 'No device contributed data for this day.',
  'k3.drawer.source.policy': 'Multi-device policy: {policy}.',
  'k3.drawer.source.lastEvent': 'Last event received at {when}.',
  'k3.drawer.source.lastEventNever': 'No event has been received for this day.',
  'k3.drawer.completeness': 'Data completeness',
  'k3.drawer.completeness.help':
    'Completeness says how much of the day was observable. An attendance figure without its completeness is a figure that lies.',
  'k3.drawer.completeness.score': 'Completeness score: {percent}%.',
  'k3.drawer.completeness.devices': '{count} device(s) · {events} events.',
  'k3.drawer.completeness.gap': 'Reporting gap: {gap}.',
  'k3.drawer.completeness.clockAnomaly': 'A device clock anomaly was detected on this day.',
  'k3.drawer.credited':
    'This day is being credited {minutes} of approved offline time, which means at least one explanation has been approved.',
  'k3.drawer.correctionSelfOnly':
    'An explanation is the employee’s own account of their day. The server refuses one filed on somebody else’s behalf whatever the caller holds, so this screen offers no such control; the employee files it from My Activity.',

  'k3.queue.heading': 'Correction queue',
  'k3.queue.noList':
    'Enter an explanation code to open one. The code is in the reply the employee got when filing, and on the Audit log screen.',
  'k3.queue.twoLevel':
    'Two levels: the manager decides first, and HR decides after that when the record was snapshotted at filing as needing HR.',
  'k3.queue.idLabel': 'Correction id',
  'k3.queue.idHint':
    'The id is in the reply the employee got when filing, and on the Audit log screen.',
  'k3.queue.open': 'Open',
  'k3.queue.subject': 'the attendance correction',
  'k3.queue.requested': 'Requested {minutes}, from {start} to {end}.',
  'k3.queue.requestedLabel': 'Requested',
  'k3.queue.reasonLabel': 'Reason',
  'k3.queue.hrLabel': 'HR gate',
  'k3.queue.decidedAtLabel': 'When',
  'k3.queue.approvedMinutesOut': 'Approved {minutes}.',
  'k3.queue.reasonCode': 'Reason: {reason}',
  'k3.queue.note': 'Employee’s note: {note}',
  'k3.queue.noNote': 'The employee left no note.',
  'k3.queue.evidence': '{count} evidence file(s) attached.',
  'k3.queue.requiresHr.true':
    'HR approval is required. That requirement was snapshotted when the request was filed and is not re-read from the company’s current policy.',
  'k3.queue.requiresHr.false':
    'HR approval is not required. That requirement was snapshotted when the request was filed and is not re-read from the company’s current policy.',
  'k3.queue.noteLabel': 'Note (required)',
  'k3.queue.noteHelp':
    'Required on both approve and reject, and written to the audit log. A rejection with no reason is the answer the employee is owed and does not get.',
  'k3.queue.approvedMinutes': 'Minutes to approve',
  'k3.queue.approvedMinutesHelp':
    'Leave it empty to approve exactly what was requested. You may approve LESS, never more.',
  'k3.queue.approve': 'Approve',
  'k3.queue.confirmApprove': 'Confirm approval',
  'k3.queue.confirmApprove.help':
    'Approving writes straight into the employee’s recorded hours and enqueues a recalculation; this screen cannot undo it. Check the correction id, then confirm.',
  'k3.queue.reject': 'Reject',
  'k3.queue.deciding': 'Sending the decision…',
  'k3.queue.decided': 'Recorded. Current status: {status}.',
  'k3.queue.decidedAt': 'Decided at {when}.',
  'k3.queue.selfDecision':
    'This is your own explanation. Nobody decides their own, at either gate, whatever they hold. The rule sits on the SUBJECT, so it cannot be routed around by having somebody else file it — and filing for somebody else is refused as well.',
  'k3.queue.outOfScope':
    'This correction belongs to an employee outside your scope. The server decides scope; this screen filtered nothing beforehand, so what you are reading is the server’s answer.',
  'k3.queue.notFound':
    'No correction with this id exists in your company. An id that does not exist and an id belonging to another company are answered identically, and that is deliberate.',
  'k3.queue.invalidState':
    'This correction has already been decided. The transitions still available are: {allowed}.',
  'k3.queue.invalidStateNoList': 'This correction has already been decided.',

  'k3.approval.pending_manager': 'Waiting on the manager',
  'k3.approval.pending_hr': 'Waiting on HR',
  'k3.approval.approved': 'Approved',
  'k3.approval.rejected': 'Rejected',
  'k3.approval.withdrawn': 'Withdrawn',

  'k3.reasonCode.meeting': 'Meeting',
  'k3.reasonCode.phone_call': 'Phone call',
  'k3.reasonCode.reading_document': 'Reading a document',
  'k3.reasonCode.paperwork': 'Paperwork',
  'k3.reasonCode.client_visit': 'Client visit',
  'k3.reasonCode.break': 'Break',
  'k3.reasonCode.offsite_work': 'Offsite work',
  'k3.reasonCode.agent_down': 'Agent not running',
  'k3.reasonCode.device_issue': 'Device problem',
  'k3.reasonCode.power_outage': 'Power outage',
  'k3.reasonCode.approved_leave': 'Approved leave',
  'k3.reasonCode.other': 'Other',

  'k3.export.heading': 'Export',
  'k3.export.unavailable':
    'This screen has no export button. Create an attendance export on the Reports screen; that needs the export-reports permission.',
  'k3.export.willSend':
    'An attendance report for the filters on screen ({from} to {to}) is created on the Reports screen.',
  'k3.export.noLocalFile':
    'There is no browser-side export either: only a file created on the Reports screen records who exported it, when, and with which filters.',

  'timeline.heading': 'Hour by hour',
  'timeline.hours.toggle': 'Every hour of the day ({count} hours)',
  'timeline.empty.title': 'No activity data for this day yet',
  'timeline.empty.body': 'Data appears once the workstation sends its first heartbeat.',
  'timeline.intro':
    'Read from the hourly rollup. Hours that have not happened are not drawn; "not computed" and "no data" are different states, and neither of them is a zero.',
  'timeline.loading': 'Loading the timeline',
  'timeline.subject': 'the hourly timeline',
  'timeline.caption': 'Activity by hour, {date}',
  'timeline.col.hour': 'Hour',
  'timeline.col.shape': 'Share',
  'timeline.col.detail': 'Detail',
  'timeline.col.state': 'Data state',
  'timeline.detail': 'Active {active} · Idle {idle}',
  'timeline.private': 'Private {duration}',
  'timeline.private.help':
    'This time WAS counted as time worked. Company policy meant the application or domain name was not collected, so this period belongs to no category — which is a different fact from "no rule matched". You are not being asked to account for it.',
  'timeline.kind.complete': 'Complete',
  'timeline.kind.complete.help': 'This hour was computed and the data is complete.',
  'timeline.kind.partial': 'Partly missing',
  'timeline.kind.partial.help': 'This hour was computed and some of its data is incomplete.',
  'timeline.kind.missing': 'Nothing observed',
  'timeline.kind.missing.help':
    'This hour was computed and nothing was observed in it. That is NOT a conclusion that you did not work.',
  'timeline.kind.notComputed': 'Not computed',
  'timeline.kind.notComputed.help':
    'This day has raw data and the rollup has not reached this hour yet. The figures on screen are a floor, not a total.',
  'timeline.kind.noData': 'No data',
  'timeline.kind.noData.help':
    'There is no record for this hour. That is NOT a conclusion that you did not work.',
  'timeline.notCaughtUp': 'The rollup has not covered the whole window on screen.',
  'timeline.notStarted': 'The selected day has not begun, so there are no hours to show.',

  'catalog.heading': 'What is collected about me',
  'catalog.intro':
    'The classification rules in force for you, as of {date}. Another department’s rules are not shown.',
  'catalog.loading': 'Loading classification rules',
  'catalog.subject': 'the classification rules',
  'catalog.empty.title': 'Your company has set no classification rules',
  'catalog.empty.body':
    'No application or domain has been categorised. That is a state of the company configuration.',
  'catalog.private.heading': 'Private domains — never named',
  'catalog.private.none': 'Your company has marked no domain as private.',
  'catalog.private.badge': 'Private',
  'catalog.private.help':
    'This domain is never named. Time spent on it is still counted as time worked.',
  'catalog.private.body':
    'Time on these domains still counts towards your online and active time; only the name is not recorded.',
  'catalog.apps.heading': 'Applications',
  'catalog.apps.none': 'No application rules yet.',
  'catalog.sites.heading': 'Domains',
  'catalog.sites.none': 'No domain rules yet.',
  'catalog.blacklisted': 'Blocked',
  'catalog.blacklisted.help': 'Your company lists this domain as blocked.',
  'catalog.scope.company': 'Whole company',
  'catalog.scope.department': 'Department',
  'catalog.scope.jobTitle': 'Job title',
  'catalog.scope.help':
    'A more specific rule wins: job title over department, department over the whole company.',
  'catalog.class.productive': 'Productive',
  'catalog.class.neutral': 'Neutral',
  'catalog.class.unproductive': 'Unproductive',
  'catalog.class.unclassified': 'Unclassified',
  'catalog.class.none': 'No productivity claim',
  'catalog.category.productive': 'Productive',
  'catalog.category.neutral': 'Neutral',
  'catalog.category.unproductive': 'Unproductive',
  'catalog.category.communication': 'Communication',
  'catalog.category.meeting': 'Meeting',
  'catalog.category.development': 'Development',
  'catalog.category.design': 'Design',
  'catalog.category.accounting': 'Accounting',
  'catalog.category.sales': 'Sales',
  'catalog.category.marketing': 'Marketing',
  'catalog.category.research': 'Research',
  'catalog.category.training': 'Training',
  'catalog.category.system': 'System',
  'catalog.category.blocked': 'Blocked',
  'catalog.category.uncategorized': 'No rule matched',

  'policy.heading': 'Policy in force, and acknowledgement',
  'policy.body':
    'The policy version, its effective date and its acknowledgement are handled by the Agent on the workstation, which shows them in its tray.',
  'policy.endpoints':
    'For desktop monitoring, the policy version and its acknowledgement are handled by the Agent using the device’s own identity rather than your web session, so they appear in the Agent and not on this page.',
  'policy.lawfulBasis':
    'Desktop monitoring and GPS location rest on the ENUMERATED grounds of the Personal Data Protection Law (Law 91/2025/QH15) and Decree 356/2025/NĐ-CP: performance of the labour contract, and compliance with the employer’s Labour Code timekeeping and wage-record obligations.',
  'policy.consentRecord':
    'The fact that you were given notice is recorded for each version of the notice, and that record is kept. For location data you can acknowledge the notice and stop collection at any time; coordinates stop being stored even if the app keeps sending them. Full text: “Cơ sở pháp lý của việc giám sát” (lawful basis for monitoring).',
  'policy.self': 'This page reads only your own data.',

  'score.heading': 'Productivity score, and how it was calculated',
  'score.loading': 'Loading the score explanation',
  'score.subject': 'the score explanation',
  'score.notScored':
    'This period has not been scored. When data is missing the system lowers confidence rather than assigning 0 — so "no score" means no score, not a low one.',
  'score.value': 'Score',
  'score.period': 'Period {from} → {to}',
  'score.formula': 'Formula version {version}',
  'score.confidence': 'Confidence {value}',
  'score.confidence.help':
    'Confidence: how far the system trusts the data behind this score. Missing data lowers confidence; it does not make the score 0.',
  'score.completeness': 'Data completeness {value}',
  'score.completeness.help':
    'Data completeness: how much of the expected data was actually collected in the period.',
  'score.unknown': 'unknown',
  'score.lowConfidence.title': 'This score rests on incomplete data',
  'score.lowConfidence.body':
    'The score is still shown in full so you can check it. Low confidence is a statement about the data, not about you.',
  'score.components': 'Components',
  'score.col.component': 'Component',
  'score.col.weight': 'Weight',
  'score.col.contribution': 'Contribution',
  'score.noData': 'not enough data',
  'score.positive': 'What raised the score',
  'score.negative': 'What lowered the score',
  'score.limitations.heading': 'Limits on use',
  'score.limitation.default':
    'A productivity score must not be the sole basis for any disciplinary or pay decision.',

  'correction.heading': 'Correction request for {date}',
  'correction.intro':
    'This files a request, not an edit. Raw data is not changed; if it is approved the system records a separate adjustment.',
  'correction.reason': 'Reason',
  'correction.reason.meeting': 'Meeting',
  'correction.reason.phone_call': 'Phone call',
  'correction.reason.reading_document': 'Reading a document',
  'correction.reason.paperwork': 'Paperwork',
  'correction.reason.client_visit': 'Client visit',
  'correction.reason.break': 'Break',
  'correction.reason.offsite_work': 'Working off site',
  'correction.reason.agent_down': 'Agent was not running',
  'correction.reason.device_issue': 'Device problem',
  'correction.reason.power_outage': 'Power cut',
  'correction.reason.approved_leave': 'Approved leave',
  'correction.reason.other': 'Other',
  'correction.start': 'From',
  'correction.end': 'To',
  'correction.note': 'Description',
  'correction.note.hint': 'Required, at least {min} characters.',
  'correction.note.other': 'With reason "Other" the description needs at least 10 characters.',
  'correction.resolved': 'The interval that will be sent: {from} → {to}',
  'correction.submit': 'Send the request',
  'correction.submitting': 'Sending…',
  'correction.cancel': 'Cancel',
  'correction.subject': 'the correction request',
  'correction.filed.title': 'Request recorded',
  'correction.filed.body':
    'The request has been recorded with an audit entry. This day’s raw data is unchanged.',
  'correction.filed.status': 'Status: {status}',
  'correction.filed.hr': 'An HR approval step is also required',
  'correction.status.pending_manager': 'Waiting on your manager',
  'correction.status.pending_hr': 'Waiting on HR',
  'correction.status.approved': 'Approved',
  'correction.status.rejected': 'Rejected',
  'correction.status.withdrawn': 'Withdrawn',

  'timer.heading': 'Timer',
  'timer.loading': 'Reading the timer',
  'timer.subject': 'the running timer',
  'timer.subject.action': 'the timer action',
  'timer.elapsed': 'Elapsed',
  'timer.startedAt': 'Started at {when}',
  'timer.pause': 'Pause',
  'timer.resume': 'Resume',
  'timer.stop': 'Stop',
  'timer.task.unnamed': 'Task with no title',
  'timer.empty.title': 'No timer is running',
  'timer.empty.body': 'Find a task below and press Start.',
  'timer.switched.title': 'Switched task',
  'timer.switched.body': 'The previous timer for "{task}" was stopped and recorded in full.',
  'timer.longRunning.title': 'This timer has been running a long time',
  'timer.longRunning.body':
    'It has been running continuously for more than {hours} hours. If the work has finished, press Stop. This is a note about elapsed time, not a company rule.',
  'timer.longRunning.short': 'Running a long time',
  'timer.mini.loading': 'Reading the timer…',
  'timer.mini.unreadable': 'Could not read the timer',
  'timer.mini.none': 'No timer running',
  'timer.state.running': 'Running',
  'timer.state.paused': 'Paused',
  'timer.state.stopped': 'Stopped',
  'timer.state.pendingApproval': 'Awaiting approval',
  'timer.state.approved': 'Approved',
  'timer.state.rejected': 'Rejected',
  'timer.source.agent': 'Observed by the Agent',
  'timer.source.web': 'Recorded from the web',
  'timer.source.manual': 'Entered by hand',
  'timer.source.api': 'From another system',
  'timer.source.manual.help':
    'Time entered by a person. Every report and every score keeps this separate from time the Agent observed.',
  'timer.source.observed.help': 'Time recorded by the system, not typed in.',
  'timer.validation.validated': 'Checked against achau',
  'timer.validation.validated.help': 'The task link has been checked against achau.',
  'timer.validation.pending': 'We could not check this with achau',
  'timer.validation.pending.help':
    'The system has not been able to reach achau to check the task. Your time is still recorded in full.',
  'timer.validation.pending.body':
    'achau could not be reached when this started, so the task link has not been checked. That is the state of the connection, not of the record: your time is recorded in full, nothing has been deleted, and the system will check again when achau is back. There is nothing for you to do.',
  'timer.validation.pending.checkedAt':
    'There is no check time, because nobody has been able to ask achau yet.',
  'timer.validation.failed': 'achau answered that the task link is not valid',
  'timer.validation.failed.help': 'achau was asked and answered no. The record is kept as it is.',
  'timer.validation.failed.body':
    'achau was asked and answered no about the task link. The time entry is kept as it is and nothing has been deleted; a manager will look at it.',
  'timer.reason.achau_unreachable': 'Reason: achau could not be reached.',
  'timer.reason.task_closed': 'Reason: the task is closed in achau.',
  'timer.reason.task_not_found': 'Reason: the task was not found in achau.',
  'timer.reason.no_permission': 'Reason: this account has no permission for that task in achau.',
  'timer.reason.unknown': 'No reason was given.',

  'tasks.heading': 'Find a task',
  'tasks.searchLabel': 'Task title or id',
  'tasks.searchHint':
    'At least {min} characters. Results are read from the task cache synchronised from achau.',
  'tasks.loading': 'Searching for tasks',
  'tasks.subject': 'the task search results',
  'tasks.empty.title': 'No task matched',
  'tasks.empty.body':
    'Results come from the cache, so a task created in achau moments ago may not appear yet.',
  'tasks.start': 'Start',
  'tasks.switch': 'Switch to this task',
  'tasks.stale': 'May be out of date',
  'tasks.stale.help': 'This row was synchronised longer ago than twice the sync interval.',
  'tasks.stale.since': 'Last synchronised: {when}.',
  'tasks.closed': 'Closed',
  'tasks.closed.help':
    'The task is closed. The server decides whether time may be logged against it, so the button stays enabled and you get the server’s answer.',
  'tasks.source.cache': 'From the cache',
  'tasks.source.achau': 'Read live from achau',
  'tasks.degraded.detail': 'This answer could not be confirmed against achau during the request.',
  'tasks.cache.stale': 'Your company’s last task sync is older than the allowed window.',
  'tasks.cache.neverSynced':
    'No task sync has ever succeeded for your company. That is an operations problem, not an empty task list.',

  'entries.heading': 'Time entries',
  'entries.loading': 'Loading time entries',
  'entries.subject': 'your time entries',
  'entries.empty.title': 'No entries in this range',
  'entries.empty.body': 'The range shown is the last {days} days.',
  'entries.excludes': 'older entries inside the same {days} days — the list stops at {limit} rows',
  'entries.running': 'running',
  'entries.stillOpen': 'not finished',
  'entries.reopened': 'Re-opened for correction',
  'entries.reopened.help':
    'The owner asked for this entry to be re-cut — a different fact from an entry typed in by hand and awaiting approval.',

  'manual.heading': 'Log time by hand',
  'manual.intro':
    'For time that was not recorded automatically. Every hand-entered record is audited and kept separate from time the Agent observed.',
  'manual.open': 'Open the manual entry form',
  'manual.close': 'Close the form',
  'manual.task': 'achau task id',
  'manual.start': 'From',
  'manual.end': 'To',
  'manual.note': 'Reason',
  'manual.note.hint': 'Required, at least {min} characters.',
  'manual.resolved': 'The interval that will be sent: {from} → {to}',
  'manual.submit': 'Record it',
  'manual.submitting': 'Recording…',
  'manual.subject': 'the manual entry',
  'manual.recorded.title': 'Recorded',
  'manual.recorded.counted': 'The entry counts immediately.',
  'manual.recorded.pending':
    'The entry is waiting on a manager, under the company policy as it stood when you filed it.',
  'manual.window.title': 'Outside the backdating window',
  'manual.window.body':
    'Company policy allows backdating only as far as {earliest}. That value comes from the server; this screen does not guess the window.',
  'manual.overlap.title': 'Overlaps another entry',
  'manual.overlap.body':
    'This interval overlaps entry {id}. Two entries that merely touch at an endpoint do not overlap.',

  'state.agentOffline.heartbeatUnavailable':
    'This screen does not read device data, so it cannot show when the device last reported; that is on the Devices screen and needs the device-management permission.',

  /* ── WP-K4 (Task 44) — /overview ── */

  'overview.loading': 'Loading the overview',
  'overview.scope.company': 'Scope: the whole company',
  'overview.scope.department': 'Scope: your department',
  'overview.scope.filtered': 'Filtered to department id {id}',
  'overview.range': 'From {from} to {to}',
  'overview.window.label': 'View, ending {date}',
  'overview.window.today': 'Today',
  'overview.window.7d': '7 days',
  'overview.window.30d': '30 days',
  'overview.department.label': 'Department',
  'overview.department.apply': 'Apply',
  'overview.department.clear': 'Clear',
  'overview.refresh': 'Read again now',
  'overview.updatedAt': 'Figures read at {at}',
  'overview.updating': 'Reading again…',
  'overview.neverUpdated': 'Nothing has been read yet',
  'overview.pollNote':
    'This page re-reads the aggregates every {aggregate} minutes and the running timers every {live} seconds, and stops while you are on another tab.',
  'overview.staleAfterError':
    'The figures below are from the last successful read ({at}). The refresh after it failed.',
  'overview.completeness':
    '{complete} employee-days complete · {partial} partial · {missing} computed with nothing observed · {notComputed} not computed yet',
  'overview.truncated':
    'One page holds {limit} rows and this window has more, so the figures above cover up to the end of {to} — not a total with the end cut off.',
  'overview.nothingCovered': 'Not one day in this window is fully covered',
  'overview.hours.uncovered.next':
    'The first page of data did not fully cover a single day, so there is no total to show. Choose a shorter window or filter by department.',

  'overview.presence.title': 'Present today',
  'overview.presence.headcount': '{count} employees in scope',
  'overview.presence.headcountUnit': 'employees in scope',
  'overview.presence.active': 'Active',
  'overview.presence.active.help':
    "Today's daily rollup records some active time. It is computed hourly, so it can lag by up to an hour.",
  'overview.presence.idle': 'Online but not active',
  'overview.presence.idle.help': 'Some online time, no active time recorded today.',
  'overview.presence.observed_none': 'Computed, nothing observed',
  'overview.presence.observed_none.help':
    'The rollup for today ran and found no activity. That is not a conclusion that this person did not work.',
  'overview.presence.not_computed': 'Not computed yet',
  'overview.presence.not_computed.help':
    'Data arrived and the daily rollup has not reached it. A number will appear; it is not a zero.',
  'overview.presence.no_activity_data': 'No activity data',
  'overview.presence.no_activity_data.help':
    'Nothing arrived for this person today. It may be a scheduled day off and it may be a machine that could not report — two different things, and the attendance column below is where they are told apart.',
  'overview.presence.noOffline':
    'There is no "offline" bucket here. Offline is a claim about a machine, and only the Devices screen publishes it (device-management permission required). This tile is about observed data, not about machines.',
  'overview.presence.empty': 'There are no employees in the scope you may see',
  'overview.presence.empty.next':
    'Clear the department filter to look wider, or ask your administrator if you need to see more people.',
  'overview.presence.noRows':
    'No rollup exists for today yet, so this tile can say nothing about presence.',

  'overview.hours.title': 'Observed online hours',
  'overview.hours.over': 'Over {days} days, {rows} employee-days, {employees} people',
  'overview.hours.active': 'Of which active: {value}',
  'overview.hours.none': 'No online time observed in this window',
  'overview.hours.none.next':
    'That is "not observed", not "no hours". A figure appears once a workstation reports for this window.',
  'overview.hours.excludes': 'employee-days that are not computed yet or only partly observed',

  'overview.ratio.title': 'Active ratio',
  'overview.ratio.terms': '{active} active out of {online} online',
  'overview.ratio.none': 'No ratio for this window',
  'overview.ratio.none.next': 'Not one online second has been observed, and 0 out of 0 is not 0%.',
  'overview.ratio.notProductive':
    'This is not a productivity ratio. The only measure available here is active time as a share of observed online time, and it says nothing about whether that time was productive.',
  'overview.ratio.partial':
    'The numerator and the denominator come from the same rows, so a day that was only partly observed understates both equally: what missing data biases is the TOTAL, not the ratio. What it narrows is the sample, and the sample is printed beside it.',

  'overview.task.title': 'Task data from achau',
  'overview.task.cache.fresh': 'Task data: fresh',
  'overview.task.cache.stale': 'Task data: stale — the sync is late',
  'overview.task.cache.never_synced': 'Task data: never synced',
  'overview.task.degraded': 'achau could not be reached during this read',
  'overview.task.syncedAt': 'The newest record was synced at {at}',
  'overview.task.noSyncedAt': 'No record to take a sync instant from.',
  'overview.task.probe':
    'This is the task-sync state of the whole company. The time above is the newest record this screen saw, not the last sweep.',
  'overview.task.rest': 'The rest of the overview does not depend on achau and keeps working.',
  'overview.task.overdue.title': 'Overdue task count: no data source',
  'overview.task.overdue.body':
    'Overdue tasks cannot be counted: tasks can only be looked up by search text, not filtered by deadline. A number built that way would be wrong, so none is shown.',
  'overview.task.overdue.needs':
    'Once tasks can be looked up by deadline, this tile becomes a number and nothing else on the screen changes.',

  'overview.trend.title': '{days}-day trend',
  'overview.trend.subtitle': 'Online and active hours per day, for the scope in view.',
  'overview.trend.noDepartments':
    'No per-department split: the rollups here do not carry a department. This is the trend of the scope you are viewing, not several departments stacked.',
  'overview.trend.caption':
    '{date}: {online} online, {active} active, {employees} people with data.',
  'overview.trend.gap': '{date}: no rollup yet — drawn as a gap, not as a zero-height bar.',
  'overview.trend.partial': '{date}: {partial} rows were only partly observed.',
  'overview.trend.axis': 'The tallest bar is {value}.',
  'overview.trend.drill': 'Open Attendance for this window',

  'overview.reports.title': 'Who needs help',
  'overview.reports.subject': 'the roster',
  'overview.reports.col.name': 'Employee',
  'overview.reports.col.attendance': 'Attendance today',
  'overview.reports.col.presence': 'Presence',
  'overview.reports.col.task': 'Running task',
  'overview.reports.col.hours': 'Observed hours',
  'overview.reports.col.drill': 'Detail',
  'overview.reports.noTask': 'No timer running',
  'overview.reports.taskUnavailable': 'Timers not read',
  'overview.reports.hoursUnknown': 'Not computed',
  'overview.reports.drill': "Open {name}'s attendance",
  'overview.reports.truncated':
    'Showing {shown} of {total} employees in scope. The tiles above are computed over the whole scope, not over this page.',

  'overview.unassigned.title': 'No task running ({count})',
  'overview.unassigned.body':
    'People with no timer running. That is a claim about timers, not about whether they are working.',
  'overview.unassigned.none': 'Everyone in scope has a timer running.',
  'overview.unassigned.unavailable':
    'The running-timer list could not be read, so this panel lists nobody — listing everybody would be a false conclusion.',

  'overview.devices.title': 'Device liveness',
  'overview.devices.note':
    'These are machines, not people. One person may have several machines, or none.',
  'overview.devices.online': 'Online',
  'overview.devices.stale': 'Stale heartbeat',
  'overview.devices.offline': 'Offline',
  'overview.devices.never': 'Never reported',
  'overview.devices.staleHelp':
    'Stale and offline are two buckets and are never merged. Liveness is recomputed from the last heartbeat on every read, never stored, and it lags pessimistically: a machine is called offline slightly early, never late.',
  'overview.devices.denied':
    'This region needs the device-management permission (workforce.manage_devices), which this account does not hold. No number is shown here — a zero would be a lie.',
  'overview.devices.truncated': 'Read {known} of {total} devices.',
  'overview.devices.subject': 'device liveness',

  'overview.alerts.title': 'Alerts',
  'overview.alerts.empty': 'No summary of alerts by severity yet',
  'overview.alerts.body': 'Read every alert for today on the Alerts screen.',

  /* ── the day sheet (Wave 1, group A) ───────────────────────────────────── */
  'overview.day.isToday': 'Today',
  'overview.day.previous': 'Previous day',
  'overview.day.next': 'Next day',
  'overview.day.backToToday': 'Back to today',
  'overview.filters.label': 'Dashboard filters',
  'overview.figures.title': "The day's three figures",
  'overview.staleAfterErrorShort':
    'The last refresh failed. The figures below are the last successful read, not the current ones.',

  'overview.exception.title': 'Needs handling today',
  'overview.exception.uncounted': 'not read',
  'overview.exception.late': 'Late',
  'overview.exception.late.body':
    "The people this day's attendance record marks as late. The count comes from the status shown in the Attendance column below, not from anything recomputed in the browser.",
  'overview.exception.late.none': 'Nobody was recorded late on this day.',
  'overview.exception.absent': 'Absent',
  'overview.exception.absent.body':
    "The people this day's attendance record marks as absent. Absent is the server's verdict, never inferred here from missing data.",
  'overview.exception.absent.none': 'Nobody was recorded absent on this day.',
  'overview.exception.noActivity': 'No activity data',
  'overview.exception.noActivity.body':
    'No rollup row exists for these people on this day. That is a statement about data, not about people: the agent may have stopped reporting, or the hour may not be computed yet. It is never rendered as a zero.',
  'overview.exception.noActivity.none': 'Everybody in scope has activity data for this day.',
  'overview.exception.unassigned': 'No running timer',
  'overview.reports.ordered':
    'Ordered by need: absent, late, incomplete attendance data, no activity data, no running timer. The order is built only from the columns shown in the table.',

  /* ═══════════════ WP-K5 (Task 45) — Devices ═══════════════ */

  'devices.intro':
    'This is the screen where a machine loses its access. Revoking is permanent and cannot be undone; a registration token is shown exactly once.',
  'devices.subject': 'the device list',

  'devices.census.title': 'Heartbeat readings',
  'devices.census.scope':
    'Counted over the {counted} devices on this page. The company has {total}.',
  'devices.census.help':
    'Online, stale and offline are three different answers and are never merged. Liveness is recomputed from the last heartbeat on every read, never stored, and it lags pessimistically: a machine is called offline slightly early, never late.',
  'devices.reading.online': 'Online',
  'devices.reading.stale': 'Stale heartbeat',
  'devices.reading.offline': 'Offline',
  'devices.reading.never_reported': 'Never reported',
  'devices.reading.neverHelp':
    'No heartbeat since enrolment. Different from offline: there is no silence to measure from, so the offline minutes are left blank rather than counted from the enrolment.',

  'devices.status.pending': 'Awaiting approval',
  'devices.status.active': 'Active',
  'devices.status.offline': 'Marked offline',
  'devices.status.suspended': 'Suspended',
  'devices.status.revoked': 'Revoked',
  'devices.status.uninstalled': 'Uninstalled',
  'devices.status.agent_outdated': 'Agent outdated',
  'devices.status.policy_error': 'Policy error',
  'devices.status.sync_error': 'Sync error',
  'devices.status.help':
    'The lifecycle status is set by an administrator, or moved to offline by the system. It is its own axis, not a heartbeat: a machine can be active and offline in the same answer.',

  'devices.filter.legend': 'Filter devices',
  'devices.filter.status': 'Lifecycle status',
  'devices.filter.statusAny': 'Any status',
  'devices.filter.q': 'Hostname',
  'devices.filter.qHint':
    'Matches part of the machine name. Not the machine fingerprint: that value is never published.',
  'devices.filter.employee': 'Employee',
  'devices.filter.employeeHint':
    'This is a filter, not a scope: results stay inside the scope you are allowed to see.',
  'devices.filter.department': 'Department',
  'devices.filter.departmentHint':
    'Matches the current department of the employee assigned to the machine.',
  'devices.filter.apply': 'Apply',
  'devices.filter.clear': 'Clear filters',
  'devices.filter.reload': 'Reload',
  'devices.filter.noOnline':
    'There is no online filter here: such a filter would fold stale, offline and never-reported into one group. Filter by lifecycle status above.',

  'devices.table.hostname': 'Hostname',
  'devices.table.employee': 'Employee',
  'devices.table.status': 'Lifecycle',
  'devices.table.liveness': 'Heartbeat',
  'devices.table.heartbeat': 'Last heartbeat',
  'devices.table.open': 'Open detail',
  'devices.table.openLabel': 'Open the detail for device {name}',
  'devices.table.noHostname': 'No hostname reported',
  'devices.table.unassigned': 'No employee assigned',
  'devices.table.unnamed': 'Name not on file',
  'devices.table.employeeCode': 'Employee #{id}',
  'devices.employeePickHint': 'Pick from the list; the id is attached for you.',
  'devices.table.truncated':
    'Showing {shown} of {total} devices. The counts above cover this page, not the whole company.',

  'devices.empty.title': 'No devices are enrolled',
  'devices.empty.body':
    'No machine in this company has enrolled yet. Mint a registration token below and hand it to whoever installs the Agent.',
  'devices.emptyFiltered.title': 'No device matches this filter',
  'devices.emptyFiltered.body':
    'The current filter matches no machine. Clear it to see the whole fleet.',

  'devices.pending.title': 'This machine is awaiting approval',
  'devices.pending.body':
    'A pending device already holds a valid credential, so the heartbeat beside it is real. But every batch of data it sends is refused and piles up in the queue on the machine. Approving the machine is what lets that queue land.',

  'devices.detail.title': 'Device detail',
  'devices.detail.select':
    'Pick a machine in the table to see its detail, its health and the actions.',
  'devices.detail.subject': 'the device detail',
  'devices.detail.identity': 'Identity',
  'devices.detail.os': 'Operating system',
  'devices.detail.agentVersion': 'Agent version',
  'devices.detail.ip': 'Last known IP',
  'devices.detail.department': 'Department (snapshot at enrolment)',
  'devices.detail.branch': 'Branch (snapshot at enrolment)',
  'devices.detail.policy': 'Policy version the machine holds',
  'devices.detail.trust': 'Trust level',
  'devices.detail.unknown': 'Not reported',
  'devices.detail.audit': 'The audit trail carried on this record',
  'devices.detail.registered': 'Enrolled at',
  'devices.detail.approved': 'Approved at',
  'devices.detail.approvedBy': 'Approved by (achau id)',
  'devices.detail.revoked': 'Revoked at',
  'devices.detail.revokeReason': 'Revocation reason',
  'devices.detail.statusReason': 'Status note',
  'devices.detail.auditNote':
    'This is what the audit log records for these transitions, read from the device record itself. The full log is on the Audit log screen.',
  'devices.detail.noReassign':
    'Reassigning the employee is not on this screen. Reassignment is its own operation, not a revoke-and-re-register — the credential authenticates the machine, not the person.',

  'devices.health.title': 'Operational health',
  'devices.health.subject': 'device health',
  'devices.health.computed':
    'Everything in this panel is computed on read and read from no column. So a sweep that has stopped running degrades the answer instead of freezing it.',
  'devices.health.offlineMinutes': 'Silent for {minutes} minutes',
  'devices.health.offlineNone': 'Online, so there is no silence to measure.',
  'devices.health.offlineNever': 'Never reported, so there is no mark to measure the silence from.',
  'devices.health.agentState': 'Agent state at the last heartbeat',
  'devices.health.agentOutdated': 'Agent is marked outdated',
  'devices.health.agentOutdatedHelp':
    'This is a stored verdict, not a version comparison: this screen does not know the current version, so a computed answer would be a guess.',
  'devices.health.policyVersion': 'Policy version',
  'devices.health.policyAck': 'Policy acknowledged at',
  'devices.health.lastEvent': 'Last event received',
  'devices.health.sequence': 'Last device sequence',
  'devices.health.queue': 'Agent local queue: {events} events',
  'devices.health.queueHelp':
    'The number the Agent reported at its last heartbeat, not a measurement by the system.',
  'devices.health.queueUnknown':
    'Queue depth unknown: this machine has never reported one, so it is left blank rather than written as 0.',
  'devices.health.rejected': 'Events refused in the last 24 hours: {count}',
  'devices.health.rejectedHelp':
    'Events this device landed that were then refused. Events refused at the door never became rows and are not counted here.',
  'devices.health.flags': 'Flags',
  'devices.health.flagsNone': 'No flags.',
  'devices.health.flag.clock_skew': 'Machine clock outside the allowed window',
  'devices.health.flag.sequence_gap': 'A gap in the event sequence',
  'devices.health.flag.sync_error': 'Sync error (the stored verdict)',
  'devices.health.flag.policy_error': 'Policy error (the stored verdict)',
  'devices.health.flagsNever':
    'Two flags — queue backpressure and credential rotation due — are never raised, because nobody has set a threshold for them. A flag raised on a guess is worse than no flag, because somebody acts on it.',

  'devices.approve.title': 'Approve device',
  'devices.approve.body':
    'Only a pending machine can be approved. A machine the clock moved to offline, or one carrying a health diagnosis, would have that verdict overwritten by an approval.',
  'devices.approve.employee': 'Employee (leave blank when the token pre-bound one)',
  'devices.approve.reason': 'Note (optional, recorded in the audit log)',
  'devices.approve.submit': 'Approve device',
  'devices.approve.submitting': 'Approving…',
  'devices.approve.done':
    'Approved. The machine is active; the heartbeat reading keeps its previous answer until the next beat.',

  'devices.revoke.open': 'Revoke device…',
  'devices.revoke.title': 'Revoke {name} — this is what will be destroyed',
  'devices.revoke.lead': 'Read all of it before confirming. Nothing after this step can be undone.',
  'devices.revoke.queueKnown':
    'The local queue is destroyed: {events} events the Agent reported at its last heartbeat, plus everything it has queued since. None of it is recoverable.',
  'devices.revoke.queueEmpty':
    'The local queue is destroyed. The Agent reported 0 events at its last heartbeat, but everything queued since then goes as well.',
  'devices.revoke.queueUnknown':
    'The local queue is destroyed and its size is not known: this machine has never reported a queue depth, so it is left blank rather than written as 0.',
  'devices.revoke.credentials':
    'Every credential the device holds is closed in the same transaction, including one still inside its rotation grace — that graced key is exactly the one an attacker who took the machine before the last rotation is holding.',
  'devices.revoke.terminal':
    'Revocation is final. It cannot be approved back and it cannot be revoked twice. A machine that comes back must enrol as a NEW device on a new registration code, and will carry a different device code.',
  'devices.revoke.keepsIngested':
    'Events already ingested are NOT deleted. Revoking cuts the machine off from this moment onward and nothing else.',
  'devices.revoke.silent':
    'The machine is not told. Its next call gets one 401 that is indistinguishable from an unknown device or a disabled company.',
  'devices.revoke.reason': 'Reason (required, at least {min} characters)',
  'devices.revoke.reasonHint':
    'Written to the device record, to every credential this closes, and to the audit log. Two database constraints demand one.',
  'devices.revoke.handle': 'Type {handle} to confirm',
  'devices.revoke.handleHint':
    'This second field is what keeps one mis-click from killing a machine.',
  'devices.revoke.handleFallback':
    'This machine has never reported a name, so its device code has to be typed — and that is the kind of machine most easily revoked by accident.',
  'devices.revoke.confirm': 'Revoke permanently',
  'devices.revoke.confirming': 'Revoking…',
  'devices.revoke.cancel': 'Cancel',
  'devices.revoke.blocked':
    'The button turns on once both the reason and the typed name are right.',
  'devices.revoke.done':
    'Revoked. Every credential is closed, the local queue is gone, and there is no path back to approved.',

  'devices.tokens.title': 'Registration tokens',
  'devices.tokens.subject': 'the registration tokens',
  'devices.tokens.intro':
    'A registration code is what the installer types into the Agent. The plain text appears exactly once, right after it is created: only a one-way hash is kept, so there is no second read and a lost code means creating another.',
  'devices.tokens.deadRows':
    'This list includes revoked, expired and exhausted tokens. Hiding the dead rows would also hide the revocation from the person who performed it.',
  'devices.tokens.create': 'Mint a registration token',
  'devices.tokens.creating': 'Minting…',
  'devices.tokens.expires': 'Lifetime (hours)',
  'devices.tokens.expiresHint':
    'At most 24 hours. The server takes seconds; this box converts for you.',
  'devices.tokens.maxUses': 'Maximum uses',
  'devices.tokens.employee': 'Employee (optional)',
  'devices.tokens.note': 'Label (optional)',
  'devices.tokens.autoApprove': 'Skip device approval',
  'devices.tokens.autoApproveHelp':
    'This skips the layer that protects a token which leaks on its way to IT, so the server accepts it only with an employee id, exactly one use, and a lifetime of at most 24 hours.',

  'devices.tokens.reveal.title': 'Registration token — this is the only time you will see it',
  'devices.tokens.reveal.warning':
    'Copy it now. Closing this panel, changing screen or reloading the page loses the plain text for good: only a one-way hash is kept and there is no way to read it back.',
  'devices.tokens.reveal.token': 'Token (plaintext)',
  'devices.tokens.reveal.url': 'Enrolment address',
  'devices.tokens.reveal.urlMissing':
    'There is no enrolment address to show because the system’s public address is not configured; ask an administrator. The installer needs it as a separate field — the address deliberately does not contain the code, because a link carrying a credential stays behind in browser history and in the logs of machines along the way.',
  'devices.tokens.reveal.copy': 'Copy token',
  'devices.tokens.reveal.copied': 'Copied.',
  'devices.tokens.reveal.copyFailed':
    'NOT copied: the browser refused. The code is still on screen right here — select it and write it down now, because it will not be shown again.',
  'devices.tokens.reveal.copyUnavailable':
    'NOT copied: this page is not allowed to copy automatically, usually because the connection is not secure. The code is still on screen right here — select it and write it down now, because it will not be shown again.',
  'devices.tokens.reveal.dismiss': 'I have saved the token — close and clear it from the screen',
  'devices.tokens.spent.title': 'Token {prefix} has been shown',
  'devices.tokens.spent.body':
    'The plaintext is no longer anywhere in this browser and no request can bring it back. If it was not saved, mint a new token and revoke this one.',

  'devices.tokens.col.prefix': 'Prefix',
  'devices.tokens.col.label': 'Label',
  'devices.tokens.col.expires': 'Expires',
  'devices.tokens.col.uses': 'Used',
  'devices.tokens.col.state': 'State',
  'devices.tokens.col.action': 'Action',
  'devices.tokens.prefixHelp':
    'These eight characters are drawn from their own random source at mint time and are not a prefix of the secret, which is why a row may show them.',
  'devices.tokens.uses': '{used}/{max}',
  'devices.tokens.state.usable': 'Usable',
  'devices.tokens.state.revoked': 'Revoked',
  'devices.tokens.state.exhausted': 'Exhausted',
  'devices.tokens.empty': 'No registration token has been minted yet.',
  'devices.tokens.revoke': 'Revoke token',
  'devices.tokens.revokeReason': 'Reason for revoking token {prefix}',
  'devices.tokens.revokeSubmit': 'Revoke this token',
  'devices.tokens.revokeDone': 'Token revoked. It can never be redeemed into a device again.',
  'devices.tokens.revokeUsedNote':
    'A token that has already been used part way through can be revoked, and that is the common case for a multi-use token: after this it can never be redeemed again, whatever the used count says.',

  'devices.offboard.title': 'Employee has left — their machines are revoked automatically',
  'devices.offboard.body':
    'When the achau sync sees that somebody is no longer with the company, a revocation of all their devices is scheduled: every machine is revoked, every credential is closed, and each machine gets one audit row. If the person is reinstated before it runs, nothing is touched. The panel below is where you check the result.',
  'devices.offboard.cannotObserve':
    'This screen cannot tell who has left: the device list does not say whether an employee is still employed. So the field below answers “which machines does this person hold”, and never asserts that they have left.',
  'devices.offboard.input': 'The employee who left',
  'devices.offboard.apply': 'List this person’s machines',
  'devices.offboard.none': 'No machine is assigned to this employee id.',
  'devices.offboard.result':
    'The device list is filtered to employee id {employee}. Each machine opens its detail, where revoking still goes through its own two confirmation steps.',
  'devices.offboard.noButton':
    'This panel deliberately carries no revoke button of its own: the decision belongs to a person, and a button here would turn a list into an instruction.',

  /* ═══════════════ WP-K5 (Task 45) — Policies ═══════════════ */

  'policies.intro':
    'This is the screen where somebody decides how closely everyone is watched. Every change makes a new version, and the summary below says whether that change tightens surveillance or loosens it.',
  'policies.subject': 'the website rules',

  'policies.rules.title': 'Private domains and website classification',
  'policies.rules.intro':
    'This is the only part of the monitoring configuration you can both read and change on this screen. Time on a domain marked private still counts as time worked; the domain itself is never recorded.',
  'policies.rules.visible':
    'Employees can read this private list about themselves, and My Activity puts it first. That is deliberate: the alert-rule catalogue is withheld from employees because it tells a watched person what trips an alert; the private list tells them what is never recorded, and a promise the protected person cannot check is not a promise.',
  'policies.rules.col.host': 'Domain',
  'policies.rules.col.privacy': 'Privacy',
  'policies.rules.col.category': 'Category',
  'policies.rules.col.scope': 'Scope',
  'policies.rules.col.version': 'Version',
  'policies.rules.private': 'Private',
  'policies.rules.named': 'Domain name recorded',
  'policies.rules.blacklisted': 'Blocked',
  'policies.rules.empty.title': 'This company has no website rules',
  'policies.rules.empty.body':
    'That is a posture, not a blank: with no rules at all, no domain is marked private and every domain is recorded by name under the default collection level.',
  'policies.rules.truncated':
    'Read {shown} of {total} rules. The summary compares only against the rules on screen.',

  'policies.editor.title': 'Draft a change',
  'policies.editor.host': 'Domain',
  'policies.editor.hostHint':
    'A bare host only: no scheme, path, query or fragment. To cover subdomains, choose the suffix match.',
  'policies.editor.matchField': 'Match',
  'policies.editor.matchField.domain': 'This domain exactly',
  'policies.editor.matchField.domain_suffix': 'This domain and all its subdomains',
  'policies.editor.category': 'Activity category',
  'policies.editor.private': 'Mark private (the domain name is not recorded)',
  'policies.editor.privateHelp':
    'Time on a private domain still counts toward total time worked and lands in no classification column, so nobody is penalised for exercising a privacy right.',
  'policies.editor.blacklist': 'Mark blocked',
  'policies.editor.scope': 'Scope',
  'policies.editor.scopeKey': 'Department id or job title',
  'policies.editor.stage': 'Add to the summary',
  'policies.editor.reset': 'Discard the draft',

  'policies.diff.title': 'Change summary',
  'policies.diff.unsaved': 'Not saved. No request has been sent.',
  'policies.diff.none':
    'The draft changes nothing about the existing rule, so there is no new version to make.',
  'policies.diff.updating': 'This updates the existing rule: version {from} becomes {to}.',
  'policies.diff.creating': 'This creates a new rule for the domain (version 1).',
  'policies.direction.tightens': 'Tightens surveillance',
  'policies.direction.loosens': 'Loosens surveillance',
  'policies.direction.judgement': 'Changes judgement, not what is collected',

  'policies.change.private.on':
    '{host}: privacy turned on — the domain name is no longer recorded, though the time is still counted as time worked.',
  'policies.change.private.off':
    '{host}: privacy turned off — the domain name STARTS being recorded for everyone in scope.',
  'policies.change.blacklist.on': '{host}: marked blocked for everyone in scope.',
  'policies.change.blacklist.off': '{host}: no longer marked blocked.',
  'policies.change.category': '{host}: category changes from {from} to {to}.',
  'policies.change.class': '{host}: productivity class changes from {from} to {to}.',
  'policies.change.new.private':
    '{host}: a new rule, marked private — this domain is recorded by default today and will not be after this.',
  'policies.change.new.named':
    '{host}: a new rule, not marked private. The domain was already recorded under the default collection level, so this changes only how it is judged and collects nothing new.',

  'policies.ack.label': 'I understand this makes the system record more than it does now.',
  'policies.ack.why':
    'This checkbox appears only when the summary contains something that tightens. A warning that fires on correct work is one whoever it blocks learns to click past.',
  'policies.save': 'Save and make a new version',
  'policies.saving': 'Saving…',
  'policies.saved': 'Saved. The rule for {host} is now at version {version}.',
  'policies.refused.title': 'The server refused the save — nothing changed',
  'policies.refused.kept':
    'The previous version is untouched: nothing is applied before the server accepts it, so there is nothing to roll back. The summary below is still what you were about to send.',
  'policies.refused.field': 'The server named the field: {fields}',

  'policies.posture.title': 'Surveillance posture — editable, on the Collection policy screen',
  'policies.posture.body':
    'The collection configuration can now be changed inside the product: read the list, create a draft, edit it and activate it, with the manage-policies permission.',
  'policies.posture.where':
    'Five of the seven fields above are edited on the Collection policy screen, alongside the count of machines that have acknowledged the version in force:',
  'policies.posture.notEditable':
    'The other two cannot be changed anywhere. Keystroke logging is refused at every layer, so no screen can turn it on. The title-masking patterns need a tool of their own, for the reason printed under that field.',
  'policies.posture.noControl':
    'This panel still carries no input, and deliberately not a disabled button either: the place to edit them is that screen, and a second copy of the same form is a second copy to fall out of step.',
  'policies.posture.noValues':
    'The current values are still not printed here, and that is a decision rather than an oversight: this panel reads no policy, the Collection policy screen reads them from the server, and printing a default in their place would be this screen inventing a company surveillance posture.',
  'policies.posture.field.level': 'Collection level',
  'policies.posture.field.idle': 'Short and extended idle thresholds',
  'policies.posture.field.windowTitle': 'Whether window titles are collected',
  'policies.posture.field.offHours': 'Whether off-hours activity is collected',
  'policies.posture.field.screenshot': 'Screenshots',
  'policies.posture.field.keylog': 'Keystroke logging',
  'policies.posture.field.redaction': 'Title-masking patterns',
  'policies.posture.redaction':
    'The title-masking patterns are written by the company administrator and run over every title collected, so each one is checked in advance: at most 200 characters, and it must be written correctly.',
  'policies.posture.failClosed':
    'This check fails closed: ONE unsafe pattern means that policy keeps no window or page titles at all, for the whole company. That is why this field needs a tool of its own.',
  'policies.posture.lawfulBasis.title': 'Lawful basis for monitoring',
  'policies.posture.lawfulBasis.body':
    'Desktop monitoring and GPS location rest on the ENUMERATED grounds of the Personal Data Protection Law (Law 91/2025/QH15, in force 2026-01-01) and Decree 356/2025/NĐ-CP: performance of the labour contract, and compliance with the employer’s Labour Code timekeeping and wage-record obligations. The purpose must be specific per monitoring activity; one blanket consent to a “monitoring policy” is not enough.',
  'policies.posture.lawfulBasis.location':
    'Location is sensitive personal data and carries a mandatory opt-out: the employee switches it on from the My Data screen, and coordinates stop being stored immediately, independently of the mobile app.',
  'policies.posture.lawfulBasis.document':
    'Full text: “Cơ sở pháp lý của việc giám sát”; impact assessment: “Đánh giá tác động bảo vệ dữ liệu”.',

  'policies.version.title': 'Policy version and device acknowledgement',
  'policies.version.body':
    'A policy is versioned and each device acknowledges which version it applied. Events from a device on an older version are accepted and re-judged under the current policy — refusing them would delete data from every device that had not polled yet, and accepting them without re-applying the policy would make the server-side pass useless in exactly the case it exists for.',
  'policies.version.where':
    'The version each machine holds, and when it acknowledged, are on the Devices screen in each machine health panel.',
  'policies.version.link': 'Open the Devices screen',

  /* ─── G9 / D-37: is this score still the number its inputs imply? ─────── */

  'score.freshness.current': 'Inputs unchanged',
  'score.freshness.current.help':
    'Every input this score was computed from is older than the score itself. Nothing has moved since it ran.',
  'score.freshness.stillReal':
    'The number below is real and it is still yours — it is not hidden, not zeroed, and you may still question it. This is NOT an error and NOT missing data: confidence and completeness describe what the run could see, while this says the inputs have moved SINCE it ran.',
  'score.freshness.scheduled.title': 'Inputs have changed — it will be recomputed automatically',
  'score.freshness.scheduled.body':
    'This period is inside the window the hourly sweep scans, so it will be recomputed without anybody asking. Nothing to do.',
  'score.freshness.manual.title': 'Inputs have changed — it will NOT be recomputed on its own',
  'score.freshness.manual.body':
    'This period is older than the window the hourly recalculation reaches, so the number stays as it is until somebody asks for this period to be recalculated.',
  'score.freshness.manual.nobody':
    'Said plainly, because this is the easiest thing to misread: NO run is coming. Waiting will not change this number. Ask a manager or operations to request a recalculation.',
  'score.freshness.scheduled.tag': 'Will recompute itself',
  'score.freshness.manual.tag': 'No run is coming',
  'score.freshness.inputs.heading': 'The inputs that moved:',
  'score.freshness.inputs.unnamed':
    'An input changed that this screen has no name for, so it is reported as changed rather than dropped.',
  'score.freshness.input.algorithm':
    'Scoring algorithm version (this record was produced by a different version)',
  'score.freshness.input.dailyAggregate': 'The daily rollup',
  'score.freshness.input.attendanceSession':
    'The attendance session (a manual attendance entry, or a correction approved or rejected, rebuilds that day’s session)',
  'score.freshness.input.timeEntry': 'A time entry over the period',
  'score.freshness.unknown.title': 'Cannot tell whether this number is up to date',
  'score.freshness.unknown.absent':
    'No freshness information came back, although it is required. This screen deliberately does NOT read that absence as “up to date”: reading it that way is the very defect the information exists to prevent.',
  'score.freshness.unknown.malformed':
    'The freshness information has a shape this screen cannot read. It does not guess.',
  'score.freshness.unknown.contradictory':
    'The freshness information contradicts itself (for example, reporting stale while saying no recalculation is needed). Believing either half would be this screen inventing an answer about your score.',

  /* ══════════════════ WP-L5 (Task 53) — alerts, incidents, data risk ═══════ */

  'common.cancel': 'Cancel',
  'filters.all': 'All',
  'filters.selected': '(selected)',

  'access.title': 'Access reason',
  'access.why':
    'Reading {subject} is sensitive access. Every read is recorded with your name, the employees on the page, and the reason you type below.',
  'access.recorded':
    'A reason for access is mandatory and no setting can turn it off. So this screen sends no request at all before you type one.',
  'access.label': 'Reason',
  'access.rule': 'At least {min} characters after trimming.',
  'access.latin1':
    'This reason contains Vietnamese diacritics, which this field cannot carry yet, so the request was not sent. Please type it without diacritics. This gap has been reported to the engineering team.',
  'access.open': 'Open {subject}',
  'access.change': 'Change the reason and re-read',
  'access.current': 'Reason in force: “{reason}”',
  'access.closed.title': 'Nothing has been read',
  'access.closed.body':
    'No request has been sent to the server. Type an access reason above to open {subject}.',

  'alerts.intro':
    'The alert queue: what has fired that nobody has signed for, and which of it is worst. This is not a log viewer.',
  'alerts.subjectWord': 'the alert inbox',
  'alerts.scopeNote':
    'The resolve-incidents permission admits you to this screen but does not widen your field of view: you still see only the scope you are allowed to see. A department-level reader sees no alert that is not attached to an employee in their branch, including machine alerts. A short queue is not a quiet week.',
  'alerts.filter.severity': 'Severity',
  'alerts.filter.status': 'Status',
  'alerts.filter.serverSide':
    'Every filter here is applied across the whole data set, one value per field. The list loads page by page and carries no total, so filtering only the page in view would give a number with nothing to check it against.',
  'alerts.status.open': 'Unacknowledged',
  'alerts.status.acknowledged': 'Acknowledged',
  'alerts.status.suppressed': 'Suppressed duplicate',
  'alerts.subject': 'Subject: {key}',
  'alerts.subject.withheld':
    'Subject: withheld (the group key names something you may not see — the whole key is replaced, not trimmed)',
  'alerts.occurrences': '{count} firings',
  'alerts.measured': 'Measured: {value}',
  'alerts.measured.withheld': 'Measured: no figure — the server published none',
  'alerts.threshold': 'Threshold: {value}',
  'alerts.threshold.withheld': 'Threshold: no figure',
  'alerts.redacted':
    'At least one detail key was withheld from this caller. The row itself is returned intact so the counts stay honest.',
  'alerts.hasIncident': 'Incident #{id} was opened from this alert.',
  'alerts.read.newest': 'Newest alert among what was read: {when}',
  'alerts.read.none': 'No alerts were read, so there is no instant to report.',
  'alerts.partial.excludes':
    'everything older than the {count} rows read — the stream continues past this page',
  'alerts.empty.title': 'No alerts match this filter',
  'alerts.empty.body':
    'The filter is severity “{severity}”, status “{status}”. This is the answer to that filter, not to the company.',
  'alerts.ack.open': 'Acknowledge',
  'alerts.ack.title': 'Acknowledge alert #{id}',
  'alerts.ack.fact.irreversible':
    'It cannot be undone: no un-acknowledging, no reassigning, no correcting the note.',
  'alerts.ack.fact.noop':
    'Pressing it again is a 200 no-op returning the original actor and instant. So the button is safe to press twice, and a second press does not overwrite the first person’s signature.',
  'alerts.ack.fact.cooldown':
    'Acknowledging starts the rule’s cooldown_minutes, from acknowledged_at. Firings inside it are recorded as suppressed occurrences — suppression is NOT deletion.',
  'alerts.ack.fact.recurrence':
    'After the cooldown, the next firing is a NEW alert with its own id and its own acknowledgement — never a re-open of this one.',
  'alerts.ack.fact.audit':
    'The note is stored word for word with the alert and becomes the reason on the audit log row.',
  'alerts.ack.note': 'Note (optional — the server allows an empty one)',
  'alerts.ack.submit': 'Acknowledge',
  'alerts.ack.submitting': 'Sending…',
  'alerts.ack.ok': 'Acknowledged at {when}.',
  'alerts.ack.okBody':
    'This cannot be undone. The next firing after the cooldown will be a new alert.',
  'alerts.ack.done': 'Acknowledged by {who} at {when}.',
  'alerts.ack.unknownActor': 'an unnamed actor',
  'alerts.ack.suppressed':
    'This is a duplicate of another alert. Acknowledging it would be refused and would leave the real alert open — acknowledge the one it duplicates.',
  'alerts.ack.readOnly':
    'Acknowledging needs resolve_incidents. This account can read but cannot decide.',

  'incidents.intro': 'Open incidents: what you may do next, and how long you have.',
  'incidents.subjectWord': 'the incident queue',
  'incidents.detail.subject': 'the incident detail',
  'incidents.scopeNote':
    'The resolve-incidents permission admits you to this screen but does not widen your field of view: you still see only the scope you are allowed to see. A department-level reader sees no incident that is not attached to an employee in their branch.',
  'incidents.filter.state': 'State',
  'incidents.open': 'Open',
  'incidents.empty.title': 'No incidents match this filter',
  'incidents.empty.body':
    'The state filter is “{state}”. This is the answer to that filter, not “the company has no incidents”.',
  'incidents.partial.excludes': 'the rest of the queue — showing {shown} of {total}',
  'incidents.row.breached': 'SLA BREACHED',
  'incidents.row.due': 'SLA due: {when}',
  'incidents.row.noSla': 'No SLA deadline for this severity',
  'incidents.detail.title': 'Incident #{number} — {title}',

  'incidents.state.new': 'New',
  'incidents.state.acknowledged': 'Acknowledged',
  'incidents.state.investigating': 'Investigating',
  'incidents.state.waitingForEmployee': 'Waiting for employee',
  'incidents.state.resolved': 'Resolved',
  'incidents.state.falsePositive': 'False positive',
  'incidents.state.escalated': 'Escalated',
  'incidents.state.closed': 'Closed',

  'incidents.move.new': 'Move back to New',
  'incidents.move.acknowledged': 'Acknowledge',
  'incidents.move.investigating': 'Move to investigating',
  'incidents.move.waitingForEmployee': 'Wait for the employee',
  'incidents.move.resolved': 'Conclude: resolved',
  'incidents.move.falsePositive': 'Conclude: false positive',
  'incidents.move.escalated': 'Escalate',
  'incidents.move.closed': 'Close the file',

  'incidents.transition.title': 'Transition',
  'incidents.transition.derived':
    'These are all {count} legal moves from “{state}”, taken from the server’s own transition table. The illegal ones are NOT rendered as greyed buttons — they do not exist on this screen at all.',
  'incidents.transition.terminal.title': 'Closed — there are no moves',
  'incidents.transition.terminal.body':
    'closed is terminal and cannot be reopened. A queue whose terminal state is not terminal is one nobody can count. If the condition recurs, open a NEW incident.',
  'incidents.transition.form': 'Move to: {to}',
  'incidents.transition.noteLabel': 'Note (required)',
  'incidents.transition.noteRule':
    'At least {min} characters. The note becomes the body of this move’s history row — a step nobody explained is a step in an incident’s life with no author.',
  'incidents.transition.resolutionLabel': 'Conclusion (required)',
  'incidents.transition.resolutionRule':
    'All three concluding states require a conclusion. Without one, the change is refused.',
  'incidents.transition.submit': 'Send',
  'incidents.transition.submitting': 'Sending…',
  'incidents.transition.ok': 'Moved to “{state}”.',
  'incidents.transition.serverAllowed': 'The server says the legal moves right now are: {list}',

  'incidents.reopen.title': 'Reopen — two things to know first',
  'incidents.reopen.clears':
    'A reopen CLEARS the current conclusion (resolution, resolution_code, resolved_at, resolved_by, verified_risk). The conclusion is not lost: it is in the history row written when it was concluded.',
  'incidents.reopen.sla':
    'A reopen does NOT grant a fresh SLA. The original deadline and any breach are kept — otherwise resolving and reopening would turn a missed deadline into a met one.',

  'incidents.sla.due.title': 'SLA — within the deadline',
  'incidents.sla.breached.title': 'SLA — BREACHED',
  'incidents.sla.concludedLate.title': 'SLA — concluded after the deadline',
  'incidents.sla.none.title': 'SLA — no deadline for this severity',
  'incidents.sla.none.body':
    'This company sets no deadline for this severity. “No deadline” is not “met the deadline”, and it is certainly not a deadline of zero.',
  'incidents.sla.due': 'Due: {when}',
  'incidents.sla.remaining': 'Remaining: {interval}',
  'incidents.sla.overdue': 'Overdue by: {interval}',
  'incidents.sla.concludedLate':
    'Concluded {interval} after the deadline — the server does not count that as a breach.',
  'incidents.sla.interval': '{hours} h {minutes} min',
  'incidents.sla.interval.unknown': 'not measurable',
  'incidents.sla.notRestarted':
    'The clock did NOT restart. The deadline above is the original one: a reopen keeps sla_due_at and keeps the breach, so this incident was overdue the moment it reopened.',
  'incidents.sla.arithmetic':
    'Due = created_at + the hours for this severity, from the active default policy. A severity change recomputes from created_at rather than from the moment of the change, so re-grading buys no time in either direction. This screen prints the server’s figure and does not compute its own.',

  'incidents.assign.title': 'Owner and severity',
  'incidents.assign.owner': 'Owner (employee code)',
  'incidents.assign.ownerNote':
    'The Workforce Pulse employee id — the first column on the People screen, not an achau account id.',
  'incidents.assign.severity': 'Severity',
  'incidents.assign.regrade':
    'Changing the severity recomputes the SLA deadline FROM created_at, not from now. An incident opened long ago is overdue the instant it is upgraded to critical — and that is the right answer.',
  'incidents.assign.save': 'Save',
  'incidents.assign.saving': 'Saving…',
  'incidents.assign.saved': 'Saved.',
  'incidents.assign.kept':
    'Nothing is applied before the server accepts it, so there is nothing to roll back. What you typed is still above, untouched.',
  'incidents.assign.conflict':
    'Somebody else changed this incident while you were typing (current version: {etag}). The screen has re-read it; look at their change and then decide. It deliberately does not resend on its own, because that would overwrite what was just saved.',
  'incidents.assign.noEtag.title': 'The change cannot be sent',
  'incidents.assign.noEtag.body':
    'This read did not carry the record version, and saving without it could overwrite somebody else’s change. So there is no control here; this is not a problem with your permissions. Reload the incident.',

  'incidents.evidence.title': 'Evidence',
  'incidents.evidence.refsOnly':
    'References only. The evidence sits in the company’s storage and cannot be opened from the browser yet, so the reference is printed rather than turned into a dead link.',
  'incidents.evidence.redacted':
    'An evidence entry was withheld from this caller — specifically the alert’s group key, where it names an application or a domain you lack view_apps / view_websites for. The whole entry is dropped rather than trimmed.',
  'incidents.evidence.noCapturedAt': 'no capture instant published',
  'incidents.evidence.empty.title': 'No evidence entries',
  'incidents.evidence.empty.body':
    'A manually opened incident carries no references. An auto-created one carries two: the alert itself, and the group key naming the subject.',
  'incidents.evidence.kind.activityEvent': 'Activity event',
  'incidents.evidence.kind.timeEntry': 'Time entry',
  'incidents.evidence.kind.attendanceDay': 'Attendance day',
  'incidents.evidence.kind.alert': 'Alert',
  'incidents.evidence.kind.file': 'File',
  'incidents.evidence.kind.note': 'Note',

  'incidents.timeline.title': 'Timeline',
  'incidents.timeline.intro':
    'The signed record of notes, oldest first, with the move each row records. A CONSTRAINT TRIGGER refuses to commit a status change that does not write the from/to pair, so the history here is complete.',
  'incidents.timeline.transition': 'Moved: {from} → {to}',
  'incidents.timeline.transitionUnknown':
    'Moved: {from} → {to} (the screen has no name for one of the two states)',
  'incidents.timeline.noTransition': 'This row records no state change.',
  'incidents.timeline.halfPair':
    'This row is missing half of a transition (from {from} to {to}). It cannot be read as a note and cannot be read as a move — tell an administrator.',
  'incidents.timeline.notRead': 'Not read — the list representation carries no comments.',
  'incidents.timeline.empty.title': 'Nothing on the record yet',
  'incidents.timeline.empty.body': 'Nobody has written anything and no move has been made.',
  'incidents.timeline.system': 'the system',
  'incidents.timeline.author': 'employee #{id}',

  'incidents.noDelete.title': 'There is no delete — and the database is what refuses',
  'incidents.noDelete.body':
    'There is no way to delete an incident, at any severity and through any screen. An incident leaves the system only when its retention period ends.',
  'incidents.noDelete.instead':
    'So there is no delete control here, and no greyed one either. An incident opened by mistake is moved to “False positive” with a conclusion, and it stays on the record.',
  'incidents.unknownState.title': 'This screen does not recognise that state',
  'incidents.unknownState.body':
    'The server returned state “{state}”, which the client’s transition table has no row for. The screen does not guess: no button is drawn. Update the client.',

  'dataRisk.intro': 'Has company data left, or nearly left — and is anybody already on it?',
  'dataRisk.subjectWord': 'the data risk page',
  'dataRisk.tiles.title': 'The four data-risk triggers',
  'dataRisk.tiles.counted':
    'Each tile counts what this page read, not the company total: the alert list loads page by page and carries no total.',
  'dataRisk.tile.read': '{count} alerts read',
  'dataRisk.tile.unacknowledged': 'Nobody has acknowledged: {count}',
  'dataRisk.tile.noneRead': 'No alerts among what was read.',
  'dataRisk.tile.link': 'Open {label} in Alerts',
  'dataRisk.partial.excludes': 'the rest of the alert stream — this page did not read it all',

  'dataRisk.domains.title': 'Blocked domains',
  'dataRisk.domains.subject': 'the domain rollup',
  'dataRisk.domains.window': 'Date range: {from} → {to}',
  'dataRisk.domains.counted':
    'The blocked filter applies only to the page in view: {matched} matched out of the {page} rows read, from {total} in total.',
  'dataRisk.domains.col.domain': 'Domain',
  'dataRisk.domains.col.seconds': 'Seconds',
  'dataRisk.domains.col.visits': 'Visits',
  'dataRisk.domains.col.employees': 'Employees',
  'dataRisk.domains.withheld': 'withheld',
  'dataRisk.domains.noEmployeeCount': 'not published',
  'dataRisk.domains.empty.title': 'No blocked domains in the page that was read',
  'dataRisk.domains.empty.body':
    'That is the answer for the page in view, not for the whole date range.',

  'dataRisk.absent.title': 'What this page cannot answer yet',
  'dataRisk.absent.body':
    'The items below are what a Data Risk page needs and does not have data for yet. They are named rather than replaced with invented figures, because a silent page reads as “nothing happened”.',
  'dataRisk.absent.usb':
    'USB events: there is no separate data source for them yet. The only USB signal this page can see is the alert for an unapproved USB device.',
  'dataRisk.absent.file':
    'File operations: there is no separate data source for them either. The only signal is the alert for a bulk file operation.',
  'dataRisk.absent.blockedSeconds':
    'Blocked time per employee: this figure is not published to any screen yet.',
  'dataRisk.absent.visits':
    'Blocked visits per employee: website figures are grouped by domain and classification with a headcount, never per person.',
  'dataRisk.absent.category':
    'Incident category: incidents do not carry a data-risk label yet, so they cannot be filtered down to it. This page reaches an incident only through the alert that opened it.',
  'dataRisk.absent.noControls':
    'This section has no controls, and deliberately no greyed ones either: a grey button says “this can be done, but not by you”, and here nobody can do it yet.',

  'trigger.extendedIdle': 'Extended idle',
  'trigger.noActiveTask': 'No active task',
  'trigger.estimateExceeded': 'Estimate exceeded',
  'trigger.productivityDrop': 'Productivity drop',
  'trigger.workloadOverload': 'Overloaded',
  'trigger.workloadUnderload': 'Underloaded',
  'trigger.lateArrival': 'Late arrival',
  'trigger.earlyLeave': 'Early leave',
  'trigger.missingHours': 'Missing hours',
  'trigger.agentOffline': 'Agent offline',
  'trigger.agentStopped': 'Agent stopped',
  'trigger.agentUninstalled': 'Agent uninstalled',
  'trigger.unknownDevice': 'Unknown device',
  'trigger.usbUnapproved': 'Unapproved USB',
  'trigger.bulkFileOperation': 'Bulk file operation',
  'trigger.blockedApp': 'Blocked application',
  'trigger.blockedWebsite': 'Blocked website',
  'trigger.afterHoursActivity': 'After-hours activity',

  /* ── WP-K8 (Task 48) — Reports ────────────────────────────────────────── */

  'k8.reports.intro':
    'Export data as a file. Every request runs in the background; this page says when it is ready and provides a download link once there is one.',

  'k8.reports.create.title': 'Request a new report',
  'k8.reports.create.type': 'Report type',
  'k8.reports.create.format': 'Format',
  'k8.reports.create.from': 'From',
  'k8.reports.create.to': 'To',
  'k8.reports.create.employees': 'Narrow to employees (employee codes, comma separated)',
  'k8.reports.create.employeesHint':
    'Left empty means the whole scope you are allowed to see — which is different from an empty list. Employee ids are the first column on the People screen.',
  'k8.reports.create.department': 'Narrow to a department (department code)',
  'k8.reports.create.submit': 'Create report',
  'k8.reports.create.submitting': 'Sending the request…',
  'k8.reports.create.builderNote':
    '5 of the 13 report types can produce a file. The other 8 are refused rather than generated empty: an empty attendance file handed to payroll is worse than a refusal.',
  'k8.reports.create.unbuilt':
    'This report type cannot produce a file yet — the request will be refused.',
  'k8.reports.create.productivityNote':
    'Productivity figures in the file are the STORED scores as at the source table, not recomputed at export time. A file generated right after a correction is approved still carries the pre-correction number.',
  'k8.reports.create.periodInvalid':
    'Both dates are needed, and the start must not be after the end.',

  'k8.reports.type.attendance': 'Attendance',
  'k8.reports.type.shift_compliance': 'Shift compliance',
  'k8.reports.type.active_idle': 'Active / idle',
  'k8.reports.type.app_usage': 'Application usage',
  'k8.reports.type.website_usage': 'Website usage',
  'k8.reports.type.task_timer': 'Task timer',
  'k8.reports.type.estimate_vs_actual': 'Estimate vs actual',
  'k8.reports.type.productivity': 'Productivity',
  'k8.reports.type.workload': 'Workload',
  'k8.reports.type.usb_file_events': 'USB and file events',
  'k8.reports.type.alerts_incidents': 'Alerts and incidents',
  'k8.reports.type.device_health': 'Device health',
  'k8.reports.type.audit_log': 'Audit log',

  'k8.reports.list.title': 'Reports this company has asked for',
  'k8.reports.list.companyWide':
    'This is the export queue for the whole company, not only the reports you requested. Every download is re-checked against your current scope, so a row listed here can still be refused when you press Download.',
  'k8.reports.list.empty.title': 'This company has exported nothing',
  'k8.reports.list.empty.body': 'No report matches the filter in force.',
  'k8.reports.list.partial': 'Showing {shown} of {total} reports.',

  'k8.reports.status.pending': 'Pending',
  'k8.reports.status.running': 'Generating',
  'k8.reports.status.done': 'Ready',
  'k8.reports.status.failed': 'Failed',
  'k8.reports.status.expired': 'Retention expired',

  'k8.reports.row.period': 'Period',
  'k8.reports.row.requested': 'Requested at',
  'k8.reports.row.completed': 'Completed at',
  'k8.reports.row.rows': 'Rows',
  'k8.reports.row.size': 'Size (bytes)',
  'k8.reports.row.scope': 'Employees in the frozen scope',
  'k8.reports.row.downloads': 'Signed links minted',
  'k8.reports.row.failure': 'Failure code',
  'k8.reports.row.heading': 'Report {id} — {type} ({format})',
  'k8.reports.row.periodAbsent': 'the server recorded no period on this row',

  'k8.reports.download': 'Download',
  'k8.reports.downloading': 'Requesting a link…',
  'k8.reports.download.granted':
    'A link for {filename} was minted. It stops being accepted at {expiresAt} and was spent the moment it was issued.',
  'k8.reports.download.grantedNoName':
    'A link was minted. It stops being accepted at {expiresAt} and was spent the moment it was issued.',
  'k8.reports.download.ttlNote':
    'A download link is a bearer credential: S3 re-checks no permission, only a signature and a 180-second expiry (SEC-48). This page holds NO link — every press runs the full authorisation chain again, mints a fresh one, and counts it into download_count. So no link on this page can expire while you are looking at it.',
  'k8.reports.download.stale':
    'The status on screen was out of date. This row has just been re-read.',
  'k8.reports.download.reasonNote':
    'Every download is recorded with the file’s real row count and the employees in the frozen scope. This action does not require a reason for access, so the screen asks for none: collecting a reason that is not needed would be collecting it under false pretences.',

  'k8.reports.noControl.pending':
    'There is no file yet, so there is no download control. The page re-reads until the file is ready.',
  'k8.reports.noControl.failed':
    'No file was produced, so there is nothing to collect. Request it again in the form above.',
  'k8.reports.noControl.expired':
    'The file was removed by retention. Nothing anybody presses brings it back; request a new one.',

  'k8.reports.absence.title': 'An empty cell does not exist in the file',
  'k8.reports.absence.body':
    'A spreadsheet reads an empty cell as 0. So wherever there is no measurement the file prints one of the four labels below as text, and a sum skips it instead of adding a zero. If you meet one, do not replace it with a number.',
  'k8.reports.absence.not_computed':
    'No aggregate row exists for that employee and date. NOT a measurement of zero.',
  'k8.reports.absence.withheld_private':
    'Time WAS worked and IS in the totals. What it was spent on is withheld by the privacy policy, so no name is shown.',
  'k8.reports.absence.data_incomplete':
    'The day exists but could not be attested (agent offline, partial data). Not an absence and not a full shift.',
  'k8.reports.absence.not_applicable':
    'The column has no meaning for this row — no shift, no formula, or no timer.',
  'k8.reports.figure.absent': '{sentinel} — {meaning}',

  'k8.reports.absent.title': 'What this screen cannot do yet',
  'k8.reports.absent.body':
    'The two items below are still missing, so they are named rather than mocked.',
  'k8.reports.absent.noScheduleList':
    'There is no list of report schedules yet: a schedule can only be reopened if you noted its code, so the schedules a company is running cannot be browsed.',
  'k8.reports.absent.noDirectory':
    'Employees, departments and recipients on this page are still typed as ids: a report takes several at once, and a one-at-a-time picker cannot say that. The ids are the first column on the People screen.',
  'k8.reports.absent.noControls':
    'There is no control in this block, and no greyed one either: a grey button says “this works, but not for you”, and the truth is that nobody can do it yet.',

  'k8.sched.title': 'Recurring report schedule',
  'k8.sched.intro':
    'A schedule generates files on a cycle and mails the recipients. The mail carries only a link back to this screen, never an attachment: a mailbox is a permanent copy that cannot be recalled.',
  'k8.sched.name': 'Schedule name',
  'k8.sched.type': 'Report type',
  'k8.sched.formats': 'Formats (at least one)',
  'k8.sched.frequency': 'Frequency',
  'k8.sched.dayOfWeek': 'Day of week',
  'k8.sched.dayOfMonth': 'Day of month',
  'k8.sched.dayOfMonthHint':
    '28 at most. Days 29–31 are REFUSED rather than moved: "monthly on the 31st" silently falling back to the 28th in February would shift a payroll report by up to three days four times a year with the row still saying 31.',
  'k8.sched.hour': 'Hour',
  'k8.sched.minute': 'Minute',
  'k8.sched.timezone': 'Time zone (empty inherits the company zone)',
  'k8.sched.localTime':
    'The run time is LOCAL wall clock in that zone, on both DST days: an 08:00 schedule fires at 08:00 local on both sides, even though the two runs are 23 or 25 hours apart.',
  'k8.sched.period': 'Which days each run reports on',
  'k8.sched.recipients': 'Recipients (employee codes, comma separated)',
  'k8.sched.recipientsHint':
    'These are employee codes inside this company; free-form email addresses are not accepted, because a schedule that took them would be a standing data-export channel to any mailbox. An employee who has left is refused too.',
  'k8.sched.enabled': 'Enable it immediately',
  'k8.sched.submit': 'Create schedule',
  'k8.sched.submitting': 'Creating the schedule…',
  'k8.sched.created': 'Schedule "{name}" created. Next run: {nextRunAt}.',
  'k8.sched.createdId':
    'The schedule you just created is open in “Manage one schedule” below. Note the code {id} down anyway: there is no list of schedules, so the code is the only way back to this record after a reload.',

  /* ── Task U19 — one schedule over GET/PATCH/DELETE /report-schedules/{id} ── */

  'k8.sched.manage.title': 'Manage one schedule',
  'k8.sched.manage.intro':
    'There is no list of schedules, so this block works on one code: the schedule you just created, or a code you wrote down.',
  'k8.sched.manage.idLabel': 'Schedule id',
  'k8.sched.manage.read': 'Read the schedule',
  'k8.sched.manage.reading': 'Reading…',
  'k8.sched.manage.subject': 'recurring report schedule',
  'k8.sched.manage.state.enabled': 'Enabled — it will run at the next occurrence',
  'k8.sched.manage.state.paused': 'Paused — the record is still here and does not run',
  'k8.sched.manage.nextRun': 'Next run',
  'k8.sched.manage.lastRun': 'Last run',
  'k8.sched.manage.lastStatus': 'Last outcome',
  'k8.sched.manage.failures': 'Consecutive failures',
  'k8.sched.manage.recipients': 'Recipients',
  'k8.sched.manage.neverRun': 'has never run',
  'k8.sched.manage.inFlight':
    'in flight — the outcome columns are cleared at enqueue so a retry does not still show last week success',
  'k8.sched.manage.status.success': 'success',
  'k8.sched.manage.status.failed': 'failed',
  'k8.sched.manage.status.skipped': 'skipped',
  'k8.sched.manage.pause': 'Pause the schedule',
  'k8.sched.manage.resume': 'Resume the schedule',
  'k8.sched.manage.toggling': 'Sending the PATCH…',
  'k8.sched.manage.pauseNote':
    'Pausing keeps the record whole: recipients, run time and next run are all kept, and it can be switched back on.',
  'k8.sched.manage.noEtag':
    'This read did not carry the record version, and saving without it could overwrite somebody else’s change. So there is no pause control yet; read the schedule again.',
  'k8.sched.manage.delete': 'Remove this schedule',
  'k8.sched.manage.deleting': 'Sending the DELETE…',
  'k8.sched.manage.deleteNote':
    'Warning: deleting a schedule removes it outright rather than marking it ended, and there is nothing to switch back on. The files it already produced still download, but lose the link back to the schedule; what remains is the audit log row. To stop it temporarily, use “Pause the schedule”.',
  'k8.sched.manage.deleted.title': 'Schedule {id} has been deleted',
  'k8.sched.manage.deleted.body':
    'The schedule has been deleted and the screen re-read it to confirm: no record is left. This is a deletion, not an ending — it does not come back. The reports it already produced still download as before.',
  'k8.sched.manage.deleted.pending': 'Re-reading to confirm…',
  'k8.sched.manage.deleted.unconfirmed':
    'The deletion was accepted, but the re-read still found the record, so this screen cannot confirm it is gone. Press “Read the schedule” again on the same code.',

  'k8.sched.freq.daily': 'Daily',
  'k8.sched.freq.weekly': 'Weekly',
  'k8.sched.freq.monthly': 'Monthly',
  'k8.sched.dow.1': 'Monday',
  'k8.sched.dow.2': 'Tuesday',
  'k8.sched.dow.3': 'Wednesday',
  'k8.sched.dow.4': 'Thursday',
  'k8.sched.dow.5': 'Friday',
  'k8.sched.dow.6': 'Saturday',
  'k8.sched.dow.7': 'Sunday',
  'k8.sched.period.previous_day': 'The previous day',
  'k8.sched.period.previous_week': 'The previous week',
  'k8.sched.period.previous_month': 'The previous month',
  'k8.sched.period.rolling_7d': 'The last 7 days',
  'k8.sched.period.rolling_30d': 'The last 30 days',
  'k8.sched.periodNote':
    'Every one of these ends YESTERDAY. A report that included the day it runs on would present a partial day as a whole one.',

  'k8.sched.problem.name.length': 'The name must be 1 to 120 characters.',
  'k8.sched.problem.name.line_break':
    'The name may not contain a line break: it leads the mail subject, and a mail subject ends at a line break.',
  'k8.sched.problem.formats.count': 'Choose 1 to 3 formats.',
  'k8.sched.problem.formats.duplicate':
    'Do not repeat a format: two files in the same format overwrite each other.',
  'k8.sched.problem.day_of_week.range': 'Choose a day of the week (Monday to Sunday).',
  'k8.sched.problem.day_of_month.range': 'The day of month must be 1 to 28.',
  'k8.sched.problem.hour_of_day.range': 'The hour must be 0 to 23.',
  'k8.sched.problem.minute_of_hour.range': 'The minute must be 0 to 59.',
  'k8.sched.problem.recipients.count': 'Between 1 and 50 employee codes are needed.',

  'k8.sched.scopeNote.title': 'A recurring schedule is a company-scope administrative act',
  'k8.sched.scopeNote.body':
    'Creating a schedule requires company-wide scope, deliberately stricter than creating a single report, because a schedule names recipients: somebody who can see only their own data could otherwise arrange for data to be mailed to a list of colleagues on a timer. This session is at {level} scope, so the create form and the manage block are not shown.',

  /* ── WP-K8 (Task 48) — Audit Log ──────────────────────────────────────── */

  'k8.audit.intro':
    'Two kinds of append-only record: administrative actions, and reads of sensitive data. Neither can be deleted or edited — that is deliberate, not an oversight.',
  'k8.audit.subjectWord': 'the audit log',
  'k8.audit.selfLogged':
    'This read is itself a sensitive access: it is recorded with the reason you gave above, and the next auditor will read it.',

  'k8.audit.filters.title': 'Filters',
  'k8.audit.filters.serverSide':
    'Every field below filters the whole log, not just the page in view. The log loads page by page and carries no total, so filtering the page alone would give a number with nothing to check it against.',
  'k8.audit.filters.logType': 'Table',
  'k8.audit.filters.logType.any': 'Both',
  'k8.audit.filters.logType.audit': 'Administrative actions',
  'k8.audit.filters.logType.data_access': 'Data reads',
  'k8.audit.filters.actor': 'Who acted (employee code)',
  'k8.audit.filters.action': 'Action',
  'k8.audit.filters.actionHint':
    'An EXACT match on the verb, for example device.revoke — not a substring search. Typing "device" returns nothing, and that does not mean no device action was audited.',
  'k8.audit.filters.resourceType': 'Resource type',
  'k8.audit.filters.resourceId': 'Resource id',
  'k8.audit.filters.from': 'From (RFC3339 UTC)',
  'k8.audit.filters.to': 'To (RFC3339 UTC)',
  'k8.audit.filters.success': 'Outcome',
  'k8.audit.filters.success.any': 'Both',
  'k8.audit.filters.success.true': 'Successes only',
  'k8.audit.filters.success.false': 'Failures only',
  'k8.audit.filters.apply': 'Apply filters',
  'k8.audit.filters.clear': 'Clear filters',

  'k8.audit.results.title': 'Results',
  'k8.audit.results.count': '{count} row(s) read on this page.',
  'k8.audit.results.noTotal':
    'The log loads page by page and carries no total, so the number above is “what this page read”, not “how many events the company has”.',
  'k8.audit.results.next': 'Next page',
  'k8.audit.empty.narrowed.title': 'No row matches these filters',
  'k8.audit.empty.narrowed.body':
    'That is a statement about the filters in force, not about the log. Widen them and read again.',
  'k8.audit.empty.wide.title': 'No row on this page',
  'k8.audit.empty.wide.body': 'No filter is set and the server still returned an empty page.',

  'k8.audit.col.when': 'When',
  'k8.audit.col.type': 'Table',
  'k8.audit.col.actor': 'Actor',
  'k8.audit.col.action': 'Action',
  'k8.audit.col.resource': 'Resource',
  'k8.audit.col.result': 'Outcome',
  'k8.audit.col.reason': 'Reason',
  'k8.audit.type.audit': 'Administrative',
  'k8.audit.type.data_access': 'Data read',
  'k8.audit.result.success': 'Succeeded',
  'k8.audit.result.failure': 'Failed',
  'k8.audit.actor.noName': 'no username on this row',
  'k8.audit.resource.none': 'no resource attached',

  'k8.audit.reason.access': 'The reason the reader gave: "{text}"',
  'k8.audit.reason.accessNotGated':
    'Policy did not require a reason for this read — not that the reader declined to give one.',
  'k8.audit.reason.action': 'Reason given with the action: "{text}"',
  'k8.audit.reason.actionNeedsNone': 'This action does not ask for a reason.',

  'k8.audit.detail.show': 'Show the detail of row {id}',
  'k8.audit.detail.hide': 'Hide the detail of row {id}',
  'k8.audit.detail.title': 'Detail',
  'k8.audit.detail.before': 'Before',
  'k8.audit.detail.after': 'After',
  'k8.audit.detail.metadataOnly':
    'The before and after sections record descriptive details only, never a secret and never private content. An item missing here means it was not recorded, NOT that it was cleared.',
  'k8.audit.detail.absent': 'not recorded',
  'k8.audit.detail.runId': 'achau sync run code',
  'k8.audit.detail.runIdNote':
    'Present when the action also mutated achau. The same value is in achau own AuditLog, so both sides can be lined up.',
  'k8.audit.detail.correlationId': 'Reference id',
  'k8.audit.detail.ip': 'IP address',
  'k8.audit.detail.userAgent': 'Browser and operating system',

  'k8.audit.immutable.title': 'This log cannot be edited or deleted',
  'k8.audit.immutable.body':
    'The log is append-only. So this page has no delete control, no edit control, and no greyed one either: a grey button would say somebody can do this and you cannot, when the truth is that nobody can, through any screen.',

  'k8.audit.export.link': 'Export this data as a file on the Reports page',
  'k8.audit.export.hint':
    'Needs the export-reports permission. The audit log is one of the 13 report types you can create on the Reports screen.',

  /* ═══════════════ WP-M4 (Task 57) — AI insights ═══════════════ */

  'm4.intro':
    'Every analysis here is a sentence a model wrote about somebody else’s work. This screen always shows the confidence and the limitations with it: neither can be hidden and neither can be collapsed.',

  'm4.disclaimer.title': 'AI output must never be the sole basis for a disciplinary decision.',
  'm4.disclaimer.body':
    'AI may not dismiss, discipline, dock pay or conclude fraud. “Recommendations” are advice, not a decision. Before acting on a person, read that analysis’s own Limitations and open its evidence.',
  'm4.disclaimer.fixed':
    'The sentence above is always shown: it cannot be closed or collapsed, and it sits above every AI result on this page.',

  'm4.subject.insights': 'the AI analysis list',
  'm4.subject.evidence': 'the evidence for this analysis',
  'm4.subject.answer': 'the AI answer',

  'm4.list.title': 'Stored analyses',
  'm4.list.caption':
    'Saved analyses, newest first. This list calls no model, so it still reads when the AI service is down.',
  'm4.list.empty.title': 'No analysis matches this filter',
  'm4.list.empty.body':
    'This is a statement about the FILTER, not about the company and not about the budget: no row falls in the selected window and type. If the company were over its monthly budget, that would be said separately in its own panel rather than as this empty state.',
  'm4.list.readCount': 'Read {count} records.',
  'm4.list.noTotal':
    'The list loads page by page and carries no total, so every number on this screen means “how much was read”, not “how much the company has”.',
  'm4.list.more': 'Read the next page',
  'm4.filter.type': 'Analysis type',
  'm4.filter.type.all': 'All',
  'm4.filter.from': 'From',
  'm4.filter.to': 'To',
  'm4.filter.apply': 'Apply',
  'm4.filter.serverSide':
    'All three filters apply to the whole list, not just the page in view: filtering one page would only answer “among what was just read”, which is not the question anybody typed.',

  'm4.card.summaryLabel': 'Conclusion',
  'm4.card.verbatim':
    'The conclusion, the limitations, the causes and the recommendations are server text shown verbatim — not translated, not truncated, not re-summarised.',
  'm4.card.limitations.title': 'Limitations of this analysis',
  'm4.card.limitations.why':
    'Read all of this before acting. A stale-score sentence is appended here after validation even when the model never mentioned it, so this is the one place that warning is certain to survive.',
  'm4.card.limitations.sentinel':
    'The server sent no limitation for this row. #DATA_INCOMPLETE is text this screen substituted — it does NOT mean "this analysis has no limitations".',
  'm4.card.confidence.label': 'Confidence',
  'm4.card.confidence.help':
    'Confidence says how much that run could SEE, not whether the conclusion is right. An arithmetic summary always has confidence 0, because it infers nothing.',
  'm4.card.confidence.absent.not_published':
    'The server published no confidence for this row. This is not zero.',
  'm4.card.confidence.absent.not_a_number':
    'The confidence received is not a readable number. This is not zero.',
  'm4.card.confidence.absent.out_of_range':
    'The confidence received falls outside the allowed range, so the screen does not show it as a measurement.',
  'm4.card.causes.title': 'Possible causes',
  'm4.card.causes.none': 'The model named no cause for this conclusion.',
  'm4.card.actions.title': 'Recommendations',
  'm4.card.actions.none': 'The model named no recommendation.',
  'm4.card.actions.advisory': 'Advisory only. No HR action is ever taken automatically.',
  'm4.card.evidence.title': 'Evidence',
  'm4.card.evidence.none':
    'This analysis cites no figures. Every conclusion must be traceable to the figures behind it — a paragraph about people with no stated basis is not one to act on.',
  'm4.card.evidence.open': 'Open evidence',
  'm4.card.evidence.close': 'Close evidence',
  'm4.card.generatedAt': 'Generated',
  'm4.card.model': 'Model',
  'm4.card.question': 'Question',
  'm4.card.period': 'Data period',
  'm4.card.jobId': 'Request code',

  'm4.status.published': 'The model answered',
  'm4.status.fallback': 'Arithmetic summary, no AI',
  'm4.status.budget': 'AI did not run — monthly budget spent',
  'm4.status.superseded': 'Superseded',
  'm4.status.unreadable': 'Not readable',

  'm4.type.summary': 'Summary',
  'm4.type.trend_comparison': 'Trend comparison',
  'm4.type.anomaly': 'Anomaly',
  'm4.type.cause_hypothesis': 'Cause hypothesis',
  'm4.type.recommendation': 'Recommendation',
  'm4.type.workload_analysis': 'Workload analysis',
  'm4.type.scoped_query': 'Scoped query',
  'm4.type.unknown': 'An analysis type with no name yet',

  'm4.card.fallback.title': 'This is an arithmetic summary, not an AI analysis',
  'm4.card.fallback.body':
    'The model could not be reached, or its answer failed validation twice. The figures below were built arithmetically from the retrieved data and are real. What is missing is the inference.',
  'm4.card.fallback.markers':
    'Four independent markers say so: the status, the diagnostic token, one sentence in Limitations, and both Causes and Recommendations being empty.',
  'm4.card.fallback.diagnosis': 'Diagnostic token',
  'm4.card.fallback.noDiagnosis':
    'No reason came with it, although there should have been one. Recorded here because it is the sign of an unusual record.',

  'm4.card.budget.title': 'No analysis, because the company spent its monthly AI budget',
  'm4.card.budget.body':
    'This request was refused BEFORE it was sent. No model call was made, so there is no result to look for.',
  'm4.card.budget.notOutage':
    'This is NOT a system fault: a budget is a matter of cost, not of whether the system is running, and reporting a system error here would wake the wrong person.',
  'm4.card.budget.notEmpty': 'It is also NOT "nothing was found". AI never ran for this request.',
  'm4.card.budget.fix':
    'Waiting does nothing this month. The limit reopens when the month rolls over, or when an operator raises WP_AI_SOFT_LIMIT.',

  'm4.card.superseded.title': 'This row was superseded',
  'm4.card.superseded.body':
    'This analysis is marked as superseded, although the generation process never sets that mark. Read it as an old record rather than acting on it.',

  'm4.card.unreadable.title': 'That analysis could not be read',
  'm4.card.unreadable.no_status': 'The record carries no status.',
  'm4.card.unreadable.unknown_status':
    'The status is “{status}”, which this screen has no name for, so it is NOT read as a published analysis. Reading an unknown state as the good one is the defect itself, not a smaller version of it.',
  'm4.card.unreadable.no_summary':
    'The row carries no conclusion. A blank paragraph here would read as "the model looked and found nothing worth saying", which is a claim nobody made.',
  'm4.card.unreadable.caveatsKept':
    'The Limitations are still rendered below: they are what has to survive every branch.',

  'm4.card.disagree.title': 'Two markers on this row contradict each other',
  'm4.card.disagree.body':
    'The contract makes ai_unavailable exactly (status === "fallback"). This row is not like that. The screen does not pick a half to believe — it renders by status and reports the contradiction here so the reader can distrust the row in front of them.',

  'm4.ask.title': 'Ask AI',
  'm4.ask.provider.label': 'Who answers',
  'm4.ask.provider.litellm': 'In-house analysis',
  'm4.ask.provider.brain': 'BrainAI',
  'm4.ask.provider.litellm.note':
    'The answer is built by this service: scoped aggregates retrieved here, with confidence, limitations and evidence.',
  'm4.ask.provider.brain.note':
    "The question goes to the company's BrainAI through the Control Plane. BrainAI reaches the data with your own permissions; this service passes the answer through and does not interpret it.",
  'm4.ask.provider.brain.help':
    'The company’s own BrainAI. It does not use the shared AI budget and saves no analysis.',
  'm4.ask.provider.brain.off':
    'This deployment has no Control Plane configured, so there is no BrainAI to reach. An administrator has to set CONTROL_PLANE_URL and restart the service.',
  'm4.ask.asked': 'Asked',
  'm4.ask.brain.runId': 'Run id',
  'm4.ask.brain.passthrough':
    'The text above is BrainAI word for word. This service does not check it, does not compute a confidence and does not save it as an analysis.',
  'm4.ask.body':
    'This is the only AI call answered synchronously, and it holds one of the process few slots for up to 60 seconds.',
  'm4.ask.suggest.title': 'Suggested questions',
  'm4.ask.suggest.note':
    'Pressing a suggestion only fills the box below; it sends NOTHING. You still press Ask — a question you have not read is one you cannot own, when the answer is a sentence about a colleague.',
  'm4.ask.suggest.hours.label': 'Hours trend',
  'm4.ask.suggest.hours.question':
    'Over the selected period, how did total active hours across the scope I may see change against the previous period, and which days moved most?',
  'm4.ask.suggest.workload.label': 'Workload balance',
  'm4.ask.suggest.workload.question':
    'How is workload distributed over this period across the people in the scope I may see, and is anyone clearly out of line with the rest?',
  'm4.ask.suggest.completeness.label': 'Data completeness',
  'm4.ask.suggest.completeness.question':
    'How many days in this period have incomplete data, and how does that affect the aggregate figures?',
  'm4.ask.suggest.alerts.label': 'Alert pattern',
  'm4.ask.suggest.alerts.question':
    'Which trigger types do the alerts raised in this period concentrate on, and do they line up with any change in the aggregates?',
  'm4.ask.label': 'Your question',
  'm4.ask.rule': 'Between 1 and {max} characters.',
  'm4.ask.submit': 'Ask',
  'm4.ask.submitting': 'Asking…',
  'm4.ask.answer.title': 'Answer',
  'm4.ask.empty':
    'Nothing has been asked in this tab. That is a statement about this tab, not about the company.',
  'm4.ask.notPersisted':
    'This conversation is not saved. It disappears when you close the tab or sign out, so copy anything you want to keep.',
  'm4.ask.noStreaming': 'The answer appears once, when it is finished, rather than word by word.',
  'm4.ask.refused.title': 'The model declined to answer this question',
  'm4.ask.refused.body':
    'The server set refused = true: the question asked for a conclusion the data cannot support, or for data outside your scope. That is an answer, not an error.',

  'm4.refusal.serverSentence': 'The server says:',
  'm4.refusal.serverSentence.withheld':
    'An explanation came with it but may not be shown word for word for this kind of error. The error code and the reference code are below.',
  'm4.refusal.budget.title': 'Cannot ask: the company spent its monthly AI budget',
  'm4.refusal.budget.body':
    'The request was refused before it reached the model, so nothing further was spent and there is no result.',
  'm4.refusal.budget.notOutage':
    'This is not an outage: the other AI features and every other screen keep working normally.',
  'm4.refusal.budget.notEmpty':
    'It is also NOT "nothing was found": AI never ran for this question.',
  'm4.refusal.budget.noRetry':
    'There is no retry control here, and specifically no greyed one: pressing again only produces another refusal. The limit reopens next month, or when an operator raises WP_AI_SOFT_LIMIT.',
  'm4.refusal.budget.period': 'Billing month',
  'm4.refusal.budget.cost': 'Spent (USD)',
  'm4.refusal.budget.limit': 'Limit (USD)',
  'm4.refusal.budget.unpublished':
    'The server did not send this number with the refusal. No zero is shown here — a zero would be a lie about the company spend.',
  'm4.refusal.budget.noEndpoint':
    'The screen cannot tell you in advance how much AI budget is left. The three numbers above are everything the system says about AI spend, and they appear only at the moment of refusal.',
  'm4.refusal.busy.title': 'Every synchronous question slot is busy',
  'm4.refusal.busy.body':
    'This process allows only a few concurrent chat turns and each holds its connection for up to 60 seconds. The server refuses immediately rather than queueing, because a deep queue here means minutes of waiting on a connection that has already been dropped.',
  'm4.refusal.busy.wait':
    'A slot will free up. This is the opposite of a spent budget: waiting here DOES work.',
  'm4.refusal.busy.slots': '{inFlight} of {limit} slots in flight.',
  'm4.refusal.busy.slotsUnknown': 'The server sent no slot count with the refusal.',
  'm4.refusal.unavailable.title': 'The model could not be called',
  'm4.refusal.unavailable.body':
    'A question normally still returns the arithmetic summary when the AI service is down, so this error means the refusal came from somewhere else. Every other screen keeps working.',

  'm4.evidence.title': 'Evidence for analysis {id}',
  'm4.evidence.body':
    'This is sensitive access: each open is recorded with the number of records actually handed over and the people actually read.',
  'm4.evidence.count': 'You can see {shown} of {total} cited items.',
  'm4.evidence.col.ref': 'Reference',
  'm4.evidence.col.value': 'Value',
  'm4.evidence.col.where': 'Where to re-read it',
  'm4.evidence.scopeNote':
    'Seeing the analysis does not mean seeing the record: every reference is re-checked against your permissions TODAY, because an analysis outlives the access that produced it.',
  'm4.evidence.threeCauses':
    'Fabricated, deleted and outside your scope are three causes with one result. You are not told which.',
  'm4.evidence.withheld.not_accessible':
    'This item was not handed over. The server keeps its place and sends NO value key — absent, rather than an empty value.',
  'm4.evidence.withheld.no_value_published':
    'The server said this item is accessible and sent no value with it. That is an unusual row rather than a refusal, so it is not counted among the items delivered.',
  'm4.evidence.malformed':
    'One item in the evidence list had the wrong shape and was skipped. Said here rather than dropped silently.',
  'm4.evidence.target.reachable':
    'The source record cannot be opened from any screen yet; the reference above is how to look it up.',
  'm4.evidence.target.noEndpoint':
    'The source record cannot be opened from any screen yet. The reference is printed rather than turned into a link — a dead link on an investigation record reads as missing evidence.',
  'm4.evidence.target.notEmitted':
    'This kind of evidence is not supported yet. If you are looking at one, tell an administrator.',
  'm4.evidence.freshness.title': 'Whether this score is up to date, as of NOW',
  'm4.evidence.freshness.body':
    'This block is about NOW, because it is a live read. The analysis’s own Limitations are about when it was produced. Two different questions; neither replaces the other.',
  'm4.evidence.empty': 'This analysis cites no record, so there is nothing to open.',

  'm4.analyze.title': 'Request a new analysis',
  'm4.analyze.body':
    'The analysis runs in the background: the request is accepted at once, and the result appears in the list when it is finished.',
  'm4.analyze.type': 'Analysis type',
  'm4.analyze.from': 'From',
  'm4.analyze.to': 'To',
  'm4.analyze.submit': 'Queue analysis',
  'm4.analyze.submitting': 'Sending…',
  'm4.analyze.queued': 'Queued. Request code: {jobId}',
  'm4.analyze.poll': 'Check for the result',
  'm4.analyze.polling': 'Checking…',
  'm4.analyze.pollEmpty':
    'This request has no result yet. An empty page here means “not finished”, NOT “no result”.',
  'm4.analyze.noJobStatus':
    'There is no progress state yet, so the screen cannot tell “queued” from “running”. The result appears in the list when it is done.',
  'm4.analyze.pollResult': 'The result of this request',
  'm4.analyze.employeeIds': 'Employee',
  'm4.analyze.employeeIds.hint':
    'Leaving it empty means no restriction to particular employees — which is not the same as an empty list.',

  'm4.absent.title': 'Limits of this analysis',
  'm4.absent.body':
    'Each item below is a question this screen cannot answer yet. They are named rather than replaced with invented figures.',
  'm4.absent.budget':
    'Remaining AI budget: this is what decides whether a question gets answered at all, and today you learn how much is left only at the moment of refusal.',
  'm4.absent.insightById':
    'Opening one analysis by its code: today you reach it only while it is still on the list page.',
  'm4.absent.alertById':
    'Opening one alert by its code: alert evidence prints a code today and cannot be opened directly.',
  'm4.absent.dailyById':
    'Opening one daily rollup by its code: today it can only be looked up by date range.',
  'm4.absent.jobStatus':
    'Progress of an analysis request: today you know it has finished only when the result appears in the list.',
  'm4.absent.conversation':
    'There is no conversation table, by design. The transcript is held by the browser and dies with the session.',
  'm4.absent.streaming':
    'An answer that appears word by word: today the answer appears once, when it is finished.',

  'm4.overview.title': 'AI analysis',
  'm4.overview.body':
    'The newest analysis in the scope you may see, rendered with the very card the AI Insights page uses — so its Limitations travel here with it rather than being trimmed to fit a tile.',
  'm4.overview.link': 'Open the AI Insights page',
  'm4.overview.denied':
    'This slot needs workforce.use_ai_analytics, which this account does not hold. No AI output is shown here.',
  'm4.overview.empty':
    'No analysis exists in the selected window. That is a statement about the filter, not about the company.',

  // ── Recruiting (Phase 1 ATS, WP-W1) ────────────────────────────────────────
  'nav.recruitingJobs': 'Jobs',
  'nav.candidates': 'Candidates',
  'nav.pipeline': 'Pipeline',
  'nav.interviews': 'Interviews',
  'nav.offers': 'Offers',
  'nav.outreach': 'Outreach',

  // ── HR / people-ops (Phase 3a/3b/3c, WP-W6/WP-W7/WP-W8) ────────────────────
  'nav.hrDocuments': 'Documents & contracts',
  'nav.onboarding': 'Onboarding',
  'nav.hrLeave': 'Leave',
  'nav.hrPerformance': 'Performance review',

  'recruiting.jobs.intro':
    "This company's job requisitions. Open one to view, edit, move its status and manage where it is posted.",
  'recruiting.jobs.scopeNote':
    'Only requisitions in the active company are shown. Scope is the server’s, never taken from a parameter.',
  'recruiting.jobs.subjectWord': 'requisitions',
  'recruiting.jobs.open': 'Open',

  'recruiting.jobs.filter.status': 'Status',
  'recruiting.jobs.filter.ownerMe': 'Only mine',
  'recruiting.jobs.filter.ownerMeHint':
    'Filters to requisitions whose recruiter is you. A row filter, not an authorization level.',

  'recruiting.jobs.empty.title': 'No requisitions match the filter',
  'recruiting.jobs.empty.body':
    'No requisitions in the {status} status. That is about the filter, not the company.',
  'recruiting.jobs.partial.excludes': 'requisitions older than the {shown} most recent shown',
  'recruiting.jobs.row.meta': '{headcount} needed · {type} · {location}',

  'recruiting.jobs.status.draft': 'Draft',
  'recruiting.jobs.status.open': 'Open',
  'recruiting.jobs.status.onHold': 'On hold',
  'recruiting.jobs.status.filled': 'Filled',
  'recruiting.jobs.status.cancelled': 'Cancelled',
  'recruiting.jobs.status.closed': 'Closed',

  'recruiting.jobs.move.draft': 'Move to draft',
  'recruiting.jobs.move.open': 'Open for hiring',
  'recruiting.jobs.move.onHold': 'Put on hold',
  'recruiting.jobs.move.filled': 'Mark filled',
  'recruiting.jobs.move.cancelled': 'Cancel',
  'recruiting.jobs.move.closed': 'Close',

  'recruiting.jobs.employmentType.none': 'Unspecified',
  'recruiting.jobs.employmentType.full_time': 'Full time',
  'recruiting.jobs.employmentType.part_time': 'Part time',
  'recruiting.jobs.employmentType.contract': 'Contract',
  'recruiting.jobs.employmentType.intern': 'Intern',

  'recruiting.jobs.create.title': 'Open a new requisition',
  'recruiting.jobs.create.intro':
    'A new requisition is always created in draft. The server stamps who created it; you only name the role.',
  'recruiting.jobs.create.titleLabel': 'Role title',
  'recruiting.jobs.create.titleRule': 'Required. At most 500 characters.',
  'recruiting.jobs.create.employmentType': 'Employment type',
  'recruiting.jobs.create.headcount': 'Headcount',
  'recruiting.jobs.create.location': 'Location',
  'recruiting.jobs.create.department': 'Department id',
  'recruiting.jobs.create.hiringManager': 'Hiring manager id',
  'recruiting.jobs.create.recruiter': 'Recruiter id',
  'recruiting.jobs.create.idHint':
    'Employee ids are the first column on the People screen; department ids are on Company settings.',
  'recruiting.jobs.create.description': 'Description',
  'recruiting.jobs.create.submit': 'Create requisition',
  'recruiting.jobs.create.submitting': 'Creating…',
  'recruiting.jobs.create.ok': 'Created "{title}" in draft.',

  'recruiting.jobs.detail.subject': 'requisition detail',
  'recruiting.jobs.detail.meta': '{headcount} needed · Recruiter: {recruiter}',
  'recruiting.jobs.detail.unassigned': 'unassigned',
  'recruiting.jobs.detail.noLocation': 'no location',
  'recruiting.jobs.detail.notOpened': 'Never opened',
  'recruiting.jobs.detail.opened': 'Opened: {when}',
  'recruiting.jobs.detail.closed': 'Closed: {when}',
  'recruiting.jobs.lastUpdated': 'Last updated: {when}',

  'recruiting.jobs.transition.title': 'Move status',
  'recruiting.jobs.transition.derived':
    'Only the legal moves from {status} are shown — {count}. An illegal move has no control.',
  'recruiting.jobs.transition.terminal.title': 'Final status',
  'recruiting.jobs.transition.terminal.body':
    'A requisition in a final status (filled, cancelled or closed) has no moves out.',
  'recruiting.jobs.transition.serverAllowed': 'The server allows: {list}',

  'recruiting.jobs.edit.title': 'Edit requisition',
  'recruiting.jobs.edit.titleLabel': 'Role title',
  'recruiting.jobs.edit.headcount': 'Headcount',
  'recruiting.jobs.edit.location': 'Location',
  'recruiting.jobs.edit.employmentType': 'Employment type',
  'recruiting.jobs.edit.save': 'Save changes',
  'recruiting.jobs.edit.saving': 'Saving…',
  'recruiting.jobs.edit.ok': 'Changes saved.',
  'recruiting.jobs.edit.noChange': 'Nothing changed to save.',

  'recruiting.jobs.postings.title': 'Postings',
  'recruiting.jobs.postings.intro': 'Each posting is created in draft. The server owns its status.',
  'recruiting.jobs.postings.subject': 'postings',
  'recruiting.jobs.postings.empty.title': 'No postings yet',
  'recruiting.jobs.postings.empty.body':
    'This requisition is not posted on any channel. Create one below.',
  'recruiting.jobs.postings.channel': 'Channel',
  'recruiting.jobs.postings.channel.internal': 'Internal',
  'recruiting.jobs.postings.channel.career_site': 'Career site',
  'recruiting.jobs.postings.channel.referral': 'Referral',
  'recruiting.jobs.postings.channel.agency': 'Agency',
  'recruiting.jobs.postings.noChannel': 'No channel',
  'recruiting.jobs.postings.slug': 'Public web address',
  'recruiting.jobs.postings.slugRule': 'Optional. Lowercase letters, digits and hyphens only.',
  'recruiting.jobs.postings.slugInvalid':
    'The web address may contain only lowercase letters, digits and hyphens.',
  'recruiting.jobs.postings.expires': 'Expires',
  'recruiting.jobs.postings.create': 'Create posting',
  'recruiting.jobs.postings.creating': 'Creating…',
  'recruiting.jobs.postings.ok': 'Posting created in draft.',
  'recruiting.jobs.postings.status.draft': 'Draft',
  'recruiting.jobs.postings.status.published': 'Published',
  'recruiting.jobs.postings.status.unpublished': 'Unpublished',
  'recruiting.jobs.postings.status.expired': 'Expired',
  'recruiting.jobs.postings.publishedAt': 'Published {when}',
  'recruiting.jobs.postings.notPublished': 'Not published',
  'recruiting.jobs.postings.expiresAt': 'expires {when}',
  'recruiting.jobs.postings.noSlug': 'no web address',

  // ── Recruiting — Candidates (Phase 1 ATS, WP-W2) ───────────────────────────
  'recruiting.candidates.intro':
    'The candidates of your company. Open one to view, edit, record consent, manage CV documents, or redact for erasure.',
  'recruiting.candidates.scopeNote':
    'Only candidates of the company in view, and redacted records are excluded. The scope is the server’s, not a query parameter.',
  'recruiting.candidates.subjectWord': 'candidates',
  'recruiting.candidates.open': 'Open',

  'recruiting.candidates.filter.source': 'Source',
  'recruiting.candidates.filter.q': 'Search by name or email',
  'recruiting.candidates.filter.qHint':
    'Case-insensitive substring match on name and email. The filter runs on the server.',

  'recruiting.candidates.empty.title': 'No candidates match the filter',
  'recruiting.candidates.empty.body':
    'No candidates match {filter}. This is a statement about the filter, not the company. Redacted records do not appear here.',
  'recruiting.candidates.partial.excludes': 'candidates older than the {shown} shown',
  'recruiting.candidates.row.meta': '{source} · {title} · {location}',
  'recruiting.candidates.filterAll': 'all sources',

  'recruiting.candidates.source.career_site': 'Career site',
  'recruiting.candidates.source.referral': 'Referral',
  'recruiting.candidates.source.agency': 'Agency',
  'recruiting.candidates.source.manual': 'Manual',
  'recruiting.candidates.source.none': 'No source recorded',

  'recruiting.candidates.redacted.badge': 'Redacted',
  'recruiting.candidates.redacted.note':
    'This record was redacted for data erasure. The personal data has been permanently deleted and cannot be restored; there is nothing left to edit or redact.',
  'recruiting.candidates.redacted.at': 'Redacted at {when}',

  'recruiting.candidates.create.title': 'Add a candidate',
  'recruiting.candidates.create.intro':
    'Only a full name is required. The server stamps the creator. A duplicate email against a non-redacted candidate is refused.',
  'recruiting.candidates.create.fullName': 'Full name',
  'recruiting.candidates.create.fullNameRule': 'Required. At most 500 characters.',
  'recruiting.candidates.create.email': 'Email',
  'recruiting.candidates.create.phone': 'Phone',
  'recruiting.candidates.create.source': 'Source',
  'recruiting.candidates.create.currentTitle': 'Current title',
  'recruiting.candidates.create.currentCompany': 'Current company',
  'recruiting.candidates.create.location': 'Location',
  'recruiting.candidates.create.linkedin': 'LinkedIn',
  'recruiting.candidates.create.consent':
    'The candidate consented to their data being held (record now)',
  'recruiting.candidates.create.submit': 'Add candidate',
  'recruiting.candidates.create.submitting': 'Adding…',
  'recruiting.candidates.create.ok': 'Added candidate "{name}".',

  'recruiting.candidates.detail.subject': 'candidate detail',
  'recruiting.candidates.detail.email': 'Email',
  'recruiting.candidates.detail.phone': 'Phone',
  'recruiting.candidates.detail.source': 'Source',
  'recruiting.candidates.detail.currentTitle': 'Title',
  'recruiting.candidates.detail.currentCompany': 'Company',
  'recruiting.candidates.detail.location': 'Location',
  'recruiting.candidates.detail.linkedin': 'LinkedIn',
  'recruiting.candidates.detail.none': '—',
  'recruiting.candidates.detail.consent': 'Data-retention consent: {when}',
  'recruiting.candidates.detail.noConsent': 'No data-retention consent recorded.',
  'recruiting.candidates.lastUpdated': 'Last updated: {when}',

  'recruiting.candidates.edit.title': 'Edit candidate',
  'recruiting.candidates.edit.fullName': 'Full name',
  'recruiting.candidates.edit.email': 'Email',
  'recruiting.candidates.edit.phone': 'Phone',
  'recruiting.candidates.edit.source': 'Source',
  'recruiting.candidates.edit.currentTitle': 'Current title',
  'recruiting.candidates.edit.currentCompany': 'Current company',
  'recruiting.candidates.edit.location': 'Location',
  'recruiting.candidates.edit.linkedin': 'LinkedIn',
  'recruiting.candidates.edit.save': 'Save changes',
  'recruiting.candidates.edit.saving': 'Saving…',
  'recruiting.candidates.edit.ok': 'Changes saved.',
  'recruiting.candidates.edit.noChange': 'Nothing to save yet.',

  'recruiting.candidates.consent.title': 'Data-retention consent',
  'recruiting.candidates.consent.record': 'Record consent (now)',
  'recruiting.candidates.consent.clear': 'Clear consent',
  'recruiting.candidates.consent.saving': 'Saving…',

  'recruiting.candidates.redact.title': 'Redact (right to erasure)',
  'recruiting.candidates.redact.warning':
    'Redacting permanently erases the candidate’s personal data (name, email, phone, LinkedIn). This CANNOT be undone.',
  'recruiting.candidates.redact.start': 'Redact this candidate',
  'recruiting.candidates.redact.confirmQuestion':
    'Confirm permanent redaction? The personal data will be deleted and cannot be restored.',
  'recruiting.candidates.redact.confirm': 'Confirm permanent redaction',
  'recruiting.candidates.redact.cancel': 'Cancel',
  'recruiting.candidates.redact.pending': 'Redacting…',
  'recruiting.candidates.redact.ok':
    'The candidate record was redacted. The personal data has been deleted.',

  'recruiting.candidates.docType.cv': 'CV',
  'recruiting.candidates.docType.cover_letter': 'Cover letter',
  'recruiting.candidates.docType.portfolio': 'Portfolio',
  'recruiting.candidates.docType.other': 'Other',
  'recruiting.candidates.docType.none': 'Unknown type',

  'recruiting.candidates.documents.title': 'Documents',
  'recruiting.candidates.documents.subject': 'documents',
  'recruiting.candidates.documents.subjectAccess': 'the candidate’s CV',
  'recruiting.candidates.documents.intro':
    'Metadata, not content. Downloading the content is a sensitive, access-logged read and requires an access reason.',
  'recruiting.candidates.documents.empty.title': 'No documents yet',
  'recruiting.candidates.documents.empty.body':
    'This candidate has no CV documents yet. Register and upload one below.',
  'recruiting.candidates.documents.uploadedAt': 'Uploaded {when}',
  'recruiting.candidates.documents.sizeKb': '{kb} KB',
  'recruiting.candidates.documents.sizeUnknown': 'size unknown',
  'recruiting.candidates.documents.meta': '{type} · {size}',

  'recruiting.candidates.documents.register.title': 'Upload a CV document',
  'recruiting.candidates.documents.register.intro':
    'The system records the file details and issues a short-lived upload link; the browser uploads the content straight to storage.',
  'recruiting.candidates.documents.register.file': 'File',
  'recruiting.candidates.documents.register.docType': 'Document type',
  'recruiting.candidates.documents.register.submit': 'Register and upload',
  'recruiting.candidates.documents.register.submitting': 'Uploading…',
  'recruiting.candidates.documents.register.ok': 'Uploaded "{filename}".',
  'recruiting.candidates.documents.register.noFile': 'Choose a file to upload.',

  'recruiting.candidates.documents.download': 'Download',
  'recruiting.candidates.documents.downloading': 'Downloading…',
  'recruiting.candidates.documents.delivered': 'Downloaded "{filename}".',
  'recruiting.candidates.documents.reasonNeeded':
    'Enter an access reason above (at least {min} characters, no diacritics) to enable download.',

  // ── Recruiting — Pipeline (Phase 1 ATS, WP-W3) ─────────────────────────────
  'recruiting.pipeline.intro':
    'A pipeline board: one column per stage, one card per application. Move an application through exactly the stages the server allows.',
  'recruiting.pipeline.scopeNote':
    'Shows only applications in the active company. The scope is set by the server, never taken from a parameter.',
  'recruiting.pipeline.subjectWord': 'applications',

  'recruiting.pipeline.filter.requisition': 'Filter by requisition',
  'recruiting.pipeline.filter.requisitionIdHint':
    'The requisition list is out of reach (needs recruiting.view_jobs), so this is a requisition-id field.',

  'recruiting.pipeline.empty.title': 'No applications yet',
  'recruiting.pipeline.empty.body':
    'Nothing matches the current filter. This is a sentence about the filter, not the company. Add an application with the form above.',
  'recruiting.pipeline.partial.excludes': 'applications older than the {shown} shown',

  'recruiting.pipeline.column.count': '{count} applications',
  'recruiting.pipeline.column.empty': 'Empty',
  'recruiting.pipeline.card.requisition': 'Requisition: {requisition}',
  'recruiting.pipeline.card.open': 'View history',

  'recruiting.pipeline.stage.applied': 'Applied',
  'recruiting.pipeline.stage.screening': 'Screening',
  'recruiting.pipeline.stage.interview': 'Interview',
  'recruiting.pipeline.stage.offer': 'Offer',
  'recruiting.pipeline.stage.hired': 'Hired',
  'recruiting.pipeline.stage.rejected': 'Rejected',
  'recruiting.pipeline.stage.withdrawn': 'Withdrawn',

  'recruiting.pipeline.move.applied': 'Move to applied',
  'recruiting.pipeline.move.screening': 'Move to screening',
  'recruiting.pipeline.move.interview': 'Move to interview',
  'recruiting.pipeline.move.offer': 'Move to offer',
  'recruiting.pipeline.move.hired': 'Hire',
  'recruiting.pipeline.move.rejected': 'Reject',
  'recruiting.pipeline.move.withdrawn': 'Withdraw',

  'recruiting.pipeline.transition.terminal':
    'An application in a terminal stage (hired, rejected or withdrawn) has no moves left.',
  'recruiting.pipeline.transition.serverAllowed': 'The server allows: {list}',

  'recruiting.pipeline.hire.label': 'Hire',
  'recruiting.pipeline.hire.tooltip':
    'Hiring is a separate Phase 2 action, not done on this screen. A move to "hired" is refused by the server here.',

  'recruiting.pipeline.history.title': 'Stage history',
  'recruiting.pipeline.history.subject': 'stage history',
  'recruiting.pipeline.history.origin': 'created',
  'recruiting.pipeline.history.system': 'system',
  'recruiting.pipeline.history.move': '{from} → {to}',
  'recruiting.pipeline.history.meta': 'By: {who} · {when}',
  'recruiting.pipeline.history.note': 'Note: {note}',
  'recruiting.pipeline.history.empty.title': 'No history',
  'recruiting.pipeline.history.empty.body': 'This application has no recorded stage moves yet.',

  'recruiting.pipeline.reject.title': 'Reject application',
  'recruiting.pipeline.reject.intro':
    'Move the application to rejected. A reason is optional but is recorded as the history note.',
  'recruiting.pipeline.reject.reason': 'Reason (optional)',
  'recruiting.pipeline.reject.submit': 'Reject application',
  'recruiting.pipeline.reject.pending': 'Rejecting…',
  'recruiting.pipeline.reject.terminal':
    'This application is already in a terminal stage and cannot be rejected again.',
  'recruiting.pipeline.reject.ok': 'Application rejected.',

  'recruiting.pipeline.create.title': 'Add an application',
  'recruiting.pipeline.create.intro':
    'Tie a candidate to a requisition. An application always opens in the "applied" stage. A duplicate candidate–requisition pair is refused.',
  'recruiting.pipeline.create.candidate': 'Candidate',
  'recruiting.pipeline.create.candidatePlaceholder': '— Choose a candidate —',
  'recruiting.pipeline.create.requisition': 'Requisition',
  'recruiting.pipeline.create.requisitionPlaceholder': '— Choose a requisition —',
  'recruiting.pipeline.create.requisitionIdHint':
    'The requisition list is out of reach (needs recruiting.view_jobs), so this is a requisition-id field.',
  'recruiting.pipeline.create.recruiter': 'Assigned recruiter',
  'recruiting.pipeline.create.recruiterHint':
    'Optional. Type a name or an email and pick from the list; leave it empty if nobody is assigned yet.',
  'recruiting.pipeline.create.submit': 'Add application',
  'recruiting.pipeline.create.submitting': 'Adding…',
  'recruiting.pipeline.create.ok': 'Application added in the "applied" stage.',

  // ── Interviews (WP-W4) ──────────────────────────────────────────────────────
  'recruiting.interviews.intro':
    'Schedule interviews against an application, manage the panel, update status, and submit scorecards.',
  'recruiting.interviews.scopeNote':
    'Only interviews in the company you are viewing are shown. The scope is decided by the server, not by a parameter.',
  'recruiting.interviews.subjectWord': 'interviews',
  'recruiting.interviews.open': 'View details',

  'recruiting.interviews.filter.application': 'Filter by application',
  'recruiting.interviews.filter.applicationIdHint':
    'The application list is out of reach (needs recruiting.view_candidates), so this is an application-id field.',
  'recruiting.interviews.filter.mine': 'Only my interviews',
  'recruiting.interviews.filter.mineHint':
    'When on, shows only the interviews whose panel you are on.',

  'recruiting.interviews.empty.title': 'No interviews yet',
  'recruiting.interviews.empty.body':
    'No interview matches the current filter. Schedule one with the form above.',
  'recruiting.interviews.partial.excludes': 'interviews older than the {shown} newest shown',

  'recruiting.interviews.row.meta': 'Kind: {kind} · {when}',
  'recruiting.interviews.row.unscheduled': 'no time set',

  'recruiting.interviews.kind.phone': 'Phone',
  'recruiting.interviews.kind.onsite': 'Onsite',
  'recruiting.interviews.kind.technical': 'Technical',
  'recruiting.interviews.kind.panel': 'Panel',
  'recruiting.interviews.kind.culture': 'Culture',
  'recruiting.interviews.kind.none': 'Unspecified',

  'recruiting.interviews.status.scheduled': 'Scheduled',
  'recruiting.interviews.status.completed': 'Completed',
  'recruiting.interviews.status.cancelled': 'Cancelled',
  'recruiting.interviews.status.no_show': 'No-show',
  'recruiting.interviews.status.title': 'Interview status',
  'recruiting.interviews.status.terminal':
    'An interview in a terminal status (completed, cancelled or no-show) has no moves left.',
  'recruiting.interviews.status.serverAllowed': 'The server allows: {list}',

  'recruiting.interviews.move.scheduled': 'Reschedule',
  'recruiting.interviews.move.completed': 'Mark completed',
  'recruiting.interviews.move.cancelled': 'Cancel interview',
  'recruiting.interviews.move.no_show': 'Mark no-show',

  'recruiting.interviews.role.lead': 'Lead',
  'recruiting.interviews.role.panelist': 'Panelist',

  'recruiting.interviews.recommendation.strong_yes': 'Strong yes',
  'recruiting.interviews.recommendation.yes': 'Yes',
  'recruiting.interviews.recommendation.no': 'No',
  'recruiting.interviews.recommendation.strong_no': 'Strong no',

  'recruiting.interviews.detail.status': 'Status',
  'recruiting.interviews.detail.kind': 'Kind',
  'recruiting.interviews.detail.start': 'Start',
  'recruiting.interviews.detail.end': 'End',
  'recruiting.interviews.detail.timezone': 'Timezone',
  'recruiting.interviews.detail.location': 'Location',
  'recruiting.interviews.detail.meetingUrl': 'Meeting link',
  'recruiting.interviews.detail.none': '—',

  'recruiting.interviews.schedule.title': 'Schedule an interview',
  'recruiting.interviews.schedule.intro':
    'Create an interview against an application. Only the application is required; the rest are optional. An interview always opens in the "scheduled" status.',
  'recruiting.interviews.schedule.application': 'Application',
  'recruiting.interviews.schedule.applicationIdHint':
    'The application list is out of reach (needs recruiting.view_candidates), so this is an application-id field.',
  'recruiting.interviews.schedule.applicationPlaceholder': '— Choose an application —',
  'recruiting.interviews.schedule.kind': 'Kind',
  'recruiting.interviews.schedule.kindPlaceholder': '— None —',
  'recruiting.interviews.schedule.start': 'Start time',
  'recruiting.interviews.schedule.end': 'End time',
  'recruiting.interviews.schedule.timezone': 'Timezone',
  'recruiting.interviews.schedule.location': 'Location',
  'recruiting.interviews.schedule.meetingUrl': 'Meeting link',
  'recruiting.interviews.schedule.panel': 'Interview panel',
  'recruiting.interviews.schedule.panelHint':
    'Optional. Add interviewers by employee id — the ids are the first column on the People screen.',
  'recruiting.interviews.schedule.panelistId': 'Employee id',
  'recruiting.interviews.schedule.panelistRole': 'Role',
  'recruiting.interviews.schedule.panelistRemove': 'Remove',
  'recruiting.interviews.schedule.panelistAdd': 'Add interviewer',
  'recruiting.interviews.schedule.submit': 'Schedule',
  'recruiting.interviews.schedule.submitting': 'Scheduling…',
  'recruiting.interviews.schedule.ok': 'Interview scheduled in the "scheduled" status.',

  'recruiting.interviews.panel.title': 'Interview panel',
  'recruiting.interviews.panel.intro':
    'Add or remove interviewers. The list is redrawn after each change.',
  'recruiting.interviews.panel.employeeId': 'Employee id',
  'recruiting.interviews.panel.employeeIdHint':
    'The Workforce Pulse employee id — the first column on the People screen, not an achau account id.',
  'recruiting.interviews.panel.role': 'Role',
  'recruiting.interviews.panel.add': 'Add to panel',
  'recruiting.interviews.panel.adding': 'Adding…',
  'recruiting.interviews.panel.subject': 'interview panel',
  'recruiting.interviews.panel.unknown':
    'No change has been made this session, so the current panel is not loaded. Add or remove someone to see the list.',
  'recruiting.interviews.panel.empty': 'The panel is empty.',
  'recruiting.interviews.panel.roleNone': 'No role set',
  'recruiting.interviews.panel.remove': 'Remove from panel',

  'recruiting.interviews.scorecard.title': 'Scorecard',
  'recruiting.interviews.scorecard.interviewer':
    'Interviewer: {name} (that is you — stamped by the server).',
  'recruiting.interviews.scorecard.ownState': 'Your scorecard is on file.',
  'recruiting.interviews.scorecard.noRecommendation': 'No recommendation',
  'recruiting.interviews.scorecard.overallShort': 'Overall: {score}/5',
  'recruiting.interviews.scorecard.submittedAt': 'Submitted {when}',
  'recruiting.interviews.scorecard.recommendation': 'Recommendation',
  'recruiting.interviews.scorecard.recommendationPlaceholder': '— Choose a recommendation —',
  'recruiting.interviews.scorecard.overall': 'Overall (1–5)',
  'recruiting.interviews.scorecard.overallPlaceholder': '— Choose a score —',
  'recruiting.interviews.scorecard.competencies': 'Competencies (optional)',
  'recruiting.interviews.scorecard.competenciesHint':
    'Add each competency with a 1–5 score. Leave empty to skip.',
  'recruiting.interviews.scorecard.competencyName': 'Competency name',
  'recruiting.interviews.scorecard.competencyScore': 'Score',
  'recruiting.interviews.scorecard.competencyRemove': 'Remove',
  'recruiting.interviews.scorecard.competencyAdd': 'Add competency',
  'recruiting.interviews.scorecard.comments': 'Comments (optional)',
  'recruiting.interviews.scorecard.submit': 'Submit scorecard',
  'recruiting.interviews.scorecard.resubmit': 'Update scorecard',
  'recruiting.interviews.scorecard.submitting': 'Submitting…',
  'recruiting.interviews.scorecard.subject': 'scorecard',
  'recruiting.interviews.scorecard.notOnPanel.title': 'You are not on this interview’s panel',
  'recruiting.interviews.scorecard.notOnPanel.body':
    'Only interviewers on the panel can submit a scorecard. If you should be reviewing, ask the coordinator to add you to the panel.',
  'recruiting.interviews.scorecard.allTitle': 'All scorecards',
  'recruiting.interviews.scorecard.allEmpty.title': 'No scorecards yet',
  'recruiting.interviews.scorecard.allEmpty.body':
    'No interviewer has submitted a scorecard for this interview yet.',
  'recruiting.interviews.scorecard.allHidden':
    'recruiting.view_candidates is required to read the whole panel’s scorecards.',
  'recruiting.interviews.scorecard.rowInterviewer': 'Interviewer {who}',
  'recruiting.interviews.scorecard.noOverall': 'No overall score',

  // ── Offers (WP-W5) ──────────────────────────────────────────────────────────
  'recruiting.offers.intro':
    'Create and drive offers for an application: draft one, approve it, send it to the candidate, and record their response.',
  'recruiting.offers.scopeNote':
    'Only offers in the company you are viewing are shown. The scope is decided by the server, never by a parameter.',
  'recruiting.offers.subjectWord': 'offers',

  'recruiting.offers.filter.application': 'Application',
  'recruiting.offers.filter.applicationPlaceholder': '— Choose an application —',
  'recruiting.offers.filter.applicationIdHint':
    'The applications list is out of reach (needs recruiting.view_candidates), so this is an application-id input.',

  'recruiting.offers.pick.title': 'Choose an application',
  'recruiting.offers.pick.body':
    'An offer hangs off an application. Choose one above to see its offers and create a new draft.',

  'recruiting.offers.empty.title': 'No offers yet',
  'recruiting.offers.empty.body':
    'This application has no offers. Create a draft with the form above.',

  'recruiting.offers.row.salary': '{amount} {currency}',
  'recruiting.offers.row.noSalary': 'No salary set',
  'recruiting.offers.row.meta': 'Start: {start} · Expires: {expires}',
  'recruiting.offers.row.noStart': 'unset',
  'recruiting.offers.row.noExpiry': 'unset',

  'recruiting.offers.status.draft': 'Draft',
  'recruiting.offers.status.pending_approval': 'Pending approval',
  'recruiting.offers.status.approved': 'Approved',
  'recruiting.offers.status.sent': 'Sent',
  'recruiting.offers.status.accepted': 'Accepted',
  'recruiting.offers.status.declined': 'Declined',
  'recruiting.offers.status.rescinded': 'Rescinded',
  'recruiting.offers.status.expired': 'Expired',
  'recruiting.offers.status.terminal':
    'An offer in a terminal state (accepted, declined, rescinded or expired) has no further actions.',
  'recruiting.offers.status.serverAllowed': 'The server allows: {list}',

  'recruiting.offers.action.approve': 'Approve',
  'recruiting.offers.action.send': 'Send to candidate',
  'recruiting.offers.action.respond_accepted': 'Record: accepted',
  'recruiting.offers.action.respond_declined': 'Record: declined',

  'recruiting.offers.create.title': 'Create a draft offer',
  'recruiting.offers.create.intro':
    'Create an offer in the "draft" status for the chosen application. Every field is optional; an empty form creates a bare draft to fill in and approve later.',
  'recruiting.offers.create.amount': 'Salary amount',
  'recruiting.offers.create.amountInvalid':
    'Enter a non-negative number, up to 12 integer and 2 decimal digits (e.g. 85000.00).',
  'recruiting.offers.create.currency': 'Currency (ISO code)',
  'recruiting.offers.create.startDate': 'Proposed start date',
  'recruiting.offers.create.expiresAt': 'Expires at',
  'recruiting.offers.create.documentKey': 'Offer document key (S3)',
  'recruiting.offers.create.documentKeyHint':
    'Optional. The stored code of the offer document; you cannot upload a file from this screen yet.',
  'recruiting.offers.create.submit': 'Create draft',
  'recruiting.offers.create.submitting': 'Creating…',
  'recruiting.offers.create.ok': 'Created the offer in the "draft" status.',

  // ── HR: employee (shared by both HR screens, WP-W6) ─────────────────────────
  'hr.employee.title': 'Employee',
  'hr.employee.intro':
    'Enter an employee id to view their HR records. The server authorises over your tree; naming an id does not widen scope.',
  'hr.employee.idLabel': 'Employee id',
  'hr.employee.idHint':
    'The Workforce Pulse employee id — the first column on the People screen, not the achau account id.',
  'hr.employee.pickLabel': 'Employee',
  'hr.employee.pickHint':
    'Type a name or an email and pick from the list. You only see people within the scope you may read.',
  'hr.employee.load': 'View records',
  'hr.employee.none.title': 'No employee selected',
  'hr.employee.none.body': 'Enter an employee id above to see their documents and contracts.',

  // ── HR: employee documents (WP-W6) ──────────────────────────────────────────
  'hr.documents.screenIntro':
    'An employee’s HR documents and employment contracts. Reading a document’s bytes is access-logged.',
  'hr.documents.scopeNote':
    'The document and contract lists are ordinary data; only the file download needs an access reason.',
  'hr.documents.title': 'Documents',
  'hr.documents.intro':
    'Metadata only — never the bytes, never a link. The bytes are downloaded separately, on the record.',
  'hr.documents.subject': 'documents',
  'hr.documents.subjectAccess': 'the employee’s HR document',
  'hr.documents.empty.title': 'No documents yet',
  'hr.documents.empty.body': 'This employee has no HR documents registered.',
  'hr.documents.uploadedAt': 'Uploaded {when}',
  'hr.documents.sizeKb': '{kb} KB',
  'hr.documents.sizeUnknown': 'size unknown',
  'hr.documents.meta': '{type} · {size}',
  'hr.documents.register.title': 'Upload a document',
  'hr.documents.register.intro':
    'Register the metadata, then upload the bytes straight to object storage. The server never proxies the bytes.',
  'hr.documents.register.file': 'File',
  'hr.documents.register.docType': 'Document type',
  'hr.documents.register.submit': 'Register and upload',
  'hr.documents.register.submitting': 'Uploading…',
  'hr.documents.register.ok': 'Uploaded “{filename}”.',
  'hr.documents.register.noFile': 'Choose a file to upload.',
  'hr.documents.download': 'Download',
  'hr.documents.downloading': 'Downloading…',
  'hr.documents.delivered': 'Downloaded “{filename}”.',
  'hr.documents.reasonNeeded':
    'Enter an access reason of at least {min} characters to enable download.',
  'hr.documents.docType.none': 'type unknown',
  'hr.documents.docType.id_card': 'ID card',
  'hr.documents.docType.passport': 'Passport',
  'hr.documents.docType.contract': 'Contract',
  'hr.documents.docType.certificate': 'Certificate',
  'hr.documents.docType.tax': 'Tax',
  'hr.documents.docType.other': 'Other',

  // ── HR: contracts (WP-W6) ───────────────────────────────────────────────────
  'hr.contracts.title': 'Employment contracts',
  'hr.contracts.intro': 'The employee’s employment contracts, most recent first.',
  'hr.contracts.subject': 'contracts',
  'hr.contracts.empty.title': 'No contracts yet',
  'hr.contracts.empty.body': 'This employee has no employment contracts on record.',
  'hr.contracts.create.title': 'Create a contract',
  'hr.contracts.create.type': 'Contract type',
  'hr.contracts.create.startDate': 'Start date',
  'hr.contracts.create.endDate': 'End date',
  'hr.contracts.create.baseSalary': 'Base salary',
  'hr.contracts.create.currency': 'Currency',
  'hr.contracts.create.status': 'Status',
  'hr.contracts.create.submit': 'Create contract',
  'hr.contracts.create.submitting': 'Creating…',
  'hr.contracts.create.ok': 'Created a “{type}” contract.',
  'hr.contracts.type.none': 'type unknown',
  'hr.contracts.type.permanent': 'Permanent',
  'hr.contracts.type.fixed_term': 'Fixed term',
  'hr.contracts.type.probation': 'Probation',
  'hr.contracts.type.intern': 'Intern',
  'hr.contracts.status.none': 'status unknown',
  'hr.contracts.status.draft': 'Draft',
  'hr.contracts.status.active': 'Active',
  'hr.contracts.status.expired': 'Expired',
  'hr.contracts.status.terminated': 'Terminated',
  'hr.contracts.row.meta': 'From {start} to {end} · {salary}',
  'hr.contracts.row.noEnd': 'open-ended',
  'hr.contracts.row.noSalary': 'no salary recorded',
  'hr.contracts.row.salary': '{amount} {currency}',
  'hr.contracts.lastUpdated': 'Updated {when}',

  // ── HR: onboarding (WP-W6) ──────────────────────────────────────────────────
  'hr.onboarding.intro':
    'The company’s standard onboarding processes, and each employee’s onboarding progress.',
  'hr.onboarding.scopeNote':
    'Templates are company configuration; checklists belong to one employee and authorise over the tree.',
  'hr.onboarding.category.none': 'uncategorised',
  'hr.onboarding.category.it': 'IT',
  'hr.onboarding.category.hr': 'HR',
  'hr.onboarding.category.manager': 'Manager',
  'hr.onboarding.taskStatus.pending': 'Pending',
  'hr.onboarding.taskStatus.done': 'Done',
  'hr.onboarding.taskStatus.skipped': 'Skipped',
  'hr.onboarding.taskStatus.blocked': 'Blocked',
  'hr.onboarding.checklistStatus.in_progress': 'In progress',
  'hr.onboarding.checklistStatus.completed': 'Completed',
  'hr.onboarding.checklistStatus.cancelled': 'Cancelled',
  'hr.onboarding.templates.title': 'Onboarding templates',
  'hr.onboarding.templates.intro':
    'The company’s standard onboarding processes. This is company configuration, not data about any one employee.',
  'hr.onboarding.templates.subject': 'templates',
  'hr.onboarding.templates.active': 'Active',
  'hr.onboarding.templates.inactive': 'Inactive',
  'hr.onboarding.templates.open': 'View steps',
  'hr.onboarding.templates.empty.title': 'No templates yet',
  'hr.onboarding.templates.empty.body':
    'The company has no onboarding templates. Create one to start.',
  'hr.onboarding.templates.create.title': 'Create a template',
  'hr.onboarding.templates.create.name': 'Template name',
  'hr.onboarding.templates.create.active': 'Active immediately',
  'hr.onboarding.templates.create.submit': 'Create template',
  'hr.onboarding.templates.create.submitting': 'Creating…',
  'hr.onboarding.templates.create.ok': 'Created the template “{name}”.',
  'hr.onboarding.items.title': 'Template steps',
  'hr.onboarding.items.intro':
    'The template’s steps, in order. The offset is how many days after the start each step falls due.',
  'hr.onboarding.items.subject': 'steps',
  'hr.onboarding.items.empty.title': 'No steps yet',
  'hr.onboarding.items.empty.body': 'This template has no steps. Add one above.',
  'hr.onboarding.items.meta': '{category} · due {offset} days after start',
  'hr.onboarding.items.create.title': 'Step title',
  'hr.onboarding.items.create.category': 'Owning team',
  'hr.onboarding.items.create.sortOrder': 'Sort order',
  'hr.onboarding.items.create.offsetDays': 'Days until due',
  'hr.onboarding.items.create.offsetHint':
    'How many days after onboarding starts this step falls due.',
  'hr.onboarding.items.create.submit': 'Add step',
  'hr.onboarding.items.create.submitting': 'Adding…',
  'hr.onboarding.items.create.ok': 'Added the step “{title}”.',
  'hr.onboarding.checklists.title': 'Onboarding by employee',
  'hr.onboarding.checklists.intro':
    'Enter an employee id to view and manage their onboarding progress.',
  'hr.onboarding.checklists.subject': 'checklists',
  'hr.onboarding.checklists.load': 'View progress',
  'hr.onboarding.checklists.none.title': 'No employee selected',
  'hr.onboarding.checklists.none.body':
    'Enter an employee id above to see their onboarding checklists.',
  'hr.onboarding.checklists.empty.title': 'No onboarding checklists',
  'hr.onboarding.checklists.empty.body':
    'This employee has no onboarding checklists. Create one from a template.',
  'hr.onboarding.checklists.progress': '{done}/{total} done',
  'hr.onboarding.checklists.startedAt': 'Started {when}',
  'hr.onboarding.checklists.notStarted': 'not started',
  'hr.onboarding.checklists.create.title': 'Create an onboarding checklist',
  'hr.onboarding.checklists.create.intro':
    'Pick a template to pre-fill its tasks, or leave it empty to create a bare checklist.',
  'hr.onboarding.checklists.create.template': 'Template',
  'hr.onboarding.checklists.create.noTemplate': 'No template (empty checklist)',
  'hr.onboarding.checklists.create.templateHint':
    'Only active templates are shown. Each template step becomes a task.',
  'hr.onboarding.checklists.create.submit': 'Create checklist',
  'hr.onboarding.checklists.create.submitting': 'Creating…',
  'hr.onboarding.tasks.subject': 'tasks',
  'hr.onboarding.tasks.none': 'This checklist has no tasks yet.',
  'hr.onboarding.tasks.meta': '{category} · due {due}',
  'hr.onboarding.tasks.noDue': 'no due date',
  'hr.onboarding.tasks.complete': 'Mark done',
  'hr.onboarding.tasks.completing': 'Saving…',
  'hr.onboarding.tasks.doneMark': 'Done',

  // ── HR: leave / time-off (WP-W7) ─────────────────────────────────────────────
  'hr.leave.intro':
    'Your remaining leave balance, requesting leave, and your own requests — plus an approvals section if you hold that permission.',
  'hr.leave.scopeNote':
    'A leave request is always for yourself — the server takes the employee from the session, and the form has no employee-id field.',
  'hr.leave.status.pending': 'Pending',
  'hr.leave.status.approved': 'Approved',
  'hr.leave.status.rejected': 'Rejected',
  'hr.leave.status.cancelled': 'Cancelled',
  'hr.leave.type.unknown': 'Unknown leave type',
  'hr.leave.types.subject': 'leave types',

  'hr.leave.my.title': 'My leave',

  'hr.leave.balances.title': 'Remaining balance',
  'hr.leave.balances.subject': 'leave balances',
  'hr.leave.balances.empty.title': 'No leave balance yet',
  'hr.leave.balances.empty.body': 'You have no leave balance recorded for this year.',
  'hr.leave.balances.col.type': 'Leave type',
  'hr.leave.balances.col.year': 'Year',
  'hr.leave.balances.col.accrued': 'Accrued',
  'hr.leave.balances.col.used': 'Used',
  'hr.leave.balances.col.remaining': 'Remaining',

  'hr.leave.request.title': 'Request leave',
  'hr.leave.request.intro':
    'A request is always for yourself. The number of days is computed by the server from the working days in the range you choose.',
  'hr.leave.request.subject': 'leave request',
  'hr.leave.request.type': 'Leave type',
  'hr.leave.request.typePlaceholder': '— Choose a leave type —',
  'hr.leave.request.startDate': 'Start date',
  'hr.leave.request.endDate': 'End date',
  'hr.leave.request.reason': 'Reason (optional)',
  'hr.leave.request.submit': 'Submit request',
  'hr.leave.request.submitting': 'Submitting…',
  'hr.leave.request.ok': 'Submitted a {days}-day request, awaiting approval.',

  'hr.leave.myRequests.title': 'My requests',
  'hr.leave.myRequests.subject': 'leave requests',
  'hr.leave.myRequests.empty.title': 'No leave requests yet',
  'hr.leave.myRequests.empty.body': 'You have not filed any leave requests.',
  'hr.leave.myRequests.row.meta': '{start} → {end} · {days} days',
  'hr.leave.myRequests.row.reason': 'Reason: {reason}',
  'hr.leave.myRequests.row.noReason': 'no reason given',
  'hr.leave.myRequests.cancel': 'Cancel request',
  'hr.leave.myRequests.cancelling': 'Cancelling…',

  'hr.leave.approvals.title': 'Leave approvals',
  'hr.leave.approvals.intro':
    'Pick an employee to see their pending leave requests and decide on them.',
  'hr.leave.approvals.subject': 'pending requests',
  'hr.leave.approvals.load': 'View pending requests',
  'hr.leave.approvals.none.title': 'No employee selected',
  'hr.leave.approvals.none.body': 'Pick an employee above to see their pending leave requests.',
  'hr.leave.approvals.empty.title': 'No pending requests',
  'hr.leave.approvals.empty.body': 'This employee has no leave requests awaiting a decision.',
  'hr.leave.approvals.approve': 'Approve',
  'hr.leave.approvals.reject': 'Reject',
  'hr.leave.approvals.deciding': 'Saving…',
  'hr.leave.approvals.insufficientBalance':
    'Not enough leave remaining: {accrued} days accrued, {used} days used, {requested} more days requested.',

  'hr.performance.intro':
    'Review cycles, goals and performance reviews. Every employee sees their own reviews and goals, and can write a self-review; a manager/HR admin can create cycles, assign goals and review employees.',
  'hr.performance.scopeNote':
    'The reviewer is always you (the session) — there is no reviewer field on any form.',

  'hr.performance.cycleStatus.draft': 'Draft',
  'hr.performance.cycleStatus.open': 'Open',
  'hr.performance.cycleStatus.closed': 'Closed',

  'hr.performance.goalStatus.active': 'Active',
  'hr.performance.goalStatus.achieved': 'Achieved',
  'hr.performance.goalStatus.missed': 'Missed',
  'hr.performance.goalStatus.cancelled': 'Cancelled',

  'hr.performance.reviewKind.self': 'Self-review',
  'hr.performance.reviewKind.manager': 'Manager review',
  'hr.performance.reviewKind.peer': 'Peer review',

  'hr.performance.cycleIdLabel': 'Review cycle id',
  'hr.performance.cycleIdHint':
    'Enter the review cycle id. There is no cycle directory to pick from here — ask HR if you do not know it.',

  'hr.performance.my.title': 'My review',

  'hr.performance.myReviews.title': 'My reviews',
  'hr.performance.myReviews.subject': 'reviews',
  'hr.performance.myReviews.empty.title': 'No reviews yet',
  'hr.performance.myReviews.empty.body': 'You have no performance reviews recorded yet.',
  'hr.performance.myReviews.row.overall': 'Overall: {overall}/5',
  'hr.performance.myReviews.row.noOverall': 'no overall rating yet',
  'hr.performance.myReviews.row.noNarrative': 'no narrative yet',

  'hr.performance.myGoals.title': 'My goals',
  'hr.performance.myGoals.subject': 'goals',
  'hr.performance.myGoals.empty.title': 'No goals yet',
  'hr.performance.myGoals.empty.body': 'You have not been assigned any goals.',
  'hr.performance.myGoals.row.noMeasure': 'no measure set',
  'hr.performance.myGoals.row.weight': 'weight {weight}',

  'hr.performance.selfReview.title': 'Write a self-review',
  'hr.performance.selfReview.intro':
    'A self-review is always about yourself — the reviewer is your own session and cannot be changed.',
  'hr.performance.selfReview.subject': 'self-review',
  'hr.performance.selfReview.overall': 'Overall rating (1–5, optional)',
  'hr.performance.selfReview.narrative': 'Narrative (optional)',
  'hr.performance.selfReview.productivityScoreId': 'Supporting productivity score id (optional)',
  'hr.performance.selfReview.submit': 'Submit self-review',
  'hr.performance.selfReview.submitting': 'Submitting…',
  'hr.performance.selfReview.ok': 'Self-review submitted.',

  'hr.performance.manage.title': 'Manage cycles & goals',

  'hr.performance.cycles.title': 'Review cycles',
  'hr.performance.cycles.intro': 'The company’s review calendar — e.g. "H2 2026".',
  'hr.performance.cycles.subject': 'review cycles',
  'hr.performance.cycles.empty.title': 'No cycles yet',
  'hr.performance.cycles.empty.body': 'This company has no review cycles yet. Create one above.',
  'hr.performance.cycles.row.period': '{start} → {end}',
  'hr.performance.cycles.row.noDate': 'not set',
  'hr.performance.cycles.create.name': 'Cycle name',
  'hr.performance.cycles.create.periodStart': 'Period start',
  'hr.performance.cycles.create.periodEnd': 'Period end',
  'hr.performance.cycles.create.status': 'Status',
  'hr.performance.cycles.create.statusDefault': '— Default (draft) —',
  'hr.performance.cycles.create.submit': 'Create cycle',
  'hr.performance.cycles.create.submitting': 'Creating…',
  'hr.performance.cycles.create.ok': 'Created the cycle “{name}”.',

  'hr.performance.manageGoals.title': 'Goals by employee',
  'hr.performance.manageGoals.intro': 'Choose an employee to view and assign goals for them.',
  'hr.performance.manageGoals.subject': 'goals',
  'hr.performance.manageGoals.employeeLabel': 'Employee',
  'hr.performance.manageGoals.none.title': 'No employee selected',
  'hr.performance.manageGoals.none.body': 'Choose an employee above to view and assign goals.',
  'hr.performance.manageGoals.empty.title': 'No goals yet',
  'hr.performance.manageGoals.empty.body': 'This employee has not been assigned any goals.',
  'hr.performance.manageGoals.create.goalTitle': 'Goal title',
  'hr.performance.manageGoals.create.description': 'Description (optional)',
  'hr.performance.manageGoals.create.measure': 'Measure (optional)',
  'hr.performance.manageGoals.create.status': 'Status',
  'hr.performance.manageGoals.create.statusDefault': '— Default (active) —',
  'hr.performance.manageGoals.create.weight': 'Weight (0–100, optional)',
  'hr.performance.manageGoals.create.submit': 'Assign goal',
  'hr.performance.manageGoals.create.submitting': 'Assigning…',
  'hr.performance.manageGoals.create.ok': 'Assigned the goal “{title}”.',

  'hr.performance.review.title': 'Team reviews',
  'hr.performance.teamReviews.subject': 'reviews',
  'hr.performance.teamReviews.load': 'View reviews',
  'hr.performance.teamReviews.cycleFilter': 'Filter by cycle id (optional)',
  'hr.performance.teamReviews.cycleFilterHint': 'Leave blank to see every cycle.',
  'hr.performance.teamReviews.none.title': 'No employee selected',
  'hr.performance.teamReviews.none.body':
    'Enter an employee id above to see their reviews and submit a manager review.',
  'hr.performance.teamReviews.empty.title': 'No reviews yet',
  'hr.performance.teamReviews.empty.body': 'This employee has no reviews recorded yet.',

  'hr.performance.submitReview.title': 'Submit a manager review',
  'hr.performance.submitReview.intro':
    'This review is about the employee selected above — the reviewer is your own session and cannot be changed.',
  'hr.performance.submitReview.subject': 'manager review',
  'hr.performance.submitReview.submit': 'Submit review',
  'hr.performance.submitReview.submitting': 'Submitting…',
  'hr.performance.submitReview.ok': 'Review submitted.',

  // ── Payroll (Task 16, Phase 8a, WP-W9) ─────────────────────────────────────
  'nav.payroll': 'Payroll',

  'payroll.intro':
    'Your own payslip, the company salary-component template, and payroll runs with a two-step approval.',
  'payroll.scopeNote':
    'Everyone sees only their own payslip. Viewing a run for the team and managing the template need their own permission, checked by the server on every request.',

  // CS-3: one rule set (VN) only; every other country is refused rather than computed.
  'payroll.countryEngine.title': 'No payroll rules for this country yet',
  'payroll.countryEngine.body':
    'The payroll engine currently has Vietnamese law only. This company is configured for {country}; change it in Company settings, or wait for the {country} rule set.',

  'payroll.periodIdLabel': 'Period id',
  'payroll.periodIdHint':
    'The period id is on a payslip you have already been issued, in the Payroll runs section below if you may read it, or your HR team can give it to you.',
  'payroll.templateIdLabel': 'Template id',

  'payroll.my.title': 'My payslip',
  'payroll.my.intro': 'Choose a period to view your own payslip.',
  'payroll.my.load': 'View payslip',
  'payroll.my.subject': 'payslip',
  'payroll.my.none.title': 'No period selected',
  'payroll.my.none.body': 'Choose a period above to view your payslip.',
  'payroll.my.empty.title': 'No payslip for this period yet',
  'payroll.my.empty.body':
    'This period has no CEO-approved payroll run yet, or you have no payslip in it. A payslip is released only once a run is officially approved.',
  'payroll.my.deductionCeiling':
    'This period’s deduction ceiling (not a deduction of its own): {value}',
  'payroll.my.net': 'Net pay: {value}',
  'payroll.my.ledger.caption':
    'Payslip ledger: one line per component, the right-hand column the amount in {currency}.',
  'payroll.my.ledger.col.line': 'Line',
  'payroll.my.ledger.col.amount': 'Amount ({currency})',

  'payroll.manage.title': 'Manage the salary-component template',
  'payroll.manage.intro':
    'The company salary-component template and the list of components (Lương CB, Lương chính, allowances, insurance, …).',

  'payroll.templates.title': 'Templates',
  'payroll.templates.subject': 'templates',
  'payroll.templates.create.name': 'Template name',
  'payroll.templates.create.active': 'Active',
  'payroll.templates.create.seedDefaults': 'Seed the Á Châu default components',
  'payroll.templates.create.seedDefaultsHint':
    'Seeds the default component list and formula version 1 from the Á Châu sample; each component may be edited afterward.',
  'payroll.templates.create.submit': 'Create template',
  'payroll.templates.create.submitting': 'Creating…',
  'payroll.templates.create.ok': 'Created the template "{name}".',
  'payroll.templates.empty.title': 'No templates yet',
  'payroll.templates.empty.body':
    'This company has no salary-component template yet. Create one above.',
  'payroll.templates.active': 'Active',
  'payroll.templates.inactive': 'Inactive',
  'payroll.templates.viewComponents': 'View components',

  'payroll.defs.title': 'Salary components',
  'payroll.defs.subject': 'salary components',
  'payroll.defs.none.title': 'No template selected',
  'payroll.defs.none.body':
    'Choose "View components" on a template above to view and add components.',
  'payroll.defs.empty.title': 'This template has no components yet',
  'payroll.defs.empty.body': 'Add the first component in the form below.',
  'payroll.defs.row.meta': 'Kind: {kind} · Source: {source}',
  'payroll.defs.row.unconfirmed': 'Not confirmed',
  'payroll.defs.create.key': 'Component key',
  'payroll.defs.create.name': 'Component name',
  'payroll.defs.create.kind': 'Kind',
  'payroll.defs.create.source': 'Source',
  'payroll.defs.create.formula': 'Formula (optional)',
  'payroll.defs.create.sortOrder': 'Sort order (optional)',
  'payroll.defs.create.submit': 'Add component',
  'payroll.defs.create.submitting': 'Adding…',
  'payroll.defs.create.ok': 'Added the component "{name}".',

  'payroll.runs.title': 'Payroll runs',
  'payroll.runs.intro':
    'Periods, payroll runs, and the two-step approval: the manager approves first, then the CEO approves officially.',

  'payroll.periods.title': 'Periods',
  'payroll.periods.pickLabel': 'Period',
  'payroll.periods.choose': '— Choose a period —',
  'payroll.periods.any': 'Every period',
  'payroll.periods.subject': 'periods',
  'payroll.periods.create.label': 'Period label (e.g. "January 2026")',
  'payroll.periods.create.start': 'Period start',
  'payroll.periods.create.end': 'Period end',
  'payroll.periods.create.submit': 'Create period',
  'payroll.periods.create.submitting': 'Creating…',
  'payroll.periods.create.ok': 'Created the period "{label}".',
  'payroll.periods.empty.title': 'No periods yet',
  'payroll.periods.empty.body': 'This company has no payroll periods yet. Create one above.',
  'payroll.periods.empty.bodyNothing':
    'This company has no payroll periods yet, so there are no payroll runs either. Create a period above first.',
  'payroll.periods.row.range': '{start} → {end}',

  'payroll.periodStatus.draft': 'Draft',
  'payroll.periodStatus.open': 'Open',
  'payroll.periodStatus.closed': 'Closed',

  'payroll.runsList.title': 'Runs',
  'payroll.runsList.subject': 'payroll runs',
  'payroll.runsList.filterPeriod': 'Filter by pay period (optional)',
  'payroll.runsList.filterPeriodHint': 'Leave blank to see every period.',
  'payroll.runsList.filterApply': 'Filter',
  'payroll.runsList.create.submit': 'Create run',
  'payroll.runsList.create.submitting': 'Creating…',
  'payroll.runsList.create.ok': 'Created payroll run #{id}.',
  'payroll.runsList.empty.title': 'No payroll runs yet',
  'payroll.runsList.empty.body': 'No payroll run matches this filter. Create one above.',
  'payroll.runsList.row.id': 'Run #{id}',
  'payroll.runsList.row.meta': 'Period {periodId} · Template {templateId}',
  'payroll.runsList.working': 'Working…',
  'payroll.runsList.calculate': 'Inputs & calculate',
  'payroll.runsList.managerApprove': 'Manager-approve',
  'payroll.runsList.ceoApprove': 'CEO-approve (official)',
  'payroll.runsList.viewItems': 'View team payslips',

  'payroll.approval.groupLabel': 'The two approval signatures of run #{id}',
  'payroll.approval.manager': 'Manager approval',
  'payroll.approval.ceo': 'CEO approval',
  'payroll.approval.unsigned': 'Not signed',
  'payroll.runsList.writePending':
    'A write for run #{id} is in flight. The other runs’ write controls are hidden until it settles — one run write at a time.',

  'payroll.runStatus.draft': 'Draft',
  'payroll.runStatus.calculated': 'Calculated',
  'payroll.runStatus.manager_approved': 'Manager-approved',
  'payroll.runStatus.ceo_approved': 'CEO-approved',
  'payroll.runStatus.paid': 'Paid',

  'payroll.runItems.subject': 'team payslips',
  'payroll.runItems.empty.title': 'No payslips yet',
  'payroll.runItems.empty.body': 'This run has no payslip within the scope you can see.',
  'payroll.runItems.row.employee': 'Employee {employeeId}',
  'payroll.runItems.row.net': 'Net pay: {net}',
  'payroll.runItems.close': 'Close',

  'payroll.inputs.title': 'Manual inputs — run #{id}',
  'payroll.inputs.subject': 'the payroll calculation',
  'payroll.inputs.intro':
    'What the engine cannot work out itself: Lương CB (the insurance base), the contractual productivity pay, the achievement rate, allowances, bonus, advances and the registered dependants. One row per person with a contract active in the period — exactly the people this run will pay.',
  'payroll.inputs.zeroRule':
    'A blank cell is not sent and counts as 0. Recalculating replaces every manual input of the previous calculation with exactly what this grid sends — the grid does not fill the old figures back in.',
  'payroll.inputs.phatLawful':
    'The Penalty column accepts only a ground the company has configured on the formula version in use, and one is required whenever there is an amount, along with a written reason — an amount with no ground, or a ground the server does not recognise, is refused. Depending on the ground, the amount either comes off net pay (possibly capped — over the cap the server refuses the whole calculation rather than quietly lowering the figure) or reduces a bonus before tax (never by more than the bonus recorded). This grid does not offer a list of grounds to pick from; type the exact code your company configured.',
  'payroll.inputs.penalty.basis.label': 'Basis',
  'payroll.inputs.penalty.basis.hint': 'the ground code your company has configured',
  'payroll.inputs.penalty.reason.label': 'Reason',
  'payroll.inputs.penalty.reason.hint':
    'at least {min} characters — name the event and the document it rests on',
  'payroll.inputs.penalty.problem.basis_required': 'An amount needs a basis.',
  'payroll.inputs.penalty.problem.reason_required': 'An amount needs a reason.',
  'payroll.inputs.penalty.problem.reason_too_short': 'The reason needs at least {min} characters.',
  'payroll.inputs.penalty.problem.amount_required': 'A basis or a reason with no amount.',
  'payroll.inputs.capExceeded':
    'A deduction of {amount} for employee #{employee} exceeds the cap your company has configured: at most {pct}% of the monthly wage remaining after compulsory insurance and personal income tax — {cap} for this period. Lower the amount, or spread it across periods.',
  'payroll.inputs.netPayExceeded':
    'A deduction of {amount} for employee #{employee} would take net pay below zero (to {net}) — the server refuses the whole calculation rather than flooring it to 0. Lower the amount, or check this period’s advance.',
  'payroll.inputs.bonusExceeded':
    'A reduction of {amount} for employee #{employee} exceeds the {bonus} bonus it comes off — the server refuses the whole calculation rather than capping it at the bonus. Lower the amount, or check this period’s bonus.',
  'payroll.inputs.issueRefused': 'Field `{field}`: {reason}',
  'payroll.inputs.issueRefusedFor': 'Employee #{employee} — “{field}”: {reason}',
  'payroll.inputs.roster.subject': 'the people this run pays',
  'payroll.inputs.roster.empty.title': 'Nobody has a contract active in this period',
  'payroll.inputs.roster.empty.body': 'Calculating this run will write no payslip.',
  'payroll.inputs.csv.title': 'Paste or choose a CSV file',
  'payroll.inputs.csv.hint':
    'The first line names the columns: {columns} — leave out any, in any order; a paste straight from Excel (TAB-separated) works too. Amounts are whole đồng, digits only: 500000 — if Excel shows 500.000, remove the column’s thousands format before copying. The achievement rate takes a decimal point: 0.9. A blank cell means not entered.',
  'payroll.inputs.csv.paste': 'CSV text',
  'payroll.inputs.csv.file': 'Or choose a CSV file',
  'payroll.inputs.csv.fileError': 'This file could not be read.',
  'payroll.inputs.csv.apply': 'Apply to the grid',
  'payroll.inputs.csv.applied': 'Applied {rows} row(s) to the grid.',
  'payroll.inputs.csv.issues': 'Applied {rows} row(s); these were not:',
  'payroll.inputs.csv.issue.empty': 'There is no header line.',
  'payroll.inputs.csv.issue.unclosed_quote':
    'Line {line}: a double quote opens and never closes — nothing applied.',
  'payroll.inputs.csv.issue.no_employee_id_column':
    'The employee code column is missing — nothing applied.',
  'payroll.inputs.csv.issue.unknown_column':
    'Column "{detail}" is not a manual input — nothing applied. Accepted columns: {columns}.',
  'payroll.inputs.csv.issue.nameless_column':
    'Column {detail} has no name in the header but holds data — nothing applied. Name it (one of: {columns}) or leave it out of the copy.',
  'payroll.inputs.csv.issue.duplicate_column': 'Column "{detail}" appears twice — nothing applied.',
  'payroll.inputs.csv.issue.too_many_rows':
    '{detail} rows, more than the {max} people one calculation takes — nothing applied.',
  'payroll.inputs.csv.issue.cell_count':
    'Line {line}: the cell count ({detail}) does not match the header — line skipped.',
  'payroll.inputs.csv.issue.bad_employee_id':
    'Line {line}: “{detail}” is not an employee code — line skipped.',
  'payroll.inputs.csv.issue.not_in_roster':
    'Line {line}: employee #{detail} has no contract active in the period, so there is no payslip to fill — line skipped.',
  'payroll.inputs.csv.issue.duplicate_employee':
    'Line {line}: employee #{detail} has more than one line — none of theirs applied.',
  'payroll.inputs.grid.caption': 'Each person’s manual inputs for this run',
  'payroll.inputs.grid.employee': 'Employee',
  'payroll.inputs.grid.unnamed': 'Employee no longer in the directory',
  'payroll.inputs.grid.cellLabel': '{field} — {employee}',
  'payroll.inputs.field.so_nguoi_phu_thuoc': 'Registered dependants',
  'payroll.inputs.hint.money': '{currency} — digits only (500000)',
  'payroll.inputs.hint.ty_le_dat': '0.9 = 90%, at most {max}',
  'payroll.inputs.hint.dependants': 'a whole number, at most {max}',
  'payroll.inputs.problem.not_a_number':
    'Not a figure this screen can read: digits only, except that the achievement rate may add a decimal part after a point (0.9, not 0,9); the dependants count is a whole number; and a run of digits too long to read is refused too.',
  'payroll.inputs.problem.not_whole_dong':
    'An amount is whole đồng, digits only — no point or comma between the thousands (500000, not 500.000).',
  'payroll.inputs.problem.below_min': 'The smallest allowed is {min}.',
  'payroll.inputs.problem.above_max': 'The largest allowed is {max}.',
  'payroll.inputs.problem.not_an_integer': 'Must be a whole number.',
  'payroll.inputs.summary':
    '{cells} cell(s) entered for {people}/{roster} people. Anyone with none has every manual input at 0.',
  'payroll.inputs.calculate': 'Calculate for {count} people',
  'payroll.inputs.blocked.problems': '{count} cell(s) are not valid yet — fix them to calculate.',
  'payroll.inputs.blocked.tooMany': 'One calculation takes manual inputs for at most {max} people.',
  'payroll.inputs.blocked.pending':
    'Waiting for the write in flight for run #{id} — one run write at a time.',
  'payroll.inputs.notCalculable':
    'This run now has an approval, so it can no longer be recalculated — the grid and the calculate control are gone. The outcome of the calculation just sent stays here.',
  'payroll.inputs.calculated': 'Calculated: {count} payslip(s).',
  'payroll.inputs.refusedReason': 'The server’s reason: {reason}',
  'payroll.inputs.clear': 'Clear every cell',
  'payroll.inputs.close': 'Close',

  'payroll.pdf.subject': 'a payslip PDF',
  'payroll.pdf.download': 'Download payslip PDF',
  'payroll.pdf.downloading': 'Downloading…',
  'payroll.pdf.delivered': 'Downloaded “{filename}”.',
  'payroll.pdf.reasonNeeded':
    'Enter an access reason of at least {min} characters to enable the PDF download.',
  'payroll.pdf.unreleased':
    'A payslip PDF can be downloaded only once the CEO has approved this run (or it is paid).',
  'payroll.pdf.notFound':
    'There is no released payslip to download: the period is gone, or the CEO has not approved the payslip yet.',

  'payroll.component.luong_cb': 'Base salary (Lương CB)',
  'payroll.component.luong_chinh_theo_hd': 'Contract salary (Lương chính theo HD)',
  'payroll.component.gio_cong': 'Hours worked (Giờ công)',
  'payroll.component.luong_chinh': 'Base pay (Lương chính)',
  'payroll.component.luong_nang_suat_hd': 'Contract productivity pay (Lương năng suất HD)',
  'payroll.component.ty_le_dat': 'Achievement rate (Tỷ lệ đạt)',
  'payroll.component.luong_hieu_qua': 'Performance pay (Lương Hiệu quả)',
  'payroll.component.gio_ot': 'Overtime hours (Giờ OT)',
  'payroll.component.luong_ot': 'Overtime pay (Lương OT)',
  'payroll.component.luong': 'Pay (Lương)',
  'payroll.component.phu_cap_dong_phuc': 'Uniform allowance',
  'payroll.component.phu_cap_xang': 'Fuel allowance',
  'payroll.component.phu_cap_khac': 'Other allowance',
  'payroll.component.tong_phu_cap': 'Total allowances',
  'payroll.component.thuong': 'Bonus',
  'payroll.component.phat': 'Penalty',
  'payroll.component.khau_tru_boi_thuong': 'Deduction per stated ground (from net pay)',
  'payroll.component.giam_thuong': 'Reduction per stated ground (from gross pay)',
  'payroll.component.khau_tru_toi_da': 'Configured monthly deduction ceiling',
  'payroll.component.bhxh_nv': 'Social insurance (employee)',
  'payroll.component.bhyt_nv': 'Health insurance (employee)',
  'payroll.component.kpcd_nv': 'Union fee (employee)',
  'payroll.component.tong_bhnv': 'Total employee insurance',
  'payroll.component.bhxh_cty': 'Social insurance (employer)',
  'payroll.component.bhyt_cty': 'Health insurance (employer)',
  'payroll.component.kpcd_cty': 'Union fee (employer)',
  'payroll.component.tong_bh_cty': 'Total employer insurance',
  'payroll.component.tong_bh_kpcd': 'Total insurance/union fee',
  'payroll.component.tong_luong': 'Gross pay (Tổng lương)',
  'payroll.component.tong_chi_tra': 'Total payout (Tổng chi trả)',
  'payroll.component.tam_ung': 'Advance',
  'payroll.component.thuc_lanh': 'Net pay (Thực lãnh)',

  // ── Geo check-in (Task 17 API, WP-W10, Phase 7 SPA) ─────────────────────────
  'nav.geo': 'Geo check-in',

  'geo.intro':
    "Check in with your browser's GPS location, your own check-in history, and office management (if you hold the permission).",

  'geo.checkin.title': 'Check in',
  'geo.checkin.intro':
    'Consent is required first; the browser will then ask for permission to read your location.',
  'geo.checkin.consent': 'I consent to my GPS location being recorded for this check-in.',
  'geo.checkin.button': 'Check in',
  'geo.checkin.locating': 'Locating…',
  'geo.checkin.submitting': 'Submitting…',
  'geo.checkin.subject': 'check-in',
  'geo.checkin.failure.permission_denied':
    'The browser denied location access. Please allow location access and try again.',
  'geo.checkin.failure.position_unavailable': 'Could not determine a location. Please try again.',
  'geo.checkin.failure.timeout': 'Locating took too long. Please try again.',
  'geo.checkin.failure.unsupported': 'This browser does not support locating.',
  'geo.checkin.conflict.geo_checkin_disabled': 'Your company has not enabled geo check-in.',
  'geo.checkin.conflict.geo_consent_required':
    'You must consent to location tracking before checking in.',
  'geo.checkin.result.within': 'Within "{office}", {distance}m from its centre.',
  'geo.checkin.result.outside':
    'Outside the nearest office "{office}", {distance}m from its centre.',
  'geo.checkin.result.noOffice': 'The company has no office configured to compare against.',
  'geo.checkin.result.noLocation':
    'You have opted out of location capture, so this check-in was recorded without any coordinates. The server enforces that, not the app.',
  'geo.checkin.outsideGeofence':
    'You are {distance} m from the nearest office (radius {radius} m). Move inside the geofence and try again.',

  'geo.badge.within': 'Within range',
  'geo.badge.outside': 'Outside range',
  'geo.badge.noLocation': 'No location collected',
  'geo.row.noOffice': 'No office',
  'geo.row.noLocation':
    'This employee has opted out of location capture, so this check-in stored no coordinates.',
  'geo.row.distance': '{distance}m away',

  'geo.my.title': 'My check-ins',
  'geo.my.subject': 'my check-ins',
  'geo.my.empty.title': 'No check-ins yet',
  'geo.my.empty.body': 'You have not checked in yet.',

  'geo.team.title': "Another employee's check-ins",
  'geo.team.intro':
    "Read another employee's check-in history — an access reason is required because location is sensitive data.",
  'geo.team.employeeIdLabel': 'Employee id',
  'geo.team.employeeIdHint':
    'The Workforce Pulse employee id — the first column on the People screen, not an achau account id.',
  'geo.team.load': 'View check-in history',
  'geo.team.subject': "an employee's check-in history",
  'geo.team.none.title': 'No employee chosen',
  'geo.team.none.body': 'Enter an employee id above to see their check-in history.',
  'geo.team.empty.title': 'No check-ins yet',
  'geo.team.empty.body': 'This employee has not checked in yet.',
  'geo.team.trustFilter': 'Show suspect only',

  'geo.trust.trusted': 'Trusted',
  'geo.trust.suspect': 'Suspect',
  'geo.trust.unverified': 'Unverified',
  'geo.trust.speed': 'Estimated travel speed: {speed} km/h',
  'geo.trust.reason.mock_location': 'The device reported a faked location',
  'geo.trust.reason.low_accuracy': 'Location accuracy is below the allowed threshold',
  'geo.trust.reason.no_accuracy': 'No accuracy data was reported',
  'geo.trust.reason.impossible_travel':
    'Travel speed since the previous check-in is above the allowed threshold',
  'geo.trust.reason.location_opted_out':
    'The employee has opted out of location capture — there are no coordinates to check. A right exercised, not a suspicion raised.',

  'geo.offices.title': 'Offices (check-in geofences)',
  'geo.offices.intro': 'The offices check-ins are measured against, and adding a new one.',
  'geo.offices.subject': 'offices',
  'geo.offices.empty.title': 'No offices yet',
  'geo.offices.empty.body': 'The company has no office configured. Add one above.',
  'geo.offices.row.meta': '{latitude}, {longitude} · {radius}m radius',
  'geo.offices.active': 'Active',
  'geo.offices.inactive': 'Inactive',
  'geo.offices.create.name': 'Office name',
  'geo.offices.create.latitude': 'Latitude',
  'geo.offices.create.longitude': 'Longitude',
  'geo.offices.create.radius': 'Radius (m, defaults to 150)',
  'geo.offices.create.active': 'Active',
  'geo.offices.create.submit': 'Add office',
  'geo.offices.create.submitting': 'Adding…',
  'geo.offices.create.ok': 'Added office "{name}".',
  'geo.offices.radiusHint': 'Minimum 50 m for GPS error; maximum 3000 m',
  'geo.offices.edit.open': 'Edit',
  'geo.offices.edit.cancel': 'Cancel',
  'geo.offices.edit.save': 'Save',
  'geo.offices.edit.saving': 'Saving…',
  'geo.offices.edit.name': 'Office name',
  'geo.offices.edit.latitude': 'Latitude',
  'geo.offices.edit.longitude': 'Longitude',
  'geo.offices.edit.radius': 'Radius (m)',
  'geo.offices.deactivate': 'Turn off',
  'geo.offices.activate': 'Turn back on',

  // ── Geo policy admin panel (Task T2-C-web, Tier-2 ops) ──────────────────────
  'geo.policy.title': "This company's location check-in policy",
  'geo.policy.subject': 'the location check-in policy',
  'geo.policy.enabled': 'Enable location-based check-in',
  'geo.policy.maxAccuracy': 'Maximum GPS accuracy error (m)',
  'geo.policy.maxAccuracy.hint':
    "A location whose reported error is above this threshold is flagged 'unverified' — never refused",
  'geo.policy.maxTravel': 'Maximum travel speed (km/h)',
  'geo.policy.maxTravel.hint':
    "A travel speed above this threshold since the previous check-in is flagged 'suspect' — never refused",
  'geo.policy.mode.legend': 'When a check-in falls outside every office geofence',
  'geo.policy.mode.allow_flag': 'Allow, only flag as outside',
  'geo.policy.mode.allow_flag.note': 'The default — recommended for offices with weak GPS.',
  'geo.policy.mode.block': 'Refuse when outside the geofence',
  'geo.policy.mode.block.note': 'The mobile app shows the distance to the nearest office.',
  'geo.policy.updated.never': 'Not configured yet (using the defaults)',
  'geo.policy.updated.at': 'Last updated: {at} by {by}',
  'geo.policy.updated.unknown': 'unknown',
  'geo.policy.save': 'Save policy',
  'geo.policy.saving': 'Saving…',
  'geo.policy.saveOk': 'Policy saved.',

  // ── Outreach (Task 18 API, WP-W11, Phase 5 SPA) ─────────────────────────────
  'outreach.intro':
    'Compliance controls for candidate outreach: channels, consent, and suppression.',

  'outreach.policy.title': 'Channel policy',
  'outreach.policy.intro':
    'Which channels the company may use to contact candidates, the daily send limit, and whether a message needs approval before it can send.',
  'outreach.policy.defaultNote':
    'Default: nothing is enabled. A company that has configured nothing shows all six channels off.',
  'outreach.policy.subjectWord': 'channel policy',
  'outreach.policy.enabled': 'Enabled',
  'outreach.policy.disabled': 'Disabled',
  'outreach.policy.paused': 'Paused',
  'outreach.policy.active': 'Active',
  'outreach.policy.approvalRequired': 'requires approval',
  'outreach.policy.approvalNotRequired': 'does not require approval',
  'outreach.policy.meta': '{paused} · Limit {limit}/day · {approval}',
  'outreach.policy.neverConfigured':
    'This company has never configured this channel — showing the default.',
  'outreach.policy.updatedAt': 'Last updated: {when}',
  'outreach.policy.highRisk.title': 'High-risk channel',
  'outreach.policy.highRisk.body':
    'Enabling this channel may violate that platform’s Terms of Service and Vietnamese or other local law (including Decree 91’s consent and rate-limit rules). Enabling it is the company’s own choice, at the company’s own risk.',
  'outreach.policy.form.enabled': 'Enable this channel',
  'outreach.policy.form.paused': 'Pause this channel',
  'outreach.policy.form.dailyRateLimit': 'Daily rate limit',
  'outreach.policy.form.requiresApproval': 'Require approval before sending',
  'outreach.policy.form.requiresApprovalLocked':
    'The server always requires approval for high-risk channels — this cannot be turned off.',
  'outreach.policy.form.save': 'Save policy',
  'outreach.policy.form.saving': 'Saving…',
  'outreach.policy.form.ok': 'Channel policy saved.',

  'outreach.channel.email': 'Email',
  'outreach.channel.linkedin_dm': 'LinkedIn — direct message',
  'outreach.channel.linkedin_comment': 'LinkedIn — comment',
  'outreach.channel.facebook_dm': 'Facebook — direct message',
  'outreach.channel.facebook_comment': 'Facebook — comment',
  'outreach.channel.web_scrape': 'Web scrape',

  'outreach.messages.title': 'Outreach messages',
  'outreach.messages.subjectWord': 'outreach message',
  'outreach.messages.empty.title': 'No messages yet',
  'outreach.messages.empty.body': 'No outreach messages have been created yet.',
  'outreach.messages.row.noConsent': 'No consent record',
  'outreach.messages.row.hasConsent': 'Consent recorded',
  'outreach.messages.action.approve': 'Approve',
  'outreach.messages.status.terminal': 'This status is final; there is no action here.',
  'outreach.messages.status.awaitingDispatch':
    'Waiting for the dispatcher to evaluate the remaining gates.',

  'outreach.messages.status.draft': 'Draft',
  'outreach.messages.status.pending_approval': 'Pending approval',
  'outreach.messages.status.approved': 'Approved',
  'outreach.messages.status.sent': 'Sent',
  'outreach.messages.status.suppressed': 'Suppressed',
  'outreach.messages.status.failed': 'Failed',
  'outreach.messages.status.cancelled': 'Cancelled',

  'outreach.messages.create.title': 'Create an outreach message',
  'outreach.messages.create.intro':
    'Sent to a candidate by id — never a typed-in address. The message opens "pending approval" and is not sent yet.',
  'outreach.messages.create.candidate': 'Candidate',
  'outreach.messages.create.candidateIdHint':
    'There is no candidate list here yet, so the candidate code is entered directly.',
  'outreach.messages.create.candidatePlaceholder': '— Choose a candidate —',
  'outreach.messages.create.channel': 'Channel',
  'outreach.messages.create.subject': 'Subject (optional)',
  'outreach.messages.create.body': 'Body',
  'outreach.messages.create.submit': 'Create message',
  'outreach.messages.create.submitting': 'Creating…',
  'outreach.messages.create.ok': 'Message created, pending approval.',

  'outreach.compliance.title': 'Consent & suppression',

  'outreach.consent.title': 'Record consent',
  'outreach.consent.intro':
    'Record that a candidate has agreed to be contacted through a specific channel.',
  'outreach.consent.basis.label': 'Basis',
  'outreach.consent.basis.candidate_initiated': 'Candidate made contact first',
  'outreach.consent.basis.explicit_optin': 'Explicit opt-in',
  'outreach.consent.basis.legitimate_interest': 'Legitimate interest',
  'outreach.consent.note': 'Note (optional)',
  'outreach.consent.submit': 'Record consent',
  'outreach.consent.submitting': 'Recording…',
  'outreach.consent.subjectWord': 'consent record',
  'outreach.consent.ok': 'Consent recorded.',

  'outreach.suppress.title': 'Add to suppression list',
  'outreach.suppress.intro':
    'Block a candidate from being contacted through a specific channel — for example, after they have opted out.',
  'outreach.suppress.reason': 'Reason (optional)',
  'outreach.suppress.submit': 'Add to suppression list',
  'outreach.suppress.submitting': 'Adding…',
  'outreach.suppress.subjectWord': 'suppression list',
  'outreach.suppress.ok': 'Added to the suppression list.',

  // ── Sourcing (Task 20 API, WP-W12, Phase 6 SPA) ─────────────────────────────
  'nav.sourcing': 'Sourcing',
  'sourcing.intro':
    'Automated candidate discovery runs. Only four low-risk channels can be triggered from here; the five high-risk channels are quarantined and always shown with a legal warning.',

  'sourcing.filter.requisition': 'Requisition',
  'sourcing.filter.requisitionIdHint':
    'There is no requisition list here yet, so the requisition code is entered directly.',

  'sourcing.runs.title': 'Discovery runs',
  'sourcing.runs.subjectWord': 'discovery run',
  'sourcing.runs.empty.title': 'No discovery runs yet',
  'sourcing.runs.empty.body': 'No discovery runs match the current filter yet.',
  'sourcing.runs.row.noRequisition': 'Not tied to a specific requisition',
  'sourcing.runs.row.leads': '{found} leads found · {leadsNew} new',

  'sourcing.run.status.pending': 'Pending',
  'sourcing.run.status.running': 'Running',
  'sourcing.run.status.completed': 'Completed',
  'sourcing.run.status.failed': 'Failed',

  'sourcing.channel.career_page': 'Company career page',
  'sourcing.channel.ats_import': 'Import from a previous recruiting system',
  'sourcing.channel.email': 'Email',
  'sourcing.channel.job_board_api': 'Partner job board',
  'sourcing.channel.linkedin_dm': 'LinkedIn — direct message',
  'sourcing.channel.linkedin_comment': 'LinkedIn — comment',
  'sourcing.channel.facebook_dm': 'Facebook — direct message',
  'sourcing.channel.facebook_comment': 'Facebook — comment',
  'sourcing.channel.web_scrape': 'Web scrape',

  'sourcing.trigger.title': 'Trigger a discovery run',
  'sourcing.trigger.intro':
    'Only the four low-risk channels — the company career page, an import from a previous recruiting system, email, and a partner job board — can be chosen here.',
  'sourcing.trigger.requisition': 'Requisition',
  'sourcing.trigger.requisitionIdHint':
    'There is no requisition list here yet, so the requisition code is entered directly.',
  'sourcing.trigger.requisitionPlaceholder': '— Choose a requisition —',
  'sourcing.trigger.channel': 'Sourcing channel',
  'sourcing.trigger.submit': 'Trigger run',
  'sourcing.trigger.submitting': 'Triggering…',
  'sourcing.trigger.subjectWord': 'discovery run',
  'sourcing.trigger.ok':
    'Run complete: {leadsFound} leads found, {leadsNew} new, {candidatesCreated} candidates created.',

  'sourcing.quarantine.title': 'High-risk channels — quarantined',
  'sourcing.quarantine.intro':
    'These five channels are never a choice on this screen, whatever company policy or server configuration says — always shown, never hidden.',
  'sourcing.quarantine.badge': 'Quarantined',
  'sourcing.quarantine.highRisk.title': 'High-risk channel',
  'sourcing.quarantine.highRisk.body':
    'LinkedIn’s and Facebook’s own Terms of Service prohibit scraping, aggregating or mass-messaging; contacting a candidate without their prior consent may also violate Decree 91/2020 (consent and rate limits) and the Personal Data Protection Law 91/2025/QH15 with Decree 356/2025. This channel cannot be triggered from this screen.',

  // ── Inbox (Task 23 API, WP-W13, Phase 4c SPA) ───────────────────────────────
  'nav.inbox': 'Inbox',
  'inbox.intro':
    'The original email a candidate sent, with its CV attachment — the context for reviewing the AI-drafted reply on the Outreach screen before it is approved.',
  'inbox.subjectWord': 'inbound email',
  'inbox.subjectAccess': 'attachment',

  'inbox.filter.status': 'Parse status',
  'inbox.filter.candidate': 'Matched candidate',

  'inbox.list.empty.title': 'No messages match this filter',
  'inbox.list.empty.body': 'No inbound messages match the current filter.',
  'inbox.list.partial.excludes': 'Showing the first {shown} messages in the inbox',
  'inbox.list.noSubject': '(no subject)',
  'inbox.list.noSender': '(unknown sender)',
  'inbox.list.noDate': '(unknown time)',
  'inbox.list.meta': 'From {from} · {when}',
  'inbox.list.unmatched': 'No candidate matched yet — a lead to triage',
  'inbox.list.matched': 'Matched candidate: {name}',
  'inbox.list.attachments': '{count} attachment(s)',
  'inbox.list.open': 'Open message',

  'inbox.detail.subject': 'inbound email',
  'inbox.detail.from': 'From',
  'inbox.detail.receivedAt': 'Received',
  'inbox.detail.none': '—',
  'inbox.detail.bodyTitle': 'Message body',
  'inbox.detail.noBody': 'This message has no text body.',
  'inbox.detail.candidateTitle': 'Matched candidate',
  'inbox.detail.unmatched': 'The sender has not matched a candidate yet — a lead to triage.',

  'inbox.attachments.title': 'Attachments',
  'inbox.attachments.empty.title': 'This message has no attachments',
  'inbox.attachments.unnamed': '(no filename)',
  'inbox.attachments.meta': '{type} · {size}',
  'inbox.attachments.typeUnknown': 'unknown type',
  'inbox.attachments.sizeUnknown': 'unknown size',
  'inbox.attachments.sizeKb': '{kb} KB',
  'inbox.attachments.rejected': 'This attachment could not be stored: {reason}',
  'inbox.attachments.rejectedUnknown': 'reason unknown',
  'inbox.attachments.download': 'Download attachment',
  'inbox.attachments.downloading': 'Downloading…',
  'inbox.attachments.reasonNeeded':
    'Enter an access reason of at least {min} characters to enable the download button.',
  'inbox.attachments.delivered': 'Downloaded: {filename}',

  'inbox.parseStatus.parsed': 'Fully parsed',
  'inbox.parseStatus.partial': 'Partially parsed',
  'inbox.parseStatus.needs_human': 'Needs human review',

  'nav.presence': 'Presence',

  'presence.intro':
    'A live coordination board: who is working, where, and on what — not a productivity ranking.',
  'presence.updatedAt': 'Updated at {at}',
  'presence.neverUpdated': 'Never updated',
  'presence.updating': 'Updating…',

  'presence.summary.heading': 'Overview by status',
  'presence.summary.loading': 'Loading the presence overview',
  'presence.summary.subject': 'presence overview',
  'presence.summary.total': '{count} people in scope, total',
  'presence.summary.staleAfterError': 'The last refresh failed; the figures above are stale.',

  'presence.filters.legend': 'Filters',
  'presence.filter.status': 'Status',
  'presence.filter.department': 'Department',
  'presence.filter.office': 'Office',
  'presence.filter.officeHint':
    'Office ids are listed on Company settings, under locations. Leave it empty for every office.',
  'presence.filter.apply': 'Apply',
  'presence.filter.reload': 'Reload',
  'presence.filter.all': 'All',

  'presence.list.heading': 'Presence list',
  'presence.list.loading': 'Loading the presence list',
  'presence.list.subject': 'presence list',
  'presence.list.empty.title': 'Nobody in scope',
  'presence.list.empty.body': 'Your access does not currently reach any employee.',
  'presence.list.staleAfterError': 'The last refresh failed; the list below is stale.',
  'presence.list.col.employee': 'Employee',
  'presence.list.col.status': 'Status',
  'presence.list.col.where': 'Where',
  'presence.list.col.onWhat': 'On what',
  'presence.list.col.lastSeen': 'Last seen',
  'presence.noDepartment': 'No department',

  'presence.where.none': 'Not checked in today',
  'presence.where.noOffice': 'No office matched',
  'presence.where.within': 'Within the office geofence',
  'presence.where.outside': 'Outside the office geofence',
  'presence.onWhat.none': 'No open task',

  'presence.status.working_onsite': 'Working, at the office',
  'presence.status.working_remote': 'Working, remote',
  'presence.status.on_break': 'On break',
  'presence.status.offline': 'Offline',
  'presence.status.help.working_onsite':
    'A task timer is running, and the check-in recorded today landed inside an office geofence.',
  'presence.status.help.working_remote':
    'A task timer is running, but no check-in recorded today landed inside an office geofence.',
  'presence.status.help.on_break': 'The task timer is paused.',
  'presence.status.help.offline': 'No task timer is open.',

  'presence.trust.trusted': 'Trusted',
  'presence.trust.suspect': 'Needs review',
  'presence.trust.unverified': 'Unverified',

  // ── Effectiveness (Task P3 API, PW2 pillar web) ─────────────────────────────
  'nav.effectiveness': 'Effectiveness',

  'effectiveness.intro':
    'Goals are the primary figure; the productivity score is only secondary context, ' +
    'always shown with its confidence; the team is read as a distribution for spotting ' +
    'who needs support — never a ranking.',
  'effectiveness.insufficientData': 'Insufficient data',
  'effectiveness.noDepartment': 'No department',
  'effectiveness.department': 'Department {id}',

  'effectiveness.framing.title': 'Not a quota, not a ranking',
  'effectiveness.framing.body':
    'Context for a supportive conversation between a manager and an employee — never an ' +
    'automatic decision, a quota, or a punitive ranking.',

  'effectiveness.list.heading': 'By employee',
  'effectiveness.list.loading': 'Loading the effectiveness list',
  'effectiveness.list.subject': 'effectiveness list',
  'effectiveness.list.empty.title': 'Nobody is in scope',
  'effectiveness.list.empty.body': 'Your access scope currently has no employees in it.',
  'effectiveness.list.col.employee': 'Employee',
  'effectiveness.list.col.goals': 'Goals',
  'effectiveness.list.col.productivity': 'Productivity score (secondary context)',
  'effectiveness.list.col.attendance': 'Attendance',

  'effectiveness.goals.counts': '{achieved}/{total} goals achieved',
  'effectiveness.goals.achievementPct': 'Achievement: {value}',

  'effectiveness.productivity.value': 'Productivity score',
  'effectiveness.productivity.confidence': 'Confidence: {value}',
  'effectiveness.productivity.confidence.help':
    'How sure this figure is, based on the data observed for the period.',
  'effectiveness.productivity.lowConfidence.title': 'Low confidence',
  'effectiveness.productivity.lowConfidence.body':
    'This score is based on incomplete data. Read it beside goals and attendance for this ' +
    'period, and do not use this score alone for a disciplinary decision.',

  'effectiveness.attendance.days': 'Present {present}/{expected} expected days',
  'effectiveness.attendance.onTimeRate': 'On-time rate: {value}',

  'effectiveness.team.heading': 'Team distribution',
  'effectiveness.team.loading': 'Loading the team distribution',
  'effectiveness.team.subject': 'team effectiveness distribution',
  'effectiveness.team.notRanking':
    'A distribution for spotting who needs support — not a ranked list of individuals.',
  'effectiveness.team.memberCount': 'People in scope: {count}',
  'effectiveness.team.goalAchievement': 'Goal achievement (distribution)',
  'effectiveness.team.productivityScore': 'Productivity score (distribution)',
  'effectiveness.team.col.min': 'Min',
  'effectiveness.team.col.p25': 'P25',
  'effectiveness.team.col.median': 'Median',
  'effectiveness.team.col.p75': 'P75',
  'effectiveness.team.col.max': 'Max',
  'effectiveness.team.lowConfidenceCount': 'Low-confidence scores: {count}',
  'effectiveness.team.noGoalDataCount': 'People with no goal data: {count}',
  'effectiveness.team.noProductivityScoreCount': 'People with no productivity score: {count}',

  // ── People directory (Task T2-A API, T2-B pillar web, manage-people pillar) ──
  'nav.people': 'People',

  'people.intro':
    'The people directory, for the scope you may see. Filters live in the URL, so a filtered ' +
    'view is shareable or bookmarkable — unlike a per-user saved filter some other products use.',
  'people.noDepartment': 'No department',
  'people.noManager': 'No manager',

  'people.picker.loading': 'Searching for an employee',
  'people.picker.subject': 'employee list',
  'people.picker.empty': 'No employee found',

  'people.filters.legend': 'Filters',
  'people.filter.search': 'Search by name or email',
  'people.filter.status': 'Status',
  'people.filter.status.active': 'Active',
  'people.filter.status.offboarded': 'Offboarded',
  'people.filter.status.all': 'All',
  'people.filter.department': 'Department',
  'people.filter.department.all': 'All departments',
  'people.filter.department.option': '{name} ({count})',
  'people.filter.role': 'Role',
  'people.filter.role.all': 'All roles',

  'people.list.heading': 'Employee list',
  'people.list.caption':
    'The employee directory for your scope, sortable by full name, department or sync time.',
  'people.list.loading': 'Loading the employee list',
  'people.list.subject': 'employee list',
  'people.list.empty.title': 'Nobody is in scope',
  'people.list.empty.body': 'Your access scope currently has no employee matching this filter.',
  'people.list.col.fullName': 'Full name',
  'people.list.col.email': 'Email',
  'people.list.col.role': 'Role',
  'people.list.col.department': 'Department',
  'people.list.col.jobTitle': 'Job title',
  'people.list.col.manager': 'Manager',
  'people.list.col.status': 'Status',
  'people.list.col.syncedAt': 'Synced at',

  'people.status.active': 'Active',
  'people.status.offboarded': 'Offboarded',

  'people.pagination.range': 'Showing {from}–{to} / {total}',
  'people.pagination.prev': 'Previous',
  'people.pagination.next': 'Next',

  'people.drawer.title': 'Profile: {name}',
  'people.drawer.close': 'Close',
  'people.drawer.loading': 'Loading the employee profile',
  'people.drawer.subject': 'employee profile',
  'people.drawer.field.email': 'Email',
  'people.drawer.field.role': 'Role',
  'people.drawer.field.jobTitle': 'Job title',
  'people.drawer.field.department': 'Department',
  'people.drawer.field.branch': 'Branch',
  'people.drawer.field.manager': 'Manager',
  'people.drawer.field.directReports': 'Direct reports',
  'people.drawer.field.status': 'Status',
  'people.drawer.field.syncedAt': 'Synced at',
  'people.drawer.field.offboardedAt': 'Offboarded at',
  'people.drawer.quickLinks.heading': 'Quick links',
  'people.drawer.quickLinks.attendance': "View this employee's attendance",
  'people.drawer.quickLinks.note':
    'Geo check-in, Presence and Effectiveness cannot yet be opened pre-filtered to one employee.',

  // ── Permission grants admin (Task T2-D, Tier-2 ops) ─────────────────────────
  'nav.adminPermissions': 'Permissions',

  'adminPermissions.subject': 'permission grant list',

  'adminPermissions.intro':
    'Each role (CEO, Manager, Employee) holds an implicit default permission set, not shown ' +
    'as rows here. A grant below records only the deviation from that default: effect ' +
    '"Allow" adds a permission code, "Deny" removes one — and when both apply to the same ' +
    'subject, "Deny" always beats "Allow". Expired or revoked rows stay in this list for ' +
    'audit purposes; they are never deleted.',

  'adminPermissions.create.open': 'Grant permission',

  'adminPermissions.list.heading': 'Grant list',
  'adminPermissions.list.caption':
    'Every permission granted in the active company, expired and revoked ones included.',
  'adminPermissions.list.loading': 'Loading the grant list',
  'adminPermissions.list.empty.title': 'No permission has been granted yet',
  'adminPermissions.list.empty.body':
    'No row matches the current filter. Default role permissions still apply as usual.',
  'adminPermissions.list.col.subject': 'Subject',
  'adminPermissions.list.col.code': 'Permission code',
  'adminPermissions.list.col.effect': 'Effect',
  'adminPermissions.list.col.reason': 'Reason',
  'adminPermissions.list.col.grantedBy': 'Granted by/at',
  'adminPermissions.list.col.expiresAt': 'Expires',
  'adminPermissions.list.col.status': 'Status',
  'adminPermissions.list.col.actions': 'Actions',
  'adminPermissions.noExpiry': 'No expiry',
  'adminPermissions.noReason': '—',
  'adminPermissions.codeCell': '{group} · {code}',
  'adminPermissions.grantedCell': '{by} at {at}',
  'adminPermissions.grantedByUnknown': 'unknown',

  'adminPermissions.subjectType.employee': 'Employee',
  'adminPermissions.subjectType.role': 'Role',
  'adminPermissions.subjectType.department': 'Department',
  'adminPermissions.subjectType.jobTitle': 'Job title',

  'adminPermissions.effect.allow': 'Allow',
  'adminPermissions.effect.deny': 'Deny',

  'adminPermissions.status.active': 'Active',
  'adminPermissions.status.expired': 'Expired',
  'adminPermissions.status.revoked': 'Revoked',

  'adminPermissions.group.workforce': 'Workforce',
  'adminPermissions.group.recruiting': 'Recruiting',
  'adminPermissions.group.hr': 'HR',
  'adminPermissions.group.payroll': 'Payroll',

  'adminPermissions.filters.legend': 'Filters',
  'adminPermissions.filter.subjectType': 'Subject',
  'adminPermissions.filter.subjectType.all': 'All subjects',
  'adminPermissions.filter.group': 'Permission group',
  'adminPermissions.filter.group.all': 'All groups',
  'adminPermissions.filter.effect': 'Effect',
  'adminPermissions.filter.effect.all': 'All',
  'adminPermissions.filter.status': 'Status',
  'adminPermissions.filter.status.all': 'All statuses',

  'adminPermissions.form.heading': 'Grant permission',
  'adminPermissions.form.subjectType.label': 'Subject type',
  'adminPermissions.form.employee.label': 'Employee',
  'adminPermissions.form.role.label': 'Role',
  'adminPermissions.form.role.placeholder': 'Choose a role',
  'adminPermissions.form.department.label': 'Department',
  'adminPermissions.form.department.placeholder': 'Choose a department',
  'adminPermissions.form.jobTitle.label': 'Job title',
  'adminPermissions.form.jobTitle.placeholder': 'e.g. HR Manager',
  'adminPermissions.form.permissionCode.label': 'Permission code',
  'adminPermissions.form.permissionCode.placeholder': '— Choose a permission code —',
  'adminPermissions.form.effect.label': 'Effect',
  'adminPermissions.form.reason.label': 'Reason',
  'adminPermissions.form.reason.hint': 'Required on this screen so the grant can be traced later.',
  'adminPermissions.form.expiresAt.label': 'Expires (optional)',
  'adminPermissions.form.submit': 'Grant permission',
  'adminPermissions.form.submitting': 'Granting…',
  'adminPermissions.form.cancel': 'Cancel',
  'adminPermissions.form.created': 'Permission granted',
  'adminPermissions.form.updated': 'Permission updated',

  'adminPermissions.revoke.action': 'Revoke',
  'adminPermissions.revoke.title': 'Revoke "{code}" from {subject}?',
  'adminPermissions.revoke.body':
    'The row moves to "Revoked" and stays in the list for audit; the permission is removed ' +
    'as soon as you confirm.',
  'adminPermissions.revoke.confirm': 'Confirm revoke',
  'adminPermissions.revoke.confirming': 'Revoking…',
  'adminPermissions.revoke.cancel': 'Cancel',
  'adminPermissions.revoke.done': 'Permission revoked.',

  'adminPermissions.code.workforce.view_self':
    'View your own data (attendance, activity, productivity).',
  'adminPermissions.code.workforce.view_department': "View your managed department's data.",
  'adminPermissions.code.workforce.view_workspace': "View the whole company's data.",
  'adminPermissions.code.workforce.manage_shifts':
    'Manage shifts; approve attendance and time corrections.',
  'adminPermissions.code.workforce.manage_policies':
    "Manage monitoring policy, access grants and the company's configuration.",
  'adminPermissions.code.workforce.manage_devices': 'Manage devices and Agent registration codes.',
  'adminPermissions.code.workforce.view_apps': 'View application-usage data.',
  'adminPermissions.code.workforce.view_websites': 'View website-visit data.',
  'adminPermissions.code.workforce.view_file_events':
    'View file-operation events (no screen for it yet).',
  'adminPermissions.code.workforce.view_usb_events':
    'View USB-device events (no screen for it yet).',
  'adminPermissions.code.workforce.view_screenshots': 'View screenshots (no screen for it yet).',
  'adminPermissions.code.workforce.export_reports':
    'Export reports and view scheduled report exports.',
  'adminPermissions.code.workforce.manage_alerts': 'Manage alert rules.',
  'adminPermissions.code.workforce.resolve_incidents':
    'View and resolve incidents; acknowledge alerts.',
  'adminPermissions.code.workforce.view_audit_logs': 'View the audit log.',
  'adminPermissions.code.workforce.manage_retention':
    'Manage the data-retention policy (no screen for it yet).',
  'adminPermissions.code.workforce.use_ai_analytics': 'Use AI analytics.',
  'adminPermissions.code.recruiting.view_jobs': 'View job postings.',
  'adminPermissions.code.recruiting.manage_jobs': 'Create and manage job postings.',
  'adminPermissions.code.recruiting.view_candidates': 'View candidate profiles.',
  'adminPermissions.code.recruiting.manage_candidates':
    'Manage candidates, outreach and the recruiting pipeline.',
  'adminPermissions.code.recruiting.view_candidate_documents': "View a candidate's documents/CV.",
  'adminPermissions.code.recruiting.submit_feedback': 'Submit interview feedback.',
  'adminPermissions.code.recruiting.manage_offers': 'Create and manage job offers.',
  'adminPermissions.code.recruiting.hire': 'Convert a hired candidate into an employee.',
  'adminPermissions.code.hr.view_employee_hr':
    "View an employee's HR record (contracts, documents).",
  'adminPermissions.code.hr.view_employee_documents':
    "View the content of an employee's HR documents.",
  'adminPermissions.code.hr.manage_employee_documents': 'Manage (upload/delete) HR documents.',
  'adminPermissions.code.hr.manage_onboarding':
    'Manage onboarding templates and a new hire’s progress.',
  'adminPermissions.code.hr.view_leave': "View someone else's leave requests and balances.",
  'adminPermissions.code.hr.request_leave': 'File your own leave request.',
  'adminPermissions.code.hr.approve_leave': 'Approve leave requests.',
  'adminPermissions.code.hr.manage_leave_types': 'Manage leave types.',
  'adminPermissions.code.hr.view_reviews': "View someone else's performance reviews.",
  'adminPermissions.code.hr.manage_review_cycles': 'Manage review cycles and goals.',
  'adminPermissions.code.hr.submit_review': "Submit a performance review (one's own or another's).",
  'adminPermissions.code.hr.view_own_review': 'View your own performance review.',
  'adminPermissions.code.payroll.view_own_payslip': 'View your own payslip.',
  'adminPermissions.code.payroll.view_payroll': 'View payroll periods, runs and their line items.',
  'adminPermissions.code.payroll.run_payroll': 'Create a payroll period and calculate a run.',
  'adminPermissions.code.payroll.approve_payroll': "Approve a payroll run (manager's step).",
  'adminPermissions.code.payroll.ceo_approve': "Approve a payroll run (CEO's step).",
  'adminPermissions.code.payroll.manage_templates': 'Manage salary component templates.',
  'adminPermissions.code.payroll.manage_regulations': 'Manage payroll formulas and regulations.',

  'nav.roster': 'Roster',
  'nav.myData': 'My data',

  'myData.intro':
    'You have the right to receive a copy of your own personal data under the Personal Data Protection Law (Law 91/2025/QH15).',
  'myData.export.title': 'Copy of my data',
  'myData.export.policy':
    'Up to {cap} requests a day. We aim to finish within {hours} hours. The file is kept for {days} days after it is assembled.',
  'myData.export.cta': 'Request a copy of my data',
  'myData.export.requesting': 'Sending the request…',
  'myData.export.subject': 'your data-copy requests',
  'myData.export.loading': 'Loading your requests',
  'myData.export.empty.title': 'You have not asked for a copy yet.',
  'myData.export.empty.body':
    'When you ask, we assemble a file of your own attendance days, GPS check-ins, activity timeline and released payslips.',
  'myData.export.history': 'Earlier requests',
  'myData.status.pending': 'Queued',
  'myData.status.running': 'Being prepared',
  'myData.status.done': 'Ready',
  'myData.status.failed': 'Failed',
  'myData.requestedAt': 'Requested at {at}',
  'myData.fileUntil': 'Downloadable until {at}',
  'myData.fileGone': 'The file is past its retention window. Ask for a new copy.',
  'myData.download': 'Download',
  'myData.failed.title': 'The file could not be assembled',
  'myData.failed.body': 'Ask for a new copy. This request stays on the record.',

  // Tier-3 U9 — region 2 of /my-data, "who has looked at my data".
  'myData.access.title': 'Who has looked at my data',
  'myData.access.intro':
    'Every read of sensitive data about you is recorded, with the reason the reader gave. ' +
    'Company-wide reads appear here too, labelled — those were not aimed at you personally.',
  'myData.access.subject': 'your access log',
  'myData.access.loading': 'Loading your access log',
  'myData.access.empty.title': 'Nobody has read your data.',
  'myData.access.empty.body':
    'When somebody does, each read appears here: who, what, when and why.',
  'myData.access.companyWide': 'company-wide',
  'myData.access.col.actor': 'Who read it',
  'myData.access.col.what': 'What',
  'myData.access.col.at': 'When',
  'myData.access.col.reason': 'Reason',
  'myData.access.at': 'At {at}',
  'myData.access.what': '{verb} · {kind}',
  'myData.access.reason': 'Reason: {reason}',
  'myData.access.more': 'Show more',
  'myData.access.moreLoading': 'Loading…',
  'myData.access.verb.view': 'Viewed',
  'myData.access.verb.list': 'Listed',
  'myData.access.verb.export': 'Exported',
  'myData.access.verb.download': 'Downloaded',
  'myData.access.verb.ai_query': 'Asked the AI assistant',
  'myData.access.kind.screenshot': 'Screenshots',
  'myData.access.kind.usb_event': 'USB events',
  'myData.access.kind.file_event': 'File events',
  'myData.access.kind.audit_log': 'Audit log',
  'myData.access.kind.employee_activity': 'Work activity',
  'myData.access.kind.employee_attendance': 'Attendance data',
  'myData.access.kind.report': 'Report',
  'myData.access.kind.incident_evidence': 'Incident evidence',
  'myData.access.kind.ai_insight': 'An AI analysis',
  'myData.access.kind.candidate_document': 'Candidate document',
  'myData.access.kind.employee_document': 'HR document',
  'myData.access.kind.employee_location': 'Check-in location',
  'myData.access.kind.dsar_export': 'Copy of my personal data',

  'roster.intro':
    'A weekly shift grid, resolved by the same order of precedence used for a single day. Labour Code warnings are for information and never block scheduling.',
  'roster.subject': 'the weekly roster',

  'roster.filters.legend': 'Filters',
  'roster.filter.department': 'Department',
  'roster.filter.department.all': 'All departments',

  'roster.week.prev': 'Previous week',
  'roster.week.next': 'Next week',
  'roster.week.thisWeek': 'This week',
  'roster.week.range': 'Week {from} – {to}',

  'roster.grid.loading': 'Loading the roster',
  'roster.grid.empty.title': 'Nobody is in scope',
  'roster.grid.empty.body': "Your access scope currently has no employee for this week's filter.",
  'roster.grid.col.employee': 'Employee',
  'roster.grid.col.weekTotal': 'Week total',
  'roster.grid.caption':
    'The weekly roster: each row is one employee, each column one day; the last column is the ' +
    'total hours scheduled this week.',
  'roster.grid.noDepartment': 'No department',

  'roster.cell.dayOff': 'Day off, per schedule',
  'roster.cell.unconfigured': 'Not configured',
  'roster.cell.open': 'View/assign shifts for {name} on {date}',

  'roster.via.job_title': 'via job title',
  'roster.via.department': 'via department',
  'roster.via.branch': 'via branch',
  'roster.via.company': 'via company',
  'roster.via.default': 'system default',

  'roster.scope.employee': 'Employee',
  'roster.scope.job_title': 'Job title',
  'roster.scope.department': 'Department',
  'roster.scope.branch': 'Branch',
  'roster.scope.company': 'Whole company',

  'roster.warning.daily_over_8h': 'Over 8h/day',
  'roster.warning.weekly_over_48h': 'Over 48h/week',
  'roster.warning.days_over_6_per_week': 'Over 6 days/week',
  'roster.warning.rest_under_12h': 'Under 12h rest',
  'roster.warning.night_work': 'Has a night shift (22:00–06:00)',
  'roster.warning.overlap': 'Overlapping shift',
  'roster.warning.unknown': 'Warning (unrecognised code)',
  'roster.warning.detail.minutes': '{minutes} minutes scheduled',
  'roster.warning.detail.days': '{days} days worked',

  'roster.drawer.title': 'Assign shifts: {name}',
  'roster.drawer.close': 'Close',
  'roster.drawer.loading': 'Loading the assignment list',
  'roster.drawer.subject': 'the assignment list',
  'roster.drawer.empty': 'This employee has no shift assignment yet.',
  'roster.drawer.list.heading': 'Assignments',
  'roster.drawer.list.note':
    'Department/branch/job-title/whole-company rows are policy that applies to many people, ' +
    'not only this employee.',
  'roster.drawer.list.col.scope': 'Scope',
  'roster.drawer.list.col.shift': 'Shift',
  'roster.drawer.list.col.effective': 'Effective',
  'roster.drawer.list.col.warnings': 'Warnings',
  'roster.drawer.list.col.actions': 'Actions',
  'roster.drawer.list.ended': 'Ended {date}',
  'roster.drawer.list.effective.openEnded': 'From {from}',
  'roster.drawer.list.effective.ranged': '{from} – {to}',

  'roster.assign.heading': 'Assign a shift',
  'roster.assign.shift': 'Shift',
  'roster.assign.shift.placeholder': '— Choose a shift —',
  'roster.assign.effectiveFrom': 'Effective from',
  'roster.assign.effectiveTo': 'Effective to (optional)',
  'roster.assign.submit': 'Assign',
  'roster.assign.submitting': 'Assigning…',
  'roster.assign.done': 'Shift assigned. Warnings do not block — you may still adjust it.',
  'roster.assign.noShifts': 'No active shift is configured yet to assign.',
  'roster.assign.skipped.employee_not_in_company':
    'This employee no longer belongs to the company, so the shift was not assigned.',
  'roster.assign.skipped.unknown':
    'This employee could not be assigned a shift. Please try again or contact an administrator.',

  'roster.end.action': 'End',
  'roster.end.title': 'End the "{shift}" assignment for {subject}?',
  'roster.end.body':
    'The assignment stops as of the end date below; attendance already computed against it is ' +
    'kept, not deleted.',
  'roster.end.effectiveTo': 'End date (blank = today)',
  'roster.end.confirm': 'Confirm end',
  'roster.end.confirming': 'Ending…',
  'roster.end.cancel': 'Cancel',
  'roster.end.alreadyEnded': 'Already ended.',

  'nav.dsarQueue': 'Data subject requests',

  'dsar.queue.intro':
    'Every data-subject request in this company. Executing an ERASURE deletes that ' +
    'person’s data permanently, immediately, and with no way back. What the law requires ' +
    'kept — payslips, HR file, contracts — and the audit trail are retained, and the result ' +
    'names every one of those records.',
  'dsar.queue.subject': 'the data-subject request queue',
  'dsar.queue.empty': 'No requests yet.',
  'dsar.queue.table.caption': 'This company’s data-subject requests',

  'dsar.queue.column.id': 'Request',
  'dsar.queue.column.employee': 'Employee',
  'dsar.queue.column.kind': 'Kind',
  'dsar.queue.column.status': 'Status',
  'dsar.queue.column.requested': 'Requested',
  'dsar.queue.column.result': 'Result',

  'dsar.kind.export': 'Copy of data',
  'dsar.kind.erasure': 'Erasure',

  'dsar.queue.progress': 'already run:',
  'dsar.queue.retained': 'Retained under a legal obligation:',
  'dsar.queue.evidence': 'Retained as the record of processing:',

  'dsar.queue.execute': 'Execute erasure…',
  'dsar.queue.executing': 'Erasing…',
  'dsar.queue.confirm.warning':
    'This deletes this employee’s raw and derived data now, and there is no way to undo it. ' +
    'No draft, no recycle bin.',
  'dsar.queue.confirm.remains':
    'What remains: payslips and HR records inside their mandatory retention window, any rows ' +
    'under legal hold, and the audit trail that records this erasure.',
  'dsar.queue.confirm.typeId': 'Type the request id to confirm',
  'dsar.queue.confirm.execute': 'Erase permanently',
  'dsar.queue.error.notOffboarded.title': 'Employee has not been offboarded',
  'dsar.queue.error.notOffboarded':
    'Only an offboarded employee can be erased. Complete their offboarding first — that is ' +
    'what revokes their devices and ends their sessions; without it their agent is still ' +
    'writing new rows while the erasure runs.',

  'nav.retention': 'Retention',

  'retention.intro':
    'One row per data class, each with its own version. Lowering a window deletes nothing ' +
    'today: the next cleanup run does, and it deletes retroactively — no grace period, no undo.',
  'retention.subject': 'the retention settings',
  'retention.table.caption': 'Retention window by data class',

  'retention.column.dataClass': 'Data class',
  'retention.column.days': 'Days kept',
  'retention.column.floor': 'Floor',
  'retention.column.updated': 'Last changed',
  'retention.column.state': 'State',

  'retention.floor.range': '{min}–{max} days allowed',
  'retention.floor.recordKeeping': 'Floor {days} days — record-keeping obligation',
  'retention.floor.bounds': 'Floor {days} days — operational bracket',
  'retention.version': 'Version {version}',
  'retention.awaitingMigration': 'No table to sweep yet',
  'retention.awaitingMigration.hint':
    'This class has nothing to sweep yet. That is a declared absence, not “0 rows deleted”.',
  'retention.enabled': 'Sweeping on',
  'retention.disabled': 'Sweeping off',

  'retention.reason': 'Reason (stored on the audit row)',
  'retention.reason.hint':
    'At least {min} characters. Every class you apply writes one audit log row.',
  'retention.acknowledge':
    'I understand the next run will delete data that is inside the current window',
  'retention.acknowledge.required':
    'This class is being shortened from {from} to {to} days. Confirm before saving.',
  'retention.save': 'Save changes',
  'retention.saving': 'Saving…',
  'retention.dirty': 'Editing {count} class(es)',
  'retention.saveOk': 'Saved {count} class(es).',

  'retention.applied': 'Saved — version {version}',
  'retention.conflict.title': 'Somebody else just changed this class',
  'retention.conflict.body':
    'You sent version {expected}; the row is now at version {actual}, holding {stored} days. ' +
    'Your change to THIS class was not applied — the field above still holds the {yours} days ' +
    'you typed, unsaved.',
  'retention.conflict.overwrite': 'Overwrite the {stored} days they just saved',
  'retention.range.required':
    '{dataClass} must be between {floor} and {max} days. Correct it before saving.',

  'retention.class.raw_activity': 'Raw activity events',
  'retention.class.heartbeat': 'Agent heartbeats',
  'retention.class.aggregate_hourly': 'Hourly aggregates',
  'retention.class.aggregate_daily': 'Daily aggregates',
  'retention.class.derived_daily': 'Work sessions and recorded time',
  'retention.class.screenshot': 'Screenshots',
  'retention.class.audit': 'Audit log',
  'retention.class.report': 'Exported reports',
  'retention.class.alert': 'Alerts',
  'retention.class.incident': 'Incidents',
  'retention.class.ai_insight': 'AI analyses',
  'retention.class.cache': 'Task cache',
  'retention.class.jobs': 'Finished background tasks',
  'retention.class.short_ops': 'Short-lived operations',
  'retention.class.recruiting_pii': 'Candidate data',
  'retention.class.hr_records': 'HR files and contracts',
  'retention.class.payroll': 'Payslips',
  'retention.class.geo_checkin': 'Location check-ins',

  // ── Company settings: country and currency (Task CS-2) ───────────────────
  'companySettings.title': 'Company settings',
  'companySettings.subject': 'the company settings',
  'companySettings.region.title': 'Country and currency',
  'companySettings.country': 'Country',
  'companySettings.currency': 'Currency',
  'companySettings.currency.hint':
    'Choosing a country suggests the currency it is usually paid in; you can change it.',
  'companySettings.engineNote':
    'The payroll engine currently applies Vietnamese rules (personal income tax, compulsory ' +
    'insurance). The country and currency here decide how amounts are shown and what the AI ' +
    'suggests. Setting a country other than Vietnam does NOT compute payroll under that ' +
    "country's law: payroll runs are refused until its rule set exists (CS-3).",
  'companySettings.timezone': 'Time zone',
  'companySettings.locale': 'Language',
  'companySettings.updated': 'Last changed',
  'companySettings.facts.hint':
    'The time zone and the language are synced from achau and are not edited on this screen.',
  'companySettings.save': 'Save settings',
  'companySettings.saving': 'Saving…',
  'companySettings.saved': 'Saved. It applies to payslips and AI suggestions from now on.',
  'companySettings.unsaved': 'Unsaved changes',

  /* Task U14 — the shared versioned-config lifecycle (KTD13). */
  'config.table.caption': 'Configuration versions, the active one, and each version’s settings',
  'config.column.version': 'Version',
  'config.column.state': 'State',
  'config.column.settings': 'Settings',
  'config.column.actions': 'Actions',
  'config.active': 'In force',
  'config.inactive': 'Not in force',
  'config.token': 'Precondition {token}',
  'config.token.none':
    'This read carried no precondition, so it cannot be edited — this is not a permission problem.',
  'config.token.unversioned':
    'This surface runs no conflict check: if two people edit, the later save wins.',
  'config.pending': 'Sending your changes…',
  'config.saved': 'Saved.',
  'config.diff.title': 'Unsaved changes, against the version in force',
  'config.diff.empty': 'Nothing changed yet. Only fields you actually edit are sent.',
  'config.diff.change': '{key}: {from} → {to}',
  'config.diff.absent': '(the version in force has no such field)',
  'config.conflict.title': 'Somebody else just changed this configuration',
  'config.conflict.token':
    'The server says the stored version is at {token}. Your change was NOT applied.',
  'config.conflict.token.unknown':
    'The server did not name the stored version. Your change was NOT applied.',
  'config.conflict.kept': 'Your draft was kept — nothing was lost.',
  'config.conflict.compare': '{key} — stored: {stored} · yours: {yours}',
  'config.conflict.stored.unknown': 'not read back yet',
  'config.conflict.loadStored': 'Read the stored version back',
  'config.conflict.overwrite': 'I understand, and I still want to overwrite their change',

  /* Task U15 — the collection policy editor and its rollout. */
  'policyEditor.title': 'Collection policy',
  'policyEditor.intro':
    'The collection policy decides what the Agent records on each person’s machine. It is the most sensitive configuration in the product; read it carefully before changing it.',
  'policyEditor.panel.title': 'Policy versions',
  'policyEditor.subject': 'the collection policy',
  'policyEditor.empty.title': 'This company has no collection policy yet',
  'policyEditor.empty.body':
    'Nothing has been written, so machines are running the built-in default. Give a version a name and create a draft. Activating PUBLISHES that draft, but does not make it the company default — see the note under the version table.',
  'policyEditor.create.name': 'Name for the new version',
  'policyEditor.create.hint': 'Type a name first; the server refuses a version without one.',
  'policyEditor.create.submit': 'Create draft',
  'policyEditor.row.edit': 'Edit this version',
  'policyEditor.row.activate': 'Activate',
  'policyEditor.activate.note':
    'Activating PUBLISHES a draft; it does not make it the company default. A policy governs machines only when it is the company default or an assignment points at it, and setting the default is not possible from this screen yet.',
  'policyEditor.form.title': 'Editing: {name} ({version})',
  'policyEditor.form.active':
    'This version IS in force. Saving mints a new version string and every machine has to acknowledge it again.',
  'policyEditor.form.draft':
    'This version is a draft: no machine has been served it, so saving does not move the version number.',
  'policyEditor.level.legend': 'Collection level',
  'policyEditor.level.1': 'Level 1 — application time and domains only',
  'policyEditor.level.2': 'Level 2 — plus window and page titles',
  'policyEditor.level.3': 'Level 3 — plus the most detailed events available',
  'policyEditor.idleShort': 'Short idle threshold (seconds)',
  'policyEditor.idleExtended': 'Extended idle threshold (seconds)',
  'policyEditor.idle.hint':
    'The extended threshold must be GREATER than the short one. If it is not, the save is refused and the wrong field is named.',
  'policyEditor.windowTitle': 'Collect window titles',
  'policyEditor.offHours': 'Collect off-hours activity',
  'policyEditor.usb': 'USB device monitoring',
  'policyEditor.file': 'File event monitoring',
  'policyEditor.screenshot': 'Screenshot capture',
  'policyEditor.basis': 'Lawful basis for screenshot capture',
  'policyEditor.basis.hint':
    'Turning screenshots on without a recorded lawful basis is refused, under the personal-data protection rules. Write it in the company’s own words, for the specific purpose.',
  'policyEditor.noListEditor':
    'The private lists, the blocked lists and the title-masking patterns cannot be edited here. One wrong character in a pattern would stop the whole company from keeping any window title, so editing them needs a tool of its own.',
  'policyEditor.noKeylog':
    'There is no keystroke-logging switch and there never will be: the system refuses it at every layer.',
  'policyEditor.save': 'Save to this version',
  'policyEditor.save.nothing': 'Nothing changed yet. Only fields you actually edit are sent.',
  'policyEditor.save.blocked':
    'Not sendable yet: a write is in flight, or a conflict above is waiting for your acknowledgement.',
  'policyEditor.discard': 'Discard unsaved changes',
  'policyEditor.rollout.title': 'How far the version in force has reached',
  'policyEditor.rollout.subject': 'the policy rollout',
  'policyEditor.rollout.noActive.title': 'No version is in force',
  'policyEditor.rollout.noActive.body':
    'Nothing has been activated AS THIS COMPANY’S DEFAULT, so there is no version for machines to acknowledge. A company can hold several activated versions and still have no default: activating is one decision and promoting to default is another, and this screen can send only the first.',
  'policyEditor.rollout.against':
    'Counted against version {version} — the version an Agent acknowledges.',
  'policyEditor.rollout.caption': 'Machine counts per bucket, against the version in force',
  'policyEditor.rollout.column.bucket': 'Bucket',
  'policyEditor.rollout.column.count': 'Machines',
  'policyEditor.rollout.acknowledged': 'Acknowledged',
  'policyEditor.rollout.pending': 'Served, not acknowledged (and online)',
  'policyEditor.rollout.stale': 'Silent (stale)',
  'policyEditor.rollout.offline': 'Offline',
  'policyEditor.rollout.online': 'Online',
  'policyEditor.rollout.total': 'Machines expected to report',
  'policyEditor.rollout.overlap':
    'The four numbers above are four questions, not four slices of a pie, so they need not add up: a machine that acknowledged and then went quiet is both acknowledged and silent. Only the served-but-not-acknowledged group requires the machine to be online — an Agent that has gone quiet is not a rollout in progress.',
  'policyEditor.rollout.asOf': 'Read at {at}.',
  'policyEditor.rollout.devices.subject': 'the machines that have not acknowledged',
  'policyEditor.rollout.devices.empty.title': 'Every machine has acknowledged this version',
  'policyEditor.rollout.devices.empty.body':
    'No machine expected to report is still on a different version string.',
  'policyEditor.rollout.devices.caption':
    'Machines that have not acknowledged the version in force',
  'policyEditor.rollout.devices.host': 'Hostname',
  'policyEditor.rollout.devices.liveness': 'Liveness',
  'policyEditor.rollout.devices.served': 'Served',
  'policyEditor.rollout.devices.acked': 'Acknowledged',
  'policyEditor.rollout.devices.never': 'never',
  'policyEditor.rollout.devices.showing':
    'Showing {shown} of {total} machines that have not acknowledged.',
  'policyEditor.rollout.devices.truncated':
    'The list stops at the first page, so the rest are not shown here; the counts above still cover all of them.',
  'policyEditor.rollout.devices.withheld':
    'Naming the machines needs workforce.manage_devices as well. The two codes are independent: administering the collection policy is not thereby administering the fleet.',

  /* ── Task U16 — alert rules and their automation actions ─────────────────── */
  'alertRules.title': 'Alert rules',
  'alertRules.intro':
    "This company's alert rule catalogue: which conditions raise an alert, how firings are grouped, and which automation actions follow. Editing what a rule says mints a new version; switching one on or off does not.",
  'alertRules.noQuota':
    'This form cannot build a quota-shaped rule: no field is a productivity score, a target or a ranking, and the field list is a closed set per trigger type. An alert is for noticing a problem, not for setting a person a quota.',
  'alertRules.subject': 'the alert rules',
  'alertRules.panel.title': 'Rule catalogue',
  'alertRules.panel.lineage': 'Versions of rule {key}',
  'alertRules.empty.title': 'This company has no alert rules yet',
  'alertRules.empty.body':
    'No rule has been created or seeded for this company. Create one to start.',
  'alertRules.back': 'Back to the catalogue',
  'alertRules.activate.note':
    'An alert rule has no separate Activate button: the status is a field of the form below and is stored with the Save button.',
  'alertRules.row.edit': 'Edit this rule',
  'alertRules.row.select': 'Select this version',
  'alertRules.create.open': 'New rule',
  'alertRules.create.cancel': 'Cancel',
  'alertRules.create.title': 'New rule',
  'alertRules.create.keyHint':
    'The rule code is generated from the name. If it collides with a rule already active, the system refuses rather than creating a second one on the same code.',
  'alertRules.create.statusHint':
    'A new rule defaults to draft: a rule that starts firing at named people the moment somebody presses a button is not a default to choose on an administrator’s behalf.',
  'alertRules.create.submit': 'Create rule',
  'alertRules.create.blocked':
    'A name, at least one condition and at least one action are needed before there is a request to send.',
  'alertRules.field.name': 'Rule name',
  'alertRules.field.trigger': 'Trigger type',
  'alertRules.field.severity': 'Severity',
  'alertRules.field.window': 'Observation window (minutes)',
  'alertRules.field.dedup': 'Deduplication window (minutes)',
  'alertRules.field.cooldown': 'Cooldown between firings (minutes)',
  'alertRules.field.groupBy': 'Group by',
  'alertRules.field.status': 'Status',
  'alertRules.trigger.noFactSource':
    'There is no data source for the trigger you picked. A rule on it would still be saved but would never run, so the form builds no condition and does not offer to create one.',
  'alertRules.condition.title': 'Conditions',
  'alertRules.condition.hint':
    'Every condition must hold. The field names are a closed list published by the trigger fact source — there is no free-text box for a field name.',
  'alertRules.condition.field': 'Field',
  'alertRules.condition.operator': 'Operator',
  'alertRules.condition.value': 'Value',
  'alertRules.condition.add': 'Add condition',
  'alertRules.condition.remove': 'Remove condition',
  'alertRules.condition.noFields': 'This trigger has no field to compare yet.',
  'alertRules.actions.legend': 'Automation actions',
  'alertRules.actions.hint':
    'Pick an action type from the closed list the server can persist. Each type renders its own parameters; a type with no parameters renders none.',
  'alertRules.action.notify_manager': 'Notify the manager',
  'alertRules.action.notify_it': 'Notify IT',
  'alertRules.action.notify_hr': 'Notify HR',
  'alertRules.action.create_incident': 'Open an incident',
  'alertRules.action.create_achau_task': 'Create a task in achau',
  'alertRules.action.note.notify_manager':
    'This action runs. The recipients come from the direct manager on the employee record, not from a parameter of the rule.',
  'alertRules.action.note.notify_it':
    'This action runs. The recipients come from the company IT group, not from a parameter of the rule.',
  'alertRules.action.note.notify_hr':
    'This action sends nothing yet: there is no notification channel to HR. A rule carrying it is still saved — read it as “nobody has been told yet”, never as “HR was told”.',
  'alertRules.action.note.create_incident':
    'The incident is already opened elsewhere: the system opens it at the same moment it writes the alert, against the company severity threshold. This action does not open a second one.',
  'alertRules.action.note.create_achau_task':
    'This action runs. The task text and its priority are derived from the alert and from the rule’s own severity, not from a parameter of the rule.',
  'alertRules.severity.informational': 'Informational',
  'alertRules.severity.low': 'Low',
  'alertRules.severity.medium': 'Medium',
  'alertRules.severity.high': 'High',
  'alertRules.severity.critical': 'Critical',
  'alertRules.form.title': 'Editing {name} (version {version})',
  'alertRules.form.trigger': 'Trigger {trigger} · status {status}',
  'alertRules.form.retired':
    'This version was superseded and is history: the server refuses a patch on it. Select the version in force to edit.',
  'alertRules.form.noTriggerEdit':
    'The trigger of an existing rule cannot be changed: it decides which fields exist, so changing it would turn every condition into a field that is not there. A different trigger is a different rule — create one.',
  'alertRules.status.hint':
    'Changing only the status moves the status on this row and mints no version. Changing it together with another field mints a new version carrying that status.',
  'alertRules.save': 'Save changes',
  'alertRules.save.nothing': 'Nothing has been edited yet.',
  'alertRules.save.blocked':
    'A version conflict on this version has not been acknowledged, or a request is in flight.',
  'alertRules.discard': 'Discard changes',
  'alertRules.noActive':
    'This rule has no version in force — it has only ever been a draft, or it is switched off — so the comparison below has nothing to compare against. What you edit here is measured against the version you selected, not against one that is running.',

  /* ── Task U17 — productivity scoring formulas ─────────────────────── */
  'nav.productivityFormulas': 'Productivity formulas',
  'formulas.title': 'Productivity scoring formula',
  'formulas.intro':
    "Each component's weight decides how the productivity score is computed. Every edit is a new version: a version any score already references is frozen for good, so a score computed in March still explains itself after the formula has moved on.",
  'formulas.noQuota':
    'A score produced by this formula always carries a per-component explanation and a confidence; missing data lowers the confidence rather than turning the score into a 0. It is context for a supportive conversation between a manager and an employee — never a target, a quota or a ranking. The nine components are a closed list: there is no free-text box here in which to name a quota.',
  'formulas.noConcurrencyCheck':
    'This screen cannot detect two people editing one formula at once: the later save wins and neither is warned. Agree with each other before editing.',
  'formulas.subject': 'the productivity formulas',
  'formulas.panel.title': 'Formula catalogue',
  'formulas.panel.lineage': 'Versions of formula {key}',
  'formulas.empty.title': 'This company has no productivity formula yet',
  'formulas.empty.body':
    'There is no formula yet, so nobody is being scored. The sample formula is: task result 25, productive time 20, on time 15, focus 15, attendance 15, quality 10 — a positive total of 100.00 — with a verified-risk penalty of 10 sitting outside that total. The button below builds exactly that, as a draft.',
  'formulas.create.default': 'Create the default formula',
  'formulas.close': 'Close the form',
  'formulas.back': 'Back to the catalogue',
  'formulas.row.edit': 'Edit this draft',
  'formulas.row.activate': 'Activate this version',
  'formulas.row.branch': 'New version from this one',
  'formulas.immutable.note':
    'Only a draft that has scored nobody can be edited in place. On every other version, the way to change it is to create a new version from it.',
  'formulas.retire.note':
    'This screen deliberately has no retire button: retiring the only version in force would mean nobody is scored at all. The way to change the formula in force is to create a new version and activate it.',
  'formulas.composer.patch': 'Editing draft {name}',
  'formulas.composer.from': 'New version based on {name} (v{version})',
  'formulas.composer.first': "The company's first formula",
  'formulas.field.name': 'Formula name',
  'formulas.field.description': 'Description',
  'formulas.field.weight': 'Weight (percentage points)',
  'formulas.components.legend': 'Components and weights',
  'formulas.components.hint':
    'Unticking a component removes it from the formula. A positive component may not be weighted 0 — untick it instead, because a component listed at no weight is one nobody can explain the presence of.',
  'formulas.direction.positive': 'positive, counts toward the 100',
  'formulas.direction.negative': 'negative, outside the 100',
  'formulas.weights.total': 'Positive weight total: {total} / {required}',
  'formulas.weights.empty': 'A formula must carry at least one component.',
  'formulas.weights.badWeight':
    'The weight of {key} is inadmissible ({problem}): it must be a finite number from 0 to 100 with at most 2 decimal places.',
  'formulas.weights.zeroPositive':
    '{key} is a positive component and may not be weighted 0 — untick it instead of leaving it at zero.',
  'formulas.weights.sum':
    'The positive weights total {total} and must be exactly {required}. This is only the early check; a wrong total is refused on save as well.',
  'formulas.weights.total.unknown':
    'Positive weight total: not computable — a weight is inadmissible, so there is no addition to report; the target is still {required}.',
  'formulas.weights.problem.not_finite': 'not a number',
  'formulas.weights.problem.negative': 'negative',
  'formulas.weights.problem.above_100': 'greater than 100',
  'formulas.weights.problem.too_many_decimals': 'more than 2 decimal places',
  'formulas.dropped':
    'The version this starts from carries {keys}, which the current list of nine no longer contains (the server marks it known: false). The wire enum is closed, so it cannot be sent back: the new version will not carry it, and its weight leaves the total with it. Redistribute that weight across the remaining components before saving — otherwise the total falls short and the refusal below will name weights you never touched.',
  'formulas.activateNow': 'Put it into force on creation',
  'formulas.activateNow.hint':
    'Sends activate: true on the same request, so creating the version and switching the rung over are one transaction — there is no moment in which the formula exists and applies to nobody. Left unticked, the new version is a draft and you activate it later from its own row.',
  'formulas.submit.create': 'Create the new version',
  'formulas.submit.patch': 'Save into the draft',
  'formulas.submit.blocked.weights':
    'Not sendable: the weights are not admissible — see the list above.',
  'formulas.submit.blocked.name': 'Not sendable: the formula needs a name.',
  'formulas.submit.blocked.pending': 'Not sendable: a request is in flight.',
  'formulas.discard': 'Discard changes',
  'formulas.help.title': 'What each component measures',
  'formulas.help.intro':
    'A weight is a decision about a person, so before raising a number, read what it is a fraction of. The sentences below describe the measurement that actually runs, including the places where it says plainly that it cannot measure.',
  'formulas.component.task_result': 'Task result',
  'formulas.component.on_time': 'On time',
  'formulas.component.quality': 'Quality',
  'formulas.component.productive_time': 'Productive time',
  'formulas.component.focus': 'Focus',
  'formulas.component.attendance': 'Attendance',
  'formulas.component.workload_balance': 'Workload balance',
  'formulas.component.verified_risk_penalty': 'Verified risk penalty',
  'formulas.component.approved_manual_adjustment': 'Approved manual adjustment',
  'formulas.component.help.task_result':
    'Average completion of the tasks whose deadline or completion falls inside the period. A closed task with no percentage counts as complete. No task in the period means this cannot be measured — an absence of tasks, not an absence of work — and the score does not become 0 because of it.',
  'formulas.component.help.on_time':
    'Tasks completed on or before their deadline, as a share of the tasks with a deadline completed in the period. No task with a deadline means no denominator, and that says nothing about a deadline being missed.',
  'formulas.component.help.quality':
    'Quality is NOT measured today: there is no review, no defect count and no rating anywhere. A formula naming this component lowers the confidence of every score and moves the score not at all. Pick it only to record that the signal is missing.',
  'formulas.component.help.productive_time':
    'Productive seconds as a share of the seconds in categories that make a productivity claim. Private time is excluded from both halves, so nobody is penalised for exercising a privacy right; the categories that claim nothing — meetings, training, development — are not under the line either.',
  'formulas.component.help.focus':
    'Active seconds as a share of observed online seconds. Idle time an approved explanation excuses is added back to the numerator, capped at the idle actually measured, so a generous approval cannot manufacture time that was never online. No online seconds means no denominator, and that does not say the person was idle.',
  'formulas.component.help.attendance':
    'Credited minutes as a share of rostered minutes, over the days that produced an attendance verdict. Approved work done outside the system still counts as credit; a day whose data could not be believed is in neither half of the fraction.',
  'formulas.component.help.workload_balance':
    'Logged time against rostered time, penalised in both directions: 60 hours on a 40-hour roster is as much a finding as 20. Hand-entered hours are counted and reported separately. No roster, or a company that does not use the timer, means this cannot be measured — a fact about how the company works, not a measurement of the person.',
  'formulas.component.help.verified_risk_penalty':
    'Penalty points from incidents that are resolved and carry a verified mark; nothing else contributes. This is the negative component: its weight sits outside the 100 and is subtracted on top. A weight of 0 is valid, and is how a company says it does not penalise risk.',
  'formulas.component.help.approved_manual_adjustment':
    'The approved manual adjustment in points, carried in the same component list so that one scoring run explains everything that went into the score, the human decision included. It is a positive component and counts toward the 100.',

  /* Task U18 — payroll formula versions (gross-to-net). */
  'payrollFormulas.title': 'Payroll formula',
  'payrollFormulas.intro':
    'Every parameter the payroll engine reads to reach net pay: the standard month, the overtime multiplier, the insurance rates, the contribution caps and the PIT tariff. They are company data, not constants in the code.',
  'payrollFormulas.subject': 'the payroll formula',
  'payrollFormulas.history':
    'The table below is the whole history, newest first: every version the company has, drafts awaiting confirmation, retired versions and the Vietnam 2026 set provisioned inactive included.',
  'payrollFormulas.immutable':
    'No version’s numbers can be edited: there is no edit and no delete. To change a number, write a new draft; a run already calculated keeps its own version. The only thing that changes on a version is its state: confirmed, activated, or replaced by another version.',
  'payrollFormulas.twoPerson':
    'The author cannot activate their own version. This screen only saves drafts; somebody else who manages payroll parameters checks every number and presses "Confirm & activate" with a written statement — stored with their name and the time.',
  'payrollFormulas.concurrency':
    'The "no conflict check" line under each version is only about saving drafts — and here no save wins over another: two people saving at once make two drafts. Confirming is checked: the server takes one at a time, and a second confirmation of the same version is refused.',
  'payrollFormulas.inForce.note':
    '"In force" is the version the server answers for today. Several versions can be activated at once, each for its own dates; a payroll run uses the activated version with the latest effective date not after the period’s first day.',
  'payrollFormulas.inForce.none':
    'No version is activated yet, so no payroll run can be created. A draft must first be confirmed and activated by a second person.',
  'payrollFormulas.inForce.subject': 'the version in force',
  'payrollFormulas.unconfirmed.title': 'Version {version} is in force but not confirmed',
  'payrollFormulas.unconfirmed.badge': 'Not confirmed',
  'payrollFormulas.unconfirmed.noWarning': 'No second person has confirmed these numbers.',
  'payrollFormulas.unconfirmed.howTo':
    'Somebody other than the author checks every number against the instrument, then presses "Confirm the numbers" on this version’s row below.',
  'payrollFormulas.confirmed.badge': 'Confirmed',
  'payrollFormulas.confirmed.by': 'by {who}, at {when}:',
  'payrollFormulas.confirmed.warningKept': 'Warning at provisioning (kept as history): {note}',
  'payrollFormulas.person.you': 'you',
  'payrollFormulas.person.id': 'employee #{id}',
  'payrollFormulas.person.unknown': 'somebody no longer in the system',
  'payrollFormulas.when.unknown': 'an unknown time',
  'payrollFormulas.panel.title': 'Parameter versions',
  'payrollFormulas.empty.title': 'This company has no formula version yet',
  'payrollFormulas.empty.body':
    'There is no parameter set yet, and this screen invents no numbers: every one is typed by a person or written by the Vietnam 2026 provisioning command. Enter the eight required parameters to save the first draft; somebody else will confirm and activate it.',
  'payrollFormulas.row.label': 'Version {version} · in force from {from}',
  'payrollFormulas.row.notInForce': 'Not the version in force',
  'payrollFormulas.row.inForceUnknown': 'Not known yet whether this is the version in force',
  'payrollFormulas.row.warning': 'Warning: {note}',
  'payrollFormulas.row.composeFrom': 'Draft from this version',
  'payrollFormulas.lifecycle.active': 'Activated',
  'payrollFormulas.lifecycle.inactive': 'Not activated',
  'payrollFormulas.author.seed': 'Written by the provisioning command — no author',
  'payrollFormulas.author.you': 'You wrote this version',
  'payrollFormulas.author.other': 'Author: employee #{id}',
  'payrollFormulas.close': 'Close',
  'payrollFormulas.create.first': 'Draft the first version',
  'payrollFormulas.create.draft': 'Draft a new version',
  'payrollFormulas.params.title': 'Parameters of version {version}',
  'payrollFormulas.params.caption': 'The parameters of version {version}',
  'payrollFormulas.params.column.name': 'Parameter',
  'payrollFormulas.params.column.value': 'Value',
  'payrollFormulas.effectiveFrom': 'In force from {from}.',
  'payrollFormulas.usage.unknown':
    'Which runs use this version is unknown — this account cannot read the list of runs.',
  'payrollFormulas.usage.none': 'No run references this version yet.',
  'payrollFormulas.usage.count': 'Referenced by {count} run(s).',
  'payrollFormulas.param.gio_chuan': 'Standard hours per month',
  'payrollFormulas.param.ot_coefficient': 'Overtime multiplier',
  'payrollFormulas.param.bhxh_nv_pct': 'Social insurance — employee',
  'payrollFormulas.param.bhyt_nv_pct': 'Health insurance — employee',
  'payrollFormulas.param.kpcd_nv_pct': 'Trade-union fund — employee',
  'payrollFormulas.param.bhxh_cty_pct': 'Social insurance — employer',
  'payrollFormulas.param.bhyt_cty_pct': 'Health insurance — employer',
  'payrollFormulas.param.kpcd_cty_pct': 'Trade-union fund — employer',
  'payrollFormulas.param.bhtn_nv_pct': 'Unemployment insurance — employee',
  'payrollFormulas.param.bhtn_cty_pct': 'Unemployment insurance — employer',
  'payrollFormulas.param.bhxh_bhyt_cap_vnd': 'Social/health contribution cap',
  'payrollFormulas.param.bhtn_cap_vnd': 'Unemployment contribution cap',
  'payrollFormulas.param.pit_personal_deduction_vnd': 'Personal deduction',
  'payrollFormulas.param.pit_dependent_deduction_vnd': 'Dependent deduction',
  'payrollFormulas.param.absent': 'this version does not carry that parameter',
  'payrollFormulas.value.fraction': '{raw} ({percent}%)',
  'payrollFormulas.value.vnd': '{value} VND/month',
  'payrollFormulas.value.hours': '{value} hours',
  'payrollFormulas.grounds.title': 'This company’s deduction grounds',
  'payrollFormulas.grounds.hint':
    'These are the basis codes a penalty may name on a payroll run stamped with this version. The list and the cap are this company’s configuration, not a constant in the source.',
  'payrollFormulas.grounds.caption': 'Deduction grounds of version {version}',
  'payrollFormulas.grounds.column.code': 'Code',
  'payrollFormulas.grounds.column.name': 'Ground',
  'payrollFormulas.grounds.column.appliesTo': 'Deducted from',
  'payrollFormulas.grounds.column.capped': 'Capped',
  'payrollFormulas.grounds.appliesTo.gross_before_tax': 'Gross pay, before tax',
  'payrollFormulas.grounds.appliesTo.net_after_tax': 'Net pay, after tax',
  'payrollFormulas.grounds.capped.yes': 'Yes',
  'payrollFormulas.grounds.capped.no': 'No',
  'payrollFormulas.grounds.absent':
    'This version configures no deduction ground — every penalty on a payroll run stamped with it is refused.',
  'payrollFormulas.cap.value':
    'Deduction cap deduction_cap_pct: {raw} ({percent}% of the pay remaining after compulsory insurance and personal income tax).',
  'payrollFormulas.cap.absent':
    'Deduction cap deduction_cap_pct: this version sets no cap — a ground marked capped is refused.',
  'payrollFormulas.brackets.title': 'Progressive PIT tariff',
  'payrollFormulas.brackets.absent': 'This version carries no tariff.',
  'payrollFormulas.brackets.open': 'Above the last bound: {percent}%',
  'payrollFormulas.brackets.upTo': 'Up to {bound} VND/month: {percent}%',
  'payrollFormulas.brackets.hint':
    'Brackets run from lowest to highest. Leave the last bracket’s bound empty to make it the open one. Rates are fractions: 0.05 is 5%.',
  'payrollFormulas.brackets.bound': 'Bracket {index} — upper bound (VND/month)',
  'payrollFormulas.brackets.rate': 'Bracket {index} — rate',
  'payrollFormulas.brackets.remove': 'Remove this bracket',
  'payrollFormulas.brackets.add': 'Add a bracket',
  'payrollFormulas.brackets.invalid': 'A bracket is incomplete or is not a number.',
  'payrollFormulas.composer.first': 'The company’s first draft',
  'payrollFormulas.composer.from':
    'A new draft, starting from the numbers of version {version} — saved only if it differs from that version',
  'payrollFormulas.composer.draft':
    'Once saved, this is a draft: not activated, not confirmed, and it changes nothing in payroll until somebody else presses "Confirm & activate".',
  'payrollFormulas.composer.blank':
    'The fields are blank because there is no version to start from. This screen fills in no statutory number for you.',
  'payrollFormulas.composer.noteLost.title': 'The draft will not carry the original’s warning',
  'payrollFormulas.composer.noteLost.body':
    'The original version carries the provisioning command’s not-confirmed warning. The new draft does not, so whoever confirms the draft will never read it. If you only mean to confirm those same numbers, use "Confirm & activate" on the original’s row instead.',
  'payrollFormulas.composer.effectiveFrom.label': 'In force from',
  'payrollFormulas.composer.effectiveFrom.hint':
    'The first day these numbers apply. Required — the screen never picks a date.',
  'payrollFormulas.composer.fields': 'Parameters',
  'payrollFormulas.composer.optional': '(optional)',
  'payrollFormulas.composer.notANumber': 'This value is not a number.',
  'payrollFormulas.composer.changes.title': 'What this draft changes against version {version}',
  'payrollFormulas.composer.changes.none':
    'No parameter changed yet. Every parameter of version {version} — including the ones with no field above, such as the deduction grounds and the deduction cap — is sent back unchanged.',
  'payrollFormulas.composer.changes.entry': '{key}: {from} → {to}',
  'payrollFormulas.composer.changes.absent': '(the original version has no such parameter)',
  'payrollFormulas.submit': 'Save the draft for somebody else to confirm',
  'payrollFormulas.blocked.invalid': 'A field is not a number yet. Fix it to save.',
  'payrollFormulas.blocked.missing': 'Required parameters are still missing: {keys}.',
  'payrollFormulas.blocked.date': 'No effective date chosen yet.',
  'payrollFormulas.blocked.unchanged':
    'Nothing differs from the original version, so there is nothing to save. To confirm these numbers, use "Confirm & activate" on that version itself — an identical copy only loses the warning.',
  'payrollFormulas.blocked.pending': 'Sending…',
  'payrollFormulas.discard': 'Discard changes',
  'payrollFormulas.confirm.openInactive': 'Confirm & activate',
  'payrollFormulas.confirm.openActive': 'Confirm the numbers',
  'payrollFormulas.confirm.self': 'You wrote this version, so somebody else must confirm it.',
  'payrollFormulas.confirm.already': 'This version is already confirmed.',
  'payrollFormulas.confirm.titleInactive':
    'Confirm and activate version {version} (in force from {from})',
  'payrollFormulas.confirm.titleActive':
    'Confirm the numbers of version {version} (in force from {from})',
  'payrollFormulas.confirm.effectInactive':
    'When you confirm, this version is activated, and every activated version with a lower number dated {from} or later stops. The server refuses if an activated version with a higher number already applies from this date or earlier. A run already created — even a draft — keeps its old version.',
  'payrollFormulas.confirm.effectActive':
    'This version is already activated. Confirming only records your statement, your name and the time; no version stops.',
  'payrollFormulas.confirm.warningTitle': 'The warning this version carries',
  'payrollFormulas.confirm.label': 'What did you check, against which instrument?',
  'payrollFormulas.confirm.rule':
    '{min} to {max} characters, not counting leading and trailing spaces. The statement is stored verbatim with your name and the time.',
  'payrollFormulas.confirm.length':
    'The statement needs {min} to {max} characters, not counting leading and trailing spaces.',
  'payrollFormulas.confirm.submitInactive': 'Confirm & activate version {version}',
  'payrollFormulas.confirm.submitActive': 'Confirm version {version}',
  'payrollFormulas.confirm.cancel': 'Cancel',
  'payrollFormulas.confirm.refused': 'The server refused ({code}). Its reason: {reason}',
  'payrollFormulas.confirm.refused.noReason': 'none given — see the note in the versions section.',
};

export const CATALOGUES = { vi, en } as const;

export type Locale = keyof typeof CATALOGUES;

export const LOCALES: readonly Locale[] = ['vi', 'en'];

/** The default, and it is not a coin toss: spec 01 §19 says Vietnamese first. */
export const DEFAULT_LOCALE: Locale = 'vi';
